import FastlyImage from '@components/common/fastly-image'
import {
  FileWithBadge,
  MonitorWithInstructor,
} from '@components/common/icons/custom-icons'
import {
  ActivitiesType,
  ActivityCardType,
  CourseDescriptionFoldType,
  CourseFirstFoldType,
  CourseNameType,
  courseResourceType,
  FaqType,
  recommendedCoursesType,
  SkillsYouWillGainFoldType,
  TestimonialType,
} from '@lib/types/course-landing-page'
import { ModuleType } from '@lib/types/course-modules'
import { BarChart, Clock, FileText } from 'react-feather'
import { ELA_TESTIMONIALS, toTestimonialsFoldData } from './testimonials'

export const COURSE_NAMES = {
  SCRATCH_PROGRAMMING: 'SCRATCH_PROGRAMMING',
  PYTHON_FOR_KIDS: 'PYTHON_FOR_KIDS',
  ROBOTICS: 'ROBOTICS',
  ROBLOX: 'ROBLOX',
  AP_COMPUTER_SCIENCE_A: 'AP_COMPUTER_SCIENCE_A',
  HPE_CODEWARS_CODE_BATTLE: 'HPE_CODEWARS_CODE_BATTLE',
  HPE_CODEWARS_HACKATHON: 'HPE_CODEWARS_HACKATHON',
  AI_AND_ML: 'AI_AND_ML',
  AI_GENIUS: 'AI_GENIUS',
  AI_EXPERT: 'AI_EXPERT',
  GAME_DEV: 'GAME_DEV',
  APP_DEV: 'APP_DEV',
  WEB_DEV: 'WEB_DEV',
  THUNKABLE: 'THUNKABLE',
  JAVA: 'JAVA',
  TRINKET: 'TRINKET',
  PYGAME: 'PYGAME',
  ANIMATION: 'ANIMATION',
  TURTLE: 'TURTLE',
  MIT_APP_INVENTOR: 'MIT_APP_INVENTOR',
  BOOTSTRAP: 'BOOTSTRAP',
  JAVASCRIPT: 'JAVASCRIPT',
  CSS: 'CSS',
  HTML: 'HTML',
  AICODE101: 'AICODE101',
  CODE_ORG: 'CODE_ORG',
  PICTOBLOX: 'PICTOBLOX',
  TEACHABLE_MACHINE: 'TEACHABLE_MACHINE',
  GOOGLE_COLAB: 'GOOGLE_COLAB',
  NODE_JS: 'NODE_JS',
  VISUAL_STUDIO_CODE: 'VISUAL_STUDIO_CODE',
  SQL: 'SQL',
  REACT_JS: 'REACT_JS',
  GIT: 'GIT',
  GITHUB: 'GITHUB',
  PUBLIC_SPEAKING_GRADE_3_5: 'PUBLIC_SPEAKING_GRADE_3_5',
  PUBLIC_SPEAKING_GRADE_1_2: 'PUBLIC_SPEAKING_GRADE_1_2',
  CODING_GRADE_1_3: 'CODING_GRADE_1_3',
  CODING_GRADE_4_5: 'CODING_GRADE_4_5',
  CODING_GRADE_6_8: 'CODING_GRADE_6_8',
  CODING_GRADE_9_10: 'CODING_GRADE_9_10',
  CODING_GRADE_11_12: 'CODING_GRADE_11_12',
}

export const COURSE_TITLES = {
  PYTHON_FOR_KIDS: 'Python Champion',
  SCRATCH_PROGRAMMING: 'Scratch Programming with AI',
  ROBLOX: 'Roblox Champion',
  AP_COMPUTER_SCIENCE_A: 'AP Computer Science A',
  HPE_CODEWARS_CODE_BATTLE: 'HPE CODEWARS CODE BATTLE',
  HPE_CODEWARS_HACKATHON: 'HPE CODEWARS HACKATHON',
  AI_AND_ML: 'AI & Machine Learning for Kids and Teens',
  THUNKABLE: 'Thunkable for Kids and Teens',
  JAVA: 'Java for Kids and Teens',
  TRINKET: 'Trinket for Kids and Teens',
  PYGAME: 'Pygame for Kids and Teens',
  ANIMATION: 'Animation for Kids',
  TURTLE: 'Turtle for Kids and Teens',
  MIT_APP_INVENTOR: 'MIT App Inventor for Kids and Teens',
  BOOTSTRAP: 'Bootstrap for Kids and Teens',
  JAVASCRIPT: 'JavaScript for Kids and Teens',
  CSS: 'CSS for Kids and Teens',
  HTML: 'HTML for Kids and Teens',
  AICODE101: 'AI Code 101 for Kids',
  CODE_ORG: 'Code.org Courses for Kids',
  PICTOBLOX: 'PictoBlox for Kids',
  TEACHABLE_MACHINE: 'Teachable Machine for Kids',
  GOOGLE_COLAB: 'Google Colab for Kids',
  NODE_JS: 'Node.js for Kids',
  VISUAL_STUDIO_CODE: 'Visual Studio Code for Kids',
  SQL: 'SQL for Kids',
  REACT_JS: 'React.js for kids',
  GIT: 'Git for kids',
  GITHUB: 'Github for kids',
  AI_GENIUS: 'AI Genius',
  AI_EXPERT: 'AI Expert',
  GAME_DEV: 'Game Development for Kids',
  APP_DEV: 'App Development for Kids',
  WEB_DEV: 'Website Development',
  PUBLIC_SPEAKING_GRADE_3_5: 'Public Speaking for Grade 3-5',
  PUBLIC_SPEAKING_GRADE_1_2: 'Public Speaking for Grade 1-2',
  CODING_GRADE_1_3: 'Coding for Grade 1-3',
  CODING_GRADE_4_5: 'Coding for Grade 4-5',
  CODING_GRADE_6_8: 'Coding for Grade 6-8',
  CODING_GRADE_9_10: 'Coding for Grade 9-10',
  CODING_GRADE_11_12: 'Coding for Grade 11-12',
}

export const COURSE_INFO: Record<CourseNameType, recommendedCoursesType> = {
  SCRATCH_PROGRAMMING: {
    courseName: COURSE_TITLES['SCRATCH_PROGRAMMING'],
    link: '/courses/scratch-programming/',
    courseQuery: 'scratch-programming-trial-class',
    image: 'scratch-course',
    grade: 'Grade 1-8',
    activity: '50+',
    lesson: 36,
    duration: '2 - 4 months',
    learningOutcome: [
      'Learn the Coding Language',
      'Make Stuff Move (Animations & Games)',
      'Meet the Super-Smart Machines (AI Basics)',
      'Design Interactive Stories and Art',
    ],
  },
  AI_GENIUS: {
    courseName: COURSE_TITLES['AI_GENIUS'],
    courseQuery: 'ai-trial-class',
    image: 'ai-genuis-course',
    grade: 'Grade 3-7',
    activity: '48+',
    lesson: 48,
    duration: '4 - 6 months',
    learningOutcome: [
      'Create AI-powered games and mobile applications',
      'Develop translators and speech-to-text tools',
      'Create tools like carbon calculators',
      'Develop a chatbot using OpenAI integration',
    ],
  },
  AI_EXPERT: {
    courseName: COURSE_TITLES['AI_EXPERT'],
    courseQuery: 'ai-trial-class',
    image: 'ai-expert-course',
    grade: 'Grade 8-12',
    activity: '48+',
    lesson: 48,
    duration: '4 - 6 months',
    learningOutcome: [
      'Learn image processing with OpenCV',
      'Turn text into stunning AI-generated images',
      'Master Python for Artificial Intelligence',
      'Build real world Projects using LLMs',
    ],
  },
  PYTHON_FOR_KIDS: {
    courseName: COURSE_TITLES['PYTHON_FOR_KIDS'],
    link: '/courses/python-for-kids/',
    courseQuery: 'python-for-kids-trial-class',
    image: 'pyhton-course',
    grade: 'Grade 6-12',
    activity: '90+',
    lesson: 44,
    duration: '4 - 6 months',
    learningOutcome: [
      'Master Python programming',
      'Build interactive games',
      'Develop GUI applications',
      'Understand & apply OOP concepts',
    ],
  },
  GAME_DEV: {
    courseName: COURSE_TITLES['GAME_DEV'],
    link: '/courses/game-development/',
    courseQuery: 'game-development-trial-class',
    image: 'game-dev-course',
    grade: 'Grade 4-8',
    activity: '50+',
    lesson: 36,
    duration: '2 - 4 months',
    learningOutcome: [
      'Code Like a Pro (For Games)',
      'Become a Game Creator (Scratch & Javascript)',
      'Think Like a Gamer (But Smarter)',
      'Animate Characters and Objects (Bringing Games to Life)',
    ],
  },
  WEB_DEV: {
    courseName: COURSE_TITLES['WEB_DEV'],
    link: '/courses/web-development/',
    courseQuery: 'web-development-trial-class',
    image: 'web-dev-course',
    grade: 'Grade 8-12',
    activity: '90+',
    lesson: 90,
    duration: '10 - 12 months',
    learningOutcome: [
      'Build responsive websites',
      'Develop interactive web pages',
      'Master website development',
      'Implement best practices in web dev',
    ],
  },
  APP_DEV: {
    courseName: COURSE_TITLES['APP_DEV'],
    link: '/courses/app-development/',
    courseQuery: 'app-development-trial-class',
    image: 'app-dev-course',
    grade: 'Grade 4-8',
    activity: '50+',
    lesson: 36,
    duration: '2 - 4 months',
    learningOutcome: [
      'Build Interactive Interfaces',
      'Design Awesome Apps',
      'Make Your App Super Smart (Integrate camera, accelerometers, GPS, etc.)',
      'Share Your App with the World',
    ],
  },
}

export const COURSE_FIRST_FOLD: Record<CourseNameType, CourseFirstFoldType> = {
  SCRATCH_PROGRAMMING: {
    name: 'Learn Scratch Programming with Top Coding Course Instructors',
    description:
      'Codingal offers online Scratch programming classes for kids with expert Computer Science instructors',
    activities: COURSE_INFO['SCRATCH_PROGRAMMING'].activity,
    modules: 6,
    lessons: COURSE_INFO['SCRATCH_PROGRAMMING'].lesson,
    quizzes: 36,
    certificate: 'Scratch developer certificate',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/codingal-scratch-curriculum.pdf',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: COURSE_INFO['SCRATCH_PROGRAMMING'].grade,
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: COURSE_INFO['SCRATCH_PROGRAMMING'].duration,
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '75+ activities & projects',
      },
    ],
  },
  HPE_CODEWARS_CODE_BATTLE: {
    name: 'Ace the HPE CodeWars Code Battle competition',
    description:
      'Learn the fundamentals of algorithm to solve complex coding problems',
    modules: 2,
    lessons: 12,
    quizzes: 100,
    certificate: 'Code Battle course certificate',
  },
  HPE_CODEWARS_HACKATHON: {
    name: 'Ace the HPE CodeWars Hackathon',
    description:
      'Learn to build your coding project with computer science expert',
    modules: 2,
    lessons: 12,
    quizzes: 100,
    certificate: 'Hackathon course certificate',
  },
  PYTHON_FOR_KIDS: {
    name:
      'Learn Python with the Top Coding Course Instructors for Kids and Teens',
    description:
      'Online 1:1 Python programming classes for kids with expert Computer Science teachers',
    activities: '100+',
    modules: 7,
    lessons: 44,
    quizzes: 44,
    certificate: 'Python developer certificate',
    curriculumPDFLink:
      'https://codingal.s3.amazonaws.com/media/curriculum/pdfs/2026/07/25/Python_Champion_2026.pdf',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/cddcb716-dea9-4938-86cc-ab4e71611520?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 6-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '5 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  ROBLOX: {
    name: 'Learn Lua with the Top Roblox Coding Courses for Kids and Teens',
    description:
      'Learn to create and publish games on Roblox Studio. Codingal offers online 1:1 coding classes for Roblox coding with expert Computer Science teachers.',
    activities: '30+',
    modules: 6,
    lessons: 36,
    quizzes: 36,
    certificate: 'Roblox game developer certificate',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/roblox-curriculum.pdf',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 4-8',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '4 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '30+ activities & projects',
      },
    ],
  },
  AP_COMPUTER_SCIENCE_A: {
    name: 'Learn AP Computer Science A  with the Top Coding Courses',
    description:
      'Prepare for the AP Computer Science A exam with Codingal. Online 1:1 classes for teens with expert instructors',
    activities: '100+',
    modules: 8,
    lessons: 48,
    quizzes: 48,
    certificate: 'AP Computer Science A certificate',
    curriculumPDFLink:
      'https://codingal.s3.amazonaws.com/media/curriculum/pdfs/2026/07/25/AP_Computer_Science_A_2026.pdf',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 11-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '5 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  AI_AND_ML: {
    name:
      'Learn Artificial Intelligence with the Top ML and AI Courses for Kids & Teens',
    description:
      'Our AI and Machine Learning courses introduce the exciting world of AI to students in grades 3-12. Through engaging, hands-on projects, kids explore real-life applications of AI! We offer two courses—AI Genius for kids and AI Expert for teens. Learners will discover concepts used in virtual assistants, self-driving cars, healthcare, and more. These courses make complex ideas easy to understand, spark curiosity, and build skills for the future!',
    activities: '50+',
    modules: 8,
    lessons: 48,
    quizzes: 48,
    certificate: 'AI & ML expert certificates',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/48ca1d04-ab79-4582-9d2f-4dd8fcfa3d74?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 3-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '3-6 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '50+ activities & projects',
      },
    ],
  },
  THUNKABLE: {
    name:
      'Learn Thunkable with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'At Codingal, kids dive into app development with Thunkable, a fun, block-based platform that brings their ideas to life! This K-12-aligned course goes beyond basic coding, students build real apps they’ll be proud to share. They’ll create motion-based games, design custom screens, track location data, and even integrate AI features like text-to-speech and language translation. Using tools like Firebase and Google Sheets, students manage real-time data for apps like weather predictors, health trackers, QR scanners, and more, all while having fun. With hands-on projects, creativity, and real-world applications, this course empowers kids to solve problems with innovative apps and grow their love for technology.',
    activities: '100+',
    displayName: 'Thunkable',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/b9b3a303-0adf-409b-addc-dd8cd9d44e6c?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    modules: 6,
    lessons: 36,
    quizzes: 36,
    certificate: 'Thunkable expert certificates',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '45-60 minutes',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  JAVA: {
    name:
      'Learn Java with the Top Coding Course Instructors  for Kids and Teens',
    description:
      'Embark on an exciting journey into the world of Java with Codingal, where kids and teens learn from top coding instructors through a structured, engaging curriculum designed to build a strong foundation in programming. Starting with core concepts like data types, operators, and control structures, your child will gradually progress to mastering loops, arrays, functions, and even advanced topics like recursion and algorithmic thinking. Every lesson is carefully crafted to boost their confidence, spark creativity, and enhance problem-solving skills through interactive coding challenges and real-world projects. What your child will learn includes mastering core programming concepts, developing essential problem-solving tools, and applying advanced logic techniques—all while having fun and staying engaged. Whether your child is just starting out or looking to take their skills to the next level, learning Java at Codingal sets them up for success not just in tech, but in any future career that values analytical thinking and creativity.',
    activities: '50+',
    displayName: 'Java',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/dbbe6c96-fc3b-420f-8449-0e4360613d75?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    modules: 8,
    quizzes: 0,
    certificate: 'Java expert certificates',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 8-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '2-4 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '50+ activities & projects',
      },
    ],
  },
  TRINKET: {
    name:
      'Learn Trinket with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'Step into the world of Python coding with Trinket, where creativity meets real programming. Trinket’s browser-based environment allows students to write Python code and instantly see their work come to life—making it one of the easiest and most engaging ways for kids to begin text-based coding. Through guided activities, learners explore Python by creating colorful Turtle-graphics designs, drawing shapes, generating patterns, and building simple interactive animations. With every project, students develop essential programming foundations such as loops, variables, sequencing, and event-based logic. They experiment, debug, and refine their ideas in real time, helping them understand how code translates into visual outcomes. These 10+ beginner-friendly Python activities strengthen problem-solving skills, spark creativity, and build confidence in writing actual code. Perfect for young learners transitioning from visual coding to text-based programming, Trinket gives kids a fun, safe, and interactive space to learn Python—no installation, no setup, just pure hands-on coding.',
    activities: '100+',
    displayName: 'Trinket',
    modules: 1,
    lessons: 6,
    quizzes: 6,
    certificate: 'Trinket expert certificates',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/90c96581-5eb9-41bd-8ea8-8a4f2e3a8e88?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Class Duration',
        description: '45-60 minutes',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '100+ activities & projects',
      },
    ],
  },
  PYGAME: {
    name:
      'Learn Pygame with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'Empower your child to move from consuming content to creating it. Codingal’s website development course teaches Pygame to kids and teens the building blocks of the web, enabling them to design and structure their own websites. With guidance from expert instructors, students will embark on a journey that combines creativity with foundational coding skills, setting the stage for future exploration in AI & coding.',
    activities: '40+',
    displayName: 'Pygame',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/d10ce52d-3a73-458f-a720-ef30e5f2df26?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    modules: 6,
    lessons: 36,
    quizzes: 36,
    certificate: 'Pygame expert certificates',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Class Duration',
        description: '45-60 minutes',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '40+ activities & projects',
      },
    ],
  },
  ANIMATION: {
    name:
      'Learn Animation with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      "Step into the world of engaging animations with the Codingal curriculum, where creativity meets coding! Using platforms like Sprite Lab, Play Lab, Game Lab, and Scratch, students transform their ideas into dynamic animations and interactive stories. Whether it's making characters groove in a Dance Party, orchestrating a hilarious Banana Chase, or animating a thrilling Space Adventure, these projects teach essential programming concepts like loops, events, and conditions; all through fun, hands-on activities. Students create interactive animations like a dazzling Fireworks Show, a whimsical Underwater Scene, or even a quirky Monster Dance-Off. By animating sprites, syncing movements to music, and designing custom effects, they bring their imagination to life while learning coding fundamentals. With every project, learners develop problem-solving skills and discover the magic of animation, making each session an exciting journey of creativity and innovation! Moving on as students transition to text-based coding, they also explore basic  Animation concepts that are commonly used  in websites using HTML CSS and basic 2D animations using Pygame while they learn  Python. Perfect for kids aged 7–12, these lessons build creativity, coding confidence, and digital illustration skills.",
    activities: '100+',
    displayName: 'Animation',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/3288169a-987a-4a4d-a3d7-9d8b7e9bcb99?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    quizzes: 36,
    certificate: 'Animation expert certificates',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 minutes',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  TURTLE: {
    name:
      'Learn Python Turtle with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'Dive into the exciting world of Python Turtle, where coding becomes a canvas for creativity! With Codingal’s interactive curriculum, students bring their ideas to life through colorful drawings, animations, and shapes — all created using simple lines of Python code. In every session, learners command their virtual “turtle” to move, turn, and draw on the screen — building designs like spirals, flowers, stars, and geometric patterns. As they experiment with loops, angles, and colors, kids learn the fundamentals of programming in a fun and visual way. Each project introduces essential coding concepts such as loops, variables, conditionals, and functions, while encouraging imagination and logical thinking. From creating a Fireworks Display or a Spinning Mandala to designing an Underwater Scene or Digital Rangoli, students learn to code while expressing their artistic ideas through patterns and motion. As students progress, they transition from simple drawings to interactive designs and mini games, applying their knowledge to Pygame and other creative Python modules. They also explore how Python integrates with HTML and CSS, giving them a glimpse into real-world programming and web-based visualization. Perfect for kids aged 7–12, Codingal’s Python Turtle lessons build a solid foundation in programming, enhance creative thinking, and boost confidence — all while making learning visually engaging and incredibly fun',
    activities: '100+',
    displayName: 'Turtle (Python)',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/f667dcdb-9a3f-45e1-8bf8-bf939c6d4f78?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  MIT_APP_INVENTOR: {
    name:
      'Learn MIT App Inventor with the Top Coding Course Instructors for Kids and Teens',
    description:
      'Want your child to go beyond just using apps? With MIT App Inventor, they’ll learn how to build them. Codingal’s MIT App Inventor course for kids and teens introduces the fundamentals of mobile app development using a drag-and-drop coding environment, powered by real programming logic. It’s the perfect platform for beginners—and with our expert instructors, your child will create real apps while gaining essential AI & coding skills that will prepare them for the digital future.',
    activities: '100+',
    displayName: 'MIT App Inventor',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/53ba5459-4647-45f4-afde-3ffbbf1f6cd8?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  BOOTSTRAP: {
    name:
      'Learn Bootstrap with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'Empower your child to move from consuming content to creating it. Codingal’s website development course teaches Bootstrap to kids and teens the building blocks of the web, enabling them to design and structure their own websites. With guidance from expert instructors, students will embark on a journey that combines creativity with foundational coding skills, setting the stage for future exploration in AI & coding.',
    activities: '40+',
    modules: 6,
    lessons: 36,
    quizzes: 36,
    displayName: 'Bootstrap',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/4008aa0b-5a5a-46b4-a52e-c82068f59334?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 minutes',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '40+ activities & projects',
      },
    ],
  },
  JAVASCRIPT: {
    name:
      'Learn Javascript with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'Step into the fascinating world of JavaScript, where coding becomes a canvas for imagination and innovation! With Codingal’s interactive curriculum, students bring their ideas to life through animations, visuals, and interactive web projects  all created using simple lines of HTML, CSS, and JavaScript. In every session, learners use JavaScript to make web pages move, respond, and react — building creative designs, interactive stories, and dynamic effects. As they experiment with loops, functions, events, and logic, kids learn the fundamentals of programming in a fun, visual, and hands-on way. Each project introduces essential coding concepts such as variables, conditional statements, functions, and event handling, while encouraging creativity and problem-solving. From creating a Fireworks Animation or an Interactive Quiz to build a Mini Game, students learn how JavaScript powers the interactive web experiences they see every day. As students progress, they transition from visual coding platforms like Scratch and Play Lab to text-based environments such as Replit, p5.js, or CodePen. They learn how HTML and CSS work together with JavaScript to design and style complete web pages — preparing them for real-world web development. Perfect for kids aged 8–14, Codingal’s JavaScript lessons build a strong foundation in programming, boost creative confidence, and make learning code as exciting as playing a game! 🎮✨',
    activities: '100+',
    displayName: 'Javascript',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/5f4279f0-2dc9-4f6c-b7fe-be8d30a49407?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 minutes',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  CSS: {
    name:
      'Learn CSS with the Top AI & Coding Course Instructors for Kids and Teens',
    description:
      "Codingal offers online CSS classes for kids and teens, designed to make web design both fun and educational. Learning CSS is a creative journey that allows children to transform plain webpages into vibrant, interactive designs. As part of our web development program for young learners, we offer a six-lesson class where students explore background effects, text styling, and advanced techniques that make their web creations more powerful. They also learn how to structure pages using HTML and enhance their appearance with CSS, gaining a strong foundation in web development. For children, CSS is like a digital paintbrush. It helps them make websites colorful, attractive, and responsive. With these skills, students can change fonts, adjust spacing, add animations, and control how their webpages look on different devices. This process introduces them to interactive web design for kids, encourages creative problem-solving, and helps them develop design skills that prepare them to be future web designers. It's truly the best way to learn CSS for kids while having fun with coding.",
    activities: '12+',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/583f8884-0883-4596-95ee-b79afbe6ce8d?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 4-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '12+ activities & projects',
      },
    ],
  },
  HTML: {
    name:
      'Learn HTML with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'Empower your child to move from consuming content to creating it. Codingal’s website development course teaches HTML to kids and teens the building blocks of the web, enabling them to design and structure their own websites. With guidance from expert instructors, students will embark on a journey that combines creativity with foundational coding skills, setting the stage for future exploration in AI & coding.',
    activities: '40+',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/bb487d79-e320-4ffc-8a55-26d997ee634f?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    modules: 5,
    lessons: 36,
    quizzes: 36,
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '40+ activities & projects',
      },
    ],
  },
  AICODE101: {
    name:
      'Learn AI Code 101 with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'At Codingal, we make learning AI an exciting adventure for kids! AI Code 101 for Kids introduces children to the world of artificial intelligence through fun, interactive projects that spark creativity and curiosity. Kids will learn how to build their own AI-powered applications, like chatbots or virtual travel bots, that solve real-world problems in engaging ways. In our AI Genius course, kids will explore the "Scratch + AI Code 101 - Introduction to AI Models" module, learning the basics of AI, such as training machines to recognize patterns, make decisions, and interact with the world. The real magic happens when they realize that they’re not just playing games but actually building the future, one amazing AI project at a time!',
    activities: '20+',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/802f05de-ab92-4824-9c90-bb2af62503eb?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-5',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '20+ activities & projects',
      },
    ],
  },
  CODE_ORG: {
    name:
      'Learn Code.org with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'At Codingal, we make coding a thrilling adventure for kids! Code.org\'s incredible platform comes to life in our curriculum, where children embark on a fun-filled journey through interactive projects. Kids will dive into creating animations, building games, and designing apps, all while learning essential coding concepts. Through projects like "Animated Alien," "Space Shooter," and "Dance Party," children will see characters move, dance, and come to life with the power of code! They\'ll also have the chance to develop real-world applications, like calculators and health guides, while collaborating with AI to create Digital Privacy Advisors and Math Table Assistants that make learning fun and interactive. By the end of the course, your child will be amazed by their newfound coding skills, realizing that with Code.org, they can build anything they imagine!',
    activities: '100+',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/16941535-1b29-4958-8f24-1d4b12d092aa?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-5',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '100+ activities & projects',
      },
    ],
  },
  PICTOBLOX: {
    name:
      'Learn Pictoblox with the Top AI & coding Course Instructors for Kids and Teens',
    description:
      'Watch your child bring AI to life with PictoBlox! Codingal’s hands-on course for Grades 3–7 lets kids explore the world of Artificial Intelligence through exciting, beginner-friendly projects using PictoBlox. From building face and object detectors to simulating self-driving cars and yoga pose checkers, kids get to experience real AI in action! No prior experience needed. And now with ChatGPT built right into PictoBlox, students can translate text, ask questions, play with grammar, or even convert movies into emojis, all through simple coding blocks! It’s an exciting way to learn how AI thinks, speaks, and helps in everyday tasks.',
    activities: '10+',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/3c6e1ac3-c296-40db-b5e0-dc32ef36e010?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 3-7',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '10+ activities & projects',
      },
    ],
  },
  TEACHABLE_MACHINE: {
    name:
      'Learn Teachable Machine with the Top AI & coding Course Instructors for Kids and Teens',
    description:
      'At Codingal, students in Grades 3–8 learn to build and train real AI models using Google’s Teachable Machine, a fun and intuitive platform for teaching computers to recognize images, sounds, and poses. Integrated across multiple AI and Scratch-based courses, students use these trained models to create interactive games, smart characters, and real-world simulations in Scratch using the MIT Raise Playground. Every lesson encourages hands-on learning, creative problem-solving, and a deeper understanding of how AI works in everyday life.',
    activities: '40+',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/1a986505-1871-48d6-95fe-128fc632da7d?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 3-8',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '40+ activities & projects',
      },
    ],
  },
  GOOGLE_COLAB: {
    name:
      'Learn Google Colab with the Top AI & coding Course Instructors for Kids and Teens',
    description:
      "Google Colab is a cloud-based coding environment that allows kids to write and run Python code right in their browser without the need to install anything! It's like a digital notebook where students can combine code, visuals, and text in one place. In our courses, kids use Google Colab to learn AI concepts, build machine learning models, visualize data, and create exciting projects. It’s safe, accessible, and perfect for young learners stepping into the world of technology.",
    activities: '100+',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/75db4f11-4bc1-4657-b12a-6e37d8c147fc?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '100+ activities & projects',
      },
    ],
  },
  NODE_JS: {
    name:
      'Learn Node.js with the Top AI & coding Course Instructors for Kids and Teens',
    description:
      'Introduce Your Child to the World of Server-Side Programming with Node.js. At Codingal, we make backend development engaging and accessible for young learners. Our Node.js for Kids module provides an interactive introduction to server-side programming, guiding students through the process of building scalable applications. Using Node.js, a powerful JavaScript runtime, kids will learn to create dynamic websites that go beyond static pages, making their coding experience both fun and impactful.',
    activities: '90+',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/0f3d2c35-a99d-4c73-adc3-5de39eeb23ac?autoplay=true&loop=true&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 8-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '90+ activities & projects',
      },
    ],
  },
  VISUAL_STUDIO_CODE: {
    name:
      'Learn Visual Studio Code with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'Visual Studio Code is a core tool used across Codingal courses like Web Development, App Development, Java, Python, and Codingal Grandmaster for Grades 9–12. At Codingal, VS Code serves as a playground for text-based coding, allowing learners to experiment, test, and build real coding projects. Students interact with this industry-standard tool to practice coding fundamentals, logical thinking, and problem-solving, applying what they learn across multiple coding disciplines. Visual Studio Code is a widely used development tool by professional software engineers, and now your child can get a head start with it. Through our curriculum, we introduce Visual Studio Code as a powerful tool for coding, turning it into a fun and hands-on learning experience. With interactive lessons, personalized mentorship, and real-world coding challenges, we help young learners build confidence while mastering this essential tool. This approach is perfect for kids who are ready to move beyond drag-and-drop coding and start working with real code in a supportive environment. Whether they want to build an app, create a game, or simply understand how software is created, our expert instructors guide them step by step in using Visual Studio Code to bring their ideas to life.',
    activities: '100+',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/a3c50518-bd48-4eaa-bacd-1c9f225d3dbf?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 9-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '100+ activities & projects',
      },
    ],
  },
  SQL: {
    name:
      'Learn SQL with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'Introduce your child to the powerful world of databases and data management with Codingal’s SQL modules in the Data Science course. These modules teach students the foundations of SQL (Structured Query Language) as part of their journey to becoming proficient data scientists. Through interactive lessons and practical projects, students will understand how to create databases, write queries, and perform complex data operations, preparing them for real-world data science and analytics challenges. SQL is essential for managing and analyzing data, which is critical in fields like AI, machine learning, and big data analytics. At Codingal, we teach students SQL through hands-on practice, equipping them with the skills they need to work with real-world datasets. As part of the Data Science course, they will progress through two core SQL modules, focusing on key concepts such as creating tables, using operators, and performing aggregate functions.',
    activities: '100+',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/d1f04589-1fbb-4755-b31b-1177c254d403?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 8-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '100+ activities & projects',
      },
    ],
  },
  REACT_JS: {
    name:
      'Learn React.js with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'Step into the fascinating world of React.js, where coding becomes a canvas for imagination and innovation! With Codingal’s interactive curriculum, students bring their ideas to life through animations, visuals, and interactive web projects  all created using simple lines of HTML, CSS, and React.js.In every session, learners use React.js to make web pages move, respond, and react,  building creative designs, interactive stories, and dynamic effects. As they experiment with loops, functions, events, and logic, kids learn the fundamentals of programming in a fun, visual, and hands-on way. Each project introduces essential coding concepts such as variables, conditional statements, functions, and event handling, while encouraging creativity and problem-solving. From creating a Fireworks Animation or an Interactive Quiz to building a Mini Game, students learn how React.js powers the interactive web experiences they see every day. As students progress, they transition from visual coding platforms like Scratch and Play Lab to text-based environments such as Replit, p5.js, or CodePen. They learn how HTML and CSS work together with React.js to design and style complete web pages,  preparing them for real-world web development. Perfect for kids aged 8–14, Codingal’s React.js lessons build a strong foundation in programming, boost creative confidence, and make learning code as exciting as playing a game!',
    activities: '100+',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/eefb0034-20ad-43ba-b3b9-49d5cb4efc85?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '100+ activities & projects',
      },
    ],
  },
  GIT: {
    name:
      'Learn GIT with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'Empower your child to move from just using technology to mastering it. Codingal’s web development course introduces GIT to kids and teens, one of the most important tools developers use worldwide. By learning GIT, children understand how to save versions of their code, collaborate on projects, and track changes just like professional programmers. With expert instructors, kids will not only design and structure their own websites but also learn the discipline of version control, a skill that builds confidence and teamwork. This hands-on journey blends creativity with core coding practices, giving students a strong foundation to grow into advanced fields like AI, software development, and innovation.',
    activities: '40+',
    modules: 6,
    lessons: 36,
    quizzes: 36,
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/d0f6d5af-f724-46a1-a1d1-7e9325700888?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '40+ activities & projects',
      },
    ],
  },
  GITHUB: {
    name:
      'Learn GitHub with Top AI & Coding Course Instructors for Kids and Teens',
    description:
      'Empower your child to move from just using technology to mastering it. Codingal’s web development course introduces GitHub to kids and teens, one of the most important tools developers use worldwide. By learning GitHub, children understand how to save versions of their code, collaborate on projects, and track changes just like professional programmers. With expert instructors, kids will not only design and structure their own websites but also learn the discipline of version control, a skill that builds confidence and teamwork. This hands-on journey blends creativity with core coding practices, giving students a strong foundation to grow into advanced fields like AI, software development, and innovation.',
    activities: '40+',
    modules: 6,
    lessons: 36,
    quizzes: 36,
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/ddaa8a7e-fb90-4861-b081-a921b06b46cf?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Live online classes',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: '40+ activities & projects',
      },
    ],
  },
  PUBLIC_SPEAKING_GRADE_3_5: {
    name: 'Public speaking for kids (grades 3-5)',
    description:
      'Public speaking becomes powerful for children in grades 3–5 (ages 8–10) when they practise regularly in a structured and guided environment. In this live course, students develop confidence, clarity, voice modulation, effective body language, storytelling ability, and presentation skills through focused speaking practice in every session.',
    activities: '100+',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grades 3-5',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '1-18 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  PUBLIC_SPEAKING_GRADE_1_2: {
    name: 'Public speaking for kids (grades 1-2)',
    description:
      'Public speaking becomes simple and joyful for children in grades 1–2 (ages 5–7) when they practice in a safe and encouraging environment. In this live, interactive course, young learners build confidence step by step as they learn to speak clearly, use expressive voices, show positive body language, tell simple stories, and share their ideas comfortably in every class.',
    activities: '100+',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grades 1-2',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '1-18 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  CODING_GRADE_1_3: {
    name: 'Online Coding Classes for Grades 1, 2 & 3 (Ages 6-9)',
    label: 'Live online classes · Grades 1 to 3 · Ages 6 to 9',
    description:
      'Help your 6 to 9 year old take their first steps into coding with live online classes built just for young learners. Early graders learn by building real things from day one, from block coding and Scratch to animations, stories, and beginner AI projects, all in a colorful, play-based environment that makes every lesson feel like an adventure.',
    activities: '100+',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grades 1-3',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '1-18 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: 'Hands-on activities & projects',
      },
    ],
  },
  CODING_GRADE_4_5: {
    name: 'Online Coding Classes for Grades 4 & 5 (Ages 9-11)',
    label: 'Live online classes · Grades 4 to 5 · Ages 9 to 11',
    description:
      'Ready for your 9 to 11 year old to go beyond the basics? Fourth and fifth graders build real projects from day one, moving from Scratch and block coding into Python, app design, and beginner AI through live online classes designed specifically for upper elementary students.',
    activities: '100+',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grades 4-5',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '1-18 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: 'Hands-on activities & projects',
      },
    ],
  },
  CODING_GRADE_6_8: {
    name: 'Online Coding Classes for Grades 6, 7 & 8 (Ages 11-14)',
    label: 'Live online classes · Grades 6 to 8 · Ages 11 to 14',
    description:
      'Give your 11 to 14 year old the skills that actually matter. Middle schoolers learn Python, web development, and AI by building real, impressive projects through live online classes designed to challenge and inspire 6th, 7th, and 8th graders at exactly the right level.',
    activities: '100+',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grades 6-8',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '1-18 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: 'Hands-on activities & projects',
      },
    ],
  },
  CODING_GRADE_9_10: {
    name: 'Online Coding Classes for Grades 9 & 10 (Ages 14-16)',
    label: 'Live online classes · Grades 9 to 10 · Ages 14 to 16',
    description:
      'Your 14 to 16 year old is ready for a serious challenge. Ninth and tenth graders work across advanced Python, data science, AI, and competitive programming through structured live online classes that build real technical depth. For early high schoolers with ambition, this is where coding ability starts to open doors to competitive colleges, tech internships, and meaningful career pathways.',
    activities: '100+',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grades 9-10',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '1-18 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: 'Hands-on activities & projects',
      },
    ],
  },
  CODING_GRADE_11_12: {
    name: 'Online Coding Classes for Grades 11 & 12 (Ages 16-18)',
    label: 'Live online classes · Grades 11 to 12 · Ages 16 to 18',
    description:
      'The coding years that count most. Junior and senior students work at an advanced level across AI, data science, and competitive programming through live online classes built around real outcomes. For college-bound coders aged 16 to 18, this is where technical ability, a strong project portfolio, and recognized certifications come together to help upper high schoolers stand out in college applications and early tech careers.',
    activities: '100+',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange2-400 xl:inline w-5 h-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Suitable for',
        description: 'Grades 11-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-blue-200 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange2-400 xl:inline w-5 h-5 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock className="text-green-100 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Duration',
        description: '1-18 Months',
      },
      {
        icon: (
          <>
            <FileText className="text-orange-600 xl:inline w-5 h-5 mb-1 xl:pr-1" />
          </>
        ),
        title: 'Program includes',
        description: 'Hands-on activities & projects',
      },
    ],
  },
}

export const COURSE_DESCRIPTION_FOLD: Record<
  CourseNameType,
  CourseDescriptionFoldType
> = {
  SCRATCH_PROGRAMMING: {
    title: 'What is Scratch Coding?',
    expandedDescription: (
      <>
        <p>
          Scratch is a free block-based programming language designed primarily
          for kids.
        </p>
        <p>
          It is a free educational website developed by MIT that provides a
          great way to learn to code early.
        </p>
        <p>
          Scratch programming is a visual programming language in which you have
          to drag and drop blocks of code and attach them in a way you connect a
          jigsaw puzzle. It can be used online or offline on your system.
        </p>
      </>
    ),
    readMoreLink:
      'https://www.codingal.com/coding-for-kids/blog/introduction-to-scratch-coding/',
  },
  HPE_CODEWARS_HACKATHON: {
    title: 'What is HPE CodeWars Hackathon?',
    expandedDescription: (
      <p>
        This is an online coding competition hosted by HPE for Grade 8-12
        students. The students can team up with friends to solve real problems
        using code. They can ideate, create an application, game, or website
        around a theme.
      </p>
    ),
  },
  HPE_CODEWARS_CODE_BATTLE: {
    title: 'What is HPE CodeWars Code Battle?',
    expandedDescription: (
      <p>
        This is a virtual programming challenge hosted by HPE for Grade 8-12
        students. The students will solve coding problems using programming
        language like C, C++, Java & Python.
      </p>
    ),
  },
  PYTHON_FOR_KIDS: {
    title: 'What is Python for Kids and Teens?',
    description:
      'Python is a text-based programming language suitable for kids to learn. It is one of the most popular programming languages. Python is used to develop apps and software. For example, YouTube and Instagram are built using Python.',
    expandedDescription: (
      <>
        <p>
          Python is used for web development, game development, building apps,
          machine learning, and more.{' '}
          <strong>Python Classes For Kids and Teens</strong> will quickly
          introduce them to principles of coding concepts and boost their
          analytical skills
        </p>
        <p>
          Python programming teaches kidsand teens concepts of variables,
          keywords, operators, loops, if-else statements, conditional
          statements, functions, recursion, exception handling, list,
          dictionary, data structures, object-oriented programming, and more. In
          this course, students will also learn to build a game and GUI from
          scratch using Python.
        </p>
        <p>
          Python programming will help kids and teens to get a grip on the
          basics of programming. Then, as kids explore what Python coding
          offers, they can learn various coding skills, from problem-solving to
          analytical thinking, that will grow with them on their coding journey
          beyond Python. It breaks coding into more easily grasped chunks so
          children can build their understanding while actively building actual
          code. It also helps the kids and teens to solve real-world problems
          using data structures and object-oriented programming in Python.
        </p>
      </>
    ),
  },
  ROBLOX: {
    title: 'What is Roblox?',
    description:
      'Roblox is a widely popular online gaming platform for kids to create and play games. Roblox is not a game; it’s a platform on which people play games created by other developers. What makes Roblox different is all the games on Roblox are made by its users. Now it depends on the creativity and imagination of the user on what kind of game they want to build.',
    expandedDescription: (
      <>
        <p>
          Games on Roblox are called ‘experiences’. Kids can play ‘experiences’
          developed by users across the globe.
        </p>
      </>
    ),
  },
  AP_COMPUTER_SCIENCE_A: {
    title: 'What is AP Computer Science A?',
    description:
      'AP in AP Computer Science A stands for Advanced Placement. It‘s an introductory college-level course. It covers java programming fundamentals, data structures and algorithms, strings and arrays and object-oriented programming.',
    expandedDescription: (
      <>
        <p>
          The AP Computer Science A course & exam is based on the java focused
          computing fundamentals. Students can understand their coding skills by
          analysing the logic and writing style and through testing code by
          understanding control flow and syntaxes. AP Computer Science A exam
          covered both multiple-choice and free-response questions.
        </p>
      </>
    ),
  },
  AI_AND_ML: {
    title: 'What is AI for kids?',
    description:
      'AI, or Artificial Intelligence, creates machines that think and learn like humans. It processes information, makes decisions, and adapts to new situations—just like our brains.',
    expandedDescription: (
      <>
        <p>
          In our AI Genius for Kids and AI Expert for Teens courses, we dive
          into how AI, a branch of computer science, performs tasks that require
          human intelligence: reasoning, recognizing shapes, understanding
          speech and images, playing games, and even driving cars!
        </p>
        <p>
          AI is everywhere—voice assistants like Siri and Alexa uses Machine
          Learning & Natural Language Processing to understand commands.
          Self-driving cars, like Tesla, use intelligent algorithms to navigate
          on their own!
        </p>
        <p>
          From autocorrect to YouTube recommendations, Instagram Reels, ChatGPT,
          and more, AI shapes our everyday lives. Join us to discover how it all
          works and its impact on the world.
        </p>
      </>
    ),
  },
  THUNKABLE: {
    title: 'What is Thunkable for kids?',
    description:
      'Thunkable for Kids is an online, block-based app development platform that allows children to create, run, and share mobile apps directly in the browser with no software installation required. It’s designed to make app building fun, interactive, and beginner-friendly.',
    expandedDescription: (
      <>
        <p>With Thunkable, kids can:</p>
        <ul>
          <li>
            Build interactive apps, games, and quizzes that run on mobile
            devices
          </li>
          <li>
            Design custom screens to create visually appealing and user-friendly
            interfaces
          </li>
          <li>
            Add smart features like text-to-speech, location tracking, and
            real-time data integration
          </li>
          <li>
            See their apps come to life instantly, helping them understand
            coding logic and app behavior
          </li>
          <li>Share projects safely with teachers, friends, or family</li>
        </ul>

        <p>
          <strong>Thunkable</strong> is the perfect platform for kids to
          practice coding, experiment creatively, and develop problem-solving
          skills while building real-world apps in a safe and engaging online
          environment.
        </p>
      </>
    ),
  },
  JAVA: {
    title: 'What is Java for kids?',
    description:
      'Java is a powerful computer programming language that lets us create everything from mobile apps to video games! Think of it as a set of building blocks you can use to design and bring your digital ideas to life—big or small. It’s a cornerstone of computer science, helping us build interactive programs, manage data, and even power popular games like Minecraft.',
    expandedDescription: (
      <>
        <p>
          Java is everywhere—Android apps run on Java, and many websites depend
          on it for smooth and secure functionality. Whether you’re writing
          simple programs or running massive servers, Java’s reliable
          performance makes it stand out.
        </p>
        <p>
          From learning the basics and writing your first lines of code to
          building exciting projects and games, Java opens up a world of
          possibilities. Discover how Java shapes our tech-driven lives—and how
          you can become a Java coding master!
        </p>
      </>
    ),
  },
  TRINKET: {
    title: 'What is Trinket for kids?',
    description:
      'Trinket for Kids is an online coding platform that lets children write, run, and share Python programs directly in their web browser—no software installation required. It is designed to make text-based coding simple, visual, and beginner-friendly, especially for young learners transitioning from block coding to real programming.',
    expandedDescription: (
      <>
        <p>With Trinket, kids can:</p>
        <ul>
          <li>
            <strong>Learn Python in a fun, interactive way</strong>
          </li>
          <li>
            <strong>
              Create drawings, patterns, and animations using Python Turtle
            </strong>
          </li>
          <li>
            <strong>
              Build simple interactive Python projects and mini-programs
            </strong>
          </li>
          <li>
            <strong>See their code run instantly</strong>
            <span>, helping them understand how programming logic works</span>
          </li>
          <li>
            <strong>Share their Python projects safely</strong>
            <span> with teachers or family using a secure link</span>
          </li>
        </ul>
        <p>
          Trinket provides a safe, accessible environment where kids can
          experiment freely, explore creativity through Python, and develop
          strong problem-solving and computational thinking skills. It’s the
          perfect starting point for children ready to begin their journey into
          real, text-based coding.
        </p>
      </>
    ),
  },
  PYGAME: {
    title: 'What is Pygame?',
    description:
      'Pygame is a free, open-source Python library for making 2D games and multimedia applications. Built on SDL (Simple DirectMedia Layer), it includes ready-made functions for graphics, sound, animation, and user input, so beginners can create games without advanced coding knowledge.',
    expandedDescription: (
      <>
        <p>
          It&apos;s widely used in schools and coding clubs because kids can
          quickly build projects like Snake, Pong, or Flappy Bird clones while
          learning programming basics such as movement, collision detection, and
          scoring.
        </p>

        <p>Pygame is popular because it&apos;s:</p>

        <ul>
          <li>
            <strong>Beginner-friendly:</strong> Simple Python commands for
            drawing, sound, and interaction.
          </li>
          <li>
            <strong>Educational:</strong> Helps kids and students learn coding
            through hands-on projects.
          </li>
          <li>
            <strong>Flexible:</strong> Works for both small hobby games and
            classroom learning.
          </li>
        </ul>

        <p>
          In short: Pygame makes learning Python fun by letting you turn ideas
          into playable games.
        </p>
      </>
    ),
  },
  ANIMATION: {
    title: 'What is Animation for kids?',
    description:
      'Animation is like a magical tool that brings pictures to life! It helps kids create moving characters, games, and interactive stories. From Minecraft avatars to Fortnite dances, animation teaches motion, timing, and sequencing. Kids also explore famous examples in cartoons like Tom and Jerry or movies like Frozen, understanding how movement makes stories engaging. Through Codingal’s animation lessons, kids learn to make buttons glow, objects spin, and characters jump, combining digital illustration with coding logic. Animation builds creativity, storytelling, and computational thinking which are essential skills for future animators and digital artists.',
    expandedDescription: (
      <>
        <p>
          Cartoons also use animation to tell amazing stories. Think about Tom
          and Jerry, where Tom chases Jerry with funny movements, or SpongeBob
          SquarePants, where SpongeBob laughs and flips burgers under the sea.
          Even movies like Frozen, where Elsa creates icy magic, or The Lion
          King, where Simba roars on Pride Rock, are brought to life with
          animation. Animation turns simple drawings into characters we love and
          makes every story exciting to watch!
        </p>
        <p>
          In our text‒based programming curriculum, you’ll dive into the
          exciting world of animation and learn how to create some seriously
          cool effects! Imagine this: every time you visit a website, you see
          buttons that change color when you hover over them, cards that grow
          bigger or glow with shadows, and even interactive games that respond
          to your clicks!
        </p>
        <p>
          These effects are all made possible by animation, and you’ll learn how
          to create them yourself. Whether it’s making characters jump, objects
          spin, or text change colors, animation lets you bring your ideas to
          life on the screen. You’ll start by learning simple animation tricks
          and gradually build up to making your own animated games and
          interactive websites. Get ready to turn your imagination into reality
          with animation!
        </p>
        <p>
          Cartoons also use animation to tell amazing stories. Think about Tom
          and Jerry, where Tom chases Jerry with funny movements, or SpongeBob
          SquarePants, where SpongeBob laughs and flips burgers under the sea.
          Even movies like Frozen, where Elsa creates icy magic, or The Lion
          King, where Simba roars on Pride Rock, are brought to life with
          animation. Animation turns simple drawings into characters we love and
          makes every story exciting to watch!
        </p>
        <p>Platforms kids will be introduced to:</p>
        <div className="space-y-3">
          <FastlyImage
            src={`/images/courses/animation/animation-1.jpg`}
            alt="HTML for kids"
            width={512}
            height={171}
          />
        </div>
      </>
    ),
  },
  TURTLE: {
    title: 'What is Python Turtle for kids?',
    description:
      'Python Turtle is a fun and beginner-friendly way for kids to learn coding through art and creativity. It’s a built-in Python library that lets students use simple commands to move a “turtle” around the screen — drawing shapes, patterns, and colorful designs as it goes.',
    expandedDescription: (
      <>
        <p>
          Kids learn programming by giving the turtle instructions like move
          forward, turn left, or change color, and instantly see their code come
          to life as drawings. This visual feedback helps children understand
          how coding works while keeping them excited and engaged.
        </p>
        <p>With Python Turtle, kids can:</p>
        <ul>
          <li>
            <strong>Create art with code:</strong> Draw geometric patterns,
            colorful spirals, and creative designs.
          </li>
          <li>
            <strong>Learn programming concepts:</strong> Understand loops,
            angles, variables, and conditionals in a hands-on way.
          </li>
          <li>
            <strong>Build mini projects:</strong> Animate designs, create
            patterns that respond to user input, and make interactive drawings.
          </li>
          <li>
            <strong>Boost problem-solving skills:</strong> Learn to think
            logically and test ideas step by step.
          </li>
          <li>
            <strong>Transition smoothly to advanced Python:</strong> Gain
            confidence with syntax and structure before exploring text-based
            coding projects.
          </li>
        </ul>
        <p>
          It’s the perfect starting point for kids to fall in love with Python
          by combining art, logic, and imagination in a way that makes coding
          both creative and easy to understand.
        </p>
      </>
    ),
  },
  BOOTSTRAP: {
    title: 'What is Bootstrap?',
    description:
      'Bootstrap is a free, open-source front-end framework used to design beautiful and responsive websites. It helps kids and beginners build professional-looking web pages easily using HTML, CSS, and JavaScript — no advanced coding required!',
    expandedDescription: (
      <>
        <p>
          Bootstrap comes with ready-made design elements like buttons,
          navigation bars, grids, and forms that automatically adjust to any
          screen size, from laptops to tablets to mobile phones. This makes it
          the perfect tool for young learners who want to bring their web design
          ideas to life quickly and creatively.
        </p>

        <p>
          It is widely used in schools, coding clubs, and online courses because
          kids can easily create fun projects like personal websites,
          portfolios, and interactive web pages, all while learning important
          web development skills like layout, styling, and responsiveness.
        </p>

        <p>Bootstrap is popular because it’s:</p>
        <ul>
          <li>
            <strong>Beginner-friendly:</strong> Kids can design stunning
            websites using simple, reusable components.
          </li>
          <li>
            <strong>Educational:</strong> Teaches real-world web design and
            development principles through hands-on practice.
          </li>
          <li>
            <strong>Flexible:</strong> Works for small classroom projects and
            large creative portfolios alike.
          </li>
        </ul>

        <p>
          <strong>In short:</strong> Bootstrap makes learning web design fun by
          letting kids turn simple ideas into professional, responsive websites
          — just like magic!
        </p>
      </>
    ),
  },
  JAVASCRIPT: {
    title: 'What is Javascript for Kids?',
    description:
      'JavaScript for Kids is an easy and fun way for children to learn how websites, games, and apps come to life! It’s the language of the web, used to make pages move, buttons react, animations play, and games respond to user actions.',
    expandedDescription: (
      <>
        <p>
          With JavaScript, kids don’t just learn to code — they learn how to
          think creatively and logically while building real-world projects they
          can see and interact with instantly.
        </p>

        <p>
          Through <strong>Codingal’s</strong> interactive lessons, kids start by
          exploring visual coding tools like <strong>Scratch</strong> or{' '}
          <strong>Play Lab</strong> to understand basic logic. Then, they move
          on to writing real JavaScript code to build interactive webpages,
          animations, and mini-games.
        </p>

        <p>Here’s what kids can do with JavaScript 👇</p>
        <ul>
          <li>
            <strong>Build interactive games:</strong> Create games where players
            click, score, or move objects on screen.
          </li>
          <li>
            <strong>Animate and design:</strong> Make shapes move, colors
            change, or elements dance with motion effects.
          </li>
          <li>
            <strong>Learn coding logic:</strong> Understand loops, variables,
            conditions, and events in a visual way.
          </li>
          <li>
            <strong>Create websites:</strong> Use HTML, CSS, and JavaScript
            together to design and control webpages.
          </li>
          <li>
            <strong>See instant results:</strong> Watch code come to life in the
            browser — no installations required!
          </li>
        </ul>

        <p>
          It’s the perfect starting point for young learners to understand how
          the digital world works, while having fun turning their ideas into
          interactive creations.
        </p>

        <p>
          With <strong>Codingal’s JavaScript classes</strong>, kids learn coding
          step by step, from drag-and-drop basics to real web development —
          building confidence, curiosity, and creativity along the way!
        </p>
      </>
    ),
  },
  CSS: {
    title: 'What is CSS for Kids? A Guide for Parents',
    description:
      'Learning to code is as essential as learning math or a new language. Understanding what CSS is is a key part of this journey, helping kids bring their creative ideas to life online through web design for kids. If HTML is the building block of a website, CSS is the design tool that makes it look amazing. Many wonder how to learn HTML and CSS effectively, and our approach makes it easy for young learners. Parents looking for CSS for kids or beginner-friendly children’s CSS programming will find our classes the perfect starting point.',
    expandedDescription: <></>,
  },
  HTML: {
    title: 'What is HTML for kids?',
    description:
      "HTML stands for HyperText Markup Language. It's the foundational language for creating web pages. You can think of it as the structure of a house - the walls, rooms, and foundation. This is super important for anyone learning HTML for kids. HTML tells the web browser what content to display and where to place it. Without HTML, websites wouldn't exist!",
    expandedDescription: (
      <>
        <div className="space-y-3">
          <FastlyImage
            src={`/images/courses/html/html-1.jpg`}
            alt="HTML for kids"
            width={512}
            height={341}
          />
        </div>
      </>
    ),
  },
  AICODE101: {
    title: 'What is AI Code 101 for kids?',
    description:
      'AI Code 101 is an educational web platform designed to introduce children to the exciting world of artificial intelligence (AI) and machine learning (ML). It allows kids to interact with the system through various commands, including written text, voice recognition, and images. The platform enables kids to practice block-based coding and Python programming, providing them with hands-on experience in developing AI-powered projects. With the help of these tools, kids will learn to train models, understand AI processes, and create interactive games, all while building foundational coding skills in a fun and engaging way.',
    expandedDescription: <></>,
  },
  CODE_ORG: {
    title: 'What is Code.org for kids?',
    description:
      'Code.org is an incredible platform for introducing kids to block-based programming and AI-powered coding experiences through fun, interactive, and engaging activities. At Codingal, we integrate Code.org into our curriculum to guide young learners through essential programming concepts using visual coding blocks and artificial intelligence tools, where they solve puzzles and create amazing projects like games and interactive stories. As they advance, they develop critical thinking and computational skills through activities that introduce fundamental concepts such as sequences, loops, conditionals, and algorithms. Code.org also helps students master foundational computer science principles and debugging techniques, building strong knowledge before transitioning to advanced programming languages. With exciting AI-enhanced learning experiences and hands-on coding challenges, students see immediate results, making learning programming both thrilling and rewarding!',
    expandedDescription: <></>,
  },
  PICTOBLOX: {
    title: 'What is Pictoblox for kids?',
    description:
      'PictoBlox is a beginner-friendly, block-based coding platform that introduces kids to real-world AI and robotics through fun, hands-on projects. At Codingal, students in Grades 3–7 use PictoBlox to build face detectors, smart translators, self-driving cars, and more! Learning how machines see, think, and interact with the world. With its drag-and-drop interface and built-in ChatGPT integration, PictoBlox makes it easy for young learners to explore Artificial Intelligence in a creative and playful way; setting the foundation for advanced coding and problem-solving skills.',
    expandedDescription: <></>,
  },
  TEACHABLE_MACHINE: {
    title: 'What is a Teachable Machine for kids?',
    description:
      'At Codingal, students explore Google’s Teachable Machine to understand the fundamentals of Artificial Intelligence in a fun, hands-on way. Tailored for Grades 3–8, Teachable Machine allows kids to train real AI models by teaching computers to recognize images, sounds, gestures, and poses using simple examples and no coding. Students then use the MIT Raise Playground to connect these models to Scratch, creating interactive games, smart characters, and AI-powered experiences. It’s an intuitive and beginner-friendly tool that helps kids understand how machines learn, while building creativity, problem-solving skills, and real-world AI knowledge.',
    expandedDescription: <></>,
  },
  GOOGLE_COLAB: {
    title: 'What is a Google Colab for kids?',
    description:
      'At Codingal, students explore Google’s Google Colab to understand the fundamentals of Artificial Intelligence in a fun, hands-on way. Tailored for Grades 3–8, Google Colab allows kids to train real AI models by teaching computers to recognize images, sounds, gestures, and poses using simple examples and no coding. Students then use the MIT Raise Playground to connect these models to Scratch, creating interactive games, smart characters, and AI-powered experiences. It’s an intuitive and beginner-friendly tool that helps kids understand how machines learn, while building creativity, problem-solving skills, and real-world AI knowledge.',
    expandedDescription: <></>,
  },
  NODE_JS: {
    title: 'What is Node.js for kids?',
    description:
      'At Codingal, we introduce Node.js as a part of our Website Development course curriculum. As students progress through Grades 8–12, they start by learning the basics of building websites using HTML, CSS, and JavaScript. Once they’re comfortable with front-end development, they explore Node.js, a JavaScript runtime used for server-side programming, to make their websites more dynamic. This curriculum, packed with 90+ hands-on activities and lessons, teaches students how to build interactive, AI-powered websites, ensuring they gain both front-end and back-end skills.',
    expandedDescription: <></>,
  },
  VISUAL_STUDIO_CODE: {
    title: 'What is Visual Studio Code for kids?',
    description:
      'Visual Studio Code is an industry-standard coding environment where learners can write, test, and organize code in a structured and interactive way. Think of it as a digital playground for text-based coding: teachers guide students while learners practice coding directly in the editor. It is used across Codingal courses to apply coding skills in real-world projects, experiments, and creative assignments.',
    expandedDescription: <></>,
  },
  SQL: {
    title: 'What is SQL for kids?',
    description:
      'SQL (Structured Query Language) is a powerful language used to manage and manipulate data stored in relational databases. It allows users to write queries to retrieve, update, and manage data in a structured and organized manner.',
    expandedDescription: <></>,
  },
  REACT_JS: {
    title: 'What is React.js for kids?',
    description:
      'React.js for Kids is an exciting introduction to modern web development, where children learn how to build real, interactive websites and apps,  just like professionals do! 🚀',
    expandedDescription: (
      <>
        <p>
          React.js (or simply React) is a popular JavaScript library developed
          by Facebook (Meta). It helps developers create web pages that update
          instantly without needing to reload, the same technology used by apps
          like Instagram, Netflix, and YouTube.
        </p>
        <p>
          In simple terms, React.js teaches kids how to build a website using
          small reusable parts called components. Instead of writing the same
          code over and over, they learn to combine components, add logic, and
          create dynamic user interfaces that respond to actions, like clicking
          buttons or typing text.
        </p>
        <p>Here’s what kids can do with React.js</p>
        <ul>
          <li>
            Build Interactive Web Apps: Create calculators, quizzes, and mini
            dashboards that respond in real time.
          </li>
          <li>
            Design Dynamic Websites: Use components and styling to make pages
            come alive.
          </li>
          <li>
            Learn Real Developer Skills: Understand JavaScript, HTML, and CSS
            while working with modern tools.
          </li>
          <li>
            Think Like a Creator: See how coding shapes the apps and sites they
            use every day.
          </li>
        </ul>
      </>
    ),
  },
  GIT: {
    title: 'What is GIT for kids?',
    description:
      'GIT stands for Global Information Tracker. It is a powerful tool that programmers use to save, track, and manage changes in their code. You can think of GIT like a time machine for coding projects, it remembers every change you make and lets you go back if something breaks.',
    expandedDescription: (
      <>
        <p>
          For kids learning GIT, it is like keeping a super-organized notebook
          where each page shows what was changed, when, and by whom. This is
          especially important when building websites, apps, or games, because
          it helps kids work together on projects without losing progress.
        </p>
        <p>
          Without GIT, coding would be messy and confusing. With GIT, kids learn
          the habits of professional developers, teamwork, version control, and
          responsibility, all while building cool projects.
        </p>
      </>
    ),
  },
  GITHUB: {
    title: 'What is GitHub for kids?',
    description:
      'GitHub is like a time machine for coding projects. It helps programmers save, track, and manage every change they make in their code. For kids, learning GitHub is like keeping a super-organized notebook, where each page shows what was changed, when, and by whom.',
    expandedDescription: (
      <>
        <p>
          This is especially helpful when building games, apps, or websites,
          because it lets kids work together on projects without losing any
          progress.
        </p>
        <p>
          Without GitHub, coding can get messy and confusing. With GitHub, kids
          learn the habits of professional developers: teamwork, version
          control, responsibility, and collaboration—all while creating cool,
          real coding projects.
        </p>
      </>
    ),
  },
  PUBLIC_SPEAKING_GRADE_3_5: {
    title: 'What is public speaking for kids? (Grades 3-5)',
    description:
      'Public speaking for grades 3–5 (ages 8–10) is the skill of sharing ideas clearly, confidently, and in an organized way in front of others.',
    expandedDescription: (
      <>
        <p>
          Students develop voice modulation, structured sentence framing,
          storytelling techniques, purposeful body language, and composure while
          speaking. In this course for grades 3–5, young learners practice
          introducing topics clearly, expressing opinions with reasons,
          narrating experiences, and presenting academic or general topics using
          a clear beginning, middle, and conclusion.
        </p>
        <p>
          They also build communication skills such as active listening, asking
          thoughtful questions, and responding in complete answers. Over time,
          students progress from guided speaking to independent, structured
          presentations across classroom and group settings.
        </p>
      </>
    ),
  },
  PUBLIC_SPEAKING_GRADE_1_2: {
    title: 'What is Public Speaking for Kids? (Grades 1-2)',
    description:
      'Public speaking for grades 1–2 (ages 5–7) means learning to share simple ideas using a clear voice in front of others. At this level, the focus is on comfort and basic expression.',
    expandedDescription: (
      <>
        <p>
          It includes speaking in full sentences, using a clear and audible
          voice, showing feelings through face and body, and staying calm while
          talking. In this Public Speaking and Communication course, children
          practice introducing themselves, talking about their likes and daily
          experiences, describing familiar people and places, and telling short
          stories with a beginning, middle, and end. They also learn to stand
          properly, look at the audience, and speak in a way that others can
          understand.
        </p>
        <p>
          The course also builds early communication habits such as listening
          when others speak, waiting for their turn, asking simple questions,
          and answering in more than one word. Practice labs give them
          structured opportunities to speak in small groups. Over time, students
          transition from hesitant sharing to clear, structured speaking in
          class and small group settings.
        </p>
      </>
    ),
  },
  CODING_GRADE_1_3: {
    title: 'What children in Grades 1 to 3 learn in coding class',
    description:
      'Children in Grades 1 to 3 learn five core coding concepts: sequencing, events, loops, patterns and simple logic. They apply these concepts by creating animations, stories, games and beginner AI projects. All learning happens through drag and drop blocks, so no typing or advanced reading is required.',
    expandedDescription: (
      <>
        <p>
          Every concept is introduced through a hands-on project. Children learn
          by building, not by watching. By the end of the program, most students
          can independently plan, build and present a short animation or game.
        </p>
      </>
    ),
    tags: [
      'Animations',
      'Storytelling',
      'Simple Games',
      'Problem Solving',
      'Creativity',
      'Live Classes',
    ],
  },
  CODING_GRADE_4_5: {
    title: 'What children in Grades 4 and 5 learn in coding class',
    description:
      'Grade 4 and 5 students move beyond block coding into Python, web development with HTML and CSS, and beginner AI. They continue building games and animations with Scratch while starting to type real code for the first time. This is a deliberate bridge stage in the Codingal curriculum, designed to make the shift from blocks to text feel natural, not stressful.',
    expandedDescription: (
      <>
        <p>
          Every concept is taught through a project. Children leave each class
          with something working on their screen. By the end of the program,
          most 4th and 5th graders can build a small Python program, design a
          web page or train an AI model for a simple task.
        </p>
      </>
    ),
    tags: [
      'Interactive games',
      'First lines of Python',
      'Beginner apps',
      'Web development',
      'HTML and CSS',
      'AI projects',
      'Logical thinking',
      'Problem solving',
    ],
  },
  CODING_GRADE_6_8: {
    title: 'What do 11 to 14 year olds learn in coding class?',
    description:
      'Coding in grades 6 to 8 is where things get real. Students move beyond visual blocks and start writing actual code, building projects they can genuinely show off. Through live classes and structured pathways, they explore Python, web development, app building, and AI in a way that is challenging but always achievable. Every lesson is hands-on, so students are always creating something, not just watching or listening. Along the way, they build logical thinking, problem solving, and the confidence to tackle increasingly complex challenges independently.',
    expandedDescription: <></>,
    tags: [
      'Build real apps',
      'Write Python code',
      'Design websites',
      'Explore AI',
      'Solve real problems',
    ],
  },
  CODING_GRADE_9_10: {
    title: 'What do 14 to 16 year olds learn in coding class?',
    description:
      'Coding for Grades 9 and 10 is about building serious technical depth. Students work across advanced Python, data science, AI, machine learning, Java, and competitive programming through live, expert-led sessions. Every class is project-based, with ninth and tenth graders tackling increasingly complex problems that reflect real technical ability. As early high schoolers develop their skills, they build the analytical thinking, independent problem-solving, and multi-language fluency that opens doors to competitive colleges and tech careers.',
    expandedDescription: <></>,
    tags: [
      'Advanced Python',
      'Data science & ML',
      'AI and machine learning',
      'Competitive programming',
      'Java',
    ],
  },
  CODING_GRADE_11_12: {
    title: 'What do 16 to 18 year olds learn in coding class?',
    description:
      'Coding for Grades 11 and 12 is advanced, purposeful, and built around real outcomes. Junior and senior students work across AI, data science, machine learning, competitive programming, and full stack development through expert-led live sessions. Every project builds the kind of professional-level ability and technical portfolio that stands out in college applications and early tech careers. As upper high schoolers refine their skills, they specialise in pathways aligned with their goals and take on increasingly ambitious independent work.',
    expandedDescription: <></>,
    tags: [
      'AP Computer Science',
      'Full-stack development',
      'Advanced Python',
      'Data science & AI',
      'Competitive programming',
    ],
  },
}

export const SKILLS_YOU_WILL_GAIN: Record<
  CourseNameType,
  SkillsYouWillGainFoldType
> = {
  SCRATCH_PROGRAMMING: {
    title:
      'The best place for kids and teens to learn Scratch basics and fundamental',
    description:
      'Learn the basic principles of coding using Scratch, start using events, coordinates and sprites, and create your own programs using code blocks.',
    skills: [
      'Learn the foundation of Computer Science using Scratch programming',
      'Become an expert in Scratch programming',
      'Learn to solve problems by creating app, games, websites',
      'Develop creative thinking to solve the problems',
      'Improve analytical thinking while creating your projects on Scratch',
      'Create engaging games, animation & apps with block based programming',
    ],
  },
  HPE_CODEWARS_HACKATHON: {
    skills: [
      'Website building principles',
      'Creative and design thinking',
      'HTML, CSS and Bootstrap',
      'Python and Python Flask',
      'JavaScript, SQL and NodeJS',
      'Website hosting',
    ],
  },
  HPE_CODEWARS_CODE_BATTLE: {
    skills: [
      'Algorithm skills and techniques',
      'Data science skills and techniques',
      'Computational thinking',
      'Problem solving',
      'Competitive coding skills',
      'Python development',
    ],
  },
  PYTHON_FOR_KIDS: {
    title:
      'The best place for kids and teens to learn Python basics and fundamentals',
    description:
      'Learn basics of programming, code interactive apps and games with this intensive online Python for Kids course.',
    skills: [
      'Learn the foundation of Computer Science using Python programming',
      'Become an expert in Python programming',
      'Learn to solve problems by creating app, games, websites',
      'Develop creative thinking to solve the problems',
      'Improve analytical thinking while creating your projects on Python',
      'Create engaging games, animation & apps with Python programming',
    ],
  },
  ROBLOX: {
    title: 'The best place for kids to learn the basics and fundamentals',
    description:
      'Learn basics of programming, code interactive 3D games with this intensive online Roblox course.',
    skills: [
      'Learn the foundation of Computer Science using Lua programming',
      'Become an expert in Roblox',
      'Learn to solve problems by creating games',
      'Develop creative thinking to solve the problems',
      'Improve analytical thinking while creating your projects on Roblox',
      'Create engaging 3D games with Roblox',
    ],
  },
  AP_COMPUTER_SCIENCE_A: {
    title: 'The best place for kids to learn the basics and fundamentals',
    description:
      'Learn fundamentals of Java programming and topics relevant to AP Computer Science A course and exam.',
    skills: [
      'Learn the foundation of Java',
      'Become an expert in AP Computer Science A',
      'Learn to solve the Free Response Question',
      'Develop creative thinking to solve the problems',
      'Improve analytical thinking',
    ],
  },
}

export const PLATFORM_ACTIVITIES: Record<string, ActivityCardType> = {
  swift_lingo: {
    activityName: 'Swift Lingo',
    activityDescription:
      'Develop an AI-powered language translator app with integrated text-to-speech functionality on MIT App Inventor.',
    activityImage: 'swift-lingo',
    altText: 'Swift Lingo Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/55dd40b6-2155-478f-a56e-8be1578d4093?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },

  palindrome_checker: {
    activityName: 'Palindrome Checker',
    activityDescription:
      'Create a Java program to check if a word or phrase is a palindrome—ignoring spaces—and discover the exciting basics of Java programming!',
    activityImage: 'palindrome-checker',
    altText: 'Palindrome Checker Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/c35a0c95-a747-49e0-88c4-8fe513901731?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  reverse_string: {
    activityName: 'Reverse String',
    activityDescription:
      'Master Java basics by reversing strings in our exciting trial class! Join now and start coding interactive solutions today!',
    activityImage: 'reverse-string',
    altText: 'Reverse String Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/0fc7d153-3582-4799-8793-6713abf11676?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },

  drag_drop_fun: {
    activityName: 'Drag & Drop Function',
    activityDescription:
      'Create an interactive experience where users can quickly move, copy, or reorder objects by dragging and dropping them on screen!',
    activityImage: 'drag-and-drop-function',
    altText: 'Drag & Drop FunctionActivity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/3c69c543-c2c4-4026-87e1-535c8fcbd439?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  flashlight_app: {
    activityName: 'Flashlight Application',
    activityDescription:
      'Turn your device into a powerful flashlight with a simple tap, providing instant light whenever you need it!',
    activityImage: 'flashlight-application',
    altText: 'Flashlight Application Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/43c29cac-9dc7-4850-8d9a-bf0ad1867c93?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  dance_party_spiderman: {
    activityName: 'Dance Party with Spiderman',
    activityDescription:
      'Make Spider-Man groove to the beat by using simple actions and music in Scratch, creating your own fun dance routine!',
    activityImage: 'dance-party-with-spiderman',
    altText: 'Dance Party with Spiderman',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/359bd39b-1375-4e84-a2fd-e797fbf43e27?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },

  python_quiz: {
    activityName: 'Python Quiz',
    activityDescription: 'Build a fun and interactive quiz game using Python.',
    activityImage: 'python-quiz',
    altText: 'Palindrome Checker Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/830dd4aa-0fb3-4d6a-8d8a-f10c5151da01?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  basic_chatbot: {
    activityName: 'Basic Chatbot',
    activityDescription:
      'Build a simple chatbot using Python that greets users, asks about their feelings, and responds to their hobbies.',
    activityImage: 'basic-chatbot',
    altText: 'Basic Chatbot Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/f5ceb016-7924-43c5-b873-176df9d96366?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  password_generator: {
    activityName: 'Password Generator',
    activityDescription:
      'Create a Python tool to generate strong, random passwords based on user-defined length.',
    activityImage: 'password-generator',
    altText: 'Password Generator Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/f01c46cf-7e6b-4367-bfa8-83347cfd3484?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },

  flappy_bird: {
    activityName: 'Flappy Bird Game',
    activityDescription:
      'Create a game where a bird avoids obstacles to score points.',
    activityImage: 'flappy-bird-game',
    altText: 'Flappy Bird Game Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/725a87a7-95e4-4c05-9eef-9ce64e9586a5?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  play_drums: {
    activityName: 'Play the Drums',
    activityDescription:
      'Create a motion-sensing drum kit using video sensing, sound effects, and event triggers in Scratch.',
    activityImage: 'play-the-drums',
    altText: 'Play the Drums Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/847a494b-3138-408d-819a-cc28fca2b168?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  space_animation: {
    activityName: 'Space Animation',
    activityDescription:
      'Use Scratch to create a scene where a rocket moves between planets while stars twinkle in the background.',
    activityImage: 'space-animation',
    altText: 'Space Animation Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/28933160-2f57-422b-adc8-8508e9ad1a1f?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  star_wars: {
    activityName: 'Star Wars',
    activityDescription:
      'Build an exciting Star Wars-themed game using block-based coding on Code.org.',
    activityImage: 'star-wars',
    altText: 'Star Wars Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/465666e3-3a57-4526-a653-4dc31f4fa643?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  save_minion: {
    activityName: 'Save the Minion',
    activityDescription:
      'Use video sensing and conditionals to interact with the screen and save a Minion by making the right moves at the right time!',
    activityImage: 'save-the-minion',
    altText: 'Save the Minion Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/06e854c6-6eee-4666-b75a-7981773ca142?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  emoji_board: {
    activityName: 'Emoji Board',
    activityDescription:
      'Create an interactive webpage that displays emojis representing moods, allowing users to explore their feelings with visual feedback.',
    activityImage: 'emoji-board',
    altText: 'Emoji Board Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/41f46e1a-bdd6-4265-8d1d-a19e95e3c392?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  student_profile_card: {
    activityName: 'Student Profile Card',
    activityDescription:
      'Design a personalized digital profile card that showcases your interests, dream job, and fun facts—styled your way with colors, photos, and more.',
    activityImage: 'student-profile-card',
    altText: 'Student Profile Card Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/b1efd76b-15c1-4574-b760-a7e95296c043?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  instagram_login_page_clone: {
    activityName: 'Instagram Login Page Clone',
    activityDescription:
      'Create a fun Instagram login page using HTML and CSS, learning to add forms, buttons, and links!',
    activityImage: 'instagram-login-page-clone',
    altText: 'Instagram Login Page Clone Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/6957c9be-71be-4c49-9f05-3d50bd2168cf?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  superhero_soundboard: {
    activityName: 'Superhero soundboard',
    activityDescription:
      'Interactive superhero soundboard webpage featuring Spiderman, Batman, and Wonder Woman images that play themed audio clips when clicked.',
    activityImage: 'superhero-soundboard',
    altText: 'Superhero soundboard Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/2b86161c-545f-4f3c-ada7-e547809cb291?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  my_ai_poet: {
    activityName: 'My AI Poet',
    activityDescription:
      'Discover AI magic by creating personalized poems based on name! Simply type a name into the AI-powered coding environment and watch ChatGPT generate a unique, creative poem instantly.',
    activityImage: 'my-ai-poet',
    altText: 'My AI Poet Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/10610fbf-edf5-495e-82cc-741b01f2dc9e?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  ai_taco: {
    activityName: 'AI Taco Chomp',
    activityDescription: `Build a fun AI-powered game where tacos fall from the sky, and players use real-time face detection to 'chomp' and eat them before they hit the ground!`,
    activityImage: 'ai-taco',
    altText: 'AI Taco Chomp Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/7be03f85-38a0-438d-9420-19b45f04337c?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  transify: {
    activityName: 'Transify',
    activityDescription:
      'Learners will create a fun translator activity in Scratch that converts text from one language to another and reads it aloud using text-to-speech',
    activityImage: 'transify',
    altText: 'Transify',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/dfa1622f-400c-4b66-854c-463a8727dbd2?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  collect_pokemons: {
    activityName: 'Collect the pokemons',
    activityDescription:
      "Create a Pokemon adventure by coding button clicks to collect characters with sound effects and narration using Code.org's App Lab.",
    activityImage: 'collect-the-pokemons',
    altText: 'Collect the pokemons Activity',
  },
  analyze_monthly_rainfall: {
    activityName: 'Analyze Monthly Rainfall',
    activityDescription:
      'Analyze and visualize monthly rainfall data using Python to calculate the average rainfall and display it in a bar chart with clear labeling and highlights.',
    activityImage: 'analyze-rainfall',
    altText: 'Analyze Monthly Rainfall Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/c4379b9f-4d7a-486c-ae39-bff1337f3915?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  score_analyzer: {
    activityName: 'Score Analyzer',
    activityDescription:
      'Create a Python program to collect subjects and exam scores, display them as a bar chart, and analyze performance with average and highest scores.',
    activityImage: 'score-analyzer',
    altText: 'Analyze Monthly Rainfall Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/254d0884-46d9-48a7-95af-1b14886f7456?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  petsona: {
    activityName: 'Petsona',
    activityDescription:
      'Students make a project where they enter their name, and ChatGPT creates a unique imaginary pet that reflects their personality traits',
    activityImage: 'petsona',
    altText: 'Petsona Activity',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/bd9ca939-4b51-4293-bc23-a630793d0878?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  ai_mood_detector: {
    activityName: 'AI Mood Detector',
    activityDescription:
      'In this activity, students will use TextBlob, a simple NLP library in Python, to detect the sentiment of any sentence they enter.',
    activityImage: 'ai-mood-detector',
    altText: 'AI Mood Detector',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/9cb311c3-c72a-4111-b04d-28e7ec39e6fb?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  ai_art_buddy: {
    activityName: 'AI Art Buddy',
    activityDescription:
      'In this Activity you will learn to use requests & PIL libraries to connect to an AI-Powered Image generation API',
    activityImage: 'ai-art-buddy',
    altText: 'AI Art Buddy',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/158d8e76-26d3-4f40-8fe1-f48a406c0a75?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  ai_guessmaster: {
    activityName: 'AI GuessMaster',
    activityDescription:
      'Build an exciting AI-powered game that guesses your secret number based on your hints and feedback',
    activityImage: 'ai-guessmaster',
    altText: 'AI GuessMaster',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/fa000353-2d3c-4eda-b011-9fa532a1c078?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  fun_picture_description: {
    activityName: 'Fun picture description',
    activityDescription:
      'Describe what you see using clear details and complete sentences. Build vocabulary, observation skills, and organized speaking.',
    activityImage: 'fun-picture-description',
    altText: 'Fun picture description',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/7b002e76-27c3-457c-b68c-500bd4c17bc6?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  impromptu_spin: {
    activityName: 'Impromptu spin',
    activityDescription:
      'Pick a random topic and speak confidently on the spot. Learn quick thinking with a simple speaking structure.',
    activityImage: 'impromptu-spin',
    altText: 'Impromptu spin',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/5c54da5a-548a-4e95-98d4-f2e45f051b91?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  introduction_to_elocution: {
    activityName: 'Introduction to elocution',
    activityDescription:
      'Add clarity and emotion through better pronunciation and expression. Practice speaking crisp words with the right tone and pauses.',
    activityImage: 'introduction-to-elocution',
    altText: 'Introduction to elocution',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/4c9a9060-1e2b-45fb-8fcc-e182f1bece59?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  mini_presentation_pitch: {
    activityName: 'Mini presentation pitch',
    activityDescription:
      'Present a topic with a strong opening, key points, and a clear closing. Build confidence, structure, and audience-friendly delivery.',
    activityImage: 'mini-presentation-pitch',
    altText: 'Mini presentation pitch',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/7220c5e2-9c10-466d-8e8f-78235c4d8eb4?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  my_storytelling_time: {
    activityName: 'My storytelling time',
    activityDescription:
      'Tell a stronger story with structure, emotion, and smooth flow. Use voice and gestures to bring characters and moments to life.',
    activityImage: 'my-storytelling-time',
    altText: 'My storytelling time',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/0c4d9ebd-95b6-43bb-ac7e-91f15b2a935c?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  my_talking_voice: {
    activityName: 'My talking voice',
    activityDescription:
      'Practice voice modulation and confident body language while speaking. Learn volume, pace, and expression to sound clear and engaging.',
    activityImage: 'my-talking-voice',
    altText: 'My talking voice',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/a2466d4f-6c25-404e-8842-add8a9c36fd5?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  my_magic_voice: {
    activityName: 'My magic voice',
    activityDescription:
      'Fun, beginner-friendly speaking activities where kids learn to talk clearly and confidently. They practice voice, expressions, and simple stories with gentle teacher feedback.',
    activityImage: 'my-magic-voice',
    altText: 'My magic voice',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/28ed0c31-7e93-4dc8-bc2d-026723c0e7b1?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  story_fun_time: {
    activityName: 'Story fun time',
    activityDescription:
      'Short, playful trial activities that help kids speak in complete sentences with confidence. Through games and mini talks, they learn voice control, body language, and storytelling.',
    activityImage: 'story-fun-time',
    altText: 'Story fun time',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/c037bc80-5873-4b44-92c6-cfae8cf440d7?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  picture_talk_game: {
    activityName: 'Picture talk game',
    activityDescription:
      'Perfect for first-time speakers. Kids practice self-introductions, emotions, and clear speech. Each activity ends with a small speaking output and supportive feedback.',
    activityImage: 'picture-talk-game',
    altText: 'Picture talk game',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/f9b1c43c-bcfe-4a1e-bd1d-2d3c4f4142b1?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  spin_and_share: {
    activityName: 'Spin and share',
    activityDescription:
      'Pick a fun topic and share a few sentences with a clear start and end. Build confidence by organizing thoughts before speaking.',
    activityImage: 'spin-and-share',
    altText: 'Spin and share',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/76ecd053-3ab8-4626-b568-19204a0ea59c?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  speak_with_feeling: {
    activityName: 'Speak with feeling',
    activityDescription:
      'Say the same line using different emotions with voice and facial expressions. Learn how tone and expressions change the meaning of words.',
    activityImage: 'speak-with-feeling',
    altText: 'Speak with feeling',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/986ca041-7c12-40c5-91a2-07d50e0d6e03?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  my_first_mini_talk: {
    activityName: 'My first mini talk',
    activityDescription:
      'Deliver a short talk with a clear hello, main idea, and goodbye. Practice speaking smoothly with confidence and simple structure.',
    activityImage: 'my-first-mini-talk',
    altText: 'My first mini talk',
    videoURL:
      'https://iframe.mediadelivery.net/embed/239253/289045ed-ea3f-48a2-8086-dfbb02ef4bb6?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
  },
  image_recognizer: {
    activityName: 'Image Recognizer',
    activityDescription:
      'Build an app that spots images, describes them, and reads results aloud with AI.',
    activityImage: 'image-recognizer',
    altText: 'Image Recognizer',
  },
  dance_party: {
    activityName: 'Dance Party',
    activityDescription:
      'Create an interactive dance party animation where characters move to the beat using loops, events, and sound blocks in Scratch.',
    activityImage: 'dance-party',
    altText: 'Dance Party Activity',
  },
  hungry_casey_2: {
    activityName: 'Hungry Casey 2',
    activityDescription:
      'Build a sequel to the classic game where Casey collects food using conditionals, loops, and scoring logic in a fun platform-style project.',
    activityImage: 'hungry-casey-2',
    altText: 'Hungry Casey 2 Activity',
  },
  calculator: {
    activityName: 'Calculator',
    activityDescription:
      'Build a fully functional calculator using Python or HTML/CSS, applying variables, functions, and user input to create a real-world app.',
    activityImage: 'calculator',
    altText: 'Calculator Activity',
  },
  health_guide_app: {
    activityName: 'Health Guide App',
    activityDescription:
      'Create a health guide app that gives tips based on user input, combining conditionals, loops, and simple data to build something genuinely useful.',
    activityImage: 'health-guide-app',
    altText: 'Health Guide App Activity',
  },
  ai_birthday_wishes: {
    activityName: 'AI Birthday Wishes',
    activityDescription:
      'Use AI to generate personalized birthday messages based on a name and fun facts, combining coding with beginner AI tools.',
    activityImage: 'ai-birthday-wishes',
    altText: 'AI Birthday Wishes Activity',
  },
  registeration_form: {
    activityName: 'Registeration Form',
    activityDescription:
      'Create a sign-up form using HTML to collect user details like name and email.',
    activityImage: 'registration-form',
    altText: 'Registeration Form',
  },
}

const {
  swift_lingo,
  palindrome_checker,
  reverse_string,
  flappy_bird,
  star_wars,
  emoji_board,
  student_profile_card,
  instagram_login_page_clone,
  superhero_soundboard,
  my_ai_poet,
  ai_taco,
  transify,
  collect_pokemons,
  analyze_monthly_rainfall,
  score_analyzer,
  petsona,
  ai_mood_detector,
  ai_art_buddy,
  ai_guessmaster,
  fun_picture_description,
  impromptu_spin,
  introduction_to_elocution,
  mini_presentation_pitch,
  my_storytelling_time,
  my_talking_voice,
  my_magic_voice,
  story_fun_time,
  picture_talk_game,
  spin_and_share,
  speak_with_feeling,
  my_first_mini_talk,
  dance_party_spiderman,
  play_drums,
  image_recognizer,
  save_minion,
  space_animation,
  python_quiz,
  basic_chatbot,
  password_generator,
  registeration_form,
} = PLATFORM_ACTIVITIES

export const ACTIVITIES: Record<CourseNameType, ActivitiesType> = {
  SCRATCH_PROGRAMMING: {
    title: '40+ Fun activities and lessons',
    activities: [
      {
        activityURL: 'https://scratch.mit.edu/projects/601010296/embed',
        activityName: 'Dance Party with Motu Patlu',
        activityDescription:
          'Dance with your favorite cartoon character  while learning Scratch.',
        altText: 'Dance Party with Motu Patlu on Scratch',
        activityImage: 'scratch-programming/dance-party-with-motu-patlu',
      },
      {
        activityURL: 'https://scratch.mit.edu/projects/617242446/embed',
        activityName: 'Mario Dash',
        altText: 'Mario Dash using Scratch platform',
        activityDescription:
          'Create your own version of the popular game Super Mario and score points for avoiding obstacles in your path.',
        activityImage: 'scratch-programming/mario-dash-v1',
      },
      {
        activityURL: 'https://scratch.mit.edu/projects/617240132/embed',
        activityName: 'Ping Pong Game',
        altText: 'Ping Pong game on Scratch',
        activityDescription:
          'Play your favorite ping pong game while learning Scratch.',
        activityImage: 'scratch-programming/ping-pong-game',
      },
      {
        activityURL: 'https://scratch.mit.edu/projects/617242052/embed',
        activityName: 'Integer Game',
        altText: 'Integer Game',
        activityDescription:
          'Create a game to learn about integers using a number line.',
        activityImage: 'scratch-programming/integer-game-v1',
      },
      {
        activityURL: 'https://scratch.mit.edu/projects/587229635/embed',
        activityName: 'Card Creator',
        altText: 'Card Creator on Scratch',
        activityDescription:
          'Create cards for any ocassion while learning Scratch.',
        activityImage: 'scratch-programming/card-creator',
      },
      {
        activityURL: 'https://scratch.mit.edu/projects/599455896/embed',
        activityName: 'Space Animation',
        altText: 'Space animation on Scratch',
        activityDescription:
          'Dive in to the infinite space with Space animation while learning Scratch.',
        activityImage: 'scratch-programming/space-animation',
      },
    ],
  },
  HPE_CODEWARS_HACKATHON: {
    title: 'Activities to master Hackathon',
    activities: [
      {
        activityName: 'Carousels',
        activityDescription:
          'Create a carousel to display multiple images that users can scroll through and to make your website more interactive.',
        activityImage: 'hpe-codewars-hackathon/activities/game-1',
        altText: 'Carousels',
      },
      {
        activityName: 'Box model',
        activityDescription:
          'Create multiple sections to display different kinds of content and to make your website easy to read and navigate.',
        activityImage: 'hpe-codewars-hackathon/activities/game-2',
        altText: 'Box model',
      },
      {
        activityName: 'Containers',
        activityDescription:
          'Create colorful boxes to give your website a bold, sprightly look and to make the content on your website stand out.',
        activityImage: 'hpe-codewars-hackathon/activities/game-3',
        altText: 'Containers',
      },
    ],
  },
  HPE_CODEWARS_CODE_BATTLE: {
    title: 'Activities to master the Code Battle',
    activities: [
      {
        activityName: 'Two odd occurring numbers',
        activityDescription:
          'If you are given a list of numbers in which all numbers occur at even times except two, write a program to find those two numbers.',
        activityImage: 'hpe-codewars-code-battle/activities/game-1',
        altText: 'Two odd occurring numbers',
      },
      {
        activityName: 'Generate all substrings of a given string',
        activityDescription:
          'Write a program that generates all the possible substrings from any string entered by the user.',
        activityImage: 'hpe-codewars-code-battle/activities/game-2',
        altText: 'Generate all substrings of a given string',
      },
      {
        activityName: 'Swap two numbers without using third variable',
        activityDescription:
          'Write a program to swap two numbers without using a third variable using Bitwise operators.',
        activityImage: 'hpe-codewars-code-battle/activities/game-3',
        altText: 'Swap two numbers without using third variable',
      },
    ],
  },
  PYTHON_FOR_KIDS: {
    title:
      '90+ Fun activities and lessons for kids to learn Python programming',
    activities: [
      {
        activityName: 'Space Invader game',
        activityDescription:
          'Learn to create an exciting video game that can help the player shoot the space invaders and gain points using the Python library Pygame.',
        activityImage: 'python-for-kids/activities/game-1',
        altText: 'Space Invader game',
        activityURL:
          'https://replit.com/@Codingal/Space-Invader-Game?v=1&embed=true',
      },
      {
        activityName: 'Diamond Pattern',
        activityDescription:
          'Learn how to create a diamond shaped pattern using nested loops in Python.',
        activityImage: 'python-for-kids/activities/game-2',
        altText: 'Diamond Pattern',
        activityURL: 'https://replit.com/@Codingal/L5BF1?v=1&embed=true',
      },
      {
        activityName: 'Denomination Calculator',
        activityDescription:
          'Create a multi window GUI application that can help the user to calculate the total number of denominations of 2000, 500 and 100 for amount entered, by using the Python library Tkinter.',
        activityImage: 'python-for-kids/activities/game-3',
        altText: 'Denomination Calculator',
        activityURL:
          'https://replit.com/@Codingal/Denomination-Counter-Application?v=1&embed=true',
      },
      {
        activityName: 'Restaurant Management System',
        activityDescription:
          'Create a Restaurant Management System to bill and track orders using the Python library Tkinter.',
        activityImage: 'python-for-kids/activities/game-4',
        altText: 'Restaurant Management System',
        activityURL: 'https://replit.com/@Codingal/SPCM7L6A1?v=1&embed=true',
      },
      {
        activityName: 'Text Editor',
        activityDescription:
          'Build text editor to save and open your files using the Python library Tkinter.',
        activityImage: 'python-for-kids/activities/game-5',
        altText: 'Text Editor',
        activityURL: 'https://replit.com/@Codingal/SPCM7L4A1?v=1&embed=true',
      },
      {
        activityName: 'Pygame',
        activityDescription:
          'Create exciting games you played in your childhood using the Pygame library in Python.',
        activityImage: 'python-for-kids/activities/game-6',
        altText: 'Pygame',
        activityURL:
          'https://replit.com/@Codingal/Level-up-this-game?v=1&embed=true',
      },
    ],
  },
  ROBLOX: {
    title: '90+ Fun activities and lessons for kids to learn Roblox',
    activities: [
      {
        activityName: 'Blocker game',
        activityDescription:
          'Build a blocker game in which you can change the speed and height of the player.',
        activityImage: 'roblox/activities/game-1',
        altText: 'Blocker game',
      },
      {
        activityName: 'End game',
        activityDescription:
          'Learn to perform various tasks by making numerous obstacles including disappearing obstacles, lava kill blocks and obstacles of different shapes.',
        activityImage: 'roblox/activities/game-2',
        altText: 'End game',
      },
      {
        activityName: 'Scrambled legs',
        activityDescription:
          'In this game, you have to get through multiple levels to reach the final goal, but it won‘t be that easy! You will have high jumps to take, tough choices to make and race against time.',
        activityImage: 'roblox/activities/game-3',
        altText: 'Scrambled legs',
      },
      {
        activityName: 'Magical stairway',
        activityDescription:
          'In this game, you have to get through the stairway, but beware, they keep on disappearing.',
        activityImage: 'roblox/activities/game-4',
        altText: 'Magical stairway',
      },
    ],
  },
  AP_COMPUTER_SCIENCE_A: {
    title:
      '100+ Fun activities and lessons for teens to learn AP Computer Science A',
    activities: [
      {
        activityName: 'Hey Java',
        activityDescription:
          'Creating the First program in Java. i.e., Hello Java',
        activityImage: 'ap-computer-science-a/activities/game-1',
        altText: 'Hey Java',
      },
      {
        activityName: 'Intro to Data Types',
        activityDescription: 'In this program, you will learn data types',
        activityImage: 'ap-computer-science-a/activities/game-2',
        altText: 'Intro to Data Types',
      },
      {
        activityName: 'Inheritance in one shot',
        activityDescription:
          'In this program, you will learn the concept of inheritance.',
        activityImage: 'ap-computer-science-a/activities/game-3',
        altText: 'Inheritance in one shot',
      },
      {
        activityName: 'Method Overloading',
        activityDescription:
          'In this activity, you will learn about method overloading in Java.',
        activityImage: 'ap-computer-science-a/activities/game-4',
        altText: 'Method Overloading',
      },
    ],
  },

  AI_AND_ML: {
    title: 'Fun trial lessons for kids & teens to learn AI & Machine Learning',
    description:
      'Our curriculum team invites your child to a fun coding trial lesson! Choose from six exciting activities for beginners and advanced learners. In a 45-minute 1:1 class, they’ll create a live project. Sign up for a free lesson today to kickstart their coding and AI journey!',
    activities: [
      ai_taco,
      petsona,
      transify,
      ai_mood_detector,
      ai_art_buddy,
      ai_guessmaster,
    ],
    conclusion: (
      <>
        <p>
          For beginners, the{' '}
          <span className="font-600">
            AI Genius — ChatGPT, AI-powered Apps & Games
          </span>{' '}
          introduces students to the exciting world of AI through platforms like
          Scratch, Thunkable, and Pictoblox and so on. This hands-on course
          ensures that students build a project in every class, making learning
          interactive and fun. They’ll create AI-powered art and music, classify
          doodles, and build chatbots, stories, games, and apps while exploring
          concepts like sentiment analysis, sound analysis, image and text
          recognition.
        </p>
        <p>
          For more advanced learners, the{' '}
          <span className="font-600">
            AI Expert— Hands-On AI, LLMs & Python
          </span>{' '}
          course offers a project-based experience. Students use LLMs for
          text-to-image, image-to-text, and text generation, and work with LLMs
          available on Hugging Face and Google Gemini APIs to create chatbots,
          translators, and image recognition tools, learning prompt engineering
          along the way.
        </p>
        <p>
          Codingal provides a fun, gamified learning experience with expert
          teachers, making it the best coding school for kids and teens to dive
          into AI!
        </p>
      </>
    ),
  },

  THUNKABLE: {
    title: 'Fun activities and lessons for kids to learn Thunkable',
    description:
      'Our curriculum team invites your child to a fun coding trial lesson! Choose from six exciting activities for beginners and advanced learners. In a 45-minute 1:1 class, they’ll create a live project. Sign up for a free lesson today to kickstart their coding and AI journey!',
    activities: [flappy_bird, collect_pokemons, star_wars],
  },
  JAVA: {
    title: 'Fun activities and lessons for kids to learn Java',
    description:
      'At Codingal, learning Java is an adventure filled with engaging and creative projects that spark your child’s imagination and enhance their programming skills. Here are some of the fun activities your child will enjoy:',
    activities: [palindrome_checker, reverse_string],
  },
  TRINKET: {
    title: 'Fun trial class activities and lessons for kids to learn Trinket',
    description:
      "Learning Trinket with Codingal is far from monotonous - it's a blast! We incorporate interactive activities, quizzes, and projects that make the learning experience super enjoyable. Students get to design their own web pages, create digital stories, and even build simple games, all while understanding the underlying Trinket structures. Get ready for some serious fun!",
    activities: [flappy_bird, collect_pokemons, star_wars],
  },
  PYGAME: {
    title: 'Fun trial class activities and lessons for kids to learn Pygame',
    description:
      "Learning Pygame with Codingal is far from monotonous - it's a blast! We incorporate interactive activities, quizzes, and projects that make the learning experience super enjoyable. Students get to design their own web pages, create digital stories, and even build simple games, all while understanding the underlying Pygame structures. Get ready for some serious fun!",
    activities: [student_profile_card, emoji_board, instagram_login_page_clone],
  },
  ANIMATION: {
    title: 'Fun trial class activities and lessons for kids to learn Animation',
    description:
      "Learning Animation with Codingal is far from monotonous - it's a blast! We incorporate interactive activities, quizzes, and projects that make the learning experience super enjoyable. Students get to design their own web pages, create digital stories, and even build simple games, all while understanding the underlying Animation structures. Get ready for some serious fun!",
    activities: [flappy_bird, collect_pokemons, star_wars],
  },
  TURTLE: {
    title:
      'Fun trial class activities and lessons for kids to learn Python Turtle',
    description:
      "Learning Python Turtle with Codingal is far from monotonous - it's a blast! We incorporate interactive activities, quizzes, and projects that make the learning experience super enjoyable. Students get to design their own web pages, create digital stories, and even build simple games, all while understanding the underlying Python Turtle structures. Get ready for some serious fun!",
    activities: [flappy_bird, collect_pokemons, star_wars],
  },
  MIT_APP_INVENTOR: {
    title: 'Fun activities and lessons for kids to learn MIT App Inventor',
    description:
      'MIT App Inventor is the perfect starting point for beginners, and at Codingal, we make learning coding for kids even more exciting with a curriculum designed just for kids like you! Our lessons teach the basics of coding while encouraging creativity, allowing students to build real apps like quiz games, health trackers, or even a doodling board. They’ll learn how coding works, solve problems, and develop important skills for the digital world. Join us and start your child’s coding for kids journey today!',
    activities: [swift_lingo],
  },
  BOOTSTRAP: {
    title: 'Fun trial class activities and lessons for kids to learn Bootstrap',
    description:
      "Learning Bootstrap with Codingal is far from monotonous - it's a blast! We incorporate interactive activities, quizzes, and projects that make the learning experience super enjoyable. Students get to design their own web pages, create digital stories, and even build simple games, all while understanding the underlying Bootstrap structures. Get ready for some serious fun!",
    activities: [student_profile_card, emoji_board, instagram_login_page_clone],
  },
  JAVASCRIPT: {
    title:
      'Fun trial class activities and lessons for kids to learn Javascript',
    description:
      "Learning  Javascript with Codingal is far from monotonous - it's a blast! We incorporate interactive activities, quizzes, and projects that make the learning experience super enjoyable. Students get to design their own web pages, create digital stories, and even build simple games, all while understanding the underlying Javascript structures. Get ready for some serious fun!",
    activities: [flappy_bird, collect_pokemons, star_wars],
  },
  CSS: {
    title: 'Fun trial lessons for kids & teens to learn CSS',
    description:
      'Our trial CSS lessons are designed to spark creativity and make web design for kids fun from the very first class. Kids and teens get hands-on with styling their own webpages by changing colors, adjusting fonts, and experimenting with layouts in real time. Guided by expert instructors, students see their code instantly transform into beautiful designs, making the learning process both exciting and rewarding. It’s the perfect introduction to the visual side of web development, and a great way to learn HTML and CSS free. Many find this the best way to explore creative coding for kids.',
    activities: [superhero_soundboard, instagram_login_page_clone, emoji_board],
  },
  HTML: {
    title: 'Fun trial class activities and lessons for kids to learn HTML',
    description:
      "Learning HTML with Codingal is far from monotonous - it's a blast! We incorporate interactive activities, quizzes, and projects that make the learning experience super enjoyable. Students get to design their own web pages, create digital stories, and even build simple games, all while understanding the underlying HTML structures. Get ready for some serious fun!",
    activities: [student_profile_card, emoji_board, instagram_login_page_clone],
  },
  AICODE101: {
    title: 'Fun trial lessons for kids & teens to learn AI code 101',
    description:
      'At Codingal, each trial class gives your child hands-on AI projects that spark creativity, curiosity, and confidence in coding. Choose from three free AI for kids trial lessons to get started and explore the exciting world of AI.',
    activities: [ai_taco, my_ai_poet, transify],
  },
  CODE_ORG: {
    title: 'Fun trial lessons for kids & teens to learn Code.org',
    description:
      'At Codingal, each trial class provides your child with hands-on Code.org projects that spark creativity, curiosity, and confidence in coding. Choose from three free Code.org trial lessons for kids to get started and explore the exciting world of coding.',
    activities: [flappy_bird, collect_pokemons, star_wars],
  },
  PICTOBLOX: {
    title: 'Fun trial lessons for kids & teens to learn Pictoblox',
    description:
      'At Codingal, PictoBlox isn’t just taught; it’s experienced. Each trial class introduces your child to the exciting world of AI through creative, hands-on coding that sparks imagination and builds real tech confidence.',
    activities: [my_ai_poet, transify],
  },
  TEACHABLE_MACHINE: {
    title: 'Fun trial lessons for kids & teens to learn Teachable Machine',
    description:
      'At Codingal, AI isn’t just explained; it’s experienced. Each trial class immerses your child in hands-on learning using Teachable Machine and Scratch, where they train real AI models and build interactive projects. It’s the perfect blend of fun and future-ready tech skills.',
    activities: [my_ai_poet, transify],
  },
  GOOGLE_COLAB: {
    title: 'Fun trial lessons for kids & teens to learn Google Colab',
    description:
      'Each lesson is paired with engaging, hands-on activities designed specifically for kids and teens. Whether it’s predicting monthly rainfall or visualizing their exam score, students explore exciting projects while learning how to code and analyze data in Google Colab.',
    activities: [analyze_monthly_rainfall, score_analyzer],
  },
  NODE_JS: {
    title: 'Fun trial lessons for kids & teens to learn Node.js',
    description:
      'At Codingal, students are encouraged to build a strong foundation in JavaScript, which serves as a stepping stone to advanced coding concepts like Node.js and React.js. In our trial lessons, students dive into two fun JavaScript activities: Superhero Soundboard and Emoji Board. Each activity is designed to spark creativity while deepening their understanding of web development.',
    activities: [superhero_soundboard, emoji_board],
  },
  VISUAL_STUDIO_CODE: {
    title:
      'Fun trial class activities and lessons for kids to learn Visual Studio Code',
    description:
      "Learning Visual Studio Code with Codingal is far from monotonous; it's a blast! We incorporate interactive activities, quizzes, and projects that make the learning experience super enjoyable. Students get to design their own web pages, create digital stories, and even build simple games, all while understanding the underlying Visual Studio Code structures. Get ready for some serious fun!",
    activities: [student_profile_card, emoji_board, instagram_login_page_clone],
  },
  SQL: {
    title: 'Fun trial class activities and lessons for kids to learn SQL',
    description:
      'Learning SQL with Codingal isn’t just about theory but it’s about applying the concepts through fun and interactive projects. Each lesson is designed to help your child work with real data, query and organize it, and apply their knowledge to build projects that reflect real-world applications. From data sorting to creating dashboards, students will enjoy the hands-on learning experience while building skills that are essential for the future.',
    activities: [flappy_bird, analyze_monthly_rainfall],
  },
  REACT_JS: {
    title: 'Fun trial class activities and lessons for kids to learn React.js',
    description:
      "Learning  React.js with Codingal is far from monotonous - it's a blast! We incorporate interactive activities, quizzes, and projects that make the learning experience super enjoyable. Students get to design their own web pages, create digital stories, and even build simple games, all while understanding the underlying React.js structures. Get ready for some serious fun!",
    activities: [flappy_bird, collect_pokemons, star_wars],
  },
  GIT: {
    title: 'Fun trial class activities and lessons for kids to learn GIT',
    description:
      "Learning GIT with Codingal is far from monotonous - it's a blast! We incorporate interactive activities, quizzes, and projects that make the learning experience super enjoyable. Students get to design their own web pages, create digital stories, and even build simple games, all while understanding the underlying GIT structures. Get ready for some serious fun!",
    activities: [student_profile_card, emoji_board, instagram_login_page_clone],
  },
  GITHUB: {
    title: 'Fun trial class activities and lessons for kids to learn GitHub',
    description:
      "Learning GitHub with Codingal is far from monotonous - it's a blast! We incorporate interactive activities, quizzes, and projects that make the learning experience super enjoyable. Students get to design their own web pages, create digital stories, and even build simple games, all while understanding the underlying GitHub structures. Get ready for some serious fun!",
    activities: [student_profile_card, emoji_board, instagram_login_page_clone],
  },
  PUBLIC_SPEAKING_GRADE_3_5: {
    title:
      'Fun trial lessons for kids and teens to learn Public Speaking & Communication (Grades 3-5)',
    description:
      'Choose from fun trial lessons designed for beginners and confident speakers. In a 30-minute live class, your child creates a speaking output and gets real feedback from an expert teacher.',
    activities: [
      my_talking_voice,
      my_storytelling_time,
      fun_picture_description,
      impromptu_spin,
      introduction_to_elocution,
      mini_presentation_pitch,
    ],
    conclusion: (
      <>
        <p>
          In the free trial lesson, your child joins a live online class with an
          expert teacher and gets a fun, guided speaking activity based on their
          grade and comfort level. The teacher starts with a quick warm-up to
          help your child feel relaxed, then helps them create a short speaking
          output such as a confident self-introduction, a mini story, or a
          60-second speech with a simple structure. Your child gets real-time
          feedback on voice clarity, pacing, posture, and confidence, and then
          gets a chance to try again with improvements.
        </p>
        <p>
          Parents are welcome to observe the session. By the end of the trial,
          you will clearly understand your child’s current speaking level, what
          skills they should build next, and which course level is the best fit.
        </p>
      </>
    ),
  },
  PUBLIC_SPEAKING_GRADE_1_2: {
    title:
      'Fun trial lessons for kids and teens to learn Public Speaking & Communication (Grades 1-2)',
    description:
      'Choose from fun trial lessons designed for beginners and confident speakers. In a 30-minute live class, your child creates a speaking output and gets real feedback from an expert teacher.',
    activities: [
      my_magic_voice,
      story_fun_time,
      picture_talk_game,
      spin_and_share,
      speak_with_feeling,
      my_first_mini_talk,
    ],
    conclusion: (
      <>
        <p>
          In the free trial lesson, your child joins a live online class with an
          expert teacher and gets a fun, guided speaking activity based on their
          grade and comfort level. The teacher starts with a quick warm-up to
          help your child feel relaxed, then helps them create a short speaking
          output such as a confident self-introduction, a mini story, or a
          60-second speech with a simple structure. Your child gets real-time
          feedback on voice clarity, pacing, posture, and confidence, and then
          gets a chance to try again with improvements.
        </p>
        <p>
          Parents are welcome to observe the session. By the end of the trial,
          you will clearly understand your child&apos;s current speaking level,
          what skills they should build next, and which course level is the best
          fit.
        </p>
      </>
    ),
  },
  CODING_GRADE_1_3: {
    title: 'Try a free coding class for Grades 1 to 3',
    description:
      'Every family can book a free 45 minute trial lesson. Your child works 1:1 with a Codingal teacher, completes a real project and receives feedback. No installation, payment or prior experience is required. Parents are welcome to observe the trial.',
    activities: [
      dance_party_spiderman,
      play_drums,
      my_ai_poet,
      ai_taco,
      transify,
      image_recognizer,
    ],
  },
  CODING_GRADE_4_5: {
    title: 'Try a free coding class for Grades 4 and 5',
    description:
      'Every family can book a free 45 minute trial lesson. Your 9 to 11 year old works 1:1 with a Codingal teacher, builds a real project from start to finish and receives feedback. No installation, payment or prior experience is required. Parents are welcome to observe.',
    activities: [
      save_minion,
      space_animation,
      my_ai_poet,
      collect_pokemons,
      transify,
      star_wars,
    ],
  },
  CODING_GRADE_6_8: {
    title: 'Try a free coding lesson for kids in Grades 6 to 8',
    description:
      'Pick a trial lesson that matches where your middle schooler is right now. In a live Codingal class, 6th, 7th, and 8th graders dive into a real project and get expert guidance tailored to their level and interests.',
    activities: [
      save_minion,
      space_animation,
      swift_lingo,
      python_quiz,
      star_wars,
      registeration_form,
    ],
  },
  CODING_GRADE_9_10: {
    title: 'Try a free coding lesson for teens in Grades 9 and 10',
    description:
      'Pick a trial lesson built for the level your 14 to 16 year old is ready for. Ninth and tenth graders work through a structured project live with an expert Codingal teacher who understands exactly what ambitious students need.',
    activities: [
      student_profile_card,
      instagram_login_page_clone,
      emoji_board,
      basic_chatbot,
      password_generator,
      score_analyzer,
    ],
  },
  CODING_GRADE_11_12: {
    title: 'Try a free coding lesson for teens in Grades 11 and 12',
    description:
      'Pick a trial lesson that matches your goals. Junior and senior students work through an advanced project live with an expert Codingal teacher, getting a clear sense of the pathway ahead for college applications and tech careers.',
    activities: [
      student_profile_card,
      instagram_login_page_clone,
      basic_chatbot,
      password_generator,
      score_analyzer,
      analyze_monthly_rainfall,
    ],
  },
}

export const TESTIMONIAL: Record<CourseNameType, TestimonialType> = {
  SCRATCH_PROGRAMMING: {
    courseName: 'Scratch Programming',
    testimonials: [
      {
        image: 'scratch-programming/testimonials/james',
        name: 'James',
        content:
          'I really enjoy my Scratch classes, my teacher is very kind. I like Codingal classes very much.',
        subheading: 'Grade 1, Codingal Student',
      },
      {
        image: 'scratch-programming/testimonials/amelia',
        name: 'Amelia',
        content:
          'I am learning to create games using the blocks. My teacher makes the class more fun for me, thank you Codingal.',
        subheading: 'Grade 4, Codingal Student',
      },
      {
        image: 'scratch-programming/testimonials/vikrant',
        name: 'Vikrant',
        content:
          'I recently created an integer game on Scratch. I am excited about creating many more games and stories.',
        subheading: 'Grade 3, Codingal Student',
      },
      {
        image: 'scratch-programming/testimonials/ada',
        name: 'Ada Sade',
        content:
          "With Codingal's Scratch course, my daughter started her 1st coding lesson. The teachers are very nice and experienced.",
        subheading: 'Codingal Parent',
        profession: 'Nurse',
      },
      {
        image: 'scratch-programming/testimonials/nala',
        name: 'Nala',
        content:
          'I enjoy dragging and dropping the blocks, the blocks are so colorful. Scratch classes are fun.',
        subheading: 'Grade 2, Codingal Student',
      },
      {
        image: 'scratch-programming/testimonials/john',
        name: 'John Thomas',
        content:
          'Codingal has made my child excited to learn coding. He always look forward to the next class.',
        subheading: 'Codingal Parent',
        profession: 'Researcher',
      },
    ],
  },
  HPE_CODEWARS_HACKATHON: {
    courseName: 'HPE Codewars Hackathon',
    testimonials: [
      {
        image: 'hpe-codewars-hackathon/testimonials/siddharth',
        name: 'Siddharth Narayanabhatla',
        content:
          'Along with developing websites, this course has also helped me improve my problem solving skills.',
        subheading: 'Grade 8, Codingal Student',
      },
      {
        image: 'hpe-codewars-hackathon/testimonials/soumya',
        name: 'Soumya Ranjan',
        content:
          'I am learning to create websites using HTML and CSS which is so fun that it makes me want to learn more.',
        subheading: 'Grade 9, Codingal Student',
      },
      {
        image: 'hpe-codewars-hackathon/testimonials/aditya',
        name: 'Aditya Viswanadha',
        content:
          'The course is very interesting. The hands-on projects helped me to improve my website building skills.',
        subheading: 'Grade 9, Codingal Student',
      },
      {
        image: 'hpe-codewars-hackathon/testimonials/seema',
        name: 'Seema Singh',
        content:
          'I cannot believe that my kid has learned to develop amazing websites at an early age. Thank you Codingal.',
        subheading: 'Codingal Parent',
      },
    ],
  },
  HPE_CODEWARS_CODE_BATTLE: {
    courseName: 'HPE Codewars Code Battle',
    testimonials: [
      {
        image: 'hpe-codewars-code-battle/testimonials/mohit',
        name: 'Mohit Hulse',
        content:
          'I always enjoy Codingal competitions as the increasing difficulty levels as we progress make it so much more exciting.',
        subheading: 'HPE CodeWars Winner, Grade 11',
      },
      {
        image: 'hpe-codewars-code-battle/testimonials/siddhant',
        name: 'Siddhant',
        content:
          'Codingal gave me the once-in-a-lifetime experience of seeing my code run on the International Space Station.',
        subheading: 'HPE CodeWars Winner, Grade 10',
      },
      {
        image: 'hpe-codewars-code-battle/testimonials/swastik',
        name: 'Swastik',
        content:
          'I won the HPE CodeWars competition and got the opportunity to run an experiment on the ISS thanks to Codingal.',
        subheading: 'HPE CodeWars Winner, Grade 12',
      },
      {
        image: 'hpe-codewars-code-battle/testimonials/sushil',
        name: 'Sushil Raaja U',
        content:
          'As a student preparing for the IOI, Codingal has helped me greatly improve my problem solving and algorithmic thinking.',
        subheading: 'HPE Code Battle #1 Winner, Grade 11',
      },
      {
        image: 'hpe-codewars-code-battle/testimonials/aarav',
        name: 'Aarav Eeshan',
        content:
          'The algorithmic problems in Codingal’s competitions are super intriguing and I always get to learn something new.',
        subheading: 'HPE Code Battle Winner, Grade 11',
      },
    ],
  },
  PYTHON_FOR_KIDS: {
    courseName: 'Python for Kids',
    testimonials: [
      {
        image: 'python-for-kids/testimonials/lily',
        name: 'Lily Zane',
        content:
          'Codingal has helped me learn Python by making the learning process fun and interactive. Colorful syntax and indentation make writing code enjoyable.',
        subheading: 'Grade 9, Codingal Student',
      },
      {
        image: 'python-for-kids/testimonials/ishan',
        name: 'Ishan',
        content:
          'My teacher gives very clear instruction. With Python programming course by Codingal, I can create my own games.',
        subheading: 'Grade 7, Codingal Student',
      },
      {
        image: 'python-for-kids/testimonials/maxwell',
        name: 'Maxwell',
        content:
          'Codingal has helped me transition from block based programming to text based programming through their Python programming course.',
        subheading: 'Grade 6, Codingal Student',
      },
      {
        image: 'python-for-kids/testimonials/juke',
        name: 'Juke Williams',
        content:
          "Codingal's Python programming course made it easier for my child to learn to build his favorite games.",
        subheading: 'Codingal Parent',
      },
      {
        image: 'python-for-kids/testimonials/julia',
        name: 'Julia',
        content:
          'The Python course at Codingal help me in scripting and creating data sets. The Python libraries make it easy to share code projects.',
        subheading: 'Grade 10, Codingal Student',
      },
      {
        image: 'python-for-kids/testimonials/andrea',
        name: 'Andrea Agnes',
        content:
          "Codingal's personalized journey has really helped my child learn Python at her own pace and understanding.",
        subheading: 'Codingal Parent',
      },
    ],
  },
  ROBLOX: {
    courseName: 'Roblox',
    testimonials: [
      {
        image: 'roblox/testimonials/mike',
        name: 'Mike',
        content:
          'The Roblox course at Codingal has helped me create my games. It is very straightforward, and I can build my own characters, terrains, and more.',
        subheading: 'Grade 5, Codingal Student',
      },
      {
        image: 'roblox/testimonials/neha',
        name: 'Neha',
        content:
          'I like to code because I can program my things and create the games I want! Whatever comes into my imagination, I can code it.',
        subheading: 'Grade 7, Codingal Student',
      },
      {
        image: 'roblox/testimonials/elliot',
        name: 'Elliot Aidan',
        content:
          'I have tried other coding programs for my kid, but I do not find them as informative and fun as Codingal’s course. My son enjoys every coding lesson. I am grateful to the teachers at Codingal; they are generous, responsive, and supportive.',
        subheading: 'Codingal Parent',
      },
      {
        image: 'roblox/testimonials/kamla',
        name: 'Kamla Sharma',
        content:
          'My daughter loves playing games. Codingal’s Roblox course is a perfect course that excites her to learn to code while building her own game in a fun way.',
        subheading: 'Codingal Parent',
      },
    ],
  },
  AP_COMPUTER_SCIENCE_A: {
    courseName: 'AP Computer Science A',
    testimonials: [
      {
        image: 'ap-computer-science-a/testimonials/leo',
        name: 'Leo Gate ',
        content:
          'The AP Computer Science A course at Codingal has helped me understand coding through exploring concepts of OOPs in Java, data structures and algorithms.',
        subheading: 'Grade 11, Codingal Student',
      },
      {
        image: 'ap-computer-science-a/testimonials/zeni',
        name: 'Zeni Fincher',
        content:
          'I went through multiple practice questions. The AP Computer Science A course  introduced me to Java programming concepts including arrays, recursion and strings.',
        subheading: 'Grade 12, Codingal Student',
      },
      {
        image: 'ap-computer-science-a/testimonials/dan',
        name: 'Dan Petra',
        content:
          'My son is preparing for college. This course has helped him test his programming knowledge. The Course challenge can really  help your child understand what they need to review.',
        subheading: 'Codingal Parent',
      },
      {
        image: 'ap-computer-science-a/testimonials/jay',
        name: 'Jay',
        content:
          'My daughter got familiar with the concepts of Java programming. This course is best suited for my child as the school she attends does not provide a similar class.',
        subheading: 'Codingal Parent',
      },
    ],
  },
  PUBLIC_SPEAKING_GRADE_3_5: {
    courseName: 'Public Speaking & Communication (Grades 3-5)',
    path: '/images/landing-page/testimonials/',
    testimonials: toTestimonialsFoldData(ELA_TESTIMONIALS['DEFAULT']).slice(
      0,
      6
    ),
  },
  PUBLIC_SPEAKING_GRADE_1_2: {
    courseName: 'Public Speaking & Communication (Grades 1-2)',
    path: '/images/landing-page/testimonials/',
    testimonials: toTestimonialsFoldData(ELA_TESTIMONIALS['DEFAULT']).slice(
      0,
      6
    ),
  },
}

const SCRATCH_PROGRAMMING_FAQ: FaqType[] = [
  {
    questionName: 'What is Scratch programming?',
    acceptedAnswerText:
      'Scratch is a free block-based programming language designed primarily for kids. It’s a visual programming interface where kids can code interactive stories, games, and animations and share their creations through an online community. Scratch coding courses for kids make learning fun and interactive.',
    acceptedAnswerJsx: (
      <>
        Scratch is a free block-based programming language designed primarily
        for kids. It’s a visual programming interface where kids can code
        interactive stories, games, and animations and share their creations
        through an online community. Scratch coding courses for kids make
        learning fun and interactive.
      </>
    ),
  },
  {
    questionName: 'What is Scratch programming used for?',
    acceptedAnswerText:
      'Scratch programming is used to create interactive stories, games, animations, and much more by dragging and dropping code blocks.',
    acceptedAnswerJsx: (
      <>
        Scratch programming is used to create interactive stories, games,
        animations, and much more by dragging and dropping code blocks.
      </>
    ),
  },
  {
    questionName: 'Is Scratch programming easy to learn for kids?',
    acceptedAnswerText: `Scratch uses a drag and drop method which lets children perform actions quickly and easily. This approach makes Scratch programming courses for kids easy to learn and fun. Coding for kids is fun & children can now master Scratch coding courses with Codingal's Scratch programming course for kids, curated especially for grade 1-8 students. The specialized scratch coding course for kids helps children develop skills such as creative thinking, problem-solving, storytelling, and animation.`,
    acceptedAnswerJsx: (
      <>
        Scratch uses a drag and drop method which lets children perform actions
        quickly and easily. This approach makes Scratch programming courses for
        kids easy to learn and fun. <br />
        Coding for kids is fun & children can now master Scratch coding courses
        with Codingal’s Scratch programming course for kids, curated especially
        for grade 1-8 students. The{' '}
        <strong>specialized scratch coding course for kids</strong> helps
        children develop skills such as creative thinking, problem-solving,
        storytelling, and animation.
      </>
    ),
  },
  {
    questionName: 'How should kids get started with Scratch programming?',
    acceptedAnswerText:
      'Here are three easy steps for kids to get started with Scratch: Sign up on the Scratch website for free. Explore the “ideas” section to access the getting started tutorial. Create your first project using Scratch.',
    acceptedAnswerJsx: (
      <>
        Here are three easy steps for kids to get started with Scratch:
        <ul>
          <li>Sign up on the Scratch website for free.</li>
          <li>
            Explore the “ideas” section to access the getting started tutorial.
          </li>
          <li>Create your first project using Scratch.</li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'How can kids learn Scratch programming for free?',
    acceptedAnswerText:
      'Kids can learn Scratch programming by signing up on the Scratch website for free. It’s a free-to-use programming language tool that allows children to learn how to code in a visually engaging way.',
    acceptedAnswerJsx: (
      <>
        Kids can learn Scratch programming by signing up on the Scratch website
        for free. It’s a free-to-use programming language tool that allows
        children to learn how to code in a visually engaging way.
      </>
    ),
  },
  {
    questionName: 'Is Scratch a good programming language for kids?',
    acceptedAnswerText:
      'Yes. Scratch is one of the best programming languages designed primarily for kids to learn to code. It encourages kids to bring their imagination to life by creating innovative projects.',
    acceptedAnswerJsx: (
      <>
        Yes. Scratch is one of the best programming languages designed primarily
        for kids to learn to code. It encourages kids to bring their imagination
        to life by creating innovative projects.
      </>
    ),
  },
  {
    questionName: 'Why should kids learn Scratch programming?',
    acceptedAnswerText:
      'Scratch is a great platform for kids to get started to learn to code. It’s an interactive, drag-and-drop programming language that allows kids to create animations, apps, and games. With Scratch, they learn to think creatively, work collaboratively, and reason systematically. Codingal offers the best Scratch programming course for kids to master the sun and interactive Scratch platform.',
    acceptedAnswerJsx: (
      <>
        Scratch is a great platform for kids to get started to learn to code.
        It’s an interactive, drag-and-drop programming language that allows kids
        to create animations, apps, and games. With Scratch, they learn to think
        creatively, work collaboratively, and reason systematically. Codingal
        offers the best Scratch programming course for kids to master the sun
        and interactive Scratch platform.
      </>
    ),
  },
  {
    questionName: 'Is Scratch a good way for kids to learn to code?',
    acceptedAnswerText:
      'Scratch is one of the best ways for kids to learn to code. It involves dragging and dropping elements instead of writing code while children still learn about coding logic. The bright and colorful interface of Scratch is engaging and fun for most kids. It provides visual clues through predefined groups of code (blocks), such as events, controls, commands, loops, conditions, and more. These blocks can be selected, modified, and sequenced to create games, apps, animations, and more. Codingal’s Scratch coding courses for kids is a top-rated coding course for kids that helps them to learn programmatic thinking and get an enthusiastic head start into real-world coding skills.',
    acceptedAnswerJsx: (
      <>
        Scratch is one of the best ways for kids to learn to code. It involves
        dragging and dropping elements instead of writing code while children
        still learn about coding logic. <br /> The bright and colorful interface
        of Scratch is engaging and fun for most kids. It provides visual clues
        through predefined groups of code (blocks), such as events, controls,
        commands, loops, conditions, and more. These blocks can be selected,
        modified, and sequenced to create games, apps, animations, and more.
        Codingal’s Scratch coding courses for kids is a top-rated coding course
        for kids that helps them to learn programmatic thinking and get an
        enthusiastic head start into real-world coding skills.
      </>
    ),
  },
  {
    questionName:
      'What are the benefits of learning Scratch programming for kids?',
    acceptedAnswerText:
      'Learning Scratch programming will help children learn the basic coding concepts along with problem-solving, project building, design thinking, and much more. Kids can now master the core principles of coding with Codingal’s best Scratch coding course for kids.',
    acceptedAnswerJsx: (
      <>
        Learning Scratch programming will help children learn the basic coding
        concepts along with problem-solving, project building, design thinking,
        and much more. Kids can now master the core principles of coding with
        Codingal’s best Scratch coding course for kids.
      </>
    ),
  },
  {
    questionName:
      'What are the learning outcomes of the Scratch programming course for kids?',
    acceptedAnswerText:
      'The Scratch programming course will help kids learn the core principles of coding using the interactive Scratch platform. Codingal’s Scratch programming course for Grade 1-8 kids helps them master the essential principles of coding including events, coordinates, loops, sequences, sprites, animation, and much more. The course will also help the kid develop other essential skills including creative thinking, problem-solving, and storytelling.',
    acceptedAnswerJsx: (
      <>
        The Scratch programming course will help kids learn the core principles
        of coding using the interactive Scratch platform. Codingal’s Scratch
        programming course for Grade 1-8 kids helps them master the essential
        principles of coding including events, coordinates, loops, sequences,
        sprites, animation, and much more. The course will also help the kid
        develop other essential skills including creative thinking,
        problem-solving, and storytelling.
      </>
    ),
  },
]

const PYTHON_FOR_KIDS_FAQ: FaqType[] = [
  {
    questionName: 'What are Python games for kids?',
    acceptedAnswerText:
      'Scratch is a free block-based programming language designed primarily for kids. It’s a visual programming interface where kids can code interactive stories, games, and animations and share their creations through an online community. Scratch coding courses for kids make learning fun and interactive.',
    acceptedAnswerJsx: (
      <>
        Kids can learn how to code in Python by creating multiple interactive
        and fun games. Some popular Python games for kids include Ping pong,
        Rock paper scissors, Snake game, Tic-Tac-Toe, etc. Python games help
        kids learn Python and explore new libraries of Python.
      </>
    ),
  },
  {
    questionName: 'What are Loops in Python?',
    acceptedAnswerText:
      'A loop statement in Python programming allows you to run a statement or group of statements multiple times. For example, the ATM software is in a loop to process transaction after transaction until you acknowledge that you have no more to do. Sometimes, we are required to execute a code several times. So there, we use the concept of loops.',
    acceptedAnswerJsx: (
      <>
        A loop statement in Python programming allows you to run a statement or
        group of statements multiple times. For example, the ATM software is in
        a loop to process transaction after transaction until you acknowledge
        that you have no more to do. Sometimes, we are required to execute a
        code several times. So there, we use the concept of loops.
      </>
    ),
  },
  {
    questionName: 'What are Python functions?',
    acceptedAnswerText:
      'A function is a block of codes of related statements that performs a specific task. Functions help break our program into small blocks of code. As the program grows larger and larger, functions make it more organized and manageable. Suppose repeated code occurs in a program. The function can include those codes and execute them when needed by calling that function. Programmers working on large projects can divide the workload by making different functions.',
    acceptedAnswerJsx: (
      <>
        A function is a block of codes of related statements that performs a
        specific task. Functions help break our program into small blocks of
        code. As the program grows larger and larger, functions make it more
        organized and manageable. Suppose repeated code occurs in a program. The
        function can include those codes and execute them when needed by calling
        that function. Programmers working on large projects can divide the
        workload by making different functions.
      </>
    ),
  },
  {
    questionName: 'What is Exception Handling in Python?',
    acceptedAnswerText:
      'An exception can be defined as an unusual condition in a program resulting in an interruption in the flow of the program. Whenever an exception occurs, the program stops the execution, and thus the other code is not executed. An exception is an object that represents the error. Python provides a way to handle the exception so that c can execute the code without any interruption.',
    acceptedAnswerJsx: (
      <>
        An exception can be defined as an unusual condition in a program
        resulting in an interruption in the flow of the program. Whenever an
        exception occurs, the program stops the execution, and thus the other
        code is not executed. An exception is an object that represents the
        error. Python provides a way to handle the exception so that c can
        execute the code without any interruption.
      </>
    ),
  },
  {
    questionName: 'What are Python Data Structures?',
    acceptedAnswerText:
      'Data Structures help you in storing the collection of data, managing it, and performing operations on them. Data Structures help you to make modifications easier on collections of data. In Python, we have four Data Structures- List, Dictionary, Tuple, and Set.',
    acceptedAnswerJsx: (
      <>
        Data Structures help you in storing the collection of data, managing it,
        and performing operations on them. Data Structures help you to make
        modifications easier on collections of data. In Python, we have four
        Data Structures- List, Dictionary, Tuple, and Set.
      </>
    ),
  },
  {
    questionName: 'What is Object Oriented Programming in Python?',
    acceptedAnswerText:
      'Object-Oriented Programming revolves around Classes and Objects and aims to increase the reusability of code. Object-oriented programming concepts create simpler code to understand, have higher readability, and are reusable. So, what exactly are these Classes and Objects? Consider Fruit as a class, Apple and Orange as Objects for better understanding. So, apples and oranges are real-world entities with some attributes and behavior. The primary idea behind OOPs is to combine data and the functions that operate with it into a single unit such that no other parts of the code may access it.',
    acceptedAnswerJsx: (
      <>
        Object-Oriented Programming revolves around Classes and Objects and aims
        to increase the reusability of code. Object-oriented programming
        concepts create simpler code to understand, have higher readability, and
        are reusable. So, what exactly are these Classes and Objects? Consider
        Fruit as a class, Apple and Orange as Objects for better understanding.
        So, apples and oranges are real-world entities with some attributes and
        behavior. The primary idea behind OOPs is to combine data and the
        functions that operate with it into a single unit such that no other
        parts of the code may access it.
      </>
    ),
  },
  {
    questionName: 'What is Python Dictionary?',
    acceptedAnswerText:
      'Python dictionary is a collection of items used to store data values. Each item in the dictionary has a key/value pair. Value of the key-value pair can be a number, a string, a list, a tuple, or even another dictionary. Python dictionary is defined by placing a sequence of items within curly {} braces. Python dictionary allows us to write effective codes.',
    acceptedAnswerJsx: (
      <>
        Python dictionary is a collection of items used to store data values.
        Each item in the dictionary has a key/value pair. Value of the key-value
        pair can be a number, a string, a list, a tuple, or even another
        dictionary. Python dictionary is defined by placing a sequence of items
        within curly {} braces. Python dictionary allows us to write effective
        codes.
      </>
    ),
  },
  {
    questionName: 'How can I download & install Python?',
    acceptedAnswerText:
      'You can download  and install Python from Python software foundation website -  https://www.python.org/',
    acceptedAnswerJsx: (
      <>
        You can download and install Python from Python software foundation
        website - https://www.python.org/
      </>
    ),
  },
  {
    questionName: 'Why should kids learn Python programming?',
    acceptedAnswerText:
      'Python is the perfect coding language to get kids started off with. Python programming for kids helps improve and develop multiple facets of their educational syllabus including maths, writing, and creativity. It will help them develop a more concise and structured approach to storytelling with the English language. It will also aid them with problem solving too.Python leads them to developing simple and efficient solutions using critical thinking.Needless to say, Python developers are in high demand.',
    acceptedAnswerJsx: (
      <>
        Python is the perfect coding language to get kids started off with.
        Python programming for kids helps improve and develop multiple facets of
        their educational syllabus including maths, writing, and creativity. It
        will help them develop a more concise and structured approach to
        storytelling with the English language. It will also aid them with
        problem solving too.Python leads them to developing simple and efficient
        solutions using critical thinking.Needless to say, Python developers are
        in high demand.
      </>
    ),
  },
  {
    questionName: 'Is Python kid-friendly?',
    acceptedAnswerText:
      'Yes, Python programming is kid-friendly. It  is a programming language that is particularly good for kids wanting to learn how to code. Python is great for kids, especially as a first language. Kids new to python programming can go right from the very first simple “Hello world” programs to creating games in the future.',
    acceptedAnswerJsx: (
      <>
        Yes, Python programming is kid-friendly. It is a programming language
        that is particularly good for kids wanting to learn how to code. Python
        is great for kids, especially as a first language. Kids new to python
        programming can go right from the very first simple “Hello world”
        programs to creating games in the future.
      </>
    ),
  },
  {
    questionName:
      'How can I be sure that Python programming is the right program for my kid?',
    acceptedAnswerText:
      'Python’s syntax is concise and easily readable by humans. It mirrors the human language far better than any other programming language which makes it an ideal language for beginners. Writing code in Python is similar to writing commands in plain English. As beginners, kids will quickly pick up the basics and learn how to use this language for game development and animation.',
    acceptedAnswerJsx: (
      <>
        Python’s syntax is concise and easily readable by humans. It mirrors the
        human language far better than any other programming language which
        makes it an ideal language for beginners. Writing code in Python is
        similar to writing commands in plain English. As beginners, kids will
        quickly pick up the basics and learn how to use this language for game
        development and animation.
      </>
    ),
  },
  {
    questionName: 'Why should kids learn to code?',
    acceptedAnswerText:
      'Coding for kids helps improve their logical thinking, critical thinking, problem-solving skills, creativity, mathematics and writing skills and also gives them valuable skills in life and at the workplace. Coding provides a competitive advantage when applying for colleges, internships, and jobs. In addition, basic programming knowledge can change how kids interact daily with the technologies they use. It’s a basic literacy–one we can’t afford to overlook.',
    acceptedAnswerJsx: (
      <>
        Coding for kids helps improve their logical thinking, critical thinking,
        problem-solving skills, creativity, mathematics and writing skills and
        also gives them valuable skills in life and at the workplace. Coding
        provides a competitive advantage when applying for colleges,
        internships, and jobs. In addition, basic programming knowledge can
        change how kids interact daily with the technologies they use. It’s a
        basic literacy–one we can’t afford to overlook.
      </>
    ),
  },
  {
    questionName:
      'My child is a beginner, is previous coding experience required?',
    acceptedAnswerText:
      'Our coding courses for kids have been developed so that a complete beginner can follow along without any trouble. With technology increasingly prevalent across so many industries, even basic coding knowledge can help kids regardless of the career they ultimately decide to pursue.',
    acceptedAnswerJsx: (
      <>
        Our coding courses for kids have been developed so that a complete
        beginner can follow along without any trouble. With technology
        increasingly prevalent across so many industries, even basic coding
        knowledge can help kids regardless of the career they ultimately decide
        to pursue.{' '}
      </>
    ),
  },
]
const ROBLOX: FaqType[] = [
  {
    questionName: 'Is Roblox free?',
    acceptedAnswerText:
      'Roblox is a free to use platform on which users can create and play 3D games.',
    acceptedAnswerJsx: (
      <>
        Roblox is a free to use platform on which users can create and play 3D
        games.
      </>
    ),
  },
  {
    questionName: 'What is Roblox used for?',
    acceptedAnswerText:
      'Roblox is an online platform on which kids can create and play games. ',
    acceptedAnswerJsx: (
      <>Roblox is an online platform on which kids can create and play games.</>
    ),
  },
  {
    questionName: 'What is Roblox studio used for?',
    acceptedAnswerText:
      'Roblox studio is used to design and code the games. You can download Roblox studio on your system and start creating games with Roblox coding.',
    acceptedAnswerJsx: (
      <>
        Roblox studio is used to design and code the games. You can download
        Roblox studio on your system and start creating games with Roblox
        coding.
      </>
    ),
  },
  {
    questionName: 'Why should kids learn to code?',
    acceptedAnswerText:
      'Coding for kids helps improve their logical thinking, critical thinking, problem-solving skills, creativity, mathematics and writing skills and also gives them valuable skills in life and at the workplace. Coding provides a competitive advantage when applying for colleges, internships, and jobs. In addition, basic programming knowledge can change how kids interact daily with the technologies they use. It’s a basic literacy–one we can’t afford to overlook.',
    acceptedAnswerJsx: (
      <>
        Coding for kids helps improve their logical thinking, critical thinking,
        problem-solving skills, creativity, mathematics and writing skills and
        also gives them valuable skills in life and at the workplace. Coding
        provides a competitive advantage when applying for colleges,
        internships, and jobs. In addition, basic programming knowledge can
        change how kids interact daily with the technologies they use. It’s a
        basic literacy–one we can’t afford to overlook.
      </>
    ),
  },
  {
    questionName: 'Which programming language does Roblox studio use?',
    acceptedAnswerText:
      'Roblox studio uses Lua scripting language. It’s relatively easy to learn, making it ideal for young codes to learn game development.',
    acceptedAnswerJsx: (
      <>
        Roblox studio uses Lua scripting language. It’s relatively easy to
        learn, making it ideal for young codes to learn game development.
      </>
    ),
  },
  {
    questionName: 'Is Roblox safe for kids?',
    acceptedAnswerText:
      'If you take advantage of all the parental control features and stay involved in what your child’s playing and who they’re playing with, Roblox can be reasonably safe for kids.',
    acceptedAnswerJsx: (
      <>
        If you take advantage of all the parental control features and stay
        involved in what your child’s playing and who they’re playing with,
        Roblox can be reasonably safe for kids.
      </>
    ),
  },
  {
    questionName: 'Is Roblox studio and Roblox same?',
    acceptedAnswerText:
      'Roblox studio is used to design and code the games. Games in Roblox are called ‘experiences’. Whereas Roblox is used to visiting the ‘experiences’.',
    acceptedAnswerJsx: (
      <>
        Roblox studio is used to design and code the games. Games in Roblox are
        called ‘experiences’. Whereas Roblox is used to visiting the
        ‘experiences’.
      </>
    ),
  },
  {
    questionName:
      'My child is a beginner, is previous coding experience required?',
    acceptedAnswerText:
      'Our coding courses for kids have been developed so that a complete beginner can follow along without any trouble. With technology increasingly prevalent across so many industries, even basic coding knowledge can help kids regardless of the career they ultimately decide to pursue. ',
    acceptedAnswerJsx: (
      <>
        Our coding courses for kids have been developed so that a complete
        beginner can follow along without any trouble. With technology
        increasingly prevalent across so many industries, even basic coding
        knowledge can help kids regardless of the career they ultimately decide
        to pursue.
      </>
    ),
  },
]
const AP_COMPUTER_SCIENCE_A: FaqType[] = [
  {
    questionName: 'What do you learn in AP Computer Science A?',
    acceptedAnswerText:
      'In AP Computer Science A, you will learn the fundamentals of java programming language, data structures and algorithms, strings and arrays and object-oriented programming.',
    acceptedAnswerJsx: (
      <>
        In AP Computer Science A, you will learn the fundamentals of java
        programming language, data structures and algorithms, strings and arrays
        and object-oriented programming.
      </>
    ),
  },
  {
    questionName: 'Is AP Computer Science A for beginners?',
    acceptedAnswerText:
      'AP Computer Science A is an introductory course for all students. Any high school student with basic understanding of Computer Science can enrol for this course.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science A is an introductory course for all students. Any
        high school student with basic understanding of Computer Science can
        enrol for this course.
      </>
    ),
  },
  {
    questionName:
      'My child is a beginner, is previous coding experience required?',
    acceptedAnswerText:
      'Our coding courses for kids and teens have been developed so that a complete beginner can follow along without any trouble. With technology increasingly prevalent across so many industries, even basic coding knowledge can help kids and teens regardless of the career they ultimately decide to pursue. ',
    acceptedAnswerJsx: (
      <>
        Our coding courses for kids and teens have been developed so that a
        complete beginner can follow along without any trouble. With technology
        increasingly prevalent across so many industries, even basic coding
        knowledge can help kids and teens regardless of the career they
        ultimately decide to pursue.
      </>
    ),
  },
  {
    questionName: 'How hard is AP Computer Science A?',
    acceptedAnswerText:
      'AP computer science A is considered easy. If you have a previous understanding of programming it will be easier for you to learn AP computer science A.',
    acceptedAnswerJsx: (
      <>
        AP computer science A is considered easy. If you have a previous
        understanding of programming it will be easier for you to learn AP
        computer science A.
      </>
    ),
  },
  {
    questionName: 'What is AP Computer Science A equivalent to?',
    acceptedAnswerText:
      'AP Computer Science A is the equivalent of a first-semester college-level computer science course. Students with no prior computer science experience can do well in AP Computer Science A, which focuses on computing skills related to Java programming.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science A is the equivalent of a first-semester
        college-level computer science course. Students with no prior computer
        science experience can do well in AP Computer Science A, which focuses
        on computing skills related to Java programming.
      </>
    ),
  },
  {
    questionName:
      'What is the difference between AP Computer Science A and principles?',
    acceptedAnswerText:
      'The AP Computer Science Principles does not have a fixed programming language; students are expected to understand programming concepts and algorithms using pseudocode and flowcharts. Teachers can use specific programming languages such as Python, Java, or C++ as examples, but they are not required for the written portion of the exam. On the other hand, AP Computer Science A focuses more on programming. Object-oriented programming is stressed, and students learn using Java as a programming language. Students must be comfortable with Java syntax, libraries, and structures for the exam.',
    acceptedAnswerJsx: (
      <>
        The AP Computer Science Principles does not have a fixed programming
        language; students are expected to understand programming concepts and
        algorithms using pseudocode and flowcharts. Teachers can use specific
        programming languages such as Python, Java, or C++ as examples, but they
        are not required for the written portion of the exam. On the other hand,
        AP Computer Science A focuses more on programming. Object-oriented
        programming is stressed, and students learn using Java as a programming
        language. Students must be comfortable with Java syntax, libraries, and
        structures for the exam.
      </>
    ),
  },
  {
    questionName: 'What do you need to know for AP Computer Science A?',
    acceptedAnswerText:
      'There is no prerequisite for AP Computer Science A. If you are in high school and have basic knowledge of programming, you can take AP Computer Science A course.',
    acceptedAnswerJsx: (
      <>
        There is no prerequisite for AP Computer Science A. If you are in high
        school and have basic knowledge of programming, you can take AP Computer
        Science A course.
      </>
    ),
  },
  {
    questionName: 'Should I take AP Computer Science A?',
    acceptedAnswerText:
      'If you want to pursue Computer Science and start earning college credit in high school, you should take AP Computer Science A. It will help you gain insight into coding by using Java and explore topics like modularity, variables, arrays, and the impact of computing.',
    acceptedAnswerJsx: (
      <>
        If you want to pursue Computer Science and start earning college credit
        in high school, you should take AP Computer Science A. It will help you
        gain insight into coding by using Java and explore topics like
        modularity, variables, arrays, and the impact of computing.
      </>
    ),
  },
  {
    questionName: 'Is AP Computer Science A worth it?',
    acceptedAnswerText:
      'AP Computer Science A is entirely focused on Java programming. If you plan to study computer science at university, taking AP computer science A will be worth it as it will help you gain knowledge about actual programming.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science A is entirely focused on Java programming. If you
        plan to study computer science at university, taking AP computer science
        A will be worth it as it will help you gain knowledge about actual
        programming.
      </>
    ),
  },
  {
    questionName: 'Is AP Computer Science A, Python or Java?',
    acceptedAnswerText:
      'The AP Computer Science A course and exam entirely focuses on Java.',
    acceptedAnswerJsx: (
      <>The AP Computer Science A course and exam entirely focuses on Java.</>
    ),
  },
  {
    questionName: 'What is AP Computer Science A vs AB?',
    acceptedAnswerText:
      'AP Computer Science A focus on object-oriented programming and tests students on their knowledge of Java. Whereas AP Computer Science AB is a discontinued course.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science A focus on object-oriented programming and tests
        students on their knowledge of Java. Whereas AP Computer Science AB is a
        discontinued course.
      </>
    ),
  },
  {
    questionName: 'How long is AP Computer Science A?',
    acceptedAnswerText:
      'AP Computer Science A will take you around a half year to complete the course.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science A will take you around a half year to complete the
        course.
      </>
    ),
  },
  {
    questionName: 'Is AP Computer Science A Python?',
    acceptedAnswerText:
      'No. In AP Computer Science A python is not covered. AP Computer Science A focuses on Java programming.',
    acceptedAnswerJsx: (
      <>
        No. In AP Computer Science A python is not covered. AP Computer Science
        A focuses on Java programming.
      </>
    ),
  },
  {
    questionName: 'Which AP Computer Science is easier?',
    acceptedAnswerText:
      'AP Computer Science principle is comparatively easy to AP Computer Science A as those with no coding experience can learn AP Computer Science principle.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science principle is comparatively easy to AP Computer
        Science A as those with no coding experience can learn AP Computer
        Science principle.
      </>
    ),
  },
  {
    questionName: 'Should I take AP CSA or AP CSP?',
    acceptedAnswerText:
      'If you are new to programming and Computer Science you should take AP CSP. If you want to pursue Computer Science in college you should take AP CSA as it focuses on Java and object-oriented programming.',
    acceptedAnswerJsx: (
      <>
        If you are new to programming and Computer Science you should take AP
        CSP. If you want to pursue Computer Science in college you should take
        AP CSA as it focuses on Java and object-oriented programming.
      </>
    ),
  },
  {
    questionName: 'Can you self-study AP CS A?',
    acceptedAnswerText:
      'You can self-study for AP CS A if you are comfortable with programming but if you are someone who is new to programming we recommend taking an online course.',
    acceptedAnswerJsx: (
      <>
        You can self-study for AP CS A if you are comfortable with programming
        but if you are someone who is new to programming we recommend taking an
        online course.
      </>
    ),
  },
  {
    questionName: 'How do you get a 5 on AP CS A?',
    acceptedAnswerText:
      'You can get a 5 on AP CS A by staying up to date with the resources, and practicing the previous year‘s questions. You can take an online course at Codingal to get the best guidance and study material.',
    acceptedAnswerJsx: (
      <>
        You can get a 5 on AP CS A by staying up to date with the resources, and
        practicing the previous year’s questions. You can take an online course
        at Codingal to get the best guidance and study material.
      </>
    ),
  },
  {
    questionName: 'What is the easiest AP course?',
    acceptedAnswerText:
      'AP Computer Science principle is considered as the easiest AP course based on the students rating. ',
    acceptedAnswerJsx: (
      <>
        AP Computer Science principle is considered as the easiest AP course
        based on the students rating.{' '}
      </>
    ),
  },

  {
    questionName: 'Why should kids and teens learn to code?',
    acceptedAnswerText:
      'Coding for kids and teens helps improve their logical thinking, critical thinking, problem-solving skills, creativity, mathematics and writing skills and also gives them valuable skills in life and at the workplace. Coding provides a competitive advantage when applying for colleges, internships, and jobs. In addition, basic programming knowledge can change how kids and teens interact daily with the technologies they use. It’s a basic literacy–one we can’t afford to overlook.',
    acceptedAnswerJsx: (
      <>
        Coding for kids and teens helps improve their logical thinking, critical
        thinking, problem-solving skills, creativity, mathematics and writing
        skills and also gives them valuable skills in life and at the workplace.
        Coding provides a competitive advantage when applying for colleges,
        internships, and jobs. In addition, basic programming knowledge can
        change how kids and teens interact daily with the technologies they use.
        It’s a basic literacy–one we can’t afford to overlook.
      </>
    ),
  },
  {
    questionName: 'How can I trust Codingal?',
    acceptedAnswerText:
      'Codingal has been founded by leading edtech entrepreneurs, IIT graduates, Google & Amazon alumni and also backed by leading investors like Y Combinator and Rebright Partners. You can know more about us here - https://www.codingal.com/about-us/. Further, we provide money back guarantee if you decide to discontinue after enrollment due to any reason.',
    acceptedAnswerJsx: (
      <>
        Codingal has been founded by leading edtech entrepreneurs, IIT
        graduates, Google & Amazon alumni and also backed by leading investors
        like Y Combinator and Rebright Partners. You can know more about us here
        - https://www.codingal.com/about-us/. Further, we provide money back
        guarantee if you decide to discontinue after enrollment due to any
        reason.
      </>
    ),
  },
]

const AI_AND_ML: FaqType[] = [
  {
    questionName: 'What is AI and machine learning for kids?',
    acceptedAnswerText:
      'AI, or Artificial Intelligence, is all about creating smart machines that can think and learn like humans! For kids, this means exploring how computers can recognize images, understand speech, and even make decisions. Machine learning, a part of AI, teaches these machines to improve over time by learning from data. In our courses, kids can dive into fun projects, like building chatbots or games, while learning these exciting concepts in a way that’s easy to understand!',
    acceptedAnswerJsx: (
      <>
        AI, or Artificial Intelligence, is all about creating smart machines
        that can think and learn like humans! For kids, this means exploring how
        computers can recognize images, understand speech, and even make
        decisions. Machine learning, a part of AI, teaches these machines to
        improve over time by learning from data. In our courses, kids can dive
        into fun projects, like building chatbots or games, while learning these
        exciting concepts in a way that’s easy to understand!
      </>
    ),
  },
  {
    questionName: 'How can kids and teens learn artificial intelligence?',
    acceptedAnswerText:
      'Kids and teens can learn artificial intelligence through engaging courses that combine hands-on projects with interactive lessons. They can start with block-based coding platforms for younger learners, where they create fun applications using visual programming. As they grow older, they can transition to text-based coding with Python and other programming languages. Our courses offer a mix of tutorials, creative projects, and real-world applications, making learning AI both fun and informative!',
    acceptedAnswerJsx: (
      <>
        Kids and teens can learn artificial intelligence through engaging
        courses that combine hands-on projects with interactive lessons. They
        can start with block-based coding platforms for younger learners, where
        they create fun applications using visual programming. As they grow
        older, they can transition to text-based coding with Python and other
        programming languages. Our courses offer a mix of tutorials, creative
        projects, and real-world applications, making learning AI both fun and
        informative!
      </>
    ),
  },
  {
    questionName: 'Are there online machine learning classes for kids?',
    acceptedAnswerText:
      "Firstly, it's important to understand that machine learning is a subset of artificial intelligence. In our courses, we cover both AI and machine learning, providing a comprehensive learning experience for kids and teens! We offer machine learning classes specifically designed for younger learners, allowing them to study at their own pace. Using engaging platforms and tools, students can explore these complex topics in a fun and accessible way. Our courses include exciting projects that help kids understand how machine learning works while developing valuable skills for the future!",
    acceptedAnswerJsx: (
      <>
        Firstly, it&apos;s important to understand that machine learning is a
        subset of artificial intelligence. In our courses, we cover both AI and
        machine learning, providing a comprehensive learning experience for kids
        and teens! We offer machine learning classes specifically designed for
        younger learners, allowing them to study at their own pace. Using
        engaging platforms and tools, students can explore these complex topics
        in a fun and accessible way. Our courses include exciting projects that
        help kids understand how machine learning works while developing
        valuable skills for the future!
      </>
    ),
  },
  {
    questionName:
      'How long does it typically take to complete an AI and Machine Learning course?',
    acceptedAnswerText:
      'Typically, if a learner opts for 2 classes a week, they can complete the 48-lesson course in 6 months, which is what we recommend considering busy study and extracurricular schedules. However, learners can choose to take more than 2 classes per week to finish the course in 3 months on a fast track, or they can opt for fewer classes, which may extend the completion time to up to a year.',
    acceptedAnswerJsx: (
      <>
        Typically, if a learner opts for 2 classes a week, they can complete the
        48-lesson course in 6 months, which is what we recommend considering
        busy study and extracurricular schedules. However, learners can choose
        to take more than 2 classes per week to finish the course in 3 months on
        a fast track, or they can opt for fewer classes, which may extend the
        completion time to up to a year.
      </>
    ),
  },
  {
    questionName: 'Is AI & Machine Learning hard to learn?',
    acceptedAnswerText:
      'Learning AI and machine learning can be challenging, but it’s manageable with the right approach! The complexity often depends on a few factors, like your prior knowledge of programming and mathematics. However, many resources and courses are designed specifically for kids and beginners, making these topics more accessible. With engaging projects, hands-on activities, and kid-friendly platforms, learners can gradually build their understanding and skills. The key is to take it step by step and have fun while exploring the exciting world of AI and ML!',
    acceptedAnswerJsx: (
      <>
        Learning AI and machine learning can be challenging, but it’s manageable
        with the right approach! The complexity often depends on a few factors,
        like your prior knowledge of programming and mathematics. However, many
        resources and courses are designed specifically for kids and beginners,
        making these topics more accessible.
        <br />
        With engaging projects, hands-on activities, and kid-friendly platforms,
        learners can gradually build their understanding and skills. The key is
        to take it step by step and have fun while exploring the exciting world
        of AI & Machine Learning!
      </>
    ),
  },
  {
    questionName: 'What can you do with machine learning?',
    acceptedAnswerText:
      'With machine learning, you can do a wide variety of things! You can create intelligent applications that can recognize images and sounds, make predictions based on data, and even power chatbots that understand and respond to user queries. Machine learning is used in areas like healthcare for disease diagnosis, finance for fraud detection, and entertainment for personalized recommendations, like suggesting movies or songs. Essentially, it allows you to build systems that learn from data and improve over time!',
    acceptedAnswerJsx: (
      <>
        With machine learning, you can do a wide variety of things! You can
        create intelligent applications that can recognize images and sounds,
        make predictions based on data, and even power chatbots that understand
        and respond to user queries. Machine learning is used in areas like
        healthcare for disease diagnosis, finance for fraud detection, and
        entertainment for personalized recommendations, like suggesting movies
        or songs. Essentially, it allows you to build systems that learn from
        data and improve over time!
      </>
    ),
  },
  {
    questionName: 'What type of jobs can kids get after learning ML and AI?',
    acceptedAnswerText:
      'After learning machine learning and AI, kids can explore exciting career paths in technology! They might start as junior data analysts, AI assistants, or software developers. As they gain more experience, they could pursue roles like data scientists, machine learning engineers, or AI researchers. The skills learned in ML and AI are in high demand, opening doors to a variety of jobs in tech, healthcare, finance, and many other industries. Plus, these skills are valuable in many future careers, not just those directly related to AI!',
    acceptedAnswerJsx: (
      <>
        After learning machine learning and AI, kids can explore exciting career
        paths in technology! They might start as junior data analysts, AI
        assistants, or software developers. As they gain more experience, they
        could pursue roles like data scientists, machine learning engineers, or
        AI researchers. The skills learned in ML and AI are in high demand,
        opening doors to a variety of jobs in tech, healthcare, finance, and
        many other industries. Plus, these skills are valuable in many future
        careers, not just those directly related to AI!
      </>
    ),
  },
  {
    questionName:
      'Does Codingal offer kids and teens Machine Learning certificates?',
    acceptedAnswerText:
      'Yes, Codingal offers machine learning certificates for kids and teens! Upon completing our courses, students receive a certificate that showcases their skills and knowledge in machine learning and artificial intelligence. This certification can be a great addition to their educational portfolio, helping them stand out in future academic or job applications. It’s a fantastic way to recognize their hard work and accomplishments in learning these important technologies!',
    acceptedAnswerJsx: (
      <>
        Yes, Codingal offers machine learning certificates for kids and teens!
        Upon completing our courses, students receive a certificate that
        showcases their skills and knowledge in machine learning and artificial
        intelligence. This certification can be a great addition to their
        educational portfolio, helping them stand out in future academic or job
        applications. It’s a fantastic way to recognize their hard work and
        accomplishments in learning these important technologies!
      </>
    ),
  },
]

const THUNKABLE: FaqType[] = [
  {
    questionName: 'What Does Thunkable Do?',
    acceptedAnswerText:
      'Thunkable is a no-code platform that enables anyone to design, build, and publish native mobile and web apps using a simple drag-and-drop interface, no coding required. It allows students, professionals, and hobbyists to quickly assemble functional apps by visually connecting components and logic blocks, making app development accessible, fast, and intuitive.',
    acceptedAnswerJsx: (
      <>
        <p>
          Thunkable is a no-code platform that enables anyone to design, build,
          and publish native mobile and web apps using a simple drag-and-drop
          interface, no coding required. It allows students, professionals, and
          hobbyists to quickly assemble functional apps by visually connecting
          components and logic blocks, making app development accessible, fast,
          and intuitive.
        </p>
      </>
    ),
  },
  {
    questionName: 'What are the keys using for the Thunkable for Kids?',
    acceptedAnswerText:
      'The key using for the Thunkable for kids are App Creation for Various Users: Founders, product agencies, educators, and individuals use Thunkable to create consumer apps, prototypes, and educational tools. Rapid Prototyping: Users can turn ideas into functional apps quickly, testing and validating concepts without a long development cycle. Business Solutions: Companies can build custom internal apps to streamline operations, integrate device sensors, or connect to existing systems, reducing reliance on paperwork. Education and Learning: Thunkable provides an interactive platform to teach and learn coding concepts, programming fundamentals, and app development, even for beginners with no prior coding experience. AI-Powered Development: The platform now leverages AI to generate app projects from simple text descriptions, which users can then customize and refine.',
    acceptedAnswerJsx: (
      <>
        <p>The key using for the Thunkable for kids are:</p>
        <ul>
          <li>
            <b>App Creation for Various Users:</b> Founders, product agencies,
            educators, and individuals use Thunkable to create consumer apps,
            prototypes, and educational tools.
          </li>
          <li>
            <b>Rapid Prototyping:</b> Users can turn ideas into functional apps
            quickly, testing and validating concepts without a long development
            cycle.
          </li>
          <li>
            <b>Business Solutions:</b> Companies can build custom internal apps
            to streamline operations, integrate device sensors, or connect to
            existing systems, reducing reliance on paperwork.
          </li>
          <li>
            <b>Education and Learning:</b> Thunkable provides an interactive
            platform to teach and learn coding concepts, programming
            fundamentals, and app development, even for beginners with no prior
            coding experience.
          </li>
          <li>
            <b>AI-Powered Development:</b> The platform now leverages AI to
            generate app projects from simple text descriptions, which users can
            then customize and refine.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'How does Thunkable work?',
    acceptedAnswerText:
      'Thunkable lets kids build apps visually using a drag-and-drop interface for components like buttons, images, and text. Logic Blocks: Add interactivity by connecting pre-built blocks, similar to Scratch. Native Features: Access device tools like the camera, GPS, and sensors. Direct Publishing: Publish apps directly to mobile platforms quickly and easily. This approach makes app development fun, hands-on, and easy to understand for beginners.',
    acceptedAnswerJsx: (
      <>
        <p>Thunkable works through a simple, visual app-building process:</p>
        <ul>
          <li>
            <b>Drag-and-Drop Interface:</b> Kids build apps by dragging and
            dropping components like buttons, text, and images.
          </li>
          <li>
            <b>Logic Blocks:</b> Add interactivity by connecting pre-built logic
            blocks, similar to Scratch.
          </li>
          <li>
            <b>Native Features:</b> Access device tools like the camera, GPS,
            and sensors.
          </li>
          <li>
            <b>Direct Publishing:</b> Publish apps directly to mobile platforms
            quickly and easily.
          </li>
        </ul>
        <p>
          This approach makes app development fun, hands-on, and easy to
          understand for beginners.
        </p>
      </>
    ),
  },
  {
    questionName: 'Why Should My Child Learn Coding with Thunkable?',
    acceptedAnswerText:
      'Learning coding with Thunkable helps kids see their ideas come to life through apps and interactive projects. It teaches logical thinking, problem-solving, and creativity while introducing coding concepts in a fun, visual, and hands-on way. With Thunkable, children also gain confidence in technology, learn to work independently or collaboratively, and build skills that are useful for school and future tech careers.',
    acceptedAnswerJsx: (
      <>
        <p>
          Learning coding with Thunkable helps kids see their ideas come to life
          through apps and interactive projects. It teaches logical thinking,
          problem-solving, and creativity while introducing coding concepts in a
          fun, visual, and hands-on way.
        </p>
        <p>
          With Thunkable, children also gain confidence in technology, learn to
          work independently or collaboratively, and build skills that are
          useful for school and future tech careers.
        </p>
      </>
    ),
  },
  {
    questionName: 'Is Thunkable safe and easy for kids to use?',
    acceptedAnswerText:
      'Yes! Thunkable is a kid-friendly, browser-based platform—no software installation or account sharing is required. It provides a safe, interactive environment where children can experiment, create apps, and learn coding confidently.',
    acceptedAnswerJsx: (
      <>
        <p>
          Yes! Thunkable is a kid-friendly, browser-based platform—no software
          installation or account sharing is required. It provides a safe,
          interactive environment where children can experiment, create apps,
          and learn coding confidently.
        </p>
      </>
    ),
  },
  {
    questionName: 'What Can Kids Create with Thunkable?',
    acceptedAnswerText:
      'With Thunkable, kids can bring their ideas to life by building a variety of apps and projects: Interactive Games and Mini-Games – Fun, playable apps that teach logic and creativity. Story Apps – Choose-your-own-adventure or interactive storytelling projects. Web Pages and Apps – Simple apps or webpages using visual blocks for layout and functionality. Educational Tools – Apps to explore math, science, or data concepts interactively. Creative Projects – Drawing, animations, or apps that use device sensors like camera and GPS. Thunkable makes it easy, fun, and safe for kids to experiment, create, and learn coding concepts while seeing instant results.',
    acceptedAnswerJsx: (
      <>
        <p>
          With Thunkable, kids can bring their ideas to life by building a
          variety of apps and projects:
        </p>
        <ul>
          <li>
            Interactive Games and Mini-Games 🎮 – Fun, playable apps that teach
            logic and creativity.
          </li>
          <li>
            Story Apps 📖 – Choose-your-own-adventure or interactive
            storytelling projects.
          </li>
          <li>
            Web Pages and Apps 🌐 – Simple apps or webpages using visual blocks
            for layout and functionality.
          </li>
          <li>
            Educational Tools 📊 – Apps to explore math, science, or data
            concepts interactively.
          </li>
          <li>
            Creative Projects 🎨 – Drawing, animations, or apps that use device
            sensors like camera and GPS.
          </li>
        </ul>
        <p>
          Thunkable makes it easy, fun, and safe for kids to experiment, create,
          and learn coding concepts while seeing instant results.
        </p>
      </>
    ),
  },
  {
    questionName: 'At what age can kids start learning Thunkable?',
    acceptedAnswerText:
      'Children as young as 8 years old can start learning Thunkable. Its beginner-friendly interface makes it ideal for elementary and middle school students just starting their coding journey.',
    acceptedAnswerJsx: (
      <>
        <p>
          Children as young as 8 years old can start learning Thunkable. Its
          beginner-friendly interface makes it ideal for elementary and middle
          school students just starting their coding journey.
        </p>
      </>
    ),
  },
  {
    questionName: 'How does Codingal teach Thunkable to kids?',
    acceptedAnswerText:
      'At Codingal, kids learn Thunkable through live online classes with expert instructors. Each session includes: Hands-on coding projects Real-time feedback Fun, creative Thunkable projects that make learning meaningful and engaging.',
    acceptedAnswerJsx: (
      <>
        <p>
          At Codingal, kids learn Thunkable through live online classes with
          expert instructors. Each session includes:
        </p>
        <ul>
          <li>Hands-on coding projects</li>
          <li>Real-time feedback</li>
          <li>
            Fun, creative Thunkable projects that make learning meaningful and
            engaging
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'Which language does Thunkable use?',
    acceptedAnswerText:
      'The language used is called React Native. A language created by Facebook and very widely used. React.js is for the web and React Native is for mobiles.',
    acceptedAnswerJsx: (
      <>
        <p>
          The language used is called <b>React Native</b> — a language created
          by Facebook and very widely used. React.js is for the web and React
          Native is for mobiles.
        </p>
      </>
    ),
  },
  {
    questionName: 'Does Thunkable require coding?',
    acceptedAnswerText:
      'With Thunkable, you can go from idea to mobile app, no code required, just unstoppable creativity.',
    acceptedAnswerJsx: (
      <>
        <p>
          With Thunkable, you can go from idea to mobile app — no code required,
          just unstoppable creativity.
        </p>
      </>
    ),
  },
  {
    questionName: 'Do kids need to install anything to use Thunkable?',
    acceptedAnswerText:
      'No installation is required! Thunkable runs entirely in a web browser, so kids can start coding instantly, anytime, anywhere.',
    acceptedAnswerJsx: (
      <>
        <p>
          No installation is required! Thunkable runs entirely in a web browser,
          so kids can start coding instantly, anytime, anywhere.
        </p>
      </>
    ),
  },
  {
    questionName: 'What are the benefits of learning Thunkable with Codingal?',
    acceptedAnswerText:
      'Learning Thunkable with Codingal helps kids: Develop problem-solving and logical thinking skills 🧠 Build real coding projects step by step Gain confidence and curiosity about technology Learn at their own pace with flexible, engaging lessons.',
    acceptedAnswerJsx: (
      <>
        <p>Learning Thunkable with Codingal helps kids:</p>
        <ul>
          <li>Develop problem-solving and logical thinking skills 🧠</li>
          <li>Build real coding projects step by step</li>
          <li>Gain confidence and curiosity about technology</li>
          <li>Learn at their own pace with flexible, engaging lessons</li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'Is Thunkable Better Than MIT App Inventor?',
    acceptedAnswerText:
      'If your goal is to create apps for a startup or business, Thunkable is usually the better option. While MIT App Inventor can still be used, it may present more limitations and challenges compared to Thunkable’s advanced features.',
    acceptedAnswerJsx: (
      <>
        <p>
          If your goal is to create apps for a startup or business, Thunkable is
          usually the better option. While MIT App Inventor can still be used,
          it may present more limitations and challenges compared to Thunkable’s
          advanced features.
        </p>
      </>
    ),
  },
]

const JAVA: FaqType[] = [
  {
    questionName: 'What is Java for Kids?',
    acceptedAnswerText:
      'Java for Kids is a beginner-friendly introduction to the fundamentals of Java programming. Java is a versatile, object-oriented language (OOP) widely used in web development, mobile apps, and more. Codingal’s course simplifies Java concepts, enabling kids to learn programming through engaging projects. Think of Java as a Swiss Army knife—just as it has tools for different tasks, Java can be used to create apps, games, websites, and even control robots!',
    acceptedAnswerJsx: (
      <>
        Java for Kids is a beginner-friendly introduction to the fundamentals of
        Java programming. Java is a versatile, object-oriented language (OOP)
        widely used in web development, mobile apps, and more. Codingal’s course
        simplifies Java concepts, enabling kids to learn programming through
        engaging projects. Think of Java as a Swiss Army knife—just as it has
        tools for different tasks, Java can be used to create apps, games,
        websites, and even control robots!
      </>
    ),
  },
  {
    questionName: 'How can kids learn Java coding?',
    acceptedAnswerText:
      'Kids can learn Java coding through interactive lessons, fun coding challenges, and real-world projects. Codingal’s structured curriculum is designed to make learning Java simple and engaging for children, with expert instructors guiding them every step of the way.',
    acceptedAnswerJsx: (
      <>
        Kids can learn Java coding through interactive lessons, fun coding
        challenges, and real-world projects. Codingal’s structured curriculum is
        designed to make learning Java simple and engaging for children, with
        expert instructors guiding them every step of the way.
      </>
    ),
  },
  {
    questionName: 'Are there online Java coding classes for kids?',
    acceptedAnswerText:
      'Yes! Codingal offers online Java coding classes specifically designed for kids. These classes provide flexibility and allow students to learn at their own pace, from anywhere, with live instruction and support.',
    acceptedAnswerJsx: (
      <>
        Yes! Codingal offers <b>online Java coding classes</b> specifically
        designed for kids. These classes provide flexibility and allow students
        to learn at their own pace, from anywhere, with live instruction and
        support.
      </>
    ),
  },
  {
    questionName: 'Is it hard to learn Java?',
    acceptedAnswerText:
      'While Java may seem challenging at first, it’s an ideal language for kids to learn because it teaches important programming concepts that are foundational for all coding. With Codingal’s beginner-friendly curriculum, Java is broken down into manageable lessons that make it fun and easy to understand.',
    acceptedAnswerJsx: (
      <>
        While Java may seem challenging at first, it’s an ideal language for
        kids to learn because it teaches important programming concepts that are
        foundational for all coding. With Codingal’s beginner-friendly
        curriculum, Java is broken down into manageable lessons that make it fun
        and easy to understand.
      </>
    ),
  },
  {
    questionName: 'What can you do with the Java programming language?',
    acceptedAnswerText:
      'Java is a powerful language used to build a wide range of applications, from websites and mobile apps to games and even robots. Learning Java opens doors for kids to create their own projects and solve real-world problems through programming.',
    acceptedAnswerJsx: (
      <>
        Java is a powerful language used to build a wide range of applications,
        from websites and mobile apps to games and even robots. Learning Java
        opens doors for kids to create their own projects and solve real-world
        problems through programming.
      </>
    ),
  },
  {
    questionName: 'Does Codingal offer certificates for Java?',
    acceptedAnswerText: `Yes! Upon completing the Java course, students receive a STEM.org-certified AI & Coding Grandmaster certificate, which recognizes their achievements and expertise in Java programming. This certificate can be proudly shared with teachers, peers, and future academic institutions or employers.`,
    acceptedAnswerJsx: (
      <>
        Yes! Upon completing the Java course, students receive a{' '}
        <b>STEM.org-certified AI & Coding Grandmaster certificate</b>, which
        recognizes their achievements and expertise in Java programming. This
        certificate can be proudly shared with teachers, peers, and future
        academic institutions or employers.
      </>
    ),
  },
  {
    questionName: 'What grades and age groups is the Java course suitable for?',
    acceptedAnswerText: `The Java course is designed for middle and high school students (grades 6 to 12). It’s perfect for kids who are progressing from block-based programming (often used in earlier coding stages) to text-based programming, allowing them to deepen their coding skills and tackle more complex projects. No prior coding experience is required, making it ideal for beginners ready to advance.`,
    acceptedAnswerJsx: (
      <>
        The Java course is designed for{' '}
        <b>middle and high school students (grades 6 to 12)</b>. It’s perfect
        for kids who are progressing from <b>block-based programming</b> (often
        used in earlier coding stages) to <b>text-based programming</b>,
        allowing them to deepen their coding skills and tackle more complex
        projects. No prior coding experience is required, making it ideal for
        beginners ready to advance.
      </>
    ),
  },
]

const TRINKET: FaqType[] = [
  {
    questionName: 'What is a Trinket for kids?',
    acceptedAnswerText:
      'Trinket for kids is an online coding platform that lets children write, run, and share code directly from their web browser—no downloads or installations needed. It’s perfect for beginners learning Python, HTML, or JavaScript, making coding simple, fun, and interactive.',
    acceptedAnswerJsx: (
      <>
        Trinket for kids is an online coding platform that lets children write,
        run, and share code directly from their web browser—no downloads or
        installations needed. It’s perfect for beginners learning Python, HTML,
        or JavaScript, making coding simple, fun, and interactive.
      </>
    ),
  },
  {
    questionName: 'Why should my child learn coding with Trinket?',
    acceptedAnswerText:
      'Trinket makes coding visual and engaging, allowing kids to see the results of their code instantly. It helps build logical thinking, creativity, and problem-solving skills, which are essential for success in school and future tech careers.',
    acceptedAnswerJsx: (
      <>
        Trinket makes coding visual and engaging, allowing kids to see the
        results of their code instantly. It helps build logical thinking,
        creativity, and problem-solving skills, which are essential for success
        in school and future tech careers.
      </>
    ),
  },
  {
    questionName: 'What can kids create with Trinket?',
    acceptedAnswerText:
      'Yes! Trinket is a kid-friendly and browser-based platform—no software installation or account sharing is required. It provides a safe, interactive environment where kids can learn and experiment with code confidently.',
    acceptedAnswerJsx: (
      <>
        Yes! Trinket is a kid-friendly and browser-based platform—no software
        installation or account sharing is required. It provides a safe,
        interactive environment where kids can learn and experiment with code
        confidently.
      </>
    ),
  },
  {
    questionName: 'What can kids create with Trinket?',
    acceptedAnswerText:
      'With Trinket, kids can: Draw and animate shapes using Python Turtle 🐢 Create mini-games and stories 🎮 Build interactive web pages using HTML and CSS 🌐 Visualize math and science concepts through code 📊',
    acceptedAnswerJsx: (
      <>
        With Trinket, kids can:
        <ul>
          <li>Draw and animate shapes using Python Turtle 🐢</li>
          <li>Create mini-games and stories 🎮</li>
          <li>Build interactive web pages using HTML and CSS 🌐</li>
          <li>Visualize math and science concepts through code 📊</li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'How does Codingal teach Trinket to kids?',
    acceptedAnswerText:
      'At Codingal, kids learn Trinket through live online classes with expert instructors. Each session includes hands-on coding, real-time feedback, and creative projects that make learning both exciting and meaningful.',
    acceptedAnswerJsx: (
      <>
        At Codingal, kids learn Trinket through live online classes with expert
        instructors. Each session includes hands-on coding, real-time feedback,
        and creative projects that make learning both exciting and meaningful.
      </>
    ),
  },
  {
    questionName: 'Do kids need to install anything to use Trinket?',
    acceptedAnswerText:
      'No installation is required! Trinket runs entirely in a web browser, so kids can start coding instantly—anytime, anywhere.',
    acceptedAnswerJsx: (
      <>
        No installation is required! Trinket runs entirely in a web browser, so
        kids can start coding instantly—anytime, anywhere.
      </>
    ),
  },
  {
    questionName: 'What are the benefits of learning Trinket with Codingal?',
    acceptedAnswerText:
      'With Trinket, kids can: Draw and animate shapes using Python Turtle 🐢 Create mini-games and stories 🎮 Build interactive web pages using HTML and CSS 🌐 Visualize math and science concepts through code 📊',
    acceptedAnswerJsx: (
      <>
        Learning Trinket with Codingal helps kids:
        <ul>
          <li>Develop strong problem-solving and logical thinking skills 🧠</li>
          <li>Build real coding projects step by step</li>
          <li>Gain confidence and curiosity about technology</li>
          <li>Learn at their own pace with flexible, engaging lessons</li>
        </ul>
      </>
    ),
  },
]

const PYGAME: FaqType[] = [
  {
    questionName: 'What is Pygame?',
    acceptedAnswerText:
      'Pygame is a Python library that helps kids create their own 2D video games. With Pygame for kids, they can design characters, add sounds, build levels, and make interactive projects. It is like turning imagination into real, playable games.',
    acceptedAnswerJsx: (
      <>
        Pygame is a Python library that helps kids create their own 2D video
        games. With Pygame for kids, they can design characters, add sounds,
        build levels, and make interactive projects. It is like turning
        imagination into real, playable games.
      </>
    ),
  },
  {
    questionName: 'Why should my child learn Pygame?',
    acceptedAnswerText:
      'Learning Pygame for kids builds creativity, logical thinking, and problem-solving skills. It is a fun way for children to learn coding while also exploring game design and storytelling. These skills are not only exciting today but also prepare them for future-ready coding careers.',
    acceptedAnswerJsx: (
      <>
        Learning Pygame for kids builds creativity, logical thinking, and
        problem-solving skills. It is a fun way for children to learn coding
        while also exploring game design and storytelling. These skills are not
        only exciting today but also prepare them for future-ready coding
        careers.
      </>
    ),
  },
  {
    questionName: 'Is Pygame difficult for kids to learn?',
    acceptedAnswerText:
      'Not at all! Pygame programming for children is highly beginner-friendly. With simple commands and step-by-step projects, kids quickly learn how to make games. Our Pygame tutorials for kids ensure that every lesson feels engaging and interactive.',
    acceptedAnswerJsx: (
      <>
        Not at all! Pygame programming for children is highly beginner-friendly.
        With simple commands and step-by-step projects, kids quickly learn how
        to make games. Our Pygame tutorials for kids ensure that every lesson
        feels engaging and interactive.
      </>
    ),
  },
  {
    questionName: 'What can my child create with Pygame?',
    acceptedAnswerText:
      'With Pygame coding for kids, children can create: Arcade-style games like Pong or Space Invaders, Adventure games with levels and challenges, Interactive Python 2D games and stories, Beginner-friendly Python Pygame projects that showcase creativity.',
    acceptedAnswerJsx: (
      <>
        With Pygame coding for kids, children can create:
        <ul>
          <li>Arcade-style games like Pong or Space Invaders</li>
          <li>Adventure games with levels and challenges</li>
          <li>Interactive Python 2D games and stories</li>
          <li>
            Beginner-friendly Python Pygame projects that showcase creativity
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'What is Pygame for Kids at Codingal?',
    acceptedAnswerText:
      'Our Pygame classes for kids introduce children to game development in a fun, hands-on way. Kids start with small projects and gradually move on to creating full games, all while learning teamwork, creativity, and coding logic.',
    acceptedAnswerJsx: (
      <>
        Our Pygame classes for kids introduce children to game development in a
        fun, hands-on way. Kids start with small projects and gradually move on
        to creating full games, all while learning teamwork, creativity, and
        coding logic.
      </>
    ),
  },
  {
    questionName: 'How can kids learn Pygame coding?',
    acceptedAnswerText:
      'Through online Pygame coding classes for kids, live workshops, and guided projects, children learn by doing. Each session includes practical exercises where they design, test, and play their own games.',
    acceptedAnswerJsx: (
      <>
        Through online Pygame coding classes for kids, live workshops, and
        guided projects, children learn by doing. Each session includes
        practical exercises where they design, test, and play their own games.
      </>
    ),
  },
  {
    questionName: 'Are there online Pygame classes for kids?',
    acceptedAnswerText:
      'Yes! Codingal offers online Pygame classes for kids with expert instructors. Our live, interactive format ensures personalized attention and fun, project-based learning. We also offer after-school Pygame workshops and Pygame coding camps.',
    acceptedAnswerJsx: (
      <>
        Yes! Codingal offers online Pygame classes for kids with expert
        instructors. Our live, interactive format ensures personalized attention
        and fun, project-based learning. We also offer after-school Pygame
        workshops and Pygame coding camps.
      </>
    ),
  },
  {
    questionName: 'Is Pygame programming hard to learn for beginners?',
    acceptedAnswerText:
      'Not at all. With beginner Pygame lessons for kids, students quickly grasp how to add characters, sounds, and actions to a game. Pygame is the perfect step after Scratch or block-based coding because it uses real Python programming in a simple way.',
    acceptedAnswerJsx: (
      <>
        Not at all. With beginner Pygame lessons for kids, students quickly
        grasp how to add characters, sounds, and actions to a game. Pygame is
        the perfect step after Scratch or block-based coding because it uses
        real Python programming in a simple way.
      </>
    ),
  },
  {
    questionName: 'What can kids do with Pygame programming?',
    acceptedAnswerText:
      'With Pygame programming for students, children can: Code and play their own games, Work on Pygame projects for beginners, Explore coding with Pygame for kids as a stepping stone to advanced Python, Collaborate with peers on interactive projects.',
    acceptedAnswerJsx: (
      <>
        With Pygame programming for students, children can:
        <ul>
          <li>Code and play their own games</li>
          <li>Work on Pygame projects for beginners</li>
          <li>
            Explore coding with Pygame for kids as a stepping stone to advanced
            Python
          </li>
          <li>Collaborate with peers on interactive projects</li>
        </ul>
      </>
    ),
  },
]

const ANIMATION: FaqType[] = [
  {
    questionName: 'What is Animation?',
    acceptedAnswerText:
      'Animation is the process of creating the illusion of movement by displaying a sequence of images or objects. It combines creativity and technology to bring characters, objects, and stories to life. Beloved cartoons like Tom and Jerry, Mickey Mouse, or movies like Frozen use animation to create lifelike movements and expressions, making characters feel real and relatable.',
    acceptedAnswerJsx: (
      <>
        Animation is the process of creating the illusion of movement by
        displaying a sequence of images or objects. It combines creativity and
        technology to bring characters, objects, and stories to life. Beloved
        cartoons like Tom and Jerry, Mickey Mouse, or movies like Frozen use
        animation to create lifelike movements and expressions, making
        characters feel real and relatable.
      </>
    ),
  },
  {
    questionName: 'How can kids and teens learn Animation?',
    acceptedAnswerText:
      'Kids and teens can learn animation through beginner-friendly platforms like Scratch, Tynker, Roblox Studio, and Codingal’s interactive courses. They explore motion, storytelling, sprite manipulation, and effects using both block-based and text-based programming.',
    acceptedAnswerJsx: (
      <>
        Kids and teens can learn animation through beginner-friendly platforms
        like Scratch, Tynker, Roblox Studio, and Codingal’s interactive courses.
        They explore motion, storytelling, sprite manipulation, and effects
        using both block-based and text-based programming.
      </>
    ),
  },
  {
    questionName: 'Are there online Animation classes for kids?',
    acceptedAnswerText:
      'Yes! Many online platforms and courses provide age-appropriate lessons in animation, covering basics like motion, loops, and storytelling, as well as advanced concepts using Python, Pygame, or web-based tools.',
    acceptedAnswerJsx: (
      <>
        Yes! Many online platforms and courses provide age-appropriate lessons
        in animation, covering basics like motion, loops, and storytelling, as
        well as advanced concepts using Python, Pygame, or web-based tools.
      </>
    ),
  },
  {
    questionName: 'How long does it take to learn Animation?',
    acceptedAnswerText:
      'The time varies based on skill level and project complexity. Kids can learn basic animation in a few weeks, while advanced animations, games, or interactive websites may take a few months of consistent practice.',
    acceptedAnswerJsx: (
      <>
        The time varies based on skill level and project complexity. Kids can
        learn basic animation in a few weeks, while advanced animations, games,
        or interactive websites may take a few months of consistent practice.
      </>
    ),
  },
  {
    questionName: 'Is Animation hard for kids to learn?',
    acceptedAnswerText:
      'No. Animation is intuitive and fun when taught with age-appropriate tools. Block-based platforms like Scratch or Tynker make it easy for kids to create projects while learning coding concepts, logic, and creativity.',
    acceptedAnswerJsx: (
      <>
        No. Animation is intuitive and fun when taught with age-appropriate
        tools. Block-based platforms like Scratch or Tynker make it easy for
        kids to create projects while learning coding concepts, logic, and
        creativity.
      </>
    ),
  },
  {
    questionName: 'What can kids do with Animation?',
    acceptedAnswerText:
      'Kids can create interactive stories, animated games, educational apps, and even 3D experiences. Animation skills can lead to future opportunities in game design, digital media, creative arts, and software development.',
    acceptedAnswerJsx: (
      <>
        Kids can create interactive stories, animated games, educational apps,
        and even 3D experiences. Animation skills can lead to future
        opportunities in game design, digital media, creative arts, and software
        development.
      </>
    ),
  },
  {
    questionName: 'What are the benefits of learning Animation for kids?',
    acceptedAnswerText:
      'Learning animation helps kids boost creativity, problem-solving, storytelling, logical thinking, and coding confidence. It also strengthens digital literacy and foundational skills for future studies.',
    acceptedAnswerJsx: (
      <>
        Learning animation helps kids boost creativity, problem-solving,
        storytelling, logical thinking, and coding confidence. It also
        strengthens digital literacy and foundational skills for future studies.
      </>
    ),
  },
  {
    questionName: 'Which tools are best for kids learning Animation?',
    acceptedAnswerText:
      'Great beginner tools include Scratch, Tynker, Roblox Studio, Pencil2D, and other block-based platforms. Older students can explore Blender, Procreate, or Adobe Animate for advanced 2D and 3D animation.',
    acceptedAnswerJsx: (
      <>
        Great beginner tools include Scratch, Tynker, Roblox Studio, Pencil2D,
        and other block-based platforms. Older students can explore Blender,
        Procreate, or Adobe Animate for advanced 2D and 3D animation.
      </>
    ),
  },
  {
    questionName: 'What age is appropriate for learning Animation?',
    acceptedAnswerText:
      'Kids can start as young as 7–8 years old with simple, block-based tools. Elementary students focus on interactive stories and games, while middle schoolers explore advanced animation techniques, AI-powered interactions, and coding integrations.',
    acceptedAnswerJsx: (
      <>
        Kids can start as young as 7–8 years old with simple, block-based tools.
        Elementary students focus on interactive stories and games, while middle
        schoolers explore advanced animation techniques, AI-powered
        interactions, and coding integrations.
      </>
    ),
  },
  {
    questionName: 'How does Animation help in other subjects?',
    acceptedAnswerText:
      'Animation reinforces math, science, and art concepts by teaching geometry, motion physics, timing, and visual design. It helps kids apply coding and logic creatively across disciplines.',
    acceptedAnswerJsx: (
      <>
        Animation reinforces math, science, and art concepts by teaching
        geometry, motion physics, timing, and visual design. It helps kids apply
        coding and logic creatively across disciplines.
      </>
    ),
  },
  {
    questionName: 'How does Animation prepare kids for the future?',
    acceptedAnswerText:
      'Animation equips children with technical, creative, and analytical skills valuable in careers like game development, media, advertising, and software design. It fosters collaboration, critical thinking, and a portfolio of real projects.',
    acceptedAnswerJsx: (
      <>
        Animation equips children with technical, creative, and analytical
        skills valuable in careers like game development, media, advertising,
        and software design. It fosters collaboration, critical thinking, and a
        portfolio of real projects.
      </>
    ),
  },
  {
    questionName: 'What’s the difference between 2D and 3D Animation?',
    acceptedAnswerText:
      '2D Animation focuses on flat images, like Scratch or Pencil2D projects, while 3D Animation adds depth, allowing characters and objects to move in a virtual space, which can be explored in Roblox Studio, Blender, or other advanced platforms.',
    acceptedAnswerJsx: (
      <>
        2D Animation focuses on flat images, like Scratch or Pencil2D projects,
        while 3D Animation adds depth, allowing characters and objects to move
        in a virtual space, which can be explored in Roblox Studio, Blender, or
        other advanced platforms.
      </>
    ),
  },
  {
    questionName: 'How does Codingal teach Animation effectively?',
    acceptedAnswerText:
      'Codingal blends interactive projects, AI-powered exercises, and fun challenges across platforms like Scratch, Code.org, Python (Pygame), web development, and Roblox Studio. Students learn by creating games, digital stories, and apps while developing coding, design, and problem-solving skills.',
    acceptedAnswerJsx: (
      <>
        Codingal blends interactive projects, AI-powered exercises, and fun
        challenges across platforms like Scratch, Code.org, Python (Pygame), web
        development, and Roblox Studio. Students learn by creating games,
        digital stories, and apps while developing coding, design, and
        problem-solving skills.
      </>
    ),
  },
  {
    questionName: 'Can Animation help kids create games and movies?',
    acceptedAnswerText:
      'Yes. Kids learn to animate sprites, control objects, design storylines, and sync actions with music or interactions, enabling them to create engaging games, cartoons, and interactive movies.',
    acceptedAnswerJsx: (
      <>
        Yes. Kids learn to animate sprites, control objects, design storylines,
        and sync actions with music or interactions, enabling them to create
        engaging games, cartoons, and interactive movies.
      </>
    ),
  },
  {
    questionName: 'How do kids practice Animation at Codingal?',
    acceptedAnswerText:
      'Students engage in quizzes, guided coding exercises, and hands-on projects like Flappy Bird, Dance Party, Star Wars animations, or interactive calculators. Each activity teaches programming, logic, creativity, and real-world animation concepts.',
    acceptedAnswerJsx: (
      <>
        Students engage in quizzes, guided coding exercises, and hands-on
        projects like Flappy Bird, Dance Party, Star Wars animations, or
        interactive calculators. Each activity teaches programming, logic,
        creativity, and real-world animation concepts.
      </>
    ),
  },
  {
    questionName: 'What can kids do with Animation?',
    acceptedAnswerText:
      'Kids can create interactive stories, animated games, educational apps, and even 3D experiences. Animation skills can lead to future opportunities in game design, digital media, creative arts, and software development.',
    acceptedAnswerJsx: (
      <>
        Kids can create interactive stories, animated games, educational apps,
        and even 3D experiences. Animation skills can lead to future
        opportunities in game design, digital media, creative arts, and software
        development.
      </>
    ),
  },
]

const TURTLE: FaqType[] = [
  {
    questionName: 'What is Python Turtle and why should kids learn it?',
    acceptedAnswerText:
      'Python Turtle is a fun way to teach coding by letting kids draw shapes, patterns, and animations using code. Learning Turtle helps children develop problem-solving, creativity, and logical thinking skills.',
    acceptedAnswerJsx: (
      <>
        <p>
          Python Turtle is a fun way to teach coding by letting kids draw
          shapes, patterns, and animations using code. Learning Turtle helps
          children develop problem-solving, creativity, and logical thinking
          skills.
        </p>
      </>
    ),
  },
  {
    questionName: 'What age group is suitable for learning Turtle?',
    acceptedAnswerText:
      'Turtle is ideal for kids aged 7 and above, especially those starting to learn Python or wanting a visual, interactive introduction to coding.',
    acceptedAnswerJsx: (
      <p>
        Turtle is ideal for kids aged 7 and above, especially those starting to
        learn Python or wanting a visual, interactive introduction to coding.
      </p>
    ),
  },
  {
    questionName: 'Do kids need prior coding knowledge to use Turtle?',
    acceptedAnswerText:
      'No prior experience is required! Turtle is beginner-friendly. Kids can start by learning simple commands to draw shapes, then progress to more complex patterns and projects.',
    acceptedAnswerJsx: (
      <p>
        No prior experience is required! Turtle is beginner-friendly. Kids can
        start by learning simple commands to draw shapes, then progress to more
        complex patterns and projects.
      </p>
    ),
  },
  {
    questionName: 'What kinds of projects can kids build with Turtle?',
    acceptedAnswerText:
      'Kids can create: Geometric shapes and colorful patterns Simple animations and drawings Fun games using graphics Creative art projects that teach coding logic',
    acceptedAnswerJsx: (
      <>
        <p>Kids can create:</p>
        <ul>
          <li>Geometric shapes and colorful patterns</li>
          <li>Simple animations and drawings</li>
          <li>Fun games using graphics</li>
          <li>Creative art projects that teach coding logic</li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'How does Turtle help kids learn programming?',
    acceptedAnswerText:
      'Turtle introduces Python concepts visually, such as loops, functions, variables, and conditionals. Kids immediately see the results of their code, making learning interactive and engaging.',
    acceptedAnswerJsx: (
      <p>
        Turtle introduces Python concepts visually, such as loops, functions,
        variables, and conditionals. Kids immediately see the results of their
        code, making learning interactive and engaging.
      </p>
    ),
  },
  {
    questionName: 'How does Codingal teach Turtle to kids?',
    acceptedAnswerText:
      'Codingal uses project-based lessons, guiding kids step by step to build drawings, animations, and games. Lessons are interactive and designed to make coding fun while teaching key Python concepts.',
    acceptedAnswerJsx: (
      <p>
        Codingal uses project-based lessons, guiding kids step by step to build
        drawings, animations, and games. Lessons are interactive and designed to
        make coding fun while teaching key Python concepts.
      </p>
    ),
  },
  {
    questionName: 'Is Turtle suitable for collaborative learning?',
    acceptedAnswerText:
      'Yes! Kids can share their Turtle projects, collaborate on patterns or mini-games, and get feedback from peers and teachers, fostering creativity and teamwork.',
    acceptedAnswerJsx: (
      <p>
        Yes! Kids can share their Turtle projects, collaborate on patterns or
        mini-games, and get feedback from peers and teachers, fostering
        creativity and teamwork.
      </p>
    ),
  },
  {
    questionName: 'Can learning Turtle help kids in the future?',
    acceptedAnswerText:
      'Absolutely! Turtle builds a strong foundation in Python programming, problem-solving, and logical thinking, preparing kids for more advanced coding and game development projects later.',
    acceptedAnswerJsx: (
      <p>
        Absolutely! Turtle builds a strong foundation in Python programming,
        problem-solving, and logical thinking, preparing kids for more advanced
        coding and game development projects later.
      </p>
    ),
  },
  {
    questionName: 'What are the benefits of learning Turtle for kids?',
    acceptedAnswerText:
      'Develops creativity through coding Teaches Python fundamentals in a visual way Improves problem-solving and logical skills Encourages hands-on, interactive learning Prepares children for advanced coding projects',
    acceptedAnswerJsx: (
      <>
        <ul>
          <li>Develops creativity through coding</li>
          <li>Teaches Python fundamentals in a visual way</li>
          <li>Improves problem-solving and logical skills</li>
          <li>Encourages hands-on, interactive learning</li>
          <li>Prepares children for advanced coding projects</li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'Is Turtle safe and easy for kids to use?',
    acceptedAnswerText:
      'Yes! Turtle is simple, beginner-friendly, and safe for children. With guidance from Codingal’s structured lessons, kids can confidently explore coding while having fun.',
    acceptedAnswerJsx: (
      <p>
        Yes! Turtle is simple, beginner-friendly, and safe for children. With
        guidance from Codingal’s structured lessons, kids can confidently
        explore coding while having fun.
      </p>
    ),
  },
  {
    questionName: 'Is Turtle a Good Python Library for Beginners?',
    acceptedAnswerText:
      'Yes! Turtle is one of the best Python libraries for beginners. It helps new learners understand the fundamentals of programming logic through fun, visual activities. Key Highlights: ✅ Simple commands like forward(), right(), and circle() make it easy to learn. ✅ Perfect for creating shapes, patterns, and colorful animations. ✅ Builds a strong foundation in loops, angles, and sequencing through visual coding. Turtle turns coding into a playful experience, making it ideal for kids and first-time programmers.',
    acceptedAnswerJsx: (
      <>
        <p>
          Yes! Turtle is one of the best Python libraries for beginners. It
          helps new learners understand the fundamentals of programming logic
          through fun, visual activities.
        </p>
        <p>
          <b>Key Highlights:</b>
        </p>
        <ul>
          <li>
            ✅ Simple commands like <code>forward()</code>, <code>right()</code>
            , and <code>circle()</code> make it easy to learn.
          </li>
          <li>
            ✅ Perfect for creating shapes, patterns, and colorful animations.
          </li>
          <li>
            ✅ Builds a strong foundation in loops, angles, and sequencing
            through visual coding.
          </li>
        </ul>
        <p>
          Turtle turns coding into a playful experience, making it ideal for
          kids and first-time programmers.
        </p>
      </>
    ),
  },
]

const MIT_APP_INVENTOR: FaqType[] = [
  {
    questionName: 'What is MIT App Inventor for Kids?',
    acceptedAnswerText:
      'MIT App Inventor is a block-based coding platform where kids can build real mobile apps using visual logic—no prior experience needed.',
    acceptedAnswerJsx: (
      <>
        MIT App Inventor is a block-based coding platform where kids can build
        real mobile apps using visual logic—no prior experience needed.
      </>
    ),
  },
  {
    questionName: 'How can kids learn MIT App Inventor coding?',
    acceptedAnswerText:
      'With Codingal’s guided curriculum, live classes, and expert teachers, kids learn to build apps step-by-step in a fun and interactive way.',
    acceptedAnswerJsx: (
      <>
        With Codingal’s guided curriculum, live classes, and expert teachers,
        kids learn to build apps step-by-step in a fun and interactive way.
      </>
    ),
  },
  {
    questionName: 'Are there online MIT App Inventor classes for kids?',
    acceptedAnswerText:
      'Yes, Codingal offers 100% online MIT App Inventor classes with live, interactive lessons accessible from anywhere.',
    acceptedAnswerJsx: (
      <>
        Yes, Codingal offers 100% online MIT App Inventor classes with live,
        interactive lessons accessible from anywhere.
      </>
    ),
  },
  {
    questionName: 'How long does it typically take to complete a course?',
    acceptedAnswerText:
      'Most students complete the MIT App Inventor course in 12–16 weeks depending on pace and depth of projects.',
    acceptedAnswerJsx: (
      <>
        Most students complete the MIT App Inventor course in 12–16 weeks
        depending on pace and depth of projects.
      </>
    ),
  },
  {
    questionName: 'Is MIT App Inventor programming hard to learn?',
    acceptedAnswerText:
      'No! The drag-and-drop interface makes it beginner-friendly and perfect for kids aged 8–16.',
    acceptedAnswerJsx: (
      <>
        No! The drag-and-drop interface makes it beginner-friendly and perfect
        for kids aged 8–16.
      </>
    ),
  },
  {
    questionName: 'What can you do with MIT App Inventor?',
    acceptedAnswerText:
      'Kids can create Android apps like calculators, quizzes, drawing tools, and even AI-enabled apps like those using speech recognition.',
    acceptedAnswerJsx: (
      <>
        Kids can create Android apps like calculators, quizzes, drawing tools,
        and even AI-enabled apps like those using speech recognition.
      </>
    ),
  },
  {
    questionName: 'What type of jobs can kids get after learning this?',
    acceptedAnswerText:
      'While a beginner platform, MIT App Inventor builds a foundation for careers in app development, software engineering, and UI/UX design.',
    acceptedAnswerJsx: (
      <>
        While a beginner platform, MIT App Inventor builds a foundation for
        careers in app development, software engineering, and UI/UX design.
      </>
    ),
  },
  {
    questionName: 'Does Codingal provide certificates for this course?',
    acceptedAnswerText:
      'Yes! Students receive a Developer Certificate from Codingal upon completion, backed by STEM.org accreditation.',
    acceptedAnswerJsx: (
      <>
        Yes! Students receive a Developer Certificate from Codingal upon
        completion, backed by STEM.org accreditation.
      </>
    ),
  },
]

const BOOTSTRAP: FaqType[] = [
  {
    questionName: 'What does Bootstrap stand for?',
    acceptedAnswerText:
      'Bootstrap is a free, open-source front-end framework that helps kids and beginners design beautiful, responsive websites easily. With Bootstrap for kids, students can use ready-made templates, grids, buttons, and forms to build websites that look great on phones, tablets, and computers, all without needing advanced coding skills.',
    acceptedAnswerJsx: (
      <>
        <p>
          Bootstrap is a free, open-source front-end framework that helps kids
          and beginners design beautiful, responsive websites easily. With
          Bootstrap for kids, students can use ready-made templates, grids,
          buttons, and forms to build websites that look great on phones,
          tablets, and computers, all without needing advanced coding skills.
        </p>
      </>
    ),
  },
  {
    questionName: 'Why should my child learn Bootstrap?',
    acceptedAnswerText:
      'Learning Bootstrap for kids boosts creativity, logical thinking, and problem-solving skills. It teaches children how modern websites are designed and styled, helping them understand how structure and visuals come together. These skills prepare kids for future-ready careers in web design, coding, and technology.',
    acceptedAnswerJsx: (
      <>
        <p>
          Learning Bootstrap for kids boosts creativity, logical thinking, and
          problem-solving skills. It teaches children how modern websites are
          designed and styled, helping them understand how structure and visuals
          come together. These skills prepare kids for future-ready careers in
          web design, coding, and technology.
        </p>
      </>
    ),
  },
  {
    questionName: 'Is Bootstrap difficult for kids to learn?',
    acceptedAnswerText:
      'Not at all! Bootstrap programming for children is highly beginner-friendly. With easy-to-use classes and prebuilt design components, kids can create impressive websites quickly. Codingal’s live Bootstrap classes guide them step by step, making learning fun, visual, and hands-on.',
    acceptedAnswerJsx: (
      <>
        <p>
          Not at all! Bootstrap programming for children is highly
          beginner-friendly. With easy-to-use classes and prebuilt design
          components, kids can create impressive websites quickly. Codingal’s
          live Bootstrap classes guide them step by step, making learning fun,
          visual, and hands-on.
        </p>
      </>
    ),
  },
  {
    questionName: 'What can my child create with Bootstrap?',
    acceptedAnswerText:
      'With Bootstrap coding for kids, children can create: Personal portfolio websites, Digital storybooks and creative blogs, Student profile cards or bio pages, Responsive landing pages and mini web apps, Beginner-friendly Bootstrap projects that showcase creativity and design.',
    acceptedAnswerJsx: (
      <>
        <p>With Bootstrap coding for kids, children can create:</p>
        <ul>
          <li>Personal portfolio websites</li>
          <li>Digital storybooks and creative blogs</li>
          <li>Student profile cards or bio pages</li>
          <li>Responsive landing pages and mini web apps</li>
          <li>
            Beginner-friendly Bootstrap projects that showcase creativity and
            design
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'What is Bootstrap for Kids at Codingal?',
    acceptedAnswerText:
      'At Codingal, our Bootstrap classes for kids introduce students to real-world web design through interactive lessons. Kids learn to structure web pages, style elements, and add interactivity — turning creative ideas into professional-looking websites.',
    acceptedAnswerJsx: (
      <>
        <p>
          At Codingal, our Bootstrap classes for kids introduce students to
          real-world web design through interactive lessons. Kids learn to
          structure web pages, style elements, and add interactivity — turning
          creative ideas into professional-looking websites.
        </p>
      </>
    ),
  },
  {
    questionName: 'How can kids learn Bootstrap coding?',
    acceptedAnswerText:
      'Children learn through live, online Bootstrap classes for kids with Codingal’s expert instructors. They follow a structured, project-based curriculum that includes fun challenges like building a Student Profile Card, Emoji Board, and Instagram Login Page Clone.',
    acceptedAnswerJsx: (
      <>
        <p>
          Children learn through live, online Bootstrap classes for kids with
          Codingal’s expert instructors. They follow a structured, project-based
          curriculum that includes fun challenges like building a Student
          Profile Card, Emoji Board, and Instagram Login Page Clone.
        </p>
      </>
    ),
  },
  {
    questionName: 'Are there online Bootstrap classes for kids?',
    acceptedAnswerText:
      'Yes! Codingal offers online Bootstrap classes for kids that make learning web design interactive and exciting. Students can join from anywhere, learn at their own pace, and get real-time feedback from instructors. We also offer after-school Bootstrap workshops and coding camps to extend their learning experience.',
    acceptedAnswerJsx: (
      <>
        <p>
          Yes! Codingal offers online Bootstrap classes for kids that make
          learning web design interactive and exciting. Students can join from
          anywhere, learn at their own pace, and get real-time feedback from
          instructors. We also offer after-school Bootstrap workshops and coding
          camps to extend their learning experience.
        </p>
      </>
    ),
  },
  {
    questionName: 'Is Bootstrap hard for beginners?',
    acceptedAnswerText:
      'Not at all. Beginner Bootstrap lessons for kids focus on simple drag-and-code learning using reusable components. With live instructor guidance, kids quickly understand grids, containers, typography, and responsive layouts — the building blocks of every website.',
    acceptedAnswerJsx: (
      <>
        <p>
          Not at all. Beginner Bootstrap lessons for kids focus on simple
          drag-and-code learning using reusable components. With live instructor
          guidance, kids quickly understand grids, containers, typography, and
          responsive layouts — the building blocks of every website.
        </p>
      </>
    ),
  },
  {
    questionName: 'What can kids do with Bootstrap programming?',
    acceptedAnswerText:
      'With Bootstrap for students, children can: Build their own websites and online portfolios, Learn web layout, design, and responsive styling, Work on Bootstrap projects for beginners, Collaborate with classmates on creative website ideas.',
    acceptedAnswerJsx: (
      <>
        <p>With Bootstrap for students, children can:</p>
        <ul>
          <li>Build their own websites and online portfolios</li>
          <li>Learn web layout, design, and responsive styling</li>
          <li>Work on Bootstrap projects for beginners</li>
          <li>Collaborate with classmates on creative website ideas</li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'What skills will my child gain from learning Bootstrap?',
    acceptedAnswerText:
      'Kids gain valuable skills such as: Web design and styling using Bootstrap’s prebuilt tools, Creative thinking through layout design and customization, Problem-solving by debugging and improving designs, Technical confidence to explore advanced frameworks and coding languages.',
    acceptedAnswerJsx: (
      <>
        <p>Kids gain valuable skills such as:</p>
        <ul>
          <li>Web design and styling using Bootstrap’s prebuilt tools</li>
          <li>Creative thinking through layout design and customization</li>
          <li>Problem-solving by debugging and improving designs</li>
          <li>
            Technical confidence to explore advanced frameworks and coding
            languages
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'Why choose Codingal for learning Bootstrap?',
    acceptedAnswerText:
      'Codingal is trusted by parents and students worldwide for delivering fun, project-based learning. Our Bootstrap for kids course combines creativity, coding, and design thinking to make web development exciting, engaging, and future-ready.',
    acceptedAnswerJsx: (
      <>
        <p>
          Codingal is trusted by parents and students worldwide for delivering
          fun, project-based learning. Our Bootstrap for kids course combines
          creativity, coding, and design thinking to make web development
          exciting, engaging, and future-ready.
        </p>
      </>
    ),
  },
  {
    questionName: 'What age group is best for learning Bootstrap?',
    acceptedAnswerText:
      'Bootstrap is ideal for middle school and high school students (ages 11–16) who already know basic HTML and CSS. It’s the perfect next step before learning JavaScript, React, or other advanced front-end technologies.',
    acceptedAnswerJsx: (
      <>
        <p>
          Bootstrap is ideal for middle school and high school students (ages
          11–16) who already know basic HTML and CSS. It’s the perfect next step
          before learning JavaScript, React, or other advanced front-end
          technologies.
        </p>
      </>
    ),
  },
  {
    questionName: 'What is taught in the Bootstrap curriculum at Codingal?',
    acceptedAnswerText:
      'The Bootstrap curriculum covers all essential topics such as: Introduction to Bootstrap, Typography and Containers, Bootstrap Utilities (Parts 1 & 2), Bootstrap Carousel and Scrollspy. By the end, students create a complete responsive website as their final project — a proud moment for every young coder!',
    acceptedAnswerJsx: (
      <>
        <p>The Bootstrap curriculum covers all essential topics such as:</p>
        <ul>
          <li>Introduction to Bootstrap</li>
          <li>Typography and Containers</li>
          <li>Bootstrap Utilities (Parts 1 & 2)</li>
          <li>Bootstrap Carousel and Scrollspy</li>
        </ul>
        <p>
          By the end, students create a complete responsive website as their
          final project — a proud moment for every young coder!
        </p>
      </>
    ),
  },
]

const JAVASCRIPT: FaqType[] = [
  {
    questionName: 'What is JavaScript?',
    acceptedAnswerText:
      'JavaScript is a powerful programming language that makes websites interactive and dynamic! While HTML gives a website its structure (like the walls of a house) and CSS makes it look pretty (like paint and furniture), JavaScript adds the actions and behaviors (like lights turning on, buttons clicking, or games playing).',
    acceptedAnswerJsx: (
      <>
        <p>
          JavaScript is a powerful programming language that makes websites
          interactive and dynamic! While HTML gives a website its structure
          (like the walls of a house) and CSS makes it look pretty (like paint
          and furniture), JavaScript adds the actions and behaviors (like lights
          turning on, buttons clicking, or games playing).
        </p>
      </>
    ),
  },
  {
    questionName: 'Why should my child learn JavaScript?',
    acceptedAnswerText:
      'Learning JavaScript opens up a world of possibilities for your child! They can create interactive games, animated stories, dynamic web pages, and even control robots. It teaches advanced problem-solving, computational thinking, and prepares them for future careers in technology, game development, and web design.',
    acceptedAnswerJsx: (
      <>
        <p>
          Learning JavaScript opens up a world of possibilities for your child!
          They can create interactive games, animated stories, dynamic web
          pages, and even control robots. It teaches advanced problem-solving,
          computational thinking, and prepares them for future careers in
          technology, game development, and web design.
        </p>
      </>
    ),
  },
  {
    questionName: 'Is JavaScript difficult for kids to learn?',
    acceptedAnswerText:
      'JavaScript can be more challenging than HTML or block-based coding, but our courses are specifically designed to make it accessible and fun for kids. We use engaging projects, visual examples, and a step-by-step approach to break down complex concepts into manageable pieces.',
    acceptedAnswerJsx: (
      <>
        <p>
          JavaScript can be more challenging than HTML or block-based coding,
          but our courses are specifically designed to make it accessible and
          fun for kids. We use engaging projects, visual examples, and a
          step-by-step approach to break down complex concepts into manageable
          pieces.
        </p>
      </>
    ),
  },
  {
    questionName: 'What age is appropriate for learning JavaScript?',
    acceptedAnswerText:
      'Our JavaScript courses are generally recommended for children aged 10 and up, especially those who have some prior experience with HTML and CSS or block-based coding like Scratch. This foundational knowledge helps them grasp JavaScript concepts more easily.',
    acceptedAnswerJsx: (
      <>
        <p>
          Our JavaScript courses are generally recommended for children aged 10
          and up, especially those who have some prior experience with HTML and
          CSS or block-based coding like Scratch. This foundational knowledge
          helps them grasp JavaScript concepts more easily.
        </p>
      </>
    ),
  },
  {
    questionName: 'What will my child be able to do after learning JavaScript?',
    acceptedAnswerText:
      "After completing our JavaScript course, your child will be able to create interactive elements on web pages, build simple web-based games, develop animated stories, and understand fundamental programming concepts like variables, loops, and functions. They'll be able to bring their web creations to life!",
    acceptedAnswerJsx: (
      <>
        <p>
          After completing our JavaScript course, your child will be able to
          create interactive elements on web pages, build simple web-based
          games, develop animated stories, and understand fundamental
          programming concepts like variables, loops, and functions.
          They&apos;ll be able to bring their web creations to life!
        </p>
      </>
    ),
  },
  {
    questionName:
      'Does my child need to know HTML and CSS before taking this JavaScript class?',
    acceptedAnswerText:
      'Yes, a basic understanding of HTML and CSS is highly recommended. JavaScript often works hand-in-hand with HTML and CSS to create interactive web experiences. We assume students have this foundational knowledge.',
    acceptedAnswerJsx: (
      <>
        <p>
          Yes, a basic understanding of HTML and CSS is highly recommended.
          JavaScript often works hand-in-hand with HTML and CSS to create
          interactive web experiences. We assume students have this foundational
          knowledge.
        </p>
      </>
    ),
  },
  {
    questionName: 'What tools or software does my child need for the class?',
    acceptedAnswerText:
      'Your child will only need a computer with internet access and a modern web browser (like Chrome or Firefox). We will use online code editors that are perfect for learning JavaScript, so no complex software installations are required.',
    acceptedAnswerJsx: (
      <>
        <p>
          Your child will only need a computer with internet access and a modern
          web browser (like Chrome or Firefox). We will use online code editors
          that are perfect for learning JavaScript, so no complex software
          installations are required.
        </p>
      </>
    ),
  },
  {
    questionName: 'How are the classes taught?',
    acceptedAnswerText:
      'Our classes are highly interactive, project-based, and designed for active learning. We use live coding demonstrations, real-world examples, and fun challenges. Students will write code, debug their programs, and build exciting projects throughout the course.',
    acceptedAnswerJsx: (
      <>
        <p>
          Our classes are highly interactive, project-based, and designed for
          active learning. We use live coding demonstrations, real-world
          examples, and fun challenges. Students will write code, debug their
          programs, and build exciting projects throughout the course.
        </p>
      </>
    ),
  },
  {
    questionName: 'What kind of projects will my child work on?',
    acceptedAnswerText:
      'Children will work on engaging projects such as building a simple calculator, creating a digital drawing board, developing a mini-game (like a guessing game or a simple platformer), and adding interactive features to their own web pages.',
    acceptedAnswerJsx: (
      <>
        <p>
          Children will work on engaging projects such as building a simple
          calculator, creating a digital drawing board, developing a mini-game
          (like a guessing game or a simple platformer), and adding interactive
          features to their own web pages.
        </p>
      </>
    ),
  },
  {
    questionName: 'Is there any support available if my child gets stuck?',
    acceptedAnswerText:
      'Absolutely! Our experienced instructors provide personalized support during live sessions and are available for questions. We also foster a supportive online community where students can collaborate, share their work, and get help from peers and mentors.',
    acceptedAnswerJsx: (
      <>
        <p>
          Absolutely! Our experienced instructors provide personalized support
          during live sessions and are available for questions. We also foster a
          supportive online community where students can collaborate, share
          their work, and get help from peers and mentors.
        </p>
      </>
    ),
  },
  {
    questionName: 'How long is the course?',
    acceptedAnswerText:
      'The course is structured into several modules, with each module focusing on specific JavaScript concepts and culminating in practical projects. The total duration can vary depending on the course format.',
    acceptedAnswerJsx: (
      <>
        <p>
          The course is structured into several modules, with each module
          focusing on specific JavaScript concepts and culminating in practical
          projects. The total duration can vary depending on the course format.
        </p>
      </>
    ),
  },
  {
    questionName:
      'What are the benefits of learning JavaScript at a young age?',
    acceptedAnswerText:
      'Learning JavaScript at a young age provides a strong foundation in computational thinking and logical reasoning. It empowers children to create dynamic and interactive digital experiences, fostering creativity and preparing them for advanced computer science studies and future careers in web development, software engineering, and game design.',
    acceptedAnswerJsx: (
      <>
        <p>
          Learning JavaScript at a young age provides a strong foundation in
          computational thinking and logical reasoning. It empowers children to
          create dynamic and interactive digital experiences, fostering
          creativity and preparing them for advanced computer science studies
          and future careers in web development, software engineering, and game
          design.
        </p>
      </>
    ),
  },
  {
    questionName: 'Can a 10-Year-Old Learn JavaScript?',
    acceptedAnswerText:
      'Absolutely! A 10-year-old can learn JavaScript easily with the right guidance. Kids at this age can understand logic, patterns, and cause-and-effect, all key to coding. At Codingal, we introduce JavaScript step-by-step through games, animations, and hands-on projects so that learning feels like play, not homework.',
    acceptedAnswerJsx: (
      <>
        <p>
          Absolutely! A 10-year-old can learn JavaScript easily with the right
          guidance. Kids at this age can understand logic, patterns, and
          cause-and-effect, all key to coding. At Codingal, we introduce
          JavaScript step-by-step through games, animations, and hands-on
          projects so that learning feels like play, not homework.
        </p>
      </>
    ),
  },
  {
    questionName: 'What Is JavaScript in Simple Words?',
    acceptedAnswerText:
      'JavaScript is a computer language that makes websites interactive. If HTML builds a web page and CSS makes it look nice, JavaScript makes it come alive — like making buttons click, animations move, or games react when you play. It’s how kids learn to talk to computers and make them do fun things! However, most kids learn faster through interactive lessons — that’s why Codingal’s live, project-based classes keep them engaged while practicing real JavaScript.',
    acceptedAnswerJsx: (
      <>
        <p>
          JavaScript is a computer language that makes websites interactive. If
          HTML builds a web page and CSS makes it look nice, JavaScript makes it
          come alive — like making buttons click, animations move, or games
          react when you play. It’s how kids learn to talk to computers and make
          them do fun things!
        </p>
        <p>
          However, most kids learn faster through interactive lessons — that’s
          why Codingal’s live, project-based classes keep them engaged while
          practicing real JavaScript.
        </p>
      </>
    ),
  },
  {
    questionName: 'Can I Learn JavaScript in 3 Days?',
    acceptedAnswerText:
      'You can learn the basics of JavaScript — like writing simple commands and displaying text — in a few days. But becoming confident takes time and practice. In Codingal’s structured program, kids progress gradually from beginner to confident coder through fun projects and real-world activities.',
    acceptedAnswerJsx: (
      <>
        <p>
          You can learn the basics of JavaScript — like writing simple commands
          and displaying text — in a few days. But becoming confident takes time
          and practice. In Codingal’s structured program, kids progress
          gradually from beginner to confident coder through fun projects and
          real-world activities.
        </p>
      </>
    ),
  },
  {
    questionName: 'Is JavaScript Similar to Java?',
    acceptedAnswerText:
      'Despite the similar names, JavaScript and Java are completely different languages. JavaScript runs in browsers to make web pages interactive, while Java is used for building applications and mobile software. JavaScript is simpler, faster to start with, and perfect for kids learning to code.',
    acceptedAnswerJsx: (
      <>
        <p>
          Despite the similar names, JavaScript and Java are completely
          different languages. JavaScript runs in browsers to make web pages
          interactive, while Java is used for building applications and mobile
          software. JavaScript is simpler, faster to start with, and perfect for
          kids learning to code.
        </p>
      </>
    ),
  },
  {
    questionName: 'What Is the Fastest Way to Learn JavaScript?',
    acceptedAnswerText:
      'The fastest way to learn JavaScript is by coding every day and building small projects. Start with simple exercises, like making text appear or creating a button that changes color. Codingal’s live lessons follow this approach, combining explanations, demos, and fun projects that reinforce each concept.',
    acceptedAnswerJsx: (
      <>
        <p>
          The fastest way to learn JavaScript is by coding every day and
          building small projects. Start with simple exercises, like making text
          appear or creating a button that changes color. Codingal’s live
          lessons follow this approach, combining explanations, demos, and fun
          projects that reinforce each concept.
        </p>
      </>
    ),
  },
  {
    questionName: 'Is JavaScript Better Than Python?',
    acceptedAnswerText:
      'Both are great! Python is excellent for logic, data, and AI, while JavaScript is perfect for building websites, animations, and games. For kids who love creating things they can see and interact with, JavaScript is the more exciting starting point.',
    acceptedAnswerJsx: (
      <>
        <p>
          Both are great! Python is excellent for logic, data, and AI, while
          JavaScript is perfect for building websites, animations, and games.
          For kids who love creating things they can see and interact with,
          JavaScript is the more exciting starting point.
        </p>
      </>
    ),
  },
]

const CSS: FaqType[] = [
  {
    questionName: 'What is CSS?',
    acceptedAnswerText:
      "CSS stands for Cascading Style Sheets. It's like the fashion designer for websites! While HTML builds the structure (like the walls and rooms of a house), CSS makes it look beautiful by adding colors, fonts, layouts, and designs (like painting the walls and arranging the furniture). This is key for anyone learning HTML and CSS.",
    acceptedAnswerJsx: (
      <>
        CSS stands for Cascading Style Sheets. It&lsquo;s like the fashion
        designer for websites! While HTML builds the structure (like the walls
        and rooms of a house), CSS makes it look beautiful by adding colors,
        fonts, layouts, and designs (like painting the walls and arranging the
        furniture). This is key for anyone learning HTML and CSS.
      </>
    ),
  },
  {
    questionName: 'Why should my child learn CSS?',
    acceptedAnswerText:
      "Learning CSS helps children develop creativity, logical thinking, and problem-solving skills. It allows them to express themselves by designing unique web pages, understand how the internet works, and gain valuable digital literacy skills that are essential for the future. It's a great way to learn HTML, CSS, and JavaScript.",
    acceptedAnswerJsx: (
      <>
        Learning CSS helps children develop creativity, logical thinking, and
        problem-solving skills. It allows them to express themselves by
        designing unique web pages, understand how the internet works, and gain
        valuable digital literacy skills that are essential for the future.
        It&lsquo;s a great way to learn HTML, CSS, and JavaScript.
      </>
    ),
  },
  {
    questionName: 'Is CSS difficult for kids to learn?',
    acceptedAnswerText:
      'Not at all! Our classes are designed to be fun, visual, and engaging, using kid-friendly analogies and hands-on projects. We start with the basics and gradually introduce more complex concepts, making it accessible and enjoyable for young learners. This makes the CSS learning curve manageable.',
    acceptedAnswerJsx: (
      <>
        Not at all! Our classes are designed to be fun, visual, and engaging,
        using kid-friendly analogies and hands-on projects. We start with the
        basics and gradually introduce more complex concepts, making it
        accessible and enjoyable for young learners. This makes the CSS learning
        curve manageable.
      </>
    ),
  },
  {
    questionName: 'What age is appropriate for learning CSS?',
    acceptedAnswerText:
      "Our CSS classes are generally suitable for children aged 8-14. However, it depends on the child's interest and prior experience. A basic        understanding of HTML is recommended, but we can provide resources for that too.",
    acceptedAnswerJsx: (
      <>
        Our CSS classes are generally suitable for children aged 8-14. However,
        it depends on the child&lsquo;s interest and prior experience. A basic
        understanding of HTML is recommended, but we can provide resources for
        that too.
      </>
    ),
  },
  {
    questionName: 'What will my child be able to do after learning CSS?',
    acceptedAnswerText:
      "After completing our CSS course, your child will be able to style their own web pages, change colors, fonts, and backgrounds, arrange elements on a page, and even make their websites look good on different devices like phones and tablets. They'll be able to create visually appealing and personalized online projects. This is how to learn HTML and CSS effectively.",
    acceptedAnswerJsx: (
      <>
        After completing our CSS course, your child will be able to style their
        own web pages, change colors, fonts, and backgrounds, arrange elements
        on a page, and even make their websites look good on different devices
        like phones and tablets. They&lsquo;ll be able to create visually
        appealing and personalized online projects.
      </>
    ),
  },
  {
    questionName:
      'Does my child need to know HTML before taking this CSS class?',
    acceptedAnswerText:
      'While not strictly required, a basic understanding of HTML (how to create headings, paragraphs, and add images) is very helpful. We do offer a quick recap of essential HTML concepts at the beginning of the course.',
    acceptedAnswerJsx: (
      <>
        While not strictly required, a basic understanding of HTML (how to
        create headings, paragraphs, and add images) is very helpful. We do
        offer a quick recap of essential HTML concepts at the beginning of the
        course.
      </>
    ),
  },
  {
    questionName: 'What tools or software does my child need for the class?',
    acceptedAnswerText:
      "Your child will only need a computer with internet access and a modern web browser (like Chrome or Firefox). We will use online code editors, so there's no need to download or install any complex software.",
    acceptedAnswerJsx: (
      <>
        Your child will only need a computer with internet access and a modern
        web browser (like Chrome or Firefox). We will use online code editors,
        so there&lsquo;s no need to download or install any complex software.
      </>
    ),
  },
  {
    questionName: 'How are the classes taught?',
    acceptedAnswerText:
      'Our classes are highly interactive and visual. We use live coding demonstrations, engaging analogies, and plenty of hands-on projects. We believe in learning by doing, so children will be actively styling their own web pages from day one.',
    acceptedAnswerJsx: (
      <>
        Our classes are highly interactive and visual. We use live coding
        demonstrations, engaging analogies, and plenty of hands-on projects. We
        believe in learning by doing, so children will be actively styling their
        own web pages from day one.
      </>
    ),
  },
  {
    questionName: 'What kind of projects will my child work on?',
    acceptedAnswerText:
      'Children will work on exciting projects like designing their own colorful storybook pages, creating a photo gallery layout, building an interactive profile page, and ultimately, designing their very own multi-page website on a topic they love.',
    acceptedAnswerJsx: (
      <>
        Children will work on exciting projects like designing their own
        colorful storybook pages, creating a photo gallery layout, building an
        interactive profile page, and ultimately, designing their very own
        multi-page website on a topic they love.
      </>
    ),
  },
  {
    questionName: 'Is there any support available if my child gets stuck?',
    acceptedAnswerText:
      'Yes! Our instructors are always available to help. We offer dedicated Q&A sessions, one-on-one support, and a community forum where students can ask questions and share their work.',
    acceptedAnswerJsx: (
      <>
        Yes! Our instructors are always available to help. We offer dedicated
        Q&A sessions, one-on-one support, and a community forum where students
        can ask questions and share their work.
      </>
    ),
  },
  {
    questionName: 'How long is the course?',
    acceptedAnswerText:
      "The course is structured into several modules, with each module focusing on specific CSS concepts and culminating in a project. The total duration can vary depending on whether it's a self-paced course, a live online class, or a summer camp format.",
    acceptedAnswerJsx: (
      <>
        The course is structured into several modules, with each module focusing
        on specific CSS concepts and culminating in a project. The total
        duration can vary depending on whether it&lsquo;s a self-paced course, a
        live online class, or a summer camp format.
      </>
    ),
  },
  {
    questionName: 'Can my child continue learning after this CSS course?',
    acceptedAnswerText:
      'Absolutely! Learning CSS is a fantastic stepping stone. After this course, your child can explore more advanced CSS techniques, delve deeper into JavaScript for interactive web elements, or even move into full-stack web development or game design. This is part of a broader journey of learning HTML CSS and JavaScript.',
    acceptedAnswerJsx: (
      <>
        Absolutely! Learning CSS is a fantastic stepping stone. After this
        course, your child can explore more advanced CSS techniques, delve
        deeper into JavaScript for interactive web elements, or even move into
        full-stack web development or game design.
      </>
    ),
  },
  {
    questionName: 'Why is CSS So Important?',
    acceptedAnswerText:
      "CSS is a powerful tool because it separates a website's content (HTML) from its design (CSS). This is a huge advantage. It means you can change the look of an entire website by editing just one file. This efficiency saves a lot of time and makes managing a website much easier. In short, CSS makes websites beautiful and user-friendly. It’s the creative skill that turns a simple webpage into an engaging, polished project.",
    acceptedAnswerJsx: (
      <>
        CSS is a powerful tool because it separates a website&lsquo;s content
        (HTML) from its design (CSS). This is a huge advantage. It means you can
        change the look of an entire website by editing just one file. This
        efficiency saves a lot of time and makes managing a website much easier.
        In short, CSS makes websites beautiful and user-friendly. It’s the
        creative skill that turns a simple webpage into an engaging, polished
        project.
      </>
    ),
  },
  {
    questionName: 'What makes CSS for kids different from regular CSS courses?',
    acceptedAnswerText:
      'CSS for kids is designed to be simple and engaging. It uses: explanations of CSS properties and selectors, Visual examples that show changes instantly, Hands-on projects kids can build step by step, Lessons tailored to a child’s age and attention span, A focus on creativity, not just coding rules',
    acceptedAnswerJsx: (
      <>
        CSS for kids is designed to be simple and engaging. It uses:
        <ul>
          <li>Easy explanations of CSS properties and selectors</li>
          <li>Visual examples that show changes instantly</li>
          <li>Hands-on projects kids can build step by step</li>
          <li>Lessons tailored to a child’s age and attention span</li>
          <li>A focus on creativity, not just coding rules</li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'How do kids actually learn CSS in your program?',
    acceptedAnswerText:
      'Kids learn CSS through: Live and interactive coding demonstrations, Immediate visual feedback when they try code, Guided exercises that explain each step, Fun projects like personal web pages and digital posters',
    acceptedAnswerJsx: (
      <>
        Kids learn CSS through:
        <ul>
          <li>Live and interactive coding demonstrations</li>
          <li>Immediate visual feedback when they try code</li>
          <li>Guided exercises that explain each step</li>
          <li>Fun projects like personal web pages and digital posters</li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'Are the lessons age-appropriate?',
    acceptedAnswerText:
      'Yes. Younger students start with basics like colors and fonts, while older ones explore advanced topics such as responsive layouts, animations, and page structure.',
    acceptedAnswerJsx: (
      <>
        Yes. Younger students start with basics like colors and fonts, while
        older ones explore advanced topics such as responsive layouts,
        animations, and page structure.
      </>
    ),
  },
  {
    questionName: 'How does CSS help boost creativity in kids?',
    acceptedAnswerText:
      'CSS encourages kids to experiment with: Colors, fonts, and themes, Layouts and spacing, Animations and effects, This freedom allows them to boost creativity and express their own design ideas.',
    acceptedAnswerJsx: (
      <>
        CSS encourages kids to experiment with:
        <ul>
          <li>Colors, fonts, and themes</li>
          <li>Layouts and spacing</li>
          <li>Animations and effects</li>
        </ul>
        This freedom allows them to boost creativity and express their own
        design ideas.
      </>
    ),
  },
  {
    questionName: 'What are some benefits of learning CSS early?',
    acceptedAnswerText:
      'Learning CSS early helps children: Develop logical and computational thinking, Improve design and problem-solving skills, Build confidence by creating real projects, Gain a strong foundation for web development and coding',
    acceptedAnswerJsx: (
      <>
        Learning CSS early helps children:
        <ul>
          <li>Develop logical and computational thinking</li>
          <li>Improve design and problem-solving skills</li>
          <li>Build confidence by creating real projects</li>
          <li>Gain a strong foundation for web development and coding</li>
        </ul>
      </>
    ),
  },
]

const HTML: FaqType[] = [
  {
    questionName: 'What is HTML?',
    acceptedAnswerText:
      "HTML (HyperText Markup Language) is the standard language for creating web pages. It's like the skeleton of a website, providing the structure and content. Think of it as the blueprint for your digital creations!",
    acceptedAnswerJsx: (
      <>
        HTML (HyperText Markup Language) is the standard language for creating
        web pages. It&lsquo;s like the skeleton of a website, providing the
        structure and content. Think of it as the blueprint for your digital
        creations!
      </>
    ),
  },
  {
    questionName: 'Why should my child learn HTML?',
    acceptedAnswerText:
      "Learning HTML helps children understand how websites are built, fosters logical thinking, and encourages creativity. It's a fundamental skill for anyone interested in web design, game development, or even just making their own cool online projects.",
    acceptedAnswerJsx: (
      <>
        Learning HTML helps children understand how websites are built, fosters
        logical thinking, and encourages creativity. It&lsquo;s a fundamental
        skill for anyone interested in web design, game development, or even
        just making their own cool online projects.
      </>
    ),
  },
  {
    questionName: 'Is HTML difficult for kids to learn?',
    acceptedAnswerText:
      'Not at all! HTML is very beginner-friendly. It uses simple tags that are easy to understand, and you can see your changes instantly in a web browser. Our courses make it fun and engaging for all ages.',
    acceptedAnswerJsx: (
      <>
        Not at all! HTML is very beginner-friendly. It uses simple tags that are
        easy to understand, and you can see your changes instantly in a web
        browser. Our courses make it fun and engaging for all ages.
      </>
    ),
  },
  {
    questionName: 'What can my child create with HTML?',
    acceptedAnswerText:
      "With HTML, your child can create personal web pages, online stories, digital photo albums, simple games, and much more! It's the first step to becoming a web developer.",
    acceptedAnswerJsx: (
      <>
        With HTML, your child can create personal web pages, online stories,
        digital photo albums, simple games, and much more! It&lsquo;s the first
        step to becoming a web developer.
      </>
    ),
  },
  {
    questionName: 'What is HTML for Kids?',
    acceptedAnswerText:
      'HTML (HyperText Markup Language) is the standard language used to create web pages. Our course introduces kids to HTML in a fun and interactive manner.',
    acceptedAnswerJsx: (
      <>
        HTML (HyperText Markup Language) is the standard language used to create
        web pages. Our course introduces kids to HTML in a fun and interactive
        manner.
      </>
    ),
  },
  {
    questionName: 'How can kids learn HTML coding?',
    acceptedAnswerText:
      'Through our structured curriculum, live classes, and hands-on projects, kids learn HTML by building real web pages from scratch.',
    acceptedAnswerJsx: (
      <>
        Through our structured curriculum, live classes, and hands-on projects,
        kids learn HTML by building real web pages from scratch.
      </>
    ),
  },
  {
    questionName: 'Are there online HTML coding classes for kids?',
    acceptedAnswerText:
      'Yes, Codingal offers online HTML classes with live instructors, ensuring personalized guidance and interactive learning.',
    acceptedAnswerJsx: (
      <>
        Yes, Codingal offers online HTML classes with live instructors, ensuring
        personalized guidance and interactive learning.
      </>
    ),
  },
  {
    questionName: 'Is HTML programming hard to learn?',
    acceptedAnswerText:
      'Not at all! HTML is highly beginner-friendly and is a perfect first step for kids starting their coding journey.',
    acceptedAnswerJsx: (
      <>
        Not at all! HTML is highly beginner-friendly and is a perfect first step
        for kids starting their coding journey.
      </>
    ),
  },
  {
    questionName: 'What can you do with HTML programming?',
    acceptedAnswerText:
      'With HTML, kids can create and structure their own web pages, laying the foundation for learning web design, JavaScript, and full web development.',
    acceptedAnswerJsx: (
      <>
        With HTML, kids can create and structure their own web pages, laying the
        foundation for learning web design, JavaScript, and full web
        development.
      </>
    ),
  },
  {
    questionName: 'Does Codingal offer kids and teens HTML certificates?',
    acceptedAnswerText:
      'Yes, students who complete the HTML curriculum receive a Codingal HTML Developer Certificate to recognize their achievement and skills.',
    acceptedAnswerJsx: (
      <>
        Yes, students who complete the HTML curriculum receive a Codingal HTML
        Developer Certificate to recognize their achievement and skills.
      </>
    ),
  },
]

const AICODE101: FaqType[] = [
  {
    questionName: 'What is AI?',
    acceptedAnswerText:
      'AI stands for Artificial Intelligence. It’s when computers or machines can think and learn like humans. They can do things like recognize faces, understand speech, play games, or even drive cars. In short, AI helps create smart machines that can solve problems and make decisions.',
    acceptedAnswerJsx: (
      <>
        AI stands for Artificial Intelligence. It’s when computers or machines
        can think and learn like humans. They can do things like recognize
        faces, understand speech, play games, or even drive cars. In short, AI
        helps create smart machines that can solve problems and make decisions.
      </>
    ),
  },
  {
    questionName: 'Why is AI important for kids to learn?',
    acceptedAnswerText:
      'AI is all around us, from games to apps on smartphones. Learning about AI helps kids to: Understand how technology works in daily life, Prepare for future jobs involving AI, Develop critical thinking about AI’s impact on society, Spark curiosity and creativity, It also introduces them to AI for kids learning in a fun, interactive way.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/ai-code-101/why-ai-is-important.jpg`}
            alt="HTML Learning for Kids"
            width={512}
            height={341}
          />
        </div>
        <p>AI is all around us, from games to apps on smartphones. </p>
        <p>Learning about AI helps kids to:</p>
        <ol>
          <li>Understand how technology works in daily life</li>
          <li>Prepare for future jobs involving AI</li>
          <li>Develop critical thinking about AI’s impact on society</li>
          <li>Spark curiosity and creativity</li>
          <li>
            It also introduces them to AI for kids learning in a fun,
            interactive way.
          </li>
        </ol>
      </>
    ),
  },
  {
    questionName: 'Is AI difficult for kids to understand?',
    acceptedAnswerText:
      'Not at all! We teach AI using visual block-based coding, simple analogies, games, and hands-on projects. Kids can learn AI basics without needing advanced math or coding skills.',
    acceptedAnswerJsx: (
      <>
        Not at all! We teach AI using visual block-based coding, simple
        analogies, games, and hands-on projects. Kids can learn AI basics
        without needing advanced math or coding skills.
      </>
    ),
  },
  {
    questionName: 'What age is suitable for learning AI?',
    acceptedAnswerText:
      'Our AI programs are ideal for children aged 8 and up. Younger kids explore pattern recognition and basic decision-making, while older children and teens learn advanced topics like machine learning for kids and ethical considerations.',
    acceptedAnswerJsx: (
      <>
        Our AI programs are ideal for children aged 8 and up. Younger kids
        explore pattern recognition and basic decision-making, while older
        children and teens learn advanced topics like machine learning for kids
        and ethical considerations.
      </>
    ),
  },
  {
    questionName: 'What will kids learn in an AI course?',
    acceptedAnswerText:
      "In an AI course, kids will learn: AI Basics: Kids will start by learning fundamental concepts like image recognition, decision trees, and chatbot development. Through exciting projects, they will see how AI works in real life. Machine Learning: They'll explore how machines can learn from data, with practical applications such as creating games, building AI-powered apps, and training smart assistants. Advanced Topics: As kids progress, they will dive into more advanced concepts like emotion detection, voice recognition, and natural language processing (NLP). These skills will prepare them for future AI applications. Interactive Projects: From AI-powered games to AI-driven chatbots and virtual assistants, students get hands-on experience that brings their creations to life. In Codingal’s AI courses for kids, students are introduced to the exciting world of Artificial Intelligence through hands-on, interactive projects. Our AI curriculum for kids is tailored to different age groups, starting with the AI Genius track for grades 3-7, which focuses on beginner-friendly tools like Scratch and Teachable Machine. In this track, kids build fun projects like AI-powered games and chatbots. For older students in grades 8-12, the AI Expert track covers more advanced concepts, including large language models (LLMs), speech recognition, and AI-driven art, giving them the tools to build sophisticated AI applications and prepare for future studies and careers in AI.",
    acceptedAnswerJsx: (
      <>
        <p>In an AI course, kids will learn:</p>
        <ul>
          <li>
            AI Basics: Kids will start by learning fundamental concepts like
            image recognition, decision trees, and chatbot development. Through
            exciting projects, they will see how AI works in real life.
          </li>
          <li>
            Machine Learning: They&lsquo;ll explore how machines can learn from
            data, with practical applications such as creating games, building
            AI-powered apps, and training smart assistants.
          </li>
          <li>
            Advanced Topics: As kids progress, they will dive into more advanced
            concepts like emotion detection, voice recognition, and natural
            language processing (NLP). These skills will prepare them for future
            AI applications.
          </li>
          <li>
            Interactive Projects: From AI-powered games to AI-driven chatbots
            and virtual assistants, students get hands-on experience that brings
            their creations to life.
          </li>
        </ul>
        <p>
          In Codingal’s AI courses for kids, students are introduced to the
          exciting world of Artificial Intelligence through hands-on,
          interactive projects. Our AI curriculum for kids is tailored to
          different age groups, starting with the AI Genius track for grades
          3-7, which focuses on beginner-friendly tools like Scratch and
          Teachable Machine. In this track, kids build fun projects like
          AI-powered games and chatbots. For older students in grades 8-12, the
          AI Expert track covers more advanced concepts, including large
          language models (LLMs), speech recognition, and AI-driven art, giving
          them the tools to build sophisticated AI applications and prepare for
          future studies and careers in AI.
        </p>
      </>
    ),
  },
  {
    questionName: 'What will my child be able to do after learning AI?',
    acceptedAnswerText:
      'After completing our AI course for kids, your child can: Understand how AI works in real life, Create simple AI projects such as voice-controlled programs or object recognition, Think critically about AI’s uses and effects, Explore advanced topics in computer science and technology, Gain confidence with tools like AI Code 101, Teachable Machine, Pictoblox, mBlock and more',
    acceptedAnswerJsx: (
      <>
        <p>After completing our AI course for kids, your child can:</p>
        <ol>
          <li>Understand how AI works in real life</li>
          <li>
            Create simple AI projects such as voice-controlled programs or
            object recognition
          </li>
          <li>Think critically about AI’s uses and effects</li>
          <li>Explore advanced topics in computer science and technology</li>
          <li>
            Gain confidence with tools like AI Code 101, Teachable Machine,
            Pictoblox, mBlock and more
          </li>
        </ol>
      </>
    ),
  },
  {
    questionName: 'Does my child need to know coding before learning AI?',
    acceptedAnswerText:
      'No prior coding experience is required for introductory AI courses. We use beginner-friendly AI tools for kids like visual block-based programming. For advanced topics, a basic understanding of Python can be helpful, and we guide students step by step.',
    acceptedAnswerJsx: (
      <>
        No prior coding experience is required for introductory AI courses. We
        use beginner-friendly AI tools for kids like visual block-based
        programming. For advanced topics, a basic understanding of Python can be
        helpful, and we guide students step by step.
      </>
    ),
  },
  {
    questionName: 'What kind of AI projects will my child work on?',
    acceptedAnswerText:
      'Kids work on exciting AI projects such as: Training a computer to recognize different objects or animals, Building a simple chatbot, Creating games where AI interacts with them, Designing predictive models using real data. These projects make AI for kids hands-on, practical, and fun.',
    acceptedAnswerJsx: (
      <>
        <p>Kids work on exciting AI projects such as:</p>
        <ol>
          <li>Training a computer to recognize different objects or animals</li>
          <li>Building a simple chatbot</li>
          <li>Creating games where AI interacts with them</li>
          <li>Designing predictive models using real data</li>
        </ol>
        <p>These projects make AI for kids hands-on, practical, and fun.</p>
      </>
    ),
  },
  {
    questionName: 'What tools or software are needed for AI classes?',
    acceptedAnswerText:
      'Students need a computer with internet access. We use safe, online AI platforms for kids that require no complex installation. Some courses use Scratch with AI extensions or Pictoblox, which are easy for kids to navigate.',
    acceptedAnswerJsx: (
      <>
        Students need a computer with internet access. We use safe, online AI
        platforms for kids that require no complex installation. Some courses
        use Scratch with AI extensions or Pictoblox, which are easy for kids to
        navigate.
      </>
    ),
  },
  {
    questionName: 'How are AI classes taught?',
    acceptedAnswerText:
      'Our AI classes for kids are interactive and project-based. Students learn through: Engaging stories and real-world examples, Hands-on activities and experiments, Step-by-step guidance from experienced CS instructors, Kids learn Artificial Intelligence for kids by creating, testing, and playing with projects.',
    acceptedAnswerJsx: (
      <>
        <p>
          Our AI classes for kids are interactive and project-based. Students
          learn through:
        </p>
        <ul>
          <li>Engaging stories and real-world examples</li>
          <li>Hands-on activities and experiments</li>
          <li>Step-by-step guidance from experienced CS instructors</li>
          <li>
            Kids learn Artificial Intelligence for kids by creating, testing,
            and playing with projects.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'Is there support if my child gets stuck?',
    acceptedAnswerText:
      'At Codingal, we provide 24/7 doubt-solving and assignment support to ensure that students receive timely help even after class hours. Our classes are designed to promote active learning i.e. students don’t just listen passively, they participate in live sessions by working on hands-on projects. This approach helps children internalize key coding & AI concepts, apply their knowledge creatively, and develop real-world problem-solving skills, making their AI learning journey both engaging and effective.',
    acceptedAnswerJsx: (
      <>
        At Codingal, we provide 24/7 doubt-solving and assignment support to
        ensure that students receive timely help even after class hours. Our
        classes are designed to promote active learning i.e. students don’t just
        listen passively, they participate in live sessions by working on
        hands-on projects. This approach helps children internalize key coding &
        AI concepts, apply their knowledge creatively, and develop real-world
        problem-solving skills, making their AI learning journey both engaging
        and effective.
      </>
    ),
  },
]

const CODE_ORG: FaqType[] = [
  {
    questionName: 'Why is Code.org important for kids to learn?',
    acceptedAnswerText:
      'Introduces fundamental coding concepts like sequencing, logic, and algorithms in a fun, interactive way. Fosters problem-solving skills by encouraging kids to think logically and break down complex tasks. Prepares kids for future careers in technology by providing them with a strong foundation in computer science.Boosts creativity by allowing kids to create games, animations, and apps, encouraging self-expression through coding. Promotes critical thinking and computational thinking, essential for tackling real-world challenges.',
    acceptedAnswerJsx: (
      <>
        <ul>
          <li>
            Introduces fundamental coding concepts like sequencing, logic, and
            algorithms in a fun, interactive way.
          </li>
          <li>
            Fosters problem-solving skills by encouraging kids to think
            logically and break down complex tasks.
          </li>
          <li>
            Prepares kids for future careers in technology by providing them
            with a strong foundation in computer science.
          </li>
          <li>
            Boosts creativity by allowing kids to create games, animations, and
            apps, encouraging self-expression through coding.
          </li>
          <li>
            Promotes critical thinking and computational thinking, essential for
            tackling real-world challenges.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'Is Code.org difficult for kids to understand?',
    acceptedAnswerText:
      "No, it's designed for beginners, with block-based coding that makes it easy for children to grasp coding concepts. The platform breaks down coding into small, manageable steps, making learning fun and engaging. Interactive tutorials and hands-on projects help kids apply what they learn and reinforce their understanding. Code.org's curriculum is structured in a way that gradually introduces more advanced topics, ensuring that kids progress at their own pace.",
    acceptedAnswerJsx: (
      <>
        No, it&lsquo;s designed for beginners, with block-based coding that
        makes it easy for children to grasp coding concepts. The platform breaks
        down coding into small, manageable steps, making learning fun and
        engaging. Interactive tutorials and hands-on projects help kids apply
        what they learn and reinforce their understanding. Code.org&lsquo;s
        curriculum is structured in a way that gradually introduces more
        advanced topics, ensuring that kids progress at their own pace.
      </>
    ),
  },
  {
    questionName: 'What age is suitable for learning Code.org?',
    acceptedAnswerText:
      "Code.org is ideal for elementary and middle school students, typically aged 5 to 12 years old. Younger kids (ages 5-7) can start with block-based coding activities like games and animations. For older kids (ages 8-12), Code.org offers more advanced challenges like creating interactive apps and working with simple programming logic. Code.org's curriculum is structured to be age-appropriate for various skill levels, so even beginners can start learning at any age.",
    acceptedAnswerJsx: (
      <>
        <ul>
          <li>
            Code.org is ideal for elementary and middle school students,
            typically aged 5 to 12 years old.
          </li>
          <li>
            Younger kids (ages 5-7) can start with block-based coding activities
            like games and animations.
          </li>
          <li>
            For older kids (ages 8-12), Code.org offers more advanced challenges
            like creating interactive apps and working with simple programming
            logic.
          </li>
          <li>
            Code.org&lsquo;s curriculum is structured to be age-appropriate for
            various skill levels, so even beginners can start learning at any
            age.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'What will kids learn in a Code.org course?',
    acceptedAnswerText:
      'Basic coding concepts like algorithms, events, sequencing, and loops using block-based coding. Game development, where kids will build their own interactive games such as Flappy Bird and Minecraft. Problem-solving skills through coding challenges that involve logical thinking and creative solutions. Critical thinking as they design and develop interactive stories, games, and animations. Hands-on projects that include designing apps, creating animations, and building digital experiences, giving practical coding skills.',
    acceptedAnswerJsx: (
      <>
        <ul>
          <li>
            Basic coding concepts like algorithms, events, sequencing, and loops
            using block-based coding.
          </li>
          <li>
            Game development, where kids will build their own interactive games
            such as Flappy Bird and Minecraft.
          </li>
          <li>
            Problem-solving skills through coding challenges that involve
            logical thinking and creative solutions.
          </li>
          <li>
            Critical thinking as they design and develop interactive stories,
            games, and animations.
          </li>
          <li>
            Hands-on projects that include designing apps, creating animations,
            and building digital experiences, giving practical coding skills.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'What will my child be able to do after learning Code.org?',
    acceptedAnswerText:
      'Create interactive projects, including simple games, animations, and apps using block-based coding. Understand the basics of coding, including sequencing, logic, and creating loops and events. Apply logic to solve problems and develop their own interactive stories and games. Build confidence in coding, giving them the skills to tackle more complex coding languages in the future like Python and JavaScript. Develop a passion for computer science and the creativity needed to pursue further learning in coding, app development, and AI technologies.',
    acceptedAnswerJsx: (
      <>
        <ul>
          <li>
            Create interactive projects, including simple games, animations, and
            apps using block-based coding.
          </li>
          <li>
            Understand the basics of coding, including sequencing, logic, and
            creating loops and events.
          </li>
          <li>
            Apply logic to solve problems and develop their own interactive
            stories and games.
          </li>
          <li>
            Build confidence in coding, giving them the skills to tackle more
            complex coding languages in the future like Python and JavaScript.
          </li>
          <li>
            Develop a passion for computer science and the creativity needed to
            pursue further learning in coding, app development, and AI
            technologies.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'What kind of Code.org projects will my child work on?',
    acceptedAnswerText:
      'Minecraft-based coding projects where kids learn coding by creating their own Minecraft games and challenges. Flappy Bird: A fun game development project where kids create their own version of the popular Flappy Bird game. Dance Party: An exciting interactive project where kids use block-based coding to make a dance party with characters. Chatbots: Kids will build their own chatbots that can respond to user queries, simulating conversations. Animation projects where kids can create characters and animate them to tell stories or respond to events.',
    acceptedAnswerJsx: (
      <>
        <ul>
          <li>
            Minecraft-based coding projects where kids learn coding by creating
            their own Minecraft games and challenges.
          </li>
          <li>
            Flappy Bird: A fun game development project where kids create their
            own version of the popular Flappy Bird game.
          </li>
          <li>
            Dance Party: An exciting interactive project where kids use
            block-based coding to make a dance party with characters.
          </li>
          <li>
            Chatbots: Kids will build their own chatbots that can respond to
            user queries, simulating conversations.
          </li>
          <li>
            Animation projects where kids can create characters and animate them
            to tell stories or respond to events.
          </li>
        </ul>
      </>
    ),
  },
]

const PICTOBLOX: FaqType[] = [
  {
    questionName: 'What is PictoBlox?',
    acceptedAnswerText: `PictoBlox is a graphical and Python programming software that serves as the perfect first step into the world of coding. Its user-friendly interface and drag-and-drop functionality remove the need for memorizing syntax or rules, which often intimidates young learners in traditional programming languages. This allows children to focus on the problem at hand while developing essential skills like logical reasoning and problem-solving which are critical abilities for today’s technology-driven world.

PictoBlox is a powerful Scratch alternative that enhances creative coding with advanced features. Beyond Scratch‘s storytelling capabilities, PictoBlox integrates AI, machine learning, and robotics modules. It facilitates a smooth transition to text-based programming by displaying Python code for visual blocks and offers real-world applications like face detection and pose tracking.`,
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/pictoblox/pictoblox-1.jpg`}
            alt="Pictoblox logo and Toby character with text Pictoblox - learn to code the fun way!"
            width={512}
            height={341}
          />
        </div>
        <p>
          PictoBlox is a graphical and Python programming software that serves
          as the perfect first step into the world of coding. Its user-friendly
          interface and drag-and-drop functionality remove the need for
          memorizing syntax or rules, which often intimidates young learners in
          traditional programming languages. This allows children to focus on
          the problem at hand while developing essential skills like logical
          reasoning and problem-solving which are critical abilities for today’s
          technology-driven world.
        </p>
        <p>
          PictoBlox is a powerful Scratch alternative that enhances creative
          coding with advanced features. Beyond Scratch&lsquo;s storytelling
          capabilities, PictoBlox integrates AI, machine learning, and robotics
          modules. It facilitates a smooth transition to text-based programming
          by displaying Python code for visual blocks and offers real-world
          applications like face detection and pose tracking.
        </p>
      </>
    ),
  },
  {
    questionName: 'Is PictoBlox easy for kids to learn?',
    acceptedAnswerText: `Yes, PictoBlox is designed with a simple, drag-and-drop block-based interface similar to Scratch. This visual approach makes complex coding concepts intuitive and engaging, even for beginners with no prior experience. Young learners can immediately start building interactive stories, games, and AI-powered applications, which keeps them motivated while boosting creativity and problem-solving skills.`,
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/pictoblox/pictoblox-2.jpg`}
            alt="Young student learning coding for kids through PictoBlox blocks"
            width={512}
            height={341}
          />
        </div>
        <p>
          Yes, PictoBlox is designed with a simple, drag-and-drop block-based
          interface similar to Scratch. This visual approach makes complex
          coding concepts intuitive and engaging, even for beginners with no
          prior experience. Young learners can immediately start building
          interactive stories, games, and AI-powered applications, which keeps
          them motivated while boosting creativity and problem-solving skills.
        </p>
      </>
    ),
  },
  {
    questionName: 'What age group is PictoBlox suitable for?',
    acceptedAnswerText: `PictoBlox is generally recommended for children aged 8 to 16. Its block-based interface makes it perfect for beginners, while its advanced AI features cater to older kids who want to explore more complex coding challenges and projects.`,
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/pictoblox/pictoblox-3.jpg`}
            alt="PictoBlox for Ages 8 to 16"
            width={512}
            height={341}
          />
        </div>
        <p>
          PictoBlox is generally recommended for children aged 8 to 16. Its
          block-based interface makes it perfect for beginners, while its
          advanced AI features cater to older kids who want to explore more
          complex coding challenges and projects.
        </p>
      </>
    ),
  },
  {
    questionName: 'Why should my child learn with PictoBlox?',
    acceptedAnswerText: `Learning with PictoBlox helps children develop computational thinking, problem-solving abilities, and creativity. By engaging with AI features, kids gain exposure to emerging technologies in a fun, interactive way. The project-based approach ensures that students are not only learning concepts but also applying them in practical, real-world projects, preparing them for future STEM opportunities.`,
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/pictoblox/pictoblox-4.jpg`}
            alt="Child exploring STEM and coding for kids using PictoBlox projects"
            width={512}
            height={341}
          />
        </div>
        <p>
          Learning with PictoBlox helps children develop computational thinking,
          problem-solving abilities, and creativity. By engaging with AI
          features, kids gain exposure to emerging technologies in a fun,
          interactive way. The project-based approach ensures that students are
          not only learning concepts but also applying them in practical,
          real-world projects, preparing them for future STEM opportunities.
        </p>
      </>
    ),
  },
  {
    questionName: 'What can kids create using PictoBlox?',
    acceptedAnswerText: `PictoBlox is a fun, beginner-friendly coding platform that lets kids dive into programming and AI. Here are some real projects they can create:

Interactive Stories: Kids can create their own digital stories, where characters speak and interact based on user input (e.g., a detective solving mysteries or a fantasy adventure).
AI Chatbot: Build a simple conversational AI that responds to questions or guides users through a process, like a virtual assistant for homework or a quiz master.
Smart Translator: Kids can design a program that translates basic phrases from one language to another, powered by AI, perfect for learning new languages.
Image Recognition App: Use AI to create a project that can recognize objects or animals in pictures, like building a "smart pet detector" that identifies your dog in photos.
Game with AI Opponent: Develop a game where the player competes against an AI-driven character that adapts to the player’s moves, like a tic-tac-toe game with an AI challenger.
AI-Powered Music Player: Create a music player that suggests songs based on mood or weather, using AI to recommend playlists based on user preferences.`,
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/pictoblox/pictoblox-5.jpg`}
            alt="fun Pictoblox coding project ideas for kids"
            width={512}
            height={341}
          />
        </div>
        <p>
          PictoBlox is a fun, beginner-friendly coding platform that lets kids
          dive into programming and AI. Here are some real projects they can
          create:
        </p>
        <ul>
          <li>
            <b>Interactive Stories</b>: Kids can create their own digital
            stories, where characters speak and interact based on user input
            (e.g., a detective solving mysteries or a fantasy adventure).
          </li>
          <li>
            <b>AI Chatbot</b>: Build a simple conversational AI that responds to
            questions or guides users through a process, like a virtual
            assistant for homework or a quiz master.
          </li>
          <li>
            <b>Smart Translator</b>: Kids can design a program that translates
            basic phrases from one language to another, powered by AI, perfect
            for learning new languages.
          </li>
          <li>
            <b>Image Recognition App</b>: Use AI to create a project that can
            recognize objects or animals in pictures, like building a
            &ldquo;smart pet detector&rdquo; that identifies your dog in photos.
          </li>
          <li>
            <b>Game with AI Opponent</b>: Develop a game where the player
            competes against an AI-driven character that adapts to the player’s
            moves, like a tic-tac-toe game with an AI challenger.
          </li>
          <li>
            <b>AI-Powered Music Player</b>: Create a music player that suggests
            songs based on mood or weather, using AI to recommend playlists
            based on user preferences.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'What will my child do in a PictoBlox course?',
    acceptedAnswerText: `Students will learn fundamental coding concepts through interactive lessons and practical projects. They explore block-based programming and AI fundamentals while creating real projects like face detectors, self-driving car simulations, Red Light, Green Light games, and ChatGPT-powered tools. Each class builds logical thinking, technical skills, and confidence in coding.`,
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/pictoblox/pictoblox-6.jpg`}
            alt="Student building AI projects in Codingal PictoBlox course for kids"
            width={512}
            height={341}
          />
        </div>
        <p>
          Students will learn fundamental coding concepts through interactive
          lessons and practical projects. They explore block-based programming
          and AI fundamentals while creating real projects like face detectors,
          self-driving car simulations, Red Light, Green Light games, and
          ChatGPT-powered tools. Each class builds logical thinking, technical
          skills, and confidence in coding.
        </p>
      </>
    ),
  },
  {
    questionName: 'What will my child do in a PictoBlox course?',
    acceptedAnswerText: `Students will learn fundamental coding concepts through interactive lessons and practical projects. They explore block-based programming and AI fundamentals while creating real projects like face detectors, self-driving car simulations, Red Light, Green Light games, and ChatGPT-powered tools. Each class builds logical thinking, technical skills, and confidence in coding.`,
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/pictoblox/pictoblox-6.jpg`}
            alt="Student building AI projects in Codingal PictoBlox course for kids"
            width={512}
            height={341}
          />
        </div>
        <p>
          Students will learn fundamental coding concepts through interactive
          lessons and practical projects. They explore block-based programming
          and AI fundamentals while creating real projects like face detectors,
          self-driving car simulations, Red Light, Green Light games, and
          ChatGPT-powered tools. Each class builds logical thinking, technical
          skills, and confidence in coding.
        </p>
      </>
    ),
  },
  {
    questionName: 'What will my child do in a PictoBlox course?',
    acceptedAnswerText: `Students will learn fundamental coding concepts through interactive lessons and practical projects. They explore block-based programming and AI fundamentals while creating real projects like face detectors, self-driving car simulations, Red Light, Green Light games, and ChatGPT-powered tools. Each class builds logical thinking, technical skills, and confidence in coding.`,
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/pictoblox/pictoblox-6.jpg`}
            alt="Student building AI projects in Codingal PictoBlox course for kids"
            width={512}
            height={341}
          />
        </div>
        <p>
          Students will learn fundamental coding concepts through interactive
          lessons and practical projects. They explore block-based programming
          and AI fundamentals while creating real projects like face detectors,
          self-driving car simulations, Red Light, Green Light games, and
          ChatGPT-powered tools. Each class builds logical thinking, technical
          skills, and confidence in coding.
        </p>
      </>
    ),
  },
  {
    questionName: 'What tools are needed for a PictoBlox class?',
    acceptedAnswerText: `A desktop or laptop with internet access. PictoBlox software, downloadable for Windows, macOS, Linux, or use the web-based version. Optional hardware kits (sensors) for specific advanced AI projects, but many projects are software-only.`,
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/pictoblox/pictoblox-7.jpg`}
            alt="Setting up PictoBlox software on a laptop for hands-on AI & coding projects"
            width={512}
            height={341}
          />
        </div>
        <ul>
          <li>A desktop or laptop with internet access.</li>
          <li>
            PictoBlox software, downloadable for Windows, macOS, Linux, or use
            the web-based version.
          </li>
          <li>
            Optional hardware kits (sensors) for specific advanced AI projects,
            but many projects are software-only.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'How is PictoBlox different from Scratch?',
    acceptedAnswerText: `While based on Scratch, PictoBlox expands its functionality to include AI and Machine Learning capabilities. It allows students to see the Python code behind their block programs, helping them transition smoothly to text-based coding while exploring AI projects. This makes PictoBlox a more comprehensive platform for learning coding for kids in a fun and future-ready way.`,
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/pictoblox/pictoblox-8.jpg`}
            alt="Comparison of Scratch and PictoBlox block-based coding platforms for kids"
            width={512}
            height={341}
          />
        </div>
        <p>
          While based on Scratch, PictoBlox expands its functionality to include
          AI and Machine Learning capabilities. It allows students to see the
          Python code behind their block programs, helping them transition
          smoothly to text-based coding while exploring AI projects. This makes
          PictoBlox a more comprehensive platform for learning coding for kids
          in a fun and future-ready way.
        </p>
      </>
    ),
  },
]

const TEACHABLE_MACHINE: FaqType[] = [
  {
    questionName: 'What is a Teachable Machine for Kids?',
    acceptedAnswerText:
      "Teachable Machine is a beginner-friendly tool that helps kids explore Artificial Intelligence (AI) in an engaging, hands-on way. Using this platform, kids can train AI models with images, sounds, or gestures, making it easier for them to understand how AI works through simple, interactive projects. It's a fun introduction to machine learning for kids with no coding experience required.",
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/teachable-machine/tm-1.jpg`}
            alt="Child interacting with Teachable Machine, learning AI concepts by training models with images, sounds, and gestures."
            width={512}
            height={341}
          />
        </div>
        <p>
          Teachable Machine is a beginner-friendly tool that helps kids explore
          Artificial Intelligence (AI) in an engaging, hands-on way. Using this
          platform, kids can train AI models with images, sounds, or gestures,
          making it easier for them to understand how AI works through simple,
          interactive projects. It&lsquo;s a fun introduction to machine
          learning for kids with no coding experience required.
        </p>
      </>
    ),
  },
  {
    questionName: 'How can kids learn AI using Teachable Machine?',
    acceptedAnswerText:
      'Kids can learn AI through guided practice using Teachable Machine. By training AI models and integrating them with other platforms like Scratch, students can work on exciting Teachable Machine projects that demonstrate how AI recognizes patterns and makes decisions. The process is broken down into simple steps, ensuring that learning AI is both fun and understandable for children.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/teachable-machine/tm-2.jpg`}
            alt="Student working on a creative project, training AI models with Teachable Machine and Scratch."
            width={512}
            height={341}
          />
        </div>
        <p>
          Kids can learn AI through guided practice using Teachable Machine. By
          training AI models and integrating them with other platforms like
          Scratch, students can work on exciting Teachable Machine projects that
          demonstrate how AI recognizes patterns and makes decisions. The
          process is broken down into simple steps, ensuring that learning AI is
          both fun and understandable for children.
        </p>
      </>
    ),
  },
  {
    questionName: 'Are there online Teachable Machine classes for kids?',
    acceptedAnswerText:
      'Yes, there are many online classes available for kids to learn Teachable Machine. These courses offer interactive tutorials where students can train AI models and create their own projects. Whether working on gesture-controlled games or voice-activated actions, kids can learn at their own pace while engaging with real-world AI examples.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/teachable-machine/tm-3.jpg`}
            alt="Child participating in an online Teachable Machine class, learning AI through live interactive lessons."
            width={512}
            height={341}
          />
        </div>
        <p>
          Yes, there are many online classes available for kids to learn
          Teachable Machine. These courses offer interactive tutorials where
          students can train AI models and create their own projects. Whether
          working on gesture-controlled games or voice-activated actions, kids
          can learn at their own pace while engaging with real-world AI
          examples.
        </p>
      </>
    ),
  },
  {
    questionName: 'Is AI hard to learn for kids?',
    acceptedAnswerText:
      "Not at all! Teachable Machine makes learning AI easy for kids. The platform's visual interface allows students to train AI models without writing code. It's a perfect introduction to AI learning, with no prior coding experience necessary. By engaging in simple Teachable Machine projects, kids can understand the core concepts of AI and machine learning through fun, interactive activities.",
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/teachable-machine/tm-4.jpg`}
            alt="Young learner confidently training AI models, demonstrating that AI can be fun and accessible."
            width={512}
            height={341}
          />
        </div>
        <p>
          Not at all! Teachable Machine makes learning AI easy for kids. The
          platform&lsquo;s visual interface allows students to train AI models
          without writing code. It&lsquo;s a perfect introduction to AI
          learning, with no prior coding experience necessary. By engaging in
          simple Teachable Machine projects, kids can understand the core
          concepts of AI and machine learning through fun, interactive
          activities.
        </p>
      </>
    ),
  },
  {
    questionName: 'What can kids create with Teachable Machine and Scratch?',
    acceptedAnswerText:
      'Kids can create a variety of exciting projects using Teachable Machine and Scratch. These projects include gesture-controlled games, voice-activated actions, and image recognition models. Through these Teachable Machine projects, kids learn to use AI to solve real-world problems, bringing their creative ideas to life in a fun and educational way.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/teachable-machine/tm-5.jpg`}
            alt="AI made easy for Kids"
            width={512}
            height={341}
          />
        </div>
        <p>
          Kids can create a variety of exciting projects using Teachable Machine
          and Scratch. These projects include gesture-controlled games,
          voice-activated actions, and image recognition models. Through these
          Teachable Machine projects, kids learn to use AI to solve real-world
          problems, bringing their creative ideas to life in a fun and
          educational way.
        </p>
      </>
    ),
  },
  {
    questionName: 'What grades and ages is this course best for?',
    acceptedAnswerText:
      'Teachable Machine is ideal for kids aged 8 to 13, typically in Grades 3-8. The course is designed to be age-appropriate and beginner-friendly, introducing kids to AI concepts and machine learning in a fun and creative way. No previous coding experience is required, making it accessible for younger students.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/teachable-machine/tm-6.jpg`}
            alt="Teachable Machine"
            width={512}
            height={341}
          />
        </div>
        <p>
          Teachable Machine is ideal for kids aged 8 to 13, typically in Grades
          3-8. The course is designed to be age-appropriate and
          beginner-friendly, introducing kids to AI concepts and machine
          learning in a fun and creative way. No previous coding experience is
          required, making it accessible for younger students.
        </p>
      </>
    ),
  },
  {
    questionName:
      'What technology or equipment does my child need for the classes?',
    acceptedAnswerText:
      "To use Teachable Machine, all your child needs is a computer or tablet with an internet connection and a web browser. There's no need for special software downloads, and everything runs in the cloud. This makes it easy for kids to jump into learning AI anytime, anywhere, using the Teachable Machine app or website.",
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/teachable-machine/tm-7.jpg`}
            alt="Teachable Machine"
            width={512}
            height={341}
          />
        </div>
        <p>
          To use Teachable Machine, all your child needs is a computer or tablet
          with an internet connection and a web browser. There&lsquo;s no need
          for special software downloads, and everything runs in the cloud. This
          makes it easy for kids to jump into learning AI anytime, anywhere,
          using the Teachable Machine app or website.
        </p>
      </>
    ),
  },
  {
    questionName: 'How does Teachable Machine help kids learn AI?',
    acceptedAnswerText:
      'Teachable Machine introduces kids to AI by allowing them to train machine learning models with simple, visual tools. Kids can work with images, sounds, and gestures to create interactive AI projects, helping them understand the basic principles of machine learning in a fun and engaging way.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/teachable-machine/tm-8.jpg`}
            alt="kids learning teachable machine "
            width={512}
            height={341}
          />
        </div>
        <p>
          Teachable Machine introduces kids to AI by allowing them to train
          machine learning models with simple, visual tools. Kids can work with
          images, sounds, and gestures to create interactive AI projects,
          helping them understand the basic principles of machine learning in a
          fun and engaging way.
        </p>
      </>
    ),
  },
  {
    questionName: 'Is Teachable Machine suitable for beginners in AI?',
    acceptedAnswerText:
      'Absolutely! Teachable Machine is an ideal tool for beginners in AI. Its drag-and-drop interface allows children to easily create AI models without needing to know complex programming languages. The step-by-step guidance helps students grasp the basics of machine learning in a visual and intuitive manner.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/teachable-machine/tm-9.jpg`}
            alt="Teachable Machine for beginners"
            width={512}
            height={341}
          />
        </div>
        <p>
          Absolutely! Teachable Machine is an ideal tool for beginners in AI.
          Its drag-and-drop interface allows children to easily create AI models
          without needing to know complex programming languages. The
          step-by-step guidance helps students grasp the basics of machine
          learning in a visual and intuitive manner.
        </p>
      </>
    ),
  },
  {
    questionName: 'How does Teachable Machine work with Scratch?',
    acceptedAnswerText:
      "Teachable Machine can be easily integrated with Scratch, enabling kids to use their trained AI models in Scratch projects. This integration allows students to bring their AI creations, such as gesture-controlled games or image recognition models, to life in a fun, interactive way by using Scratch's visual coding environment.",
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/teachable-machine/tm-10.jpg`}
            alt="Teachable Machine Scratch"
            width={512}
            height={341}
          />
        </div>
        <p>
          Teachable Machine can be easily integrated with Scratch, enabling kids
          to use their trained AI models in Scratch projects. This integration
          allows students to bring their AI creations, such as
          gesture-controlled games or image recognition models, to life in a
          fun, interactive way by using Scratch’s visual coding environment.
        </p>
      </>
    ),
  },
  {
    questionName:
      'What are some Teachable Machine examples for kids to explore?',
    acceptedAnswerText:
      'Some great Teachable Machine examples for kids include: Gesture-controlled games where kids can play by moving their hands. Image recognition models that can identify animals or objects. AI models that detect sounds or emotions based on voice input. These examples help children see the potential of AI and machine learning in a fun, creative environment.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/teachable-machine/tm-11.jpg`}
            alt="Examples of Teachable Machine Projects for Kids"
            width={512}
            height={341}
          />
        </div>
        <p>Some great Teachable Machine examples for kids include:</p>
        <ul>
          <li>
            Gesture-controlled games where kids can play by moving their hands.
          </li>
          <li>
            Image recognition models that can identify animals or objects.
          </li>
          <li>
            AI models that detect sounds or emotions based on voice input.
          </li>
        </ul>
        <p>
          These examples help children see the potential of AI and machine
          learning in a fun, creative environment.
        </p>
      </>
    ),
  },
]

const GOOGLE_COLAB: FaqType[] = [
  {
    questionName: 'What is Google Colab?',
    acceptedAnswerText:
      'Google Colab (short for Colaboratory) is a free, cloud-based platform that lets you write and run Python code directly in your web browser. It provides access to powerful computing resources, including GPUs and TPUs, making it ideal for machine learning and data analysis tasks.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/google-colab/gc-1.jpg`}
            alt="Google Colab for kids"
            width={512}
            height={341}
          />
        </div>
        <p>
          Google Colab (short for Colaboratory) is a free, cloud-based platform
          that lets you write and run Python code directly in your web browser.
          It provides access to powerful computing resources, including GPUs and
          TPUs, making it ideal for machine learning and data analysis tasks.
        </p>
      </>
    ),
  },
  {
    questionName: 'Is Google Colab free for students?',
    acceptedAnswerText:
      'Yes, Google Colab is completely free to use. It allows students to write and execute Python code without the need for software installation, making it accessible for learning coding and AI.',
    acceptedAnswerJsx: (
      <>
        Yes, Google Colab is completely free to use. It allows students to write
        and execute Python code without the need for software installation,
        making it accessible for learning coding and AI.
      </>
    ),
  },
  {
    questionName: 'How can kids and teens learn Google Colab?',
    acceptedAnswerText:
      'Kids and teens can learn Google Colab through structured, interactive classes that teach Python and data science concepts. At Codingal, students learn through hands-on projects and guided tutorials using Google Colab to grasp key programming concepts.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/google-colab/gc-2.jpg`}
            alt="Google Colab course for kids"
            width={512}
            height={341}
          />
        </div>
        <p>
          Kids and teens can learn Google Colab through structured, interactive
          classes that teach Python and data science concepts. At Codingal,
          students learn through hands-on projects and guided tutorials using
          Google Colab to grasp key programming concepts.
        </p>
      </>
    ),
  },
  {
    questionName: 'What age is suitable for learning Google Colab?',
    acceptedAnswerText:
      'Google Colab is best suited for students aged 10 and up, especially those interested in learning Python, data science, and machine learning. It’s an ideal tool for beginners and intermediate learners who are comfortable using a computer and have a Google account.',
    acceptedAnswerJsx: (
      <>
        Google Colab is best suited for students aged 10 and up, especially
        those interested in learning Python, data science, and machine learning.
        It’s an ideal tool for beginners and intermediate learners who are
        comfortable using a computer and have a Google account.
      </>
    ),
  },
  {
    questionName: 'How does Google Colab help kids learn AI and coding?',
    acceptedAnswerText:
      'Google Colab simplifies learning AI and coding by providing an interactive environment where kids can run Python code in real-time. It allows them to experiment with datasets, run AI models, and explore data science concepts without needing a powerful computer.',
    acceptedAnswerJsx: (
      <>
        Google Colab simplifies learning AI and coding by providing an
        interactive environment where kids can run Python code in real-time. It
        allows them to experiment with datasets, run AI models, and explore data
        science concepts without needing a powerful computer.
      </>
    ),
  },
  {
    questionName: 'Can we write Python code in Google Colab?',
    acceptedAnswerText:
      'Yes, Google Colab supports Python as its primary language. It allows kids to write, execute, and experiment with Python code, making it an excellent tool for learning programming and exploring machine learning.',
    acceptedAnswerJsx: (
      <>
        Yes, Google Colab supports Python as its primary language. It allows
        kids to write, execute, and experiment with Python code, making it an
        excellent tool for learning programming and exploring machine learning.
      </>
    ),
  },
  {
    questionName: 'How does Google Colab compare to Jupyter Notebook?',
    acceptedAnswerText:
      'Google Colab is built on top of Jupyter Notebooks, offering similar functionality but with the added benefit of cloud-based computing. Colab provides free access to GPUs and TPUs, making it more suitable for heavy machine learning tasks, while Jupyter Notebooks typically runs locally on your device.',
    acceptedAnswerJsx: (
      <>
        Google Colab is built on top of Jupyter Notebooks, offering similar
        functionality but with the added benefit of cloud-based computing. Colab
        provides free access to GPUs and TPUs, making it more suitable for heavy
        machine learning tasks, while Jupyter Notebooks typically runs locally
        on your device.
      </>
    ),
  },
  {
    questionName: 'What can you do with Google Colab?',
    acceptedAnswerText:
      'In Google Colab, kids can: Write Python code Solve math problems Create simple games Analyze data Train AI models for tasks like image recognition and text analysis Visualize data using graphs and charts',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/google-colab/gc-3.jpg`}
            alt="Google Colab applications"
            width={512}
            height={341}
          />
        </div>
        <p>In Google Colab, kids can:</p>
        <ul>
          <li>Write Python code</li>
          <li>Solve math problems</li>
          <li>Create simple games</li>
          <li>Analyze data</li>
          <li>
            Train AI models for tasks like image recognition and text analysis
          </li>
          <li>Visualize data using graphs and charts</li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'Is Google Colab good for beginners?',
    acceptedAnswerText:
      'Yes, Google Colab is beginner-friendly. Its browser-based interface makes it easy to start coding without the need to install any software. Students can learn and experiment with Python and AI concepts through a simple, intuitive platform.',
    acceptedAnswerJsx: (
      <>
        Yes, Google Colab is beginner-friendly. Its browser-based interface
        makes it easy to start coding without the need to install any software.
        Students can learn and experiment with Python and AI concepts through a
        simple, intuitive platform.
      </>
    ),
  },
  {
    questionName: 'Can I install Python in Google Colab?',
    acceptedAnswerText:
      'Python is already pre-installed in Google Colab, so you don’t need to install anything. You can start coding right away and use Google’s powerful cloud computing resources.',
    acceptedAnswerJsx: (
      <>
        Python is already pre-installed in Google Colab, so you don’t need to
        install anything. You can start coding right away and use Google’s
        powerful cloud computing resources.
      </>
    ),
  },
  {
    questionName: 'Can kids move from Google Colab to Python?',
    acceptedAnswerText:
      'Yes, Google Colab is an excellent way for kids to start learning Python. It provides a smooth transition from block-based coding platforms to text-based programming. Kids can understand how code functions in Colab, and then apply that knowledge to more advanced Python programming.',
    acceptedAnswerJsx: (
      <>
        Yes, Google Colab is an excellent way for kids to start learning Python.
        It provides a smooth transition from block-based coding platforms to
        text-based programming. Kids can understand how code functions in Colab,
        and then apply that knowledge to more advanced Python programming.
      </>
    ),
  },
  {
    questionName: 'Can we use C++ in Google Colab?',
    acceptedAnswerText:
      'While Google Colab is primarily designed for Python, it is possible to run C++ code by setting up the necessary environment and compilers. However, the platform is best suited for Python.',
    acceptedAnswerJsx: (
      <>
        While Google Colab is primarily designed for Python, it is possible to
        run C++ code by setting up the necessary environment and compilers.
        However, the platform is best suited for Python.
      </>
    ),
  },
  {
    questionName: 'How can kids use Google Colab for machine learning?',
    acceptedAnswerText:
      'Google Colab is widely used for machine learning. Kids can train AI models using pre-built datasets, run machine learning algorithms, and experiment with neural networks, all while utilizing powerful cloud resources.',
    acceptedAnswerJsx: (
      <>
        Google Colab is widely used for machine learning. Kids can train AI
        models using pre-built datasets, run machine learning algorithms, and
        experiment with neural networks, all while utilizing powerful cloud
        resources.
      </>
    ),
  },
  {
    questionName: 'Does Google Colab require a Google account?',
    acceptedAnswerText:
      'Yes, a Google account is required to access Google Colab. It allows users to save their projects in Google Drive and share them easily with others.',
    acceptedAnswerJsx: (
      <>
        Yes, a Google account is required to access Google Colab. It allows
        users to save their projects in Google Drive and share them easily with
        others.
      </>
    ),
  },
  {
    questionName: 'How much does Google Colab cost?',
    acceptedAnswerText:
      'Google Colab is free to use, but users can upgrade to Google Colab Pro for $9.99 per month for enhanced features like more powerful hardware and longer session durations.',
    acceptedAnswerJsx: (
      <>
        Google Colab is free to use, but users can upgrade to Google Colab Pro
        for $9.99 per month for enhanced features like more powerful hardware
        and longer session durations.
      </>
    ),
  },
]

const NODE_JS: FaqType[] = [
  {
    questionName: 'What is Node.js for Kids?',
    acceptedAnswerText:
      'Node.js for kids is a beginner-friendly course that introduces children and teens to backend development using JavaScript. This course helps them understand how to create web servers, work with databases, and build dynamic websites by teaching them server-side coding.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/nodejs/nodejs-2.jpg`}
            alt="Node.Js course for kids"
            width={512}
            height={341}
          />
        </div>
        <p>
          Node.js for kids is a beginner-friendly course that introduces
          children and teens to backend development using JavaScript. This
          course helps them understand how to create web servers, work with
          databases, and build dynamic websites by teaching them server-side
          coding.
        </p>
      </>
    ),
  },
  {
    questionName: 'How do kids learn Node.js in this course?',
    acceptedAnswerText:
      'In this Node.js course, students learn through hands-on, project-based lessons. They build simple web servers, work with databases, and create interactive web applications. Each project illustrates how backend coding functions in real-world scenarios.',
    acceptedAnswerJsx: (
      <>
        In this Node.js course, students learn through hands-on, project-based
        lessons. They build simple web servers, work with databases, and create
        interactive web applications. Each project illustrates how backend
        coding functions in real-world scenarios.
      </>
    ),
  },
  {
    questionName: 'What age group is suitable for the Node.js for teens class?',
    acceptedAnswerText:
      'Our Node.js for teens class targets middle and high school students, typically aged 12-18. It’s ideal for those who want to deepen their understanding of backend development and sharpen their programming skills.',
    acceptedAnswerJsx: (
      <>
        Our Node.js for teens class targets middle and high school students,
        typically aged 12-18. It’s ideal for those who want to deepen their
        understanding of backend development and sharpen their programming
        skills.
      </>
    ),
  },
  {
    questionName: 'Is Node.js good for teens to learn?',
    acceptedAnswerText:
      'Yes! Node.js is a great platform for teens to extend their JavaScript skills and transition into backend development. It equips them with the know-how to build robust web applications and prepares them for future tech careers.',
    acceptedAnswerJsx: (
      <>
        Yes! Node.js is a great platform for teens to extend their JavaScript
        skills and transition into backend development. It equips them with the
        know-how to build robust web applications and prepares them for future
        tech careers.
      </>
    ),
  },
  {
    questionName: 'What are the benefits of learning Node.js?',
    acceptedAnswerText:
      'Learning Node.js lays a strong foundation in backend development, enhancing problem-solving skills, logical thinking, and a clear understanding of web application architecture. It also prepares students for future tech careers by providing in-demand skills.',
    acceptedAnswerJsx: (
      <>
        Learning Node.js lays a strong foundation in backend development,
        enhancing problem-solving skills, logical thinking, and a clear
        understanding of web application architecture. It also prepares students
        for future tech careers by providing in-demand skills.
      </>
    ),
  },
  {
    questionName:
      'What’s the difference between Node.js and Python for back-end development?',
    acceptedAnswerText:
      'While both Node.js and Python are popular for backend development, Node.js is built on JavaScript, making it a perfect choice for developers already familiar with JavaScript. Python is often used for data science and AI, while Node.js shines in web development and real-time applications.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/nodejs/nodejs-3.jpg`}
            alt="Node.js vs python"
            width={512}
            height={341}
          />
        </div>
        <p>
          While both Node.js and Python are popular for backend development,
          Node.js is built on JavaScript, making it a perfect choice for
          developers already familiar with JavaScript. Python is often used for
          data science and AI, while Node.js shines in web development and
          real-time applications.
        </p>
      </>
    ),
  },
  {
    questionName:
      'What kind of projects will kids work on in Node.js for teens?',
    acceptedAnswerText:
      'Teens in the Node.js for teens class will work on exciting projects like: Building a web server from scratch. Developing simple APIs to fetch and display data. Creating a chat app with real-time communication using Socket.io. Implementing a basic database system to store and manage data.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/nodejs/nodejs-4.jpg`}
            alt="Node.js for teen"
            width={512}
            height={341}
          />
        </div>
        <p>
          Teens in the Node.js for teens class will work on exciting projects
          like:
        </p>
        <ul>
          <li>Building a web server from scratch.</li>
          <li>Developing simple APIs to fetch and display data.</li>
          <li>
            Creating a chat app with real-time communication using Socket.io.
          </li>
          <li>
            Implementing a basic database system to store and manage data.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'Can kids transition from Scratch/Python to Node.js?',
    acceptedAnswerText:
      'Yes, transitioning from Scratch or Python to Node.js is a natural progression. Node.js builds upon JavaScript knowledge from Scratch and Python, allowing kids to develop more complex projects with advanced features like databases and APIs. Progression Path: From Scratch/Python to Node.js Start with Scratch: Kids learn the basics of coding logic, sequences, loops, and events through visual blocks and fun games. Move to Python: They begin writing real code, learning about syntax, variables, functions, and how to build simple programs. Transition to JavaScript & Node.js: Now ready for more advanced development, kids use JavaScript (already introduced visually in Scratch) and step into Node.js to build real-world backend projects. Build Complex Apps: With Node.js, they can work with APIs, databases, servers, and create interactive apps like chat tools, games with online leaderboards, and more.',
    acceptedAnswerJsx: (
      <>
        <p>
          Yes, transitioning from Scratch or Python to Node.js is a natural
          progression. Node.js builds upon JavaScript knowledge from Scratch and
          Python, allowing kids to develop more complex projects with advanced
          features like databases and APIs.
        </p>
        <h3 className="font-600 text-lg">
          Progression Path: From Scratch/Python to Node.js
        </h3>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/nodejs/nodejs-5.jpg`}
            alt="Coding learning path from scratch to python and node js"
            width={512}
            height={341}
          />
        </div>
        <ol>
          <li>
            <b>Start with Scratch</b>: Kids learn the basics of coding logic,
            sequences, loops, and events through visual blocks and fun games.
          </li>
          <li>
            <b>Move to Python</b>: They begin writing real code, learning about
            syntax, variables, functions, and how to build simple programs.
          </li>
          <li>
            <b>Transition to JavaScript & Node.js</b>: Now ready for more
            advanced development, kids use JavaScript (already introduced
            visually in Scratch) and step into Node.js to build real-world
            backend projects.
          </li>
          <li>
            <b>Build Complex Apps</b>: With Node.js, they can work with{' '}
            <b>APIs, databases, servers,</b> and create interactive apps like
            chat tools, games with online leaderboards, and more.
          </li>
        </ol>
      </>
    ),
  },
  {
    questionName:
      'How Is Node.js for Kids Different from Other Coding Courses?',
    acceptedAnswerText:
      'Most coding courses focus on front-end or visual coding (e.g., Scratch, HTML/CSS). Node.js teaches backend development—how the web works behind the scenes. Kids learn to build servers, APIs, and databases, not just websites. Promotes full-stack thinking early on, not just UI design. Prepares kids for real-world tech skills used by professionals.',
    acceptedAnswerJsx: (
      <>
        <ul>
          <li>
            Most coding courses focus on front-end or visual coding (e.g.,
            Scratch, HTML/CSS).
          </li>
          <li>
            Node.js teaches backend development—how the web works behind the
            scenes.
          </li>
          <li>
            Kids learn to build servers, APIs, and databases, not just websites.
          </li>
          <li>Promotes full-stack thinking early on, not just UI design.</li>
          <li>
            Prepares kids for real-world tech skills used by professionals.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'Are Node.js classes for teens available online?',
    acceptedAnswerText:
      'Yes, online Node.js for kids classes are available, offering interactive lessons with 1:1 tutoring. Kids can learn at their own pace and get expert support in building real-world applications.',
    acceptedAnswerJsx: (
      <>
        Yes, online Node.js for kids classes are available, offering interactive
        lessons with 1:1 tutoring. Kids can learn at their own pace and get
        expert support in building real-world applications.
      </>
    ),
  },
  {
    questionName:
      'What are the prerequisites for learning Node.js for beginners?',
    acceptedAnswerText:
      'While there are no strict prerequisites for Node.js for beginners, students should be familiar with JavaScript basics. If they’ve already worked with Scratch or Python, they will find the transition to Node.js easier.',
    acceptedAnswerJsx: (
      <>
        While there are no strict prerequisites for Node.js for beginners,
        students should be familiar with JavaScript basics. If they’ve already
        worked with Scratch or Python, they will find the transition to Node.js
        easier.
      </>
    ),
  },
  {
    questionName: 'Can my child earn a Node.js certificate?',
    acceptedAnswerText:
      'Yes, upon completion of the Node.js for kids course, students receive a Codingal Node.js Certificate. This certificate showcases their skills and projects, making it a valuable addition to academic portfolios or resumes.',
    acceptedAnswerJsx: (
      <>
        Yes, upon completion of the Node.js for kids course, students receive a
        Codingal Node.js Certificate. This certificate showcases their skills
        and projects, making it a valuable addition to academic portfolios or
        resumes.
      </>
    ),
  },
]

const VISUAL_STUDIO_CODE: FaqType[] = [
  {
    questionName: 'What is Visual Studio Code?',
    acceptedAnswerText:
      'Visual Studio Code is a professional coding editor used as a playground for text-based coding for kids and teens.',
    acceptedAnswerJsx: (
      <>
        Visual Studio Code is a professional coding editor used as a playground
        for <b>text-based coding for kids and teens.</b>
      </>
    ),
  },
  {
    questionName: 'Why should my child learn Visual Studio Code?',
    acceptedAnswerText:
      'Learning VS Code teaches coding workflows, debugging, project organization, and text-based programming skills.',
    acceptedAnswerJsx: (
      <>
        Learning VS Code teaches coding workflows, debugging, project
        organization, and text-based programming skills.
      </>
    ),
  },
  {
    questionName: 'Is Visual Studio Code difficult for kids to learn?',
    acceptedAnswerText:
      'No, it is beginner-friendly and designed to make coding intuitive for students of all skill levels.',
    acceptedAnswerJsx: (
      <>
        No, it is beginner-friendly and designed to make coding intuitive for
        students of all skill levels.
      </>
    ),
  },
  {
    questionName: 'What can my child create with Visual Studio Code?',
    acceptedAnswerText:
      'Text-based applications, mini games, interactive projects, and structured coding exercises.',
    acceptedAnswerJsx: (
      <>
        Text-based applications, mini games, interactive projects, and
        structured coding exercises.
      </>
    ),
  },
  {
    questionName: 'What is Visual Studio Code for kids?',
    acceptedAnswerText:
      'A coding playground where kids and teens learn to apply coding concepts across multiple courses.',
    acceptedAnswerJsx: (
      <>
        A coding playground where kids and teens learn to apply coding concepts
        across multiple courses.
      </>
    ),
  },
  {
    questionName: 'How can kids learn Visual Studio Code coding?',
    acceptedAnswerText:
      'Through live classes, interactive coding projects, and guided hands-on exercises.',
    acceptedAnswerJsx: (
      <>
        Through live classes, interactive coding projects, and guided hands-on
        exercises.
      </>
    ),
  },
  {
    questionName:
      'Are there online Visual Studio Code coding classes for kids?',
    acceptedAnswerText:
      'Yes, Codingal offers live, online VS Code sessions with expert instructors.',
    acceptedAnswerJsx: (
      <>
        Yes, Codingal offers live, online VS Code sessions with expert
        instructors.
      </>
    ),
  },
  {
    questionName:
      'Does Codingal offer kids and teens Visual Studio Code certificates?',
    acceptedAnswerText:
      'Yes, learners earn a Codingal Visual Studio Code Certificate upon completion, validating their text-based coding skills.',
    acceptedAnswerJsx: (
      <>
        Yes, learners earn a <b>Codingal Visual Studio Code Certificate</b> upon
        completion, validating their text-based coding skills.
      </>
    ),
  },
  {
    questionName: 'What will students gain from using Visual Studio Code?',
    acceptedAnswerText:
      'Proficiency in writing, testing, and debugging code, project organization, problem-solving skills, and confidence in professional coding environments.',
    acceptedAnswerJsx: (
      <>
        Proficiency in writing, testing, and debugging code, project
        organization, problem-solving skills, and confidence in professional
        coding environments.
      </>
    ),
  },
]

const SQL: FaqType[] = [
  {
    questionName: 'What is SQL?',
    acceptedAnswerText:
      'SQL stands for Structured Query Language. It is a programming language used to manage and interact with databases. In simple terms, SQL helps kids learn how to organize, retrieve, and modify data stored in databases, just like managing an organized collection of information. It’s a fundamental skill for understanding how websites, apps, and games work under the hood.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/sql/sql-1.jpg`}
            alt="Child learning SQL commands to manage a database"
            width={512}
            height={341}
          />
        </div>
        <p>
          SQL stands for <b>Structured Query Language</b>. It is a programming
          language used to manage and interact with databases. In simple terms,
          SQL helps kids learn how to organize, retrieve, and modify data stored
          in databases, just like managing an organized collection of
          information. It’s a fundamental skill for understanding how websites,
          apps, and games work under the hood.
        </p>
      </>
    ),
  },
  {
    questionName: 'Why should my child learn SQL?',
    acceptedAnswerText:
      'Learning SQL helps children: Build foundational database skills: Understanding how data is stored, retrieved, and manipulated. Enhance problem-solving abilities: SQL teaches logical thinking and how to break down tasks into manageable parts. Prepare for tech careers: SQL is a key skill in data science, web development, and many other tech fields.',
    acceptedAnswerJsx: (
      <>
        <p>Learning SQL helps children:</p>
        <ul>
          <li>
            <b>Build foundational database skills</b>: Understanding how data is
            stored, retrieved, and manipulated.
          </li>
          <li>
            <b>Enhance problem-solving abilities</b>: SQL teaches logical
            thinking and how to break down tasks into manageable parts.
          </li>
          <li>
            <b>Prepare for tech careers</b>: SQL is a key skill in data science,
            web development, and many other tech fields.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'What kind of projects will my child work on in SQL?',
    acceptedAnswerText:
      'Your child will engage in interactive projects such as: Building a simple database: Organizing data like names, addresses, or game scores. Data queries: Writing SQL queries to retrieve, insert, and update information in their databases. Database management systems: Creating and managing databases for small websites or applications.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/sql/sql-2.jpg`}
            alt="Child building SQL database project"
            width={512}
            height={341}
          />
        </div>
        <p>Your child will engage in interactive projects such as:</p>
        <ul>
          <li>
            <b>Building a simple database:</b> Organizing data like names,
            addresses, or game scores.
          </li>
          <li>
            <b>Data queries:</b> Writing SQL queries to retrieve, insert, and
            update information in their databases.
          </li>
          <li>
            <b>Database management systems:</b> Creating and managing databases
            for small websites or applications.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'What can my child do after learning SQL?',
    acceptedAnswerText:
      'After learning SQL, your child will: Create and manage databases: For apps, websites, and games. Write and execute SQL queries: To retrieve, modify, and manage data. Use SQL with other programming languages: Such as Python for data analysis, creating interactive websites, or working on full-stack development projects.',
    acceptedAnswerJsx: (
      <>
        <div className="pb-4">
          <FastlyImage
            src={`/images/courses/sql/sql-3.jpg`}
            alt="Child writing SQL queries for website project"
            width={512}
            height={341}
          />
        </div>
        <p>After learning SQL, your child will:</p>
        <ul>
          <li>
            <b>Create and manage databases:</b> For apps, websites, and games.
          </li>
          <li>
            <b>Write and execute SQL queries:</b> To retrieve, modify, and
            manage data.
          </li>
          <li>
            <b>Use SQL with other programming languages:</b> Such as Python for
            data analysis, creating interactive websites, or working on
            full-stack development projects.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'How does learning SQL help kids in their coding journey?',
    acceptedAnswerText:
      'SQL is a critical skill for kids who want to move from front-end development to back-end development. Understanding databases and how data is managed opens doors to more advanced topics, such as: Full-stack development, Data science, Artificial Intelligence (AI).  It provides kids with a strong foundation in data management and logical thinking, which will be useful in their tech careers.',
    acceptedAnswerJsx: (
      <>
        <p>
          SQL is a critical skill for kids who want to move from front-end
          development to back-end development. Understanding databases and how
          data is managed opens doors to more advanced topics, such as:
        </p>
        <ul>
          <li>
            <b>Full-stack development</b>
          </li>
          <li>
            <b>Data science</b>
          </li>
          <li>
            <b>Artificial Intelligence (AI)</b>
          </li>
        </ul>
        <p>
          It provides kids with a strong foundation in data management and
          logical thinking, which will be useful in their tech careers.
        </p>
      </>
    ),
  },
  {
    questionName: 'What SQL for Kids : explained in a simple way?',
    acceptedAnswerText:
      "SQL (Structured Query Language) is essential for kids to understand how data is organized, retrieved, and manipulated in databases. Learning SQL for kids often includes: Simplified Concepts: Understanding the importance of databases and how to organize data. Core SQL Commands: Mastering commands like SELECT, INSERT, UPDATE, and DELETE to interact with databases. Hands-on Practice: Writing real SQL queries to retrieve and manage data, often applied in fun projects like game scores, databases for toys, or creating digital libraries. Real-World Applications: Learning how SQL is used in everyday applications, from websites to online games, making learning engaging and practical. Developing Analytical Skills: Kids will develop problem-solving skills as they work with data and learn to design structured data systems. Example SQL Query for Kids: Imagine you have a table called “Toys” with columns for ToyName and ToyType. Here's a simple query to find all toys that are of the “action” type: SELECT ToyName, ToyType FROM Toys WHERE ToyType = 'action';. This query would retrieve all toy names and types from the “Toys” table that are of type “action.”By following this guide, you can see how SQL learning for kids is both fun and educational, laying the foundation for future careers in data science, AI, and full-stack development. At Codingal, we ensure kids not only learn SQL but also gain essential problem-solving skills and creativity in coding.",
    acceptedAnswerJsx: (
      <>
        <p>
          SQL (Structured Query Language) is essential for kids to understand
          how data is organized, retrieved, and manipulated in databases.
          Learning SQL for kids often includes:
        </p>
        <ol>
          <li>
            <b>Simplified Concepts:</b> Understanding the importance of
            databases and how to organize data.
          </li>
          <li>
            <b>Core SQL Commands:</b> Mastering commands like SELECT, INSERT,
            UPDATE, and DELETE to interact with databases.
          </li>
          <li>
            <b>Hands-on Practice:</b> Writing real SQL queries to retrieve and
            manage data, often applied in fun projects like game scores,
            databases for toys, or creating digital libraries.
          </li>
          <li>
            <b>Real-World Applications:</b> Learning how SQL is used in everyday
            applications, from websites to online games, making learning
            engaging and practical.
          </li>
          <li>
            <b>Developing Analytical Skills:</b> Kids will develop
            problem-solving skills as they work with data and learn to design
            structured data systems.
          </li>
        </ol>
        <h3 className="font-600 text-lg">
          Imagine you have a table called “Toys” with columns for ToyName and
          ToyType. Here&apos;s a simple query to find all toys that are of the
          “action” type:
        </h3>
        <pre>
          {`SELECT ToyName, ToyType \nFROM Toys \nWHERE ToyType = &#39;action&#39;;`}
        </pre>
        <p>
          This query would retrieve all toy names and types from the “Toys”
          table that are of type “action.”
        </p>
        <p>
          By following this guide, you can see how <b>SQL learning for kids</b>{' '}
          is both fun and educational, laying the foundation for future careers
          in <b>data science</b>, <b>AI</b>, and <b>full-stack development</b>.
          At <b>Codingal</b>, we ensure kids not only learn SQL but also gain
          essential problem-solving skills and creativity in coding.
        </p>
      </>
    ),
  },
]

const REACT_JS: FaqType[] = [
  {
    questionName: 'What is React.js for Kids?',
    acceptedAnswerText:
      'React.js for Kids is a beginner-friendly way for children to learn how modern websites and apps are built. It’s a JavaScript library created by Meta (Facebook) that helps kids build interactive and dynamic web pages using reusable parts called components. At Codingal, students use React.js to design fun projects like to-do lists, quizzes, and dashboards that respond instantly when users interact.',
    acceptedAnswerJsx: (
      <>
        React.js for Kids is a beginner-friendly way for children to learn how
        modern websites and apps are built. It’s a JavaScript library created by
        Meta (Facebook) that helps kids build interactive and dynamic web pages
        using reusable parts called components. At Codingal, students use
        React.js to design fun projects like to-do lists, quizzes, and
        dashboards that respond instantly when users interact.
      </>
    ),
  },
  {
    questionName: 'Why Should Kids Learn React.js?',
    acceptedAnswerText:
      'Learning React.js gives kids real-world web development skills used by companies like Netflix, Instagram, and YouTube. It helps them understand how apps react to user actions while teaching problem-solving, logical thinking, and creativity. React also prepares kids for advanced concepts like Next.js and full-stack development, giving them a strong head start in future tech careers.',
    acceptedAnswerJsx: (
      <>
        Learning React.js gives kids real-world web development skills used by
        companies like Netflix, Instagram, and YouTube. It helps them understand
        how apps react to user actions while teaching problem-solving, logical
        thinking, and creativity. React also prepares kids for advanced concepts
        like Next.js and full-stack development, giving them a strong head start
        in future tech careers.
      </>
    ),
  },
  {
    questionName: 'Is React.js Easy for Kids to Learn?',
    acceptedAnswerText:
      'Yes! React.js is easier than it sounds, especially when taught step by step. Codingal introduces React concepts gradually, starting from HTML, CSS, and JavaScript basics before moving to React’s core topics like components, props, and state. Kids see instant results in the browser, which keeps learning fun and motivating.',
    acceptedAnswerJsx: (
      <>
        Yes! React.js is easier than it sounds, especially when taught step by
        step. Codingal introduces React concepts gradually, starting from HTML,
        CSS, and JavaScript basics before moving to React’s core topics like
        components, props, and state. Kids see instant results in the browser,
        which keeps learning fun and motivating.
      </>
    ),
  },
  {
    questionName: 'What Skills Do Kids Learn in React.js?',
    acceptedAnswerText:
      'Through React.js, kids learn: How to build reusable components, Managing state and props to control data, Handling events like clicks and inputs, Using JavaScript to create interactivity, Fetching and displaying data from APIs. These skills help them think like real developers and design interactive web applications confidently.',
    acceptedAnswerJsx: (
      <>
        Through React.js, kids learn:
        <ul>
          <li>How to build reusable components</li>
          <li>Managing state and props to control data</li>
          <li>Handling events like clicks and inputs</li>
          <li>Using JavaScript to create interactivity</li>
          <li>Fetching and displaying data from APIs</li>
        </ul>
        These skills help them think like real developers and design interactive
        web applications confidently.
      </>
    ),
  },
  {
    questionName: 'What Can Kids Build with React.js?',
    acceptedAnswerText:
      'Kids can build exciting real-world projects such as: Interactive calculators, Quiz or trivia apps, To-do lists or planners, Color pickers and design boards, Mini dashboards that update live. Every project helps kids connect creativity with coding logic while understanding how professional web apps work.',
    acceptedAnswerJsx: (
      <>
        Kids can build exciting real-world projects such as:
        <ul>
          <li>Interactive calculators</li>
          <li>Quiz or trivia apps</li>
          <li>To-do lists or planners</li>
          <li>Color pickers and design boards</li>
          <li>Mini dashboards that update live</li>
        </ul>
        Every project helps kids connect creativity with coding logic while
        understanding how professional web apps work.
      </>
    ),
  },
  {
    questionName: 'What Is the Best Age to Learn React.js?',
    acceptedAnswerText:
      'The ideal age to start learning React.js is 10–16 years old, once kids are comfortable with basic JavaScript concepts like loops, functions, and variables. Younger learners can begin with Scratch or JavaScript for Kids before progressing to React.js.',
    acceptedAnswerJsx: (
      <>
        The ideal age to start learning React.js is 10–16 years old, once kids
        are comfortable with basic JavaScript concepts like loops, functions,
        and variables. Younger learners can begin with Scratch or JavaScript for
        Kids before progressing to React.js.
      </>
    ),
  },
  {
    questionName: 'How Is React.js Different from JavaScript?',
    acceptedAnswerText:
      'JavaScript is the main programming language, while React.js is a library built with JavaScript. React makes it easier to create complex, interactive web apps by reusing components and updating only what changes — making apps faster and smoother.',
    acceptedAnswerJsx: (
      <>
        JavaScript is the main programming language, while React.js is a library
        built with JavaScript. React makes it easier to create complex,
        interactive web apps by reusing components and updating only what
        changes — making apps faster and smoother.
      </>
    ),
  },
  {
    questionName: 'How Does Codingal Teach React.js to Kids?',
    acceptedAnswerText:
      'Codingal uses live, instructor-led online classes with hands-on projects and personalized guidance. Each lesson builds on the previous one, covering topics like React render HTML, events, props, and state. Kids learn through practice, coding, testing, and refining their projects under expert supervision.',
    acceptedAnswerJsx: (
      <>
        Codingal uses live, instructor-led online classes with hands-on projects
        and personalized guidance. Each lesson builds on the previous one,
        covering topics like React render HTML, events, props, and state. Kids
        learn through practice, coding, testing, and refining their projects
        under expert supervision.
      </>
    ),
  },
  {
    questionName:
      'What Makes Codingal the Best Place to Learn React.js for Kids?',
    acceptedAnswerText:
      'Codingal combines live classes, STEM-accredited curriculum, and project-based learning to make React.js fun and practical for kids. Students not only learn to code but also create real web apps, gaining skills that prepare them for future STEM success.',
    acceptedAnswerJsx: (
      <>
        Codingal combines live classes, STEM-accredited curriculum, and
        project-based learning to make React.js fun and practical for kids.
        Students not only learn to code but also create real web apps, gaining
        skills that prepare them for future STEM success.
      </>
    ),
  },
]

const GIT: FaqType[] = [
  {
    questionName: 'What is GIT for Kids?',
    acceptedAnswerText:
      'GIT is a version control system used by professional developers to track and manage code. In our GIT for kids program, we simplify it into fun, easy lessons so children can save their projects, undo mistakes, and collaborate with friends, just like real coders!',
    acceptedAnswerJsx: (
      <>
        GIT is a version control system used by professional developers to track
        and manage code. In our GIT for kids program, we simplify it into fun,
        easy lessons so children can save their projects, undo mistakes, and
        collaborate with friends, just like real coders!
      </>
    ),
  },
  {
    questionName: 'Why should my child learn GIT?',
    acceptedAnswerText:
      'When kids learn GIT for kids, they gain problem-solving skills, confidence in coding, and teamwork experience. It’s more than just coding , GIT teaches project management, logical thinking, and prepares children for a future-ready coding journey.',
    acceptedAnswerJsx: (
      <>
        When kids learn GIT for kids, they gain problem-solving skills,
        confidence in coding, and teamwork experience. It’s more than just
        coding , GIT teaches project management, logical thinking, and prepares
        children for a future-ready coding journey.
      </>
    ),
  },
  {
    questionName: 'Is GIT programming difficult for children?',
    acceptedAnswerText:
      'Not at all! Our beginner GIT lessons for kids make learning GIT simple and fun. We use playful analogies and interactive GIT tutorials for kids so even young students can understand GIT basics and use commands like commit or branch with ease.',
    acceptedAnswerJsx: (
      <>
        Not at all! Our beginner GIT lessons for kids make learning GIT simple
        and fun. We use playful analogies and interactive GIT tutorials for kids
        so even young students can understand GIT basics and use commands like
        commit or branch with ease.
      </>
    ),
  },
  {
    questionName: 'What can my child do with GIT?',
    acceptedAnswerText:
      'With GIT coding for students, your child can: Work on GIT projects for beginners, like games or websites. Use GITHub for kids to share projects and collaborate with classmates. Build coding confidence through GIT version control for beginners, knowing they can undo mistakes anytime.',
    acceptedAnswerJsx: (
      <>
        With GIT coding for students, your child can:
        <ul>
          <li>Work on GIT projects for beginners, like games or websites.</li>
          <li>
            Use GITHub for kids to share projects and collaborate with
            classmates.
          </li>
          <li>
            Build coding confidence through GIT version control for beginners,
            knowing they can undo mistakes anytime.
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'How do kids learn GIT coding at Codingal?',
    acceptedAnswerText:
      'Kids learn through live, interactive sessions, online GIT classes for kids, and hands-on projects. Our GIT coding courses for kids cover everything from GIT basics for kids to GIT collaboration for students, ensuring children build practical coding skills.',
    acceptedAnswerJsx: (
      <>
        Kids learn through live, interactive sessions, online GIT classes for
        kids, and hands-on projects. Our GIT coding courses for kids cover
        everything from GIT basics for kids to GIT collaboration for students,
        ensuring children build practical coding skills.
      </>
    ),
  },
  {
    questionName: 'Are there after-school GIT programs for kids?',
    acceptedAnswerText:
      'Yes! Codingal offers after-school GIT programs, GIT workshops for students, and even GITHub coding camps for kids. These fun, structured sessions give children the chance to learn teamwork and problem-solving with GIT.',
    acceptedAnswerJsx: (
      <>
        Yes! Codingal offers after-school GIT programs, GIT workshops for
        students, and even GITHub coding camps for kids. These fun, structured
        sessions give children the chance to learn teamwork and problem-solving
        with GIT.
      </>
    ),
  },
  {
    questionName: 'What is GITHub for Kids?',
    acceptedAnswerText:
      'GITHub is where developers store and share projects. In our Learn GITHub for kids lessons, students explore safe, beginner-friendly ways to upload their projects, collaborate, and experience teamwork — boosting their confidence in coding.',
    acceptedAnswerJsx: (
      <>
        GITHub is where developers store and share projects. In our Learn GITHub
        for kids lessons, students explore safe, beginner-friendly ways to
        upload their projects, collaborate, and experience teamwork — boosting
        their confidence in coding.
      </>
    ),
  },
  {
    questionName: 'Is GIT training suitable for school students?',
    acceptedAnswerText:
      'Absolutely! Our GIT training for school students is designed for beginners, with step-by-step GIT tutorials for kids that make coding exciting, not intimidating.',
    acceptedAnswerJsx: (
      <>
        Absolutely! Our GIT training for school students is designed for
        beginners, with step-by-step GIT tutorials for kids that make coding
        exciting, not intimidating.
      </>
    ),
  },
  {
    questionName: 'What are the benefits of GIT for children?',
    acceptedAnswerText:
      'Kids coding skills with GIT improve quickly. They learn teamwork with GIT for kids and develop GIT problem-solving skills. They gain experience in GIT for project management kids, preparing them for advanced coding in the future. With GITHub collaboration for students, kids become future-ready coders.',
    acceptedAnswerJsx: (
      <>
        Kids coding skills with GIT improve quickly.
        <ul>
          <li>
            They learn teamwork with GIT for kids and develop GIT
            problem-solving skills.
          </li>
          <li>
            They gain experience in GIT for project management kids, preparing
            them for advanced coding in the future.
          </li>
          <li>
            With GITHub collaboration for students, kids become future-ready
            coders.
          </li>
        </ul>
      </>
    ),
  },
]

const GITHUB: FaqType[] = [
  {
    questionName: 'What is GitHub for Kids?',
    acceptedAnswerText:
      'GitHub is a version control system used by professional developers to track and manage code. In our GitHub for Kids program, we simplify it into fun, easy lessons so children can save their projects, undo mistakes, and collaborate with friends, just like real coders!',
    acceptedAnswerJsx: (
      <>
        GitHub is a version control system used by professional developers to
        track and manage code. In our GitHub for Kids program, we simplify it
        into fun, easy lessons so children can save their projects, undo
        mistakes, and collaborate with friends, just like real coders!
      </>
    ),
  },
  {
    questionName: 'Why should my child learn GitHub?',
    acceptedAnswerText:
      'When kids learn GitHub, they gain problem-solving skills, coding confidence, and teamwork experience. It’s more than just coding, GitHub teaches project management, logical thinking, and prepares children for a future-ready coding journey.',
    acceptedAnswerJsx: (
      <>
        When kids learn GitHub, they gain problem-solving skills, coding
        confidence, and teamwork experience. It’s more than just coding, GitHub
        teaches project management, logical thinking, and prepares children for
        a future-ready coding journey.
      </>
    ),
  },
  {
    questionName: 'Is GitHub Safe for teens?',
    acceptedAnswerText:
      'Yes! GitHub is safe for teens when used with guidance. Teens can store projects, track changes, and collaborate in a secure environment while learning professional coding practices and version control.',
    acceptedAnswerJsx: (
      <>
        Yes! GitHub is safe for teens when used with guidance. Teens can store
        projects, track changes, and collaborate in a secure environment while
        learning professional coding practices and version control.
      </>
    ),
  },
  {
    questionName: 'Is GitHub Easy for Beginners?',
    acceptedAnswerText:
      'GitHub has a user-friendly interface that makes it accessible for novice coders. While using Git alone often requires technical knowledge and command-line skills, GitHub simplifies the process. In fact, some people even use GitHub to manage non-coding projects, like writing books, because it’s so easy to organize and track changes.',
    acceptedAnswerJsx: (
      <>
        GitHub has a user-friendly interface that makes it accessible for novice
        coders. While using Git alone often requires technical knowledge and
        command-line skills, GitHub simplifies the process. In fact, some people
        even use GitHub to manage non-coding projects, like writing books,
        because it’s so easy to organize and track changes.
      </>
    ),
  },
  {
    questionName: 'What can my child do with GitHub?',
    acceptedAnswerText:
      'With GitHub platform, your child can: Work on beginner projects, like games or websites Share projects and collaborate safely with classmates Build coding confidence through version control, knowing they can undo mistakes anytime',
    acceptedAnswerJsx: (
      <>
        With GitHub platform, your child can:
        <ul>
          <li>Work on beginner projects, like games or websites</li>
          <li>Share projects and collaborate safely with classmates</li>
          <li>
            Build coding confidence through version control, knowing they can
            undo mistakes anytime
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'How do kids learn GitHub coding at Codingal?',
    acceptedAnswerText:
      'Kids learn through live, interactive online sessions and hands-on projects. Our GitHub coding courses cover everything from basics to collaboration, ensuring children build practical coding skills they can apply immediately.',
    acceptedAnswerJsx: (
      <>
        Kids learn through live, interactive online sessions and hands-on
        projects. Our GitHub coding courses cover everything from basics to
        collaboration, ensuring children build practical coding skills they can
        apply immediately.
      </>
    ),
  },
  {
    questionName: 'Are there after-school GitHub programs for kids?',
    acceptedAnswerText:
      'Yes! Codingal offers after-school GitHub programs, workshops, and coding camps. These structured sessions let children learn teamwork, problem-solving, and version control in a fun, interactive environment.',
    acceptedAnswerJsx: (
      <>
        Yes! Codingal offers after-school GitHub programs, workshops, and coding
        camps. These structured sessions let children learn teamwork,
        problem-solving, and version control in a fun, interactive environment.
      </>
    ),
  },
  {
    questionName: 'What is GitHub for Kids?',
    acceptedAnswerText:
      'GitHub is where developers store and share projects. In our GitHubHub lessons for kids, students explore safe, beginner-friendly ways to upload projects, collaborate, and practice teamwork, boosting their coding confidence.',
    acceptedAnswerJsx: (
      <>
        GitHub is where developers store and share projects. In our GitHubHub
        lessons for kids, students explore safe, beginner-friendly ways to
        upload projects, collaborate, and practice teamwork, boosting their
        coding confidence.
      </>
    ),
  },
  {
    questionName: 'Is GitHub training suitable for school students?',
    acceptedAnswerText:
      'Absolutely! Our GitHub training for school students is designed for beginners, with step-by-step tutorials that make coding exciting and easy to understand.',
    acceptedAnswerJsx: (
      <>
        Absolutely! Our GitHub training for school students is designed for
        beginners, with step-by-step tutorials that make coding exciting and
        easy to understand.
      </>
    ),
  },
  {
    questionName: 'What are the benefits of using GitHub for children?',
    acceptedAnswerText:
      'Kids’ coding skills improve quickly with GitHub They learn teamwork and collaboration through shared projects They gain project management and problem-solving experience They become future-ready coders, prepared for advanced coding and real-world projects',
    acceptedAnswerJsx: (
      <>
        Kids’ coding skills improve quickly with GitHub
        <ul>
          <li>They learn teamwork and collaboration through shared projects</li>
          <li>They gain project management and problem-solving experience</li>
          <li>
            They become future-ready coders, prepared for advanced coding and
            real-world projects
          </li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'Can a 14-Year-Old Use GitHub?',
    acceptedAnswerText:
      'Yes! GitHub is perfect for teens. At 14, kids can create projects, save changes, collaborate with friends, and learn real-world coding and version control skills safely.',
    acceptedAnswerJsx: (
      <>
        Yes! GitHub is perfect for teens. At 14, kids can create projects, save
        changes, collaborate with friends, and learn real-world coding and
        version control skills safely.
      </>
    ),
  },
  {
    questionName: 'What is an important consideration while using GitHub?',
    acceptedAnswerText:
      "There is an important consideration while using the GitHub, Parental guidance: For younger users in particular, supervision is recommended. Emphasize process, not just code: GitHub is about more than writing code, it's also about managing different versions, collaborating, and documenting projects. Teaching these concepts is just as valuable as the programming itself. Build a portfolio: Encourage kids to use GitHub to store all their coding projects. This builds an impressive portfolio that can be useful for college applications and future jobs.",
    acceptedAnswerJsx: (
      <>
        There is an important consideration while using the GitHub:
        <ul>
          <li>
            <strong>Parental guidance:</strong> For younger users in particular,
            supervision is recommended.
          </li>
          <li>
            <strong>Emphasize process, not just code:</strong> GitHub is about
            more than writing code, it&apos;s also about managing different
            versions, collaborating, and documenting projects. Teaching these
            concepts is just as valuable as the programming itself.
          </li>
          <li>
            <strong>Build a portfolio:</strong> Encourage kids to use GitHub to
            store all their coding projects. This builds an impressive portfolio
            that can be useful for college applications and future jobs.
          </li>
        </ul>
      </>
    ),
  },
]

const PUBLIC_SPEAKING_GRADE_1_2_FAQ: FaqType[] = [
  {
    questionName: 'What is public speaking for Grade 1 and Grade 2 kids?',
    acceptedAnswerText:
      'Public speaking for Grade 1-2 kids is learning to use their voice clearly, share short stories, and speak in front of others with confidence and expression.',
    acceptedAnswerJsx: (
      <>
        Public speaking for Grade 1-2 kids is learning to use their voice
        clearly, share short stories, and speak in front of others with
        confidence and expression.
      </>
    ),
  },
  {
    questionName:
      'Are these public speaking classes for young kids live or recorded?',
    acceptedAnswerText:
      'These are live public speaking classes for kids with real-time speaking practice and warm, encouraging feedback from an expert teacher.',
    acceptedAnswerJsx: (
      <>
        These are live public speaking classes for kids with real-time speaking
        practice and warm, encouraging feedback from an expert teacher.
      </>
    ),
  },
  {
    questionName: 'Is this an online public speaking course for Grade 1-2?',
    acceptedAnswerText:
      'Yes. This is an online public speaking course where Grade 1-2 kids practice speaking in every class and build confidence step-by-step.',
    acceptedAnswerJsx: (
      <>
        Yes. This is an online public speaking course where Grade 1-2 kids
        practice speaking in every class and build confidence step-by-step.
      </>
    ),
  },
  {
    questionName:
      'My child is in Grade 1. Is this public speaking course suitable for them?',
    acceptedAnswerText:
      'Yes. This public speaking and communication course is designed for young learners in Grade 1 and Grade 2. It uses simple, fun, age-appropriate speaking activities.',
    acceptedAnswerJsx: (
      <>
        Yes. This public speaking and communication course is designed for young
        learners in Grade 1 and Grade 2. It uses simple, fun, age-appropriate
        speaking activities.
      </>
    ),
  },
  {
    questionName: 'How will my child build confidence in public speaking?',
    acceptedAnswerText:
      'Children build confidence through regular, short speaking activities with a supportive teacher who gives positive feedback and gentle corrections every class.',
    acceptedAnswerJsx: (
      <>
        Children build confidence through regular, short speaking activities
        with a supportive teacher who gives positive feedback and gentle
        corrections every class.
      </>
    ),
  },
  {
    questionName:
      'What skills will my child learn in this public speaking course?',
    acceptedAnswerText:
      'Children learn voice clarity, expressive reading, simple storytelling, self-introduction, and listening skills that form the foundation of confident communication.',
    acceptedAnswerJsx: (
      <>
        Children learn voice clarity, expressive reading, simple storytelling,
        self-introduction, and listening skills that form the foundation of
        confident communication.
      </>
    ),
  },
  {
    questionName:
      'Can I join from anywhere if I am searching for public speaking classes for Grade 1-2 kids near me?',
    acceptedAnswerText:
      'Yes. This is a fully online public speaking course, so your child can attend from anywhere in the world with a device and internet connection.',
    acceptedAnswerJsx: (
      <>
        Yes. This is a fully online public speaking course, so your child can
        attend from anywhere in the world with a device and internet connection.
      </>
    ),
  },
]

const PUBLIC_SPEAKING_GRADE_3_5: FaqType[] = [
  {
    questionName:
      'Are these public speaking classes for kids in grades 3-5 suitable for shy students?',
    acceptedAnswerText:
      'Yes. We build confidence through structured speaking practice, so shy kids learn how to speak clearly with a simple plan and steady presence.',
    acceptedAnswerJsx: (
      <>
        Yes. We build confidence through structured speaking practice, so shy
        kids learn how to speak clearly with a simple plan and steady presence.
      </>
    ),
  },
  {
    questionName: 'How do you help ages 8-10 overcome public speaking anxiety?',
    acceptedAnswerText:
      'We teach practical tools like structure, pacing, pausing, and confident body language. With repeat practice and feedback, kids feel calmer and more prepared.',
    acceptedAnswerJsx: (
      <>
        We teach practical tools like structure, pacing, pausing, and confident
        body language. With repeat practice and feedback, kids feel calmer and
        more prepared.
      </>
    ),
  },
  {
    questionName:
      'What will my child learn in public speaking for kids ages 8-10?',
    acceptedAnswerText:
      'Kids learn speech structure, voice modulation, stage presence, storytelling impact, persuasive speaking, and debate foundations like reasoning and rebuttals.',
    acceptedAnswerJsx: (
      <>
        Kids learn speech structure, voice modulation, stage presence,
        storytelling impact, persuasive speaking, and debate foundations like
        reasoning and rebuttals.
      </>
    ),
  },
  {
    questionName:
      'Do you teach presentation skills for school and competitions in grades 3-5?',
    acceptedAnswerText:
      'Yes. Kids practice strong openings, clear key points, confident closings, and audience engagement, which helps in classroom presentations and competitions.',
    acceptedAnswerJsx: (
      <>
        Yes. Kids practice strong openings, clear key points, confident
        closings, and audience engagement, which helps in classroom
        presentations and competitions.
      </>
    ),
  },
  {
    questionName: 'Do you teach how to give a presentation for ages 8-10?',
    acceptedAnswerText:
      'Yes. Kids learn a step-by-step presentation format and practice delivering it with clarity, confidence, and strong delivery.',
    acceptedAnswerJsx: (
      <>
        Yes. Kids learn a step-by-step presentation format and practice
        delivering it with clarity, confidence, and strong delivery.
      </>
    ),
  },
  {
    questionName:
      'Do you cover impromptu speaking and handling Q and A for grades 3-5?',
    acceptedAnswerText:
      'Yes. Kids practice speaking on the spot with structure, and they learn how to answer questions clearly without freezing.',
    acceptedAnswerJsx: (
      <>
        Yes. Kids practice speaking on the spot with structure, and they learn
        how to answer questions clearly without freezing.
      </>
    ),
  },
  {
    questionName:
      'Do you help with speech ideas and speech topics for kids in English for grades 3-5?',
    acceptedAnswerText:
      'Yes. We provide school-friendly speech ideas and simple templates that help kids plan and speak confidently.',
    acceptedAnswerJsx: (
      <>
        Yes. We provide school-friendly speech ideas and simple templates that
        help kids plan and speak confidently.
      </>
    ),
  },
  {
    questionName:
      'Do you cover 1 minute and 2 minute speech topics for ages 8-10?',
    acceptedAnswerText:
      'Yes. Kids practice 1 minute and 2 minute speaking drills to improve fluency, structure, and confidence under time limits.',
    acceptedAnswerJsx: (
      <>
        Yes. Kids practice 1 minute and 2 minute speaking drills to improve
        fluency, structure, and confidence under time limits.
      </>
    ),
  },
  {
    questionName:
      'Do you support elocution competition topics and declamation topics for kids?',
    acceptedAnswerText:
      'Yes. Kids practice pronunciation, pauses, expression, voice control, and confident delivery that helps in elocution and declamation style formats.',
    acceptedAnswerJsx: (
      <>
        Yes. Kids practice pronunciation, pauses, expression, voice control, and
        confident delivery that helps in elocution and declamation style
        formats.
      </>
    ),
  },
  {
    questionName:
      'Can families in the US and UK join if they are searching for public speaking classes for kids near me?',
    acceptedAnswerText:
      'Yes. The program is live online, so families across the US and UK can join from anywhere and still get expert feedback and real speaking practice.',
    acceptedAnswerJsx: (
      <>
        Yes. The program is live online, so families across the US and UK can
        join from anywhere and still get expert feedback and real speaking
        practice.
      </>
    ),
  },
]

const CODING_GRADE_1_3_FAQ: FaqType[] = [
  {
    questionName:
      "Are Codingal's coding classes for Grades 1 to 3 suitable for complete beginners?",
    acceptedAnswerText:
      'Yes. Every Codingal course for Grades 1 to 3 starts with drag and drop tools such as Scratch, Code.org and block coding made for first time coders. No prior experience is needed, and no typing or advanced reading is required.',
    acceptedAnswerJsx: (
      <>
        Yes. Every Codingal course for Grades 1 to 3 starts with drag and drop
        tools such as Scratch, Code.org and block coding made for first time
        coders. No prior experience is needed, and no typing or advanced reading
        is required.
      </>
    ),
  },
  {
    questionName: 'What will my 1st, 2nd or 3rd grader learn first?',
    acceptedAnswerText:
      'Children start with simple commands, sequencing, events and basic logic on Scratch and Code.org. From there, 1st, 2nd and 3rd graders progress to Scratch animations, beginner apps on MIT App Inventor and early AI projects on AI Code 101.',
    acceptedAnswerJsx: (
      <>
        Children start with simple commands, sequencing, events and basic logic
        on Scratch and Code.org. From there, 1st, 2nd and 3rd graders progress
        to Scratch animations, beginner apps on MIT App Inventor and early AI
        projects on AI Code 101.
      </>
    ),
  },
  {
    questionName:
      'Does my child need typing experience to start coding in Grade 1, 2 or 3?',
    acceptedAnswerText:
      "No. Grades 1 to 3 begin entirely with block coding on Scratch and Code.org. Typing is introduced later at the child's own pace, typically around Grade 4 and 5 when Python is introduced.",
    acceptedAnswerJsx: (
      <>
        No. Grades 1 to 3 begin entirely with block coding on Scratch and
        Code.org. Typing is introduced later at the child&apos;s own pace,
        typically around Grade 4 and 5 when Python is introduced.
      </>
    ),
  },
  {
    questionName: 'Will my child learn Scratch?',
    acceptedAnswerText:
      "Yes. Scratch, built by MIT Media Lab, is the main tool for Grades 1 to 3 at Codingal. Children create stories, animations, games and interactive projects using Scratch's drag and drop blocks.",
    acceptedAnswerJsx: (
      <>
        Yes. Scratch, built by MIT Media Lab, is the main tool for Grades 1 to 3
        at Codingal. Children create stories, animations, games and interactive
        projects using Scratch&apos;s drag and drop blocks.
      </>
    ),
  },
  {
    questionName: 'Do children in Grades 1 to 3 learn AI?',
    acceptedAnswerText:
      'Yes, at a beginner level. Grade 1 to 3 AI projects on AI Code 101 involve image recognition, translation and guided AI activities built for young learners aged 6 to 9.',
    acceptedAnswerJsx: (
      <>
        Yes, at a beginner level. Grade 1 to 3 AI projects on AI Code 101
        involve image recognition, translation and guided AI activities built
        for young learners aged 6 to 9.
      </>
    ),
  },
  {
    questionName:
      'How long is each Codingal class for 1st, 2nd and 3rd graders?',
    acceptedAnswerText:
      'Each session is 45 to 60 minutes. Courses range from 2 to 18 months depending on the track, with AI and Coding Champion running 4 to 6 months and AI and Coding Grandmaster running 12 to 18 months.',
    acceptedAnswerJsx: (
      <>
        Each session is 45 to 60 minutes. Courses range from 2 to 18 months
        depending on the track, with AI and Coding Champion running 4 to 6
        months and AI and Coding Grandmaster running 12 to 18 months.
      </>
    ),
  },
  {
    questionName: 'What is the difference between 1:1 and small group classes?',
    acceptedAnswerText:
      '1:1 offers fully personalized pacing. Small group classes (up to 4 students) add peer interaction. Both formats are live and project based.',
    acceptedAnswerJsx: (
      <>
        1:1 offers fully personalized pacing. Small group classes (up to 4
        students) add peer interaction. Both formats are live and project based.
      </>
    ),
  },
  {
    questionName:
      'How do I choose the right coding course for my child in Grades 1 to 3?',
    acceptedAnswerText:
      "Start with a free 45 minute trial. A Codingal teacher assesses your child's pace and interest and recommends the best fit course from the five Grade 1 to 3 tracks.",
    acceptedAnswerJsx: (
      <>
        Start with a free 45 minute trial. A Codingal teacher assesses your
        child&apos;s pace and interest and recommends the best fit course from
        the five Grade 1 to 3 tracks.
      </>
    ),
  },
  {
    questionName: 'Will my child only make games in coding class?',
    acceptedAnswerText:
      'No. Games are one part of the journey. Children in Grades 1 to 3 also build animations, apps, AI projects and interactive stories across block coding, Scratch and MIT App Inventor.',
    acceptedAnswerJsx: (
      <>
        No. Games are one part of the journey. Children in Grades 1 to 3 also
        build animations, apps, AI projects and interactive stories across block
        coding, Scratch and MIT App Inventor.
      </>
    ),
  },
  {
    questionName: 'Can parents observe the free trial class?',
    acceptedAnswerText:
      "Yes. Parents are welcome to sit in on the trial lesson to understand their child's current level and learning path.",
    acceptedAnswerJsx: (
      <>
        Yes. Parents are welcome to sit in on the trial lesson to understand
        their child&apos;s current level and learning path.
      </>
    ),
  },
  {
    questionName: 'Do 1st, 2nd and 3rd graders get a coding certificate?',
    acceptedAnswerText:
      'Yes. Students earn a STEM.org accredited Coding Explorer Certificate on program completion. Milestone certificates are also awarded along the way.',
    acceptedAnswerJsx: (
      <>
        Yes. Students earn a STEM.org accredited Coding Explorer Certificate on
        program completion. Milestone certificates are also awarded along the
        way.
      </>
    ),
  },
  {
    questionName: 'Is there homework between coding classes for Grades 1 to 3?',
    acceptedAnswerText:
      'Yes, short and optional practice activities and quizzes. Nothing that feels like traditional schoolwork.',
    acceptedAnswerJsx: (
      <>
        Yes, short and optional practice activities and quizzes. Nothing that
        feels like traditional schoolwork.
      </>
    ),
  },
]

const CODING_GRADE_6_8_FAQ: FaqType[] = [
  {
    questionName:
      "Are Codingal's coding classes for Grades 6 to 8 suitable for beginners?",
    acceptedAnswerText:
      'Yes. Classes support both beginners and students with prior coding experience. Middle schoolers start at the level that is right for them, with the free trial class helping identify the best pathway and starting point.',
    acceptedAnswerJsx: (
      <>
        Yes. Classes support both beginners and students with prior coding
        experience. Middle schoolers start at the level that is right for them,
        with the free trial class helping identify the best pathway and starting
        point.
      </>
    ),
  },
  {
    questionName: 'What do students in Grades 6 to 8 learn first?',
    acceptedAnswerText:
      'Most students begin with structured lessons in Python, HTML, and CSS through hands-on project work. As 6th, 7th, and 8th graders build confidence, they progress into JavaScript, data science, app development, and beginner AI.',
    acceptedAnswerJsx: (
      <>
        Most students begin with structured lessons in Python, HTML, and CSS
        through hands-on project work. As 6th, 7th, and 8th graders build
        confidence, they progress into JavaScript, data science, app
        development, and beginner AI.
      </>
    ),
  },
  {
    questionName: 'Will my child learn real programming languages?',
    acceptedAnswerText:
      'Yes. Grades 6 to 8 is where students move into real, typed programming languages. Middle schoolers work with Python, HTML, CSS, and JavaScript through structured Codingal classes designed for this age group.',
    acceptedAnswerJsx: (
      <>
        Yes. Grades 6 to 8 is where students move into real, typed programming
        languages. Middle schoolers work with Python, HTML, CSS, and JavaScript
        through structured Codingal classes designed for this age group.
      </>
    ),
  },
  {
    questionName: 'What kinds of projects will my child build?',
    acceptedAnswerText:
      'Students build functional websites, Python programs, data projects, AI applications, and mobile apps through hands-on, guided lessons that increase in complexity and ambition with every class.',
    acceptedAnswerJsx: (
      <>
        Students build functional websites, Python programs, data projects, AI
        applications, and mobile apps through hands-on, guided lessons that
        increase in complexity and ambition with every class.
      </>
    ),
  },
  {
    questionName: 'Do students in Grades 6 to 8 learn AI and data science?',
    acceptedAnswerText:
      'Yes. Students explore beginner AI and data science through real projects involving datasets, machine learning concepts, and data visualization tools designed for the middle school level.',
    acceptedAnswerJsx: (
      <>
        Yes. Students explore beginner AI and data science through real projects
        involving datasets, machine learning concepts, and data visualization
        tools designed for the middle school level.
      </>
    ),
  },
  {
    questionName: 'Will my child learn web development?',
    acceptedAnswerText:
      'Yes. Students are introduced to HTML, CSS, Bootstrap, and JavaScript through structured lessons, progressing toward building and designing their own functional websites and interactive web projects.',
    acceptedAnswerJsx: (
      <>
        Yes. Students are introduced to HTML, CSS, Bootstrap, and JavaScript
        through structured lessons, progressing toward building and designing
        their own functional websites and interactive web projects.
      </>
    ),
  },
  {
    questionName: 'How are the classes taught?',
    acceptedAnswerText:
      'Classes are live and teacher-led. Middle schoolers learn in 1:1 or small group Codingal sessions through hands-on projects, structured challenges, and real-time feedback from expert instructors who understand this age group.',
    acceptedAnswerJsx: (
      <>
        Classes are live and teacher-led. Middle schoolers learn in 1:1 or small
        group Codingal sessions through hands-on projects, structured
        challenges, and real-time feedback from expert instructors who
        understand this age group.
      </>
    ),
  },
  {
    questionName: 'What is the difference between 1:1 and small group classes?',
    acceptedAnswerText:
      '1:1 classes offer personalised pacing and dedicated support, while small group classes bring peer interaction and collaborative problem-solving. Both formats are live, guided, and project-based.',
    acceptedAnswerJsx: (
      <>
        1:1 classes offer personalised pacing and dedicated support, while small
        group classes bring peer interaction and collaborative problem-solving.
        Both formats are live, guided, and project-based.
      </>
    ),
  },
  {
    questionName: 'How do I choose the right course for my child?',
    acceptedAnswerText:
      "The right pathway depends on your child's current level and goals. The free trial Codingal class helps assess where your middle schooler is and recommends the best-fit track, whether that is beginner, intermediate, or advanced.",
    acceptedAnswerJsx: (
      <>
        The right pathway depends on your child&apos;s current level and goals.
        The free trial Codingal class helps assess where your middle schooler is
        and recommends the best-fit track, whether that is beginner,
        intermediate, or advanced.
      </>
    ),
  },
  {
    questionName: 'Will my child only learn games?',
    acceptedAnswerText:
      'No. While games are part of the journey, students at this level focus primarily on real-world projects including websites, data tools, AI applications, and functional apps that reflect genuine coding ability.',
    acceptedAnswerJsx: (
      <>
        No. While games are part of the journey, students at this level focus
        primarily on real-world projects including websites, data tools, AI
        applications, and functional apps that reflect genuine coding ability.
      </>
    ),
  },
  {
    questionName: 'Do students get certificates?',
    acceptedAnswerText:
      'Yes. Students earn certificates as they complete structured pathways and learning milestones. Selected Codingal programs also include STEM.org accredited certificates that strengthen academic portfolios and high school records.',
    acceptedAnswerJsx: (
      <>
        Yes. Students earn certificates as they complete structured pathways and
        learning milestones. Selected Codingal programs also include STEM.org
        accredited certificates that strengthen academic portfolios and high
        school records.
      </>
    ),
  },
  {
    questionName: 'Can parents observe the free trial class?',
    acceptedAnswerText:
      "Yes. Parents are welcome to sit in on the trial lesson to understand their child's current level, coding strengths, and the best-fit pathway going forward.",
    acceptedAnswerJsx: (
      <>
        Yes. Parents are welcome to sit in on the trial lesson to understand
        their child&apos;s current level, coding strengths, and the best-fit
        pathway going forward.
      </>
    ),
  },
]

const CODING_GRADE_9_10_FAQ: FaqType[] = [
  {
    questionName:
      "Are Codingal's coding classes for Grades 9 and 10 suitable for beginners?",
    acceptedAnswerText:
      'Yes, though most students at this level have some prior coding experience. Ninth and tenth graders are assessed during the free trial class so Codingal can recommend the right starting point and pathway for their current level.',
    acceptedAnswerJsx: (
      <>
        Yes, though most students at this level have some prior coding
        experience. Ninth and tenth graders are assessed during the free trial
        class so Codingal can recommend the right starting point and pathway for
        their current level.
      </>
    ),
  },
  {
    questionName: 'What do students in Grades 9 and 10 learn?',
    acceptedAnswerText:
      'Students work across advanced Python, data science, AI, machine learning, Java, and competitive programming through structured live Codingal classes. Early high schoolers build real technical depth through ambitious, hands-on project work at every stage.',
    acceptedAnswerJsx: (
      <>
        Students work across advanced Python, data science, AI, machine
        learning, Java, and competitive programming through structured live
        Codingal classes. Early high schoolers build real technical depth
        through ambitious, hands-on project work at every stage.
      </>
    ),
  },
  {
    questionName: 'Will my child learn multiple programming languages?',
    acceptedAnswerText:
      'Yes. Students at this level work across Python, JavaScript, Java, HTML, and CSS depending on their chosen pathway, building the kind of multi-language fluency that is valued in college computer science programs.',
    acceptedAnswerJsx: (
      <>
        Yes. Students at this level work across Python, JavaScript, Java, HTML,
        and CSS depending on their chosen pathway, building the kind of
        multi-language fluency that is valued in college computer science
        programs.
      </>
    ),
  },
  {
    questionName: 'What kinds of projects will my child build?',
    acceptedAnswerText:
      'Students build data science tools, AI applications, machine learning models, advanced Python programs, and competitive programming solutions through structured, hands-on Codingal classes designed for this level.',
    acceptedAnswerJsx: (
      <>
        Students build data science tools, AI applications, machine learning
        models, advanced Python programs, and competitive programming solutions
        through structured, hands-on Codingal classes designed for this level.
      </>
    ),
  },
  {
    questionName: 'Do students in Grades 9 and 10 learn AI and data science?',
    acceptedAnswerText:
      'Yes. Students explore real datasets, build AI applications, train machine learning models, and develop analytical skills that are directly relevant to college-level computer science and data careers.',
    acceptedAnswerJsx: (
      <>
        Yes. Students explore real datasets, build AI applications, train
        machine learning models, and develop analytical skills that are directly
        relevant to college-level computer science and data careers.
      </>
    ),
  },
  {
    questionName: 'How are the classes taught?',
    acceptedAnswerText:
      'Classes are live and expert-led. Ninth and tenth graders learn in 1:1 or small group Codingal sessions through structured project work, guided challenges, and real-time feedback from instructors who specialise in advanced coding for high school students.',
    acceptedAnswerJsx: (
      <>
        Classes are live and expert-led. Ninth and tenth graders learn in 1:1 or
        small group Codingal sessions through structured project work, guided
        challenges, and real-time feedback from instructors who specialise in
        advanced coding for high school students.
      </>
    ),
  },
  {
    questionName: 'What is the difference between 1:1 and small group classes?',
    acceptedAnswerText:
      '1:1 classes offer personalised pacing, deeper support, and a curriculum tailored to the individual student. Small group classes bring peer challenge and collaborative problem-solving. Both formats are live, structured, and project-based.',
    acceptedAnswerJsx: (
      <>
        1:1 classes offer personalised pacing, deeper support, and a curriculum
        tailored to the individual student. Small group classes bring peer
        challenge and collaborative problem-solving. Both formats are live,
        structured, and project-based.
      </>
    ),
  },
  {
    questionName:
      'How does coding at this level help with college applications?',
    acceptedAnswerText:
      'Students build a strong project portfolio, earn STEM.org accredited certificates, and develop advanced technical skills that stand out in college applications, particularly for computer science, engineering, and technology programs.',
    acceptedAnswerJsx: (
      <>
        Students build a strong project portfolio, earn STEM.org accredited
        certificates, and develop advanced technical skills that stand out in
        college applications, particularly for computer science, engineering,
        and technology programs.
      </>
    ),
  },
  {
    questionName: 'How do I choose the right course for my child?',
    acceptedAnswerText:
      "The right pathway depends on your child's current coding ability and future goals. The free Codingal trial class assesses their level and recommends the most suitable track, whether that is advanced Python, data science, AI, or competitive programming.",
    acceptedAnswerJsx: (
      <>
        The right pathway depends on your child&apos;s current coding ability
        and future goals. The free Codingal trial class assesses their level and
        recommends the most suitable track, whether that is advanced Python,
        data science, AI, or competitive programming.
      </>
    ),
  },
  {
    questionName: 'Do students get certificates?',
    acceptedAnswerText:
      'Yes. Students earn certificates as they complete structured pathways and milestones. Selected Codingal programs include STEM.org accredited certificates that are recognised in college applications, academic portfolios, and early tech career contexts.',
    acceptedAnswerJsx: (
      <>
        Yes. Students earn certificates as they complete structured pathways and
        milestones. Selected Codingal programs include STEM.org accredited
        certificates that are recognised in college applications, academic
        portfolios, and early tech career contexts.
      </>
    ),
  },
  {
    questionName: 'Can parents observe the free trial class?',
    acceptedAnswerText:
      "Yes. Parents are welcome to sit in on the trial lesson to understand their child's current level, technical strengths, and the best-fit advanced coding pathway going forward.",
    acceptedAnswerJsx: (
      <>
        Yes. Parents are welcome to sit in on the trial lesson to understand
        their child&apos;s current level, technical strengths, and the best-fit
        advanced coding pathway going forward.
      </>
    ),
  },
]

const CODING_GRADE_11_12_FAQ: FaqType[] = [
  {
    questionName:
      "Are Codingal's coding classes for Grades 11 and 12 suitable for beginners?",
    acceptedAnswerText:
      "Most students at this level have prior coding experience, though structured beginner entry points are available. The free trial class assesses your child's current ability and recommends the most appropriate pathway for their goals and level.",
    acceptedAnswerJsx: (
      <>
        Most students at this level have prior coding experience, though
        structured beginner entry points are available. The free trial class
        assesses your child&apos;s current ability and recommends the most
        appropriate pathway for their goals and level.
      </>
    ),
  },
  {
    questionName: 'What do students in Grades 11 and 12 learn?',
    acceptedAnswerText:
      'Junior and senior students work at an advanced level across AI, data science, machine learning, competitive programming, and full stack development through expert-led live Codingal classes built around real-world outcomes and college readiness.',
    acceptedAnswerJsx: (
      <>
        Junior and senior students work at an advanced level across AI, data
        science, machine learning, competitive programming, and full stack
        development through expert-led live Codingal classes built around
        real-world outcomes and college readiness.
      </>
    ),
  },
  {
    questionName:
      'Will my child learn industry-relevant programming languages?',
    acceptedAnswerText:
      'Yes. Students work across Python, JavaScript, Java, HTML, CSS, and data science tools depending on their chosen pathway, building the kind of professional-level fluency that is valued by universities and tech employers alike.',
    acceptedAnswerJsx: (
      <>
        Yes. Students work across Python, JavaScript, Java, HTML, CSS, and data
        science tools depending on their chosen pathway, building the kind of
        professional-level fluency that is valued by universities and tech
        employers alike.
      </>
    ),
  },
  {
    questionName: 'What kinds of projects will my child build?',
    acceptedAnswerText:
      'College-bound coders build machine learning models, data science applications, full stack web projects, competitive programming solutions, and AI-powered tools through ambitious, hands-on Codingal classes designed for this level.',
    acceptedAnswerJsx: (
      <>
        College-bound coders build machine learning models, data science
        applications, full stack web projects, competitive programming
        solutions, and AI-powered tools through ambitious, hands-on Codingal
        classes designed for this level.
      </>
    ),
  },
  {
    questionName:
      'How does coding at this level help with college applications?',
    acceptedAnswerText:
      'Students build an impressive project portfolio, earn STEM.org accredited AI & Coding Grandmaster Certificates, and develop advanced technical skills that stand out in college applications, scholarship considerations, and early tech career opportunities.',
    acceptedAnswerJsx: (
      <>
        Students build an impressive project portfolio, earn STEM.org accredited
        AI & Coding Grandmaster Certificates, and develop advanced technical
        skills that stand out in college applications, scholarship
        considerations, and early tech career opportunities.
      </>
    ),
  },
  {
    questionName: 'Do students in Grades 11 and 12 learn AI and data science?',
    acceptedAnswerText:
      'Yes. AI and data science are central to the Grades 11 and 12 learning journey. Upper high schoolers work with real datasets, build predictive models, and develop AI applications at a level that reflects professional-grade technical understanding.',
    acceptedAnswerJsx: (
      <>
        Yes. AI and data science are central to the Grades 11 and 12 learning
        journey. Upper high schoolers work with real datasets, build predictive
        models, and develop AI applications at a level that reflects
        professional-grade technical understanding.
      </>
    ),
  },
  {
    questionName: 'How are the classes taught?',
    acceptedAnswerText:
      'Classes are live and expert-led. Junior and senior students learn in 1:1 or small group Codingal sessions through advanced project work, structured challenges, and real-time feedback from instructors who specialise in high-level coding for college-bound students.',
    acceptedAnswerJsx: (
      <>
        Classes are live and expert-led. Junior and senior students learn in 1:1
        or small group Codingal sessions through advanced project work,
        structured challenges, and real-time feedback from instructors who
        specialise in high-level coding for college-bound students.
      </>
    ),
  },
  {
    questionName: 'What is the difference between 1:1 and small group classes?',
    acceptedAnswerText:
      '1:1 classes offer a fully personalised curriculum, deeper technical support, and pacing tailored to the individual student. Small group classes bring peer challenge and collaborative problem-solving. Both formats are live, structured, and outcome-focused.',
    acceptedAnswerJsx: (
      <>
        1:1 classes offer a fully personalised curriculum, deeper technical
        support, and pacing tailored to the individual student. Small group
        classes bring peer challenge and collaborative problem-solving. Both
        formats are live, structured, and outcome-focused.
      </>
    ),
  },
  {
    questionName: 'How do I choose the right course for my child?',
    acceptedAnswerText:
      "The right pathway depends on your child's current coding ability, interests, and college or career goals. The free Codingal trial class assesses their level and recommends the most suitable advanced track, whether that is AI, data science, competitive programming, or full stack development.",
    acceptedAnswerJsx: (
      <>
        The right pathway depends on your child&apos;s current coding ability,
        interests, and college or career goals. The free Codingal trial class
        assesses their level and recommends the most suitable advanced track,
        whether that is AI, data science, competitive programming, or full stack
        development.
      </>
    ),
  },
  {
    questionName: 'Do students get certificates?',
    acceptedAnswerText:
      'Yes. Students earn STEM.org accredited AI & Coding Grandmaster Certificates upon completing structured advanced pathways, a recognised professional credential that strengthens college applications, academic portfolios, and early tech career profiles significantly.',
    acceptedAnswerJsx: (
      <>
        Yes. Students earn STEM.org accredited AI & Coding Grandmaster
        Certificates upon completing structured advanced pathways, a recognised
        professional credential that strengthens college applications, academic
        portfolios, and early tech career profiles significantly.
      </>
    ),
  },
  {
    questionName: 'Can parents observe the free trial class?',
    acceptedAnswerText:
      "Yes. Parents are welcome to sit in on the trial lesson to understand their child's current technical level, strengths, and the best-fit advanced coding pathway for their college and career goals.",
    acceptedAnswerJsx: (
      <>
        Yes. Parents are welcome to sit in on the trial lesson to understand
        their child&apos;s current technical level, strengths, and the best-fit
        advanced coding pathway for their college and career goals.
      </>
    ),
  },
]

const CODING_GRADE_4_5_FAQ: FaqType[] = [
  {
    questionName:
      "Are Codingal's coding classes for Grades 4 and 5 suitable for complete beginners?",
    acceptedAnswerText:
      'Yes. Classes support both first time coders and students with prior experience. A free 45 minute Codingal trial helps identify the right starting point for your 4th or 5th grader.',
    acceptedAnswerJsx: (
      <>
        Yes. Classes support both first time coders and students with prior
        experience. A free 45 minute Codingal trial helps identify the right
        starting point for your 4th or 5th grader.
      </>
    ),
  },
  {
    questionName:
      'What do 4th and 5th graders learn first in Codingal coding classes?',
    acceptedAnswerText:
      'Most 4th and 5th graders begin with project based lessons in Scratch and Code.org. As upper elementary students build confidence, they progress into Python, web development with HTML and CSS, and beginner AI.',
    acceptedAnswerJsx: (
      <>
        Most 4th and 5th graders begin with project based lessons in Scratch and
        Code.org. As upper elementary students build confidence, they progress
        into Python, web development with HTML and CSS, and beginner AI.
      </>
    ),
  },
  {
    questionName:
      'Will my 4th or 5th grader learn both visual coding and real programming?',
    acceptedAnswerText:
      'Yes. Grade 4 and 5 is a bridge stage at Codingal. Fourth and fifth graders continue using Scratch while being introduced to typed code in Python and HTML, so the transition feels gradual rather than stressful.',
    acceptedAnswerJsx: (
      <>
        Yes. Grade 4 and 5 is a bridge stage at Codingal. Fourth and fifth
        graders continue using Scratch while being introduced to typed code in
        Python and HTML, so the transition feels gradual rather than stressful.
      </>
    ),
  },
  {
    questionName: 'What kinds of projects will my 4th or 5th grader build?',
    acceptedAnswerText:
      'Students build games, apps, Scratch projects, webpages, beginner Python programs and interactive AI projects. Every project grows in complexity with each class.',
    acceptedAnswerJsx: (
      <>
        Students build games, apps, Scratch projects, webpages, beginner Python
        programs and interactive AI projects. Every project grows in complexity
        with each class.
      </>
    ),
  },
  {
    questionName: 'Do 4th and 5th graders learn AI?',
    acceptedAnswerText:
      'Yes. Students explore beginner AI through image recognition, speech input and interactive AI projects on AI Code 101, all designed for 9 to 11 year olds.',
    acceptedAnswerJsx: (
      <>
        Yes. Students explore beginner AI through image recognition, speech
        input and interactive AI projects on AI Code 101, all designed for 9 to
        11 year olds.
      </>
    ),
  },
  {
    questionName: 'Will my 4th or 5th grader learn web development?',
    acceptedAnswerText:
      'Yes. Fourth and fifth graders are introduced to HTML, CSS, Bootstrap and beginner JavaScript. They work toward building their own portfolio websites and interactive pages.',
    acceptedAnswerJsx: (
      <>
        Yes. Fourth and fifth graders are introduced to HTML, CSS, Bootstrap and
        beginner JavaScript. They work toward building their own portfolio
        websites and interactive pages.
      </>
    ),
  },
  {
    questionName: 'How long is each class for Grades 4 and 5?',
    acceptedAnswerText:
      'Each session is 45 to 60 minutes. Courses range from 2 to 18 months depending on the track, from AI and Coding Champion (4 to 6 months) to AI and Coding Grandmaster (12 to 18 months).',
    acceptedAnswerJsx: (
      <>
        Each session is 45 to 60 minutes. Courses range from 2 to 18 months
        depending on the track, from AI and Coding Champion (4 to 6 months) to
        AI and Coding Grandmaster (12 to 18 months).
      </>
    ),
  },
  {
    questionName: 'How are the classes taught?',
    acceptedAnswerText:
      'Classes are live and teacher led. Fourth and fifth graders learn in 1:1 or small group sessions through hands-on projects, guided challenges and real time feedback.',
    acceptedAnswerJsx: (
      <>
        Classes are live and teacher led. Fourth and fifth graders learn in 1:1
        or small group sessions through hands-on projects, guided challenges and
        real time feedback.
      </>
    ),
  },
  {
    questionName: 'What is the difference between 1:1 and small group classes?',
    acceptedAnswerText:
      '1:1 offers fully personalized pacing. Small group classes (up to 4 students) add peer interaction. Both formats are live and project based.',
    acceptedAnswerJsx: (
      <>
        1:1 offers fully personalized pacing. Small group classes (up to 4
        students) add peer interaction. Both formats are live and project based.
      </>
    ),
  },
  {
    questionName:
      'How do I choose the right coding course for my 4th or 5th grader?',
    acceptedAnswerText:
      "Start with a free trial. A Codingal teacher will recommend the best fit course from the six Grade 4 and 5 tracks based on your child's pace, prior experience and interest.",
    acceptedAnswerJsx: (
      <>
        Start with a free trial. A Codingal teacher will recommend the best fit
        course from the six Grade 4 and 5 tracks based on your child&apos;s
        pace, prior experience and interest.
      </>
    ),
  },
  {
    questionName: 'Will my 4th or 5th grader only learn games?',
    acceptedAnswerText:
      'No. Students also build apps, websites, Scratch projects, Python programs and beginner AI experiences throughout the Grade 4 and 5 path.',
    acceptedAnswerJsx: (
      <>
        No. Students also build apps, websites, Scratch projects, Python
        programs and beginner AI experiences throughout the Grade 4 and 5 path.
      </>
    ),
  },
  {
    questionName: 'Do students in Grades 4 and 5 get a coding certificate?',
    acceptedAnswerText:
      'Yes. Students earn STEM.org accredited certificates on program completion. Milestone certificates are also awarded along the way.',
    acceptedAnswerJsx: (
      <>
        Yes. Students earn STEM.org accredited certificates on program
        completion. Milestone certificates are also awarded along the way.
      </>
    ),
  },
  {
    questionName: 'Can parents observe the free trial class?',
    acceptedAnswerText:
      "Yes. Parents are welcome to sit in on the trial lesson to understand their child's current level and best fit learning path.",
    acceptedAnswerJsx: (
      <>
        Yes. Parents are welcome to sit in on the trial lesson to understand
        their child&apos;s current level and best fit learning path.
      </>
    ),
  },
]

export type HowClassWorksStepType = {
  step: number
  title: string
  duration: string
  description: string
}

export type HowClassWorksDataType = {
  gradeLabel: string
  steps: HowClassWorksStepType[]
}

export const HOW_CLASS_WORKS: Partial<Record<
  CourseNameType,
  HowClassWorksDataType
>> = {
  CODING_GRADE_1_3: {
    gradeLabel: 'Grade 1 to 3',
    steps: [
      {
        step: 1,
        title: 'Warm up',
        duration: '5 minutes',
        description:
          'Quick recap of the previous class and an unplugged question to get thinking started.',
      },
      {
        step: 2,
        title: 'Project build',
        duration: '25 to 35 minutes',
        description:
          'The child codes alongside the teacher, concept by concept.',
      },
      {
        step: 3,
        title: 'Challenge',
        duration: '10 minutes',
        description:
          'The child applies the new concept to a fresh mini task on their own.',
      },
      {
        step: 4,
        title: 'Wrap up',
        duration: '5 to 10 minutes',
        description:
          'The child presents their work. Teacher gives feedback and sets the next session goal.',
      },
    ],
  },
  CODING_GRADE_4_5: {
    gradeLabel: 'Grade 4 and 5',
    steps: [
      {
        step: 1,
        title: 'Warm up',
        duration: '5 minutes',
        description:
          'Quick recap of the previous class and a concept check to prime the session.',
      },
      {
        step: 2,
        title: 'Project build',
        duration: '25 to 35 minutes',
        description:
          'The child codes alongside the teacher, concept by concept, in Scratch, Python or web tools.',
      },
      {
        step: 3,
        title: 'Challenge',
        duration: '10 minutes',
        description:
          'The child applies the new concept to a fresh mini task on their own.',
      },
      {
        step: 4,
        title: 'Wrap up',
        duration: '5 to 10 minutes',
        description:
          'The child presents their work. The teacher gives feedback and sets the next session goal.',
      },
    ],
  },
  CODING_GRADE_6_8: {
    gradeLabel: 'Grade 6 to 8',
    steps: [
      {
        step: 1,
        title: 'Warm up',
        duration: '5 minutes',
        description:
          'Quick recap of the previous class and a concept check to prime the session.',
      },
      {
        step: 2,
        title: 'Project build',
        duration: '25 to 35 minutes',
        description:
          'The student codes alongside the teacher, concept by concept, in Python, JavaScript or web tools.',
      },
      {
        step: 3,
        title: 'Challenge',
        duration: '10 minutes',
        description:
          'The student applies the new concept to a fresh mini task on their own.',
      },
      {
        step: 4,
        title: 'Wrap up',
        duration: '5 to 10 minutes',
        description:
          'The student presents their work. Teacher gives feedback and sets the next session goal.',
      },
    ],
  },
  CODING_GRADE_9_10: {
    gradeLabel: 'Grade 9 to 10',
    steps: [
      {
        step: 1,
        title: 'Warm up',
        duration: '5 minutes',
        description:
          'Quick recap of the previous class and a concept check to prime the session.',
      },
      {
        step: 2,
        title: 'Project build',
        duration: '25 to 35 minutes',
        description:
          'The student codes alongside the teacher, concept by concept, in Python, Java or data science tools.',
      },
      {
        step: 3,
        title: 'Challenge',
        duration: '10 minutes',
        description:
          'The student applies the new concept to a fresh mini task on their own.',
      },
      {
        step: 4,
        title: 'Wrap up',
        duration: '5 to 10 minutes',
        description:
          'The student presents their work. Teacher gives feedback and sets the next session goal.',
      },
    ],
  },
  CODING_GRADE_11_12: {
    gradeLabel: 'Grade 11 to 12',
    steps: [
      {
        step: 1,
        title: 'Warm up',
        duration: '5 minutes',
        description:
          'Quick recap of the previous class and a concept check to prime the session.',
      },
      {
        step: 2,
        title: 'Project build',
        duration: '25 to 35 minutes',
        description:
          'The student codes alongside the teacher, concept by concept, across advanced Python, AI or data science.',
      },
      {
        step: 3,
        title: 'Challenge',
        duration: '10 minutes',
        description:
          'The student applies the new concept to a fresh mini task on their own.',
      },
      {
        step: 4,
        title: 'Wrap up',
        duration: '5 to 10 minutes',
        description:
          'The student presents their work. Teacher gives feedback and sets the next session goal.',
      },
    ],
  },
}

export const FAQ: Record<CourseNameType, FaqType[]> = {
  SCRATCH_PROGRAMMING: SCRATCH_PROGRAMMING_FAQ,
  PYTHON_FOR_KIDS: PYTHON_FOR_KIDS_FAQ,
  ROBLOX: ROBLOX,
  AP_COMPUTER_SCIENCE_A: AP_COMPUTER_SCIENCE_A,
  AI_AND_ML: AI_AND_ML,
  THUNKABLE: THUNKABLE,
  JAVA: JAVA,
  TRINKET: TRINKET,
  PYGAME: PYGAME,
  ANIMATION: ANIMATION,
  TURTLE: TURTLE,
  MIT_APP_INVENTOR: MIT_APP_INVENTOR,
  BOOTSTRAP: BOOTSTRAP,
  JAVASCRIPT: JAVASCRIPT,
  CSS: CSS,
  HTML: HTML,
  AICODE101: AICODE101,
  CODE_ORG: CODE_ORG,
  PICTOBLOX,
  TEACHABLE_MACHINE,
  GOOGLE_COLAB,
  NODE_JS,
  VISUAL_STUDIO_CODE,
  SQL,
  REACT_JS,
  GIT,
  GITHUB,
  PUBLIC_SPEAKING_GRADE_3_5,
  PUBLIC_SPEAKING_GRADE_1_2: PUBLIC_SPEAKING_GRADE_1_2_FAQ,
  CODING_GRADE_1_3: CODING_GRADE_1_3_FAQ,
  CODING_GRADE_4_5: CODING_GRADE_4_5_FAQ,
  CODING_GRADE_6_8: CODING_GRADE_6_8_FAQ,
  CODING_GRADE_9_10: CODING_GRADE_9_10_FAQ,
  CODING_GRADE_11_12: CODING_GRADE_11_12_FAQ,
}

export const COURSE_MODULE_DATA: Record<CourseNameType, ModuleType[]> = {
  SCRATCH_PROGRAMMING: [
    {
      title: 'Scratch Programming Fundamentals',
      moduleNumber: 1,
      logo: '/images/courses/scratch-programming/scratch-module-1.png',
      outline:
        'Get introduced to the Scratch platform and understand the block-based coding interface.',
      lessons: [
        {
          title: 'Introduction to Scratch',
          outline:
            'Build your first Scratch project, play with sprites, and learn controls and navigation.',
        },
        {
          title: 'Direction Sense',
          outline:
            'Understand the concept of time and direction by learning how and when to move the sprites.',
        },
        {
          title: 'Moving and gliding',
          outline: 'Learn to build games using move and glide blocks.',
        },
        {
          title: 'Tour the Solar System',
          outline: 'Build a block-based project around the solar system.',
        },
        {
          title: 'Transitions',
          outline:
            'Learn the concept of transition and motion to form logical connections between knowledge and interaction.',
        },
        {
          title: 'Conditional statements and Loops',
          outline:
            'Learn about conditional statements (if-else) and loops (for,repeat) in Scratch.',
        },
      ],
    },
    {
      title: 'Scratch Intermediate',
      moduleNumber: 2,
      logo: '/images/courses/scratch-programming/scratch-module-2.png',
      outline:
        'Get introduced to coordinates, understand the pen feature, and learn to use the broadcast block.',
      lessons: [
        {
          title: 'Mario Dash',
          outline:
            'Create a super fun Mario Dash game while understanding the use of sensing operators ',
        },
        {
          title: 'Catch Game',
          outline:
            'You will learn to create clones and use them in a project while making a catch game in Scratch.',
        },
        {
          title: 'DJ Cat',
          outline: 'Create a fantastic DJ project using looks and sounds.',
        },
        {
          title: 'Drawing with Pen',
          outline: 'Draw with pen blocks in Scratch.',
        },
        {
          title: 'Interlink Characters',
          outline:
            'Interlink different characters present on the stage using broadcast in Scratch.',
        },
        {
          title: 'Create a quiz',
          outline: 'Create a quiz using variables and broadcast.',
        },
      ],
    },
    {
      title: 'Scratch advance',
      moduleNumber: 3,
      logo: '/images/courses/scratch-programming/scratch-module-3.png',
      outline:
        'Learn to clone your sprites and understand the conditional statements and number system.',
      lessons: [
        {
          title: 'Snake and Food',
          outline: 'Create a Snake and food game using the concept of cloning.',
        },
        {
          title: 'Integer game',
          outline: 'Create integer games using a number line in Scratch.',
        },
        {
          title: 'Fundamentals of game building',
          outline:
            'Learn the basics of game building and how to make a car game in Scratch.',
        },
        {
          title: 'Car Game',
          outline:
            'Get introduced to Functions in Scratch. Build a Car Game using the concepts of motion.',
        },
        {
          title: 'Hungry Casey I',
          outline:
            'Create a firing game,"Hungry Casey” using variables and cloning in Scratch.',
        },
        {
          title: 'Hungry Casey II',
          outline:
            'Create a firing game,"Hungry Casey” using variables and cloning in Scratch.',
        },
      ],
    },
    {
      title: 'Scratch Specialization',
      moduleNumber: 4,
      logo: '/images/courses/scratch-programming/scratch-module-4.png',
      outline:
        'Introduction to animation effects, create an advanced level of games, and learn advanced features like text to speech.',
      lessons: [
        {
          title: 'Build your microscope',
          outline: 'Build a microscope using Scratch.',
        },
        {
          title: 'My Al friend GIGA',
          outline: 'Make an Artificial intelligence friend GIGA in Scratch.',
        },
        {
          title: 'Pacman I',
          outline: 'Create the popular Pacman game in Scratch.',
        },
        {
          title: 'Pacman II',
          outline: 'Create the popular Pacman game in Scratch.',
        },
        {
          title: 'Paint I',
          outline:
            'Create a paint project in Scratch using concepts learned so far.',
        },
        {
          title: 'Paint II',
          outline:
            'Create a paint project in Scratch using concepts learned so far.',
        },
      ],
    },
    {
      title: 'Artificial Intelligence - I',
      moduleNumber: 5,
      logo: '/images/courses/scratch-programming/scratch-module-5.png',
      outline:
        'Introduction to the magical world of Artificial Intelligence. Build projects using the text, sound, and image training.',
      lessons: [
        {
          title: 'Introduction to Artificial Intelligence',
          outline:
            'Learn Artificial Intelligence using some real-life examples. Introduction to a new platform called machine learning for kids.',
        },
        {
          title: 'Application Programming Interface',
          outline: 'Learn about Application Programming Interface (API).',
        },
        {
          title: 'Introduction to Machine Learning',
          outline:
            'Understand the difference between Machine Learning and Deep learning. Learn to train the computer to recognize sounds.',
        },
        {
          title: 'Types of Machine Learning',
          outline:
            'Learn about the types and categories of machine learning with multiple examples. Train the computer to recognize colors.',
        },
        {
          title: 'Deep Learning',
          outline:
            'Understand Deep learning, Image Processing, and Image Recognition. Train the computer to recognize handwriting.',
        },
        {
          title: 'Image classification',
          outline:
            'Learn about Image classification and teach a computer how to recognize handwriting.',
        },
      ],
    },
    {
      title: 'Artificial Intelligence - II',
      moduleNumber: 6,
      logo: '/images/courses/scratch-programming/scratch-module-6.png',
      outline:
        'Create your own Al project by understanding Artificial Intelligence with the help of text, vision training and speech recognition.',
      lessons: [
        {
          title: 'Speech Recognition',
          outline:
            'Understand the difference between speech and voice recognition, create a spy project in Scratch that can recognize your speech.',
        },
        {
          title: 'Supervised learning',
          outline:
            'Understand what is supervised learning and train computers to recognize the language.',
        },
        {
          title: 'Create Riddles',
          outline: 'Train your models to create Riddles.',
        },
        {
          title: 'Overfitting',
          outline:
            'Learn about overfitting and how computers can make mistakes if poorly trained.',
        },
        {
          title: 'Neural  Networks I',
          outline:
            'Learn about the Neural Networks concept. Create a Rock Paper Scissors game using all the concepts you have learned in this module.',
        },
        {
          title: 'Rock Paper Scissors Game',
          outline:
            'Learn about the Neural Networks concept. Create a Rock Paper Scissors game using all the concepts you have learned in this module.',
        },
      ],
    },
  ],
  HPE_CODEWARS_HACKATHON: [
    {
      title: 'Frontend development',
      moduleNumber: 1,
      logo: '/images/courses/hpe-codewars-hackathon/module-1.png',
      outline:
        'Introduction to HTML, links, tables, media and semantic elements in HTML. You will also learn forms in HTML and get introduced to CSS, background effects and text styling properties along with psuedo classes and box model.',
      lessons: [
        {
          title: 'Introduction to HTML',
          outline:
            'You will be introduced to the structure of HTML, br tag, comments, header tags, paragraph, formatting tags, block quotes, semantics, section and article.',
        },
        {
          title: 'Links, tables, media and semantic elements in HTML',
          outline:
            'Get introduced to hyperlink, list types, table, image, audio, videos, media, footer and nav tag.',
        },
        {
          title: 'Forms in HTML',
          outline:
            'You will learn about HTML form, form elements, name attributes and input attributes.',
        },
        {
          title: 'Introduction to CSS',
          outline:
            'Learn about basics of CSS and types of CSS & selectors along with the comments in CSS.',
        },
        {
          title: 'Background effects and text styling properties',
          outline:
            'In this lesson you will learn about CSS background and color, CSS border, CSS gradients, CSS Fonts, CSS text-decoration and text-transform.',
        },
        {
          title: 'Pseudo classes',
          outline:
            'You will learn about Pseudo classes, CSS forms,navigation bar, CSS Float',
        },
      ],
    },
    {
      title: 'Advance Frontend development',
      moduleNumber: 2,
      logo: '/images/courses/hpe-codewars-hackathon/module-2.png',
      outline:
        'Introduction to box model, positioning, opacity, bootstrap, containers in bootstrap, bootstrap utilities, syntax guide and commenting, variables. You will also learn functions in JS, scope of variable in functions, methods, loops, switch and website demo day.',
      lessons: [
        {
          title: 'Box model',
          outline:
            'In this lesson you will learn about box model, positioning and opacity.',
        },
        {
          title: 'Introduction to Bootstrap',
          outline:
            'In this lesson you will learn get introduced to bootstrap, containers in bootstrap',
        },
        {
          title: 'Bootstrap utilities',
          outline: 'Get introduced bootstrap utilities.',
        },
        {
          title: 'Introduction to JavaScript',
          outline:
            'In this lesson you will get introduced syntax guide and commenting, variables, Functions in JS, Scope of variable in functions.',
        },
        {
          title: 'Introduction to methods, loops and switches in JavaScript',
          outline:
            'You will get introduced to methods, loops and switches in JavaScript.',
        },
        {
          title: 'Website Demo Day',
          outline: 'Website Demo Day and get ready to build your project.',
        },
      ],
    },
  ],
  HPE_CODEWARS_CODE_BATTLE: [
    {
      title: 'Basics of implementation of Mathematics',
      moduleNumber: 1,
      logo: '/images/courses/hpe-codewars-code-battle/module-1.png',
      outline:
        'Introduction to Python, print statements, variables, and operators Introduction to conditional statement & various loops in Python Introduction to functions in Python.',
      lessons: [
        {
          title: 'Welcome to Python programming',
          outline:
            'You will be introduced to Python programming, print statements, variables, and operators.',
        },
        {
          title: 'Loops in Python',
          outline:
            'Get introduced to conditional statements and various loops in Python. ',
        },
        {
          title: 'Introduction to functions in Python',
          outline:
            'You will get well equipped with the basics of functions in Python.',
        },
        {
          title: 'Data Structures in Python',
          outline:
            'Learn about Operations on list, Python data structures - lists, tuples and their functions.',
        },
        {
          title: 'Mathematical magic',
          outline:
            'Learn about Palindrome number, Computing power a^b, Prime number and its properties, Armstrong numbers, Factors of a number.',
        },
        {
          title: 'Algorithm & Array',
          outline:
            'You will learn about important algorithms based on odd occurring numbers in an array and how to find them using bit manipulation.',
        },
      ],
    },
    {
      title: 'Recursion, array, searching and sorting',
      moduleNumber: 2,
      logo: '/images/courses/hpe-codewars-code-battle/module-1.png',
      outline:
        'Learn the concepts of recursions, arrays and algorithms. solve problems and get ready for the challenge.',
      lessons: [
        {
          title: 'Recursive problems',
          outline:
            'In this lesson  you will learn about recursive problems and algorithms.',
        },
        {
          title: 'Array problems',
          outline:
            'In this lesson you will learn about reverse arrays in groups, array problems and algorithms.',
        },
        {
          title: 'Selection and bubble sort',
          outline:
            'Learn about the Kadane algorithm and how you can use it to find the maximum array sum and its applications, bubble sort and selection sort.',
        },
        {
          title: 'Insertion and Quick sort',
          outline:
            'In this lesson you will solve general problems using algorithms in the data structure, insertion sort and quick sort.',
        },
        {
          title: 'Comparison between Sorting Algorithms',
          outline:
            'You will learn what is a merge sort and how the sorting algorithm differs from each other based on time and space complexity.',
        },
        {
          title: 'Practice problems',
          outline: 'Solve practice problems and get ready for the finale.',
        },
      ],
    },
  ],
  PYTHON_FOR_KIDS: [
    {
      title: 'Python Basics',
      moduleNumber: 1,
      outline:
        'Get introduced to Python, including data types, operators, and conditional statements, and write basic codes in Python.',
      logo: '/images/courses/python-for-kids/modules/module-1.png',
      lessons: [
        {
          title: 'Welcome to Python programming',
          outline:
            'Learn about computer programs, different programming languages, introduction to Python, the history of Python, algorithms, flowcharts, and repl.it platform.',
        },
        {
          title: 'Getting started with programming',
          outline:
            'Introduction to print statements, identifiers, keywords, and variables in Python.',
        },
        {
          title: 'Data types in Python',
          outline:
            'Learn about data types, typecasting, user input, and string operations.',
        },
        {
          title: 'Python operators I',
          outline:
            'Learn about Python operators and the application of various basic operators in Python.',
        },
        {
          title: 'Conditional statements',
          outline:
            'Learn about decision-making statements in Python programs. Also, learn to use the if-else statement.',
        },
        {
          title: 'Python operators II',
          outline:
            'Learn about conditional statements if, elif, else, and the logical operators used in Python programming.',
        },
        {
          title: 'Python operators III',
          outline:
            'Learn about identity operators, membership operators, and bitwise operators and the application of these operators in Python programming.',
        },
        {
          title: 'Python challenge',
          outline:
            'Learn to write programs using all the concepts learned in the module.',
        },
      ],
    },
    {
      title: 'Loops in Python',
      moduleNumber: 2,
      outline:
        'Learn about conditional statements, understand essential programming concepts, i.e., loops and nested conditional statements, and use Python library Turtle for creating graphics.',
      logo: '/images/courses/python-for-kids/modules/module-2.png',
      lessons: [
        {
          title: 'Nested conditional statements',
          outline:
            'Learn about nested conditional statements, the basics of conditional statements, and work on nested conditional statements in Python programming.',
        },
        {
          title: 'Loops',
          outline:
            'Learn about programming loops and their types, the use of loops in programs, and how to work with them to solve various problems.',
        },
        {
          title: 'While loop',
          outline:
            'Get introduced to the while loop, how and when to use them in your Python program.',
        },
        {
          title: 'Nested loop',
          outline:
            'Learn about nested loops, how to use nested loops, and the syntax of the nested loops in the Python program.',
        },
        {
          title: 'Patterns',
          outline:
            'Get introduced to nested loop’s application and how to make exciting patterns through nested loops in Python programming.',
        },
        {
          title: 'Introduction to turtle',
          outline:
            'Learn about Turtle library in Python to create graphics and learn some essential functions used in the Turtle library.',
        },
      ],
    },
    {
      title: 'Python Functions and Modules',
      moduleNumber: 3,
      outline:
        'Learn about the concept of functions in Python and exceptions in programming.',
      logo: '/images/courses/python-for-kids/modules/module-3.png',
      lessons: [
        {
          title: 'Function',
          outline:
            'Get well equipped with knowledge of the basics of functions in Python.',
        },
        {
          title: 'Arguments',
          outline:
            'Get introduced to arguments in Python functions, types of arguments, recursion, and docstring here.',
        },
        {
          title: 'Keywords',
          outline:
            'Learn about a few essential keywords like return, continue, break, and pass in a Python program.',
        },
        {
          title: 'Exception Handling',
          outline:
            'Learn about exception handling, various error handling techniques, and essential exceptions in Python.',
        },
        {
          title: 'Random and math module',
          outline:
            'Learn about the Random and math module, know about predefined and essential methods in your Python program that these libraries provide.',
        },
        {
          title: 'Date, time, and calendar',
          outline:
            "Learn about the Python program's date, time, and calendar module.",
        },
      ],
    },
    {
      title: 'Data Structures in Python',
      moduleNumber: 4,
      outline:
        "Learn about the concept of data structures, requirements, and the details of Python's most commonly used data structures. In this module, you will also learn how to debug the code.",
      logo: '/images/courses/python-for-kids/modules/module-4.png',
      lessons: [
        {
          title: 'Getting started with Lists',
          outline:
            'Get introduced to data structures, create a list, and perform operations on a list.',
        },
        {
          title: "It's a tuple!",
          outline: 'Learn about tuple and perform operations on a tuple.',
        },
        {
          title: 'All about Dictionary',
          outline:
            'Get introduced to Python data structure - Dictionaries and perform various operations and iterations on the dictionary in Python.',
        },
        {
          title: 'Sets and Arrays',
          outline:
            'Learn about sets and arrays used in Python and perform different operations on sets and arrays.',
        },
        {
          title: 'Advanced Python functions',
          outline:
            'Learn advanced functions used in Python programming like - my_ai_poet, zip, and exit, also the concepts of list comprehension and dictionary comprehension.',
        },
        {
          title: 'Create games',
          outline:
            'Create games like Tic Tac Toe and Number Guessing Game using all the previously learned concepts.',
        },
      ],
    },
    {
      title: 'Object Oriented Programming',
      moduleNumber: 5,
      outline:
        'Learn about Object Oriented Programming concepts and how to implement them in Python to optimize the code and make it reusable.',
      logo: '/images/courses/python-for-kids/modules/module-5.png',
      lessons: [
        {
          title: 'Object-Oriented Programming',
          outline:
            'Get introduced to basic object-oriented programming concepts in Python, like - objects, classes, and attributes.',
        },
        {
          title: 'More on Object-Oriented Programming',
          outline:
            'Learn about essential concepts of Object-Oriented Programming to understand how classes work.',
        },
        {
          title: 'Inheritance',
          outline:
            'Get introduced to inheritance in Python and implement inheritance in Python to make the code reusable.',
        },
        {
          title: 'Encapsulation and Special functions',
          outline:
            'Learn about encapsulation that helps you restrict access to variables and methods.',
        },
        {
          title: 'Abstraction and Polymorphism',
          outline:
            'Get knowledge about abstraction and polymorphism to showcase only the essential aspects and hide the inner workings/implementation.',
        },
        {
          title: 'Challenges in Object-Oriented Programming',
          outline:
            'Learn about operator overloading and practice previously learned concepts of Object-Oriented Programming in Python.',
        },
      ],
    },
    {
      title: 'Game Building with Pygame',
      moduleNumber: 6,
      outline:
        'Learn about all the necessary components for creating a video game step by step using the Pygame module of Python.',
      logo: '/images/courses/python-for-kids/modules/module-6.png',
      lessons: [
        {
          title: 'Let’s begin with Pygame',
          outline:
            'Learn about basic concepts of the game building using Pygame and create video games through the Pygame library.',
        },
        {
          title: 'Basic game building concepts',
          outline:
            'Create polygons using the functions of Pygame and use key detection in the game.',
        },
        {
          title: "Let's Add Sprites",
          outline:
            'Learn to add sprites to the Pygame Screen and control them in the game.',
        },
        {
          title: "Let's Level Up The Game",
          outline:
            'Learn to make a game interactive with different effects like catchy background, collisions, and sound to your game.',
        },
        {
          title: 'Space Invader Project - Part 1',
          outline:
            'Build the capstone project of a space invader game to implement all the learnings of Pygame.',
        },
        {
          title: 'Space Invader Project - Part 2',
          outline:
            'Build the capstone project of a space invader game to implement all the learnings of Pygame.',
        },
      ],
    },
    {
      title: 'GUI using Python Tkinter',
      moduleNumber: 7,
      outline:
        'Get introduced to the Python library Tkinter to create GUI applications that perform the required task without showing the backend code like an application. At the end of this module, you will develop an application.',
      logo: '/images/courses/python-for-kids/modules/module-7.png',
      lessons: [
        {
          title: 'Widgets for starters!',
          outline:
            'Learn about the basics of the Python library Tkinter and the different widget options available.',
        },
        {
          title: 'Tkinter geometry managers',
          outline:
            'Learn how to use various geometry managers to organize better and control your GUI application layout using Python Tkinter.',
        },
        {
          title: 'Where’s the event?',
          outline:
            "Learn about events, use event handlers in Tkinter, and increase an application's interactivity by using a message box.",
        },
        {
          title: 'Build a text editor',
          outline:
            'Create a text editor application using the Python Tkinter library, and learn to open and save the files using Tkinter in Python.',
        },
        {
          title: 'Denomination calculator',
          outline:
            'Learn to use Top Level in the Tkinter library to add multiple windows to the application.',
        },
        {
          title: 'Restaurant Management System',
          outline:
            'Create a Restaurant Management System application using all the concepts from the Tkinter library of Python.',
        },
      ],
    },
  ],
  ROBLOX: [
    {
      title: 'Introduction to Roblox studio',
      moduleNumber: 1,
      logo: '/images/courses/roblox/roblox-module-1.png',
      outline:
        'Get introduced to Roblox coding while building fun games on Roblox studio. Learn Lua, the text-based scripting language used in Roblox.',
      lessons: [
        {
          title: 'Discovering Roblox',
          outline:
            'In this lesson, we will introduce students to Roblox and get them familiarized with the platform.',
        },
        {
          title: 'Basic Roblox coding',
          outline:
            'Get introduced to basic Roblox coding by adding more elements to the game and increasing difficulty.',
        },
        {
          title: 'Adding advance obstacles through coding',
          outline:
            'Deep-dive into Roblox programming by increasing difficulty through speed blocks, lava blocks and much more.',
        },
        {
          title: 'Creating a User Interface in Roblox',
          outline:
            'Learn how to create a user interface through scripting in Roblox.',
        },
        {
          title: 'Creating Models Using Roblox Properties',
          outline:
            'Using properties of Roblox programming, build the model of a house including doors, windows and stairs by introducing the concept of Union & Negate.',
        },
        {
          title: 'Creating a Mini Game in Roblox',
          outline:
            'Recall previously learned lessons and use all the concepts to make a mini-game.',
        },
      ],
    },
    {
      title: 'Creating an Obby',
      moduleNumber: 2,
      logo: '/images/courses/roblox/roblox-module-2.png',
      outline:
        'Using simple shapes, create a mini Obby game. Learn to build a multi-level game that has different obstacles.',
      lessons: [
        {
          title: 'Setting up Game Information',

          outline:
            'Learn how to add a game name, description and a game icon on the Roblox website. Start building the scene for the game in this module.',
        },
        {
          title: 'Building Levels',
          outline:
            'Set up the scene further by adding obstacles with different difficulty levels while learning Roblox coding.',
        },
        {
          title: 'Creating Checkpoints',
          outline:
            'Set up the scene further by adding checkpoints at different difficulty levels.',
        },
        {
          title: 'Creating a Shop with a Shopping Menu',
          outline:
            'Add a shop at the beginning of the Roblox game. Once the player visits the shop, a shopping menu will appear.',
        },
        {
          title: 'New Obstacles Incoming!',
          outline:
            'Set up a game scene in Roblox with spinning balls, disappearing stairs and obstacles on which, when the player steps, the dimensions of the player change.',
        },
        {
          title: 'Tick! Tock!',
          outline:
            'Set up a timer in the Roblox game. The player will have to finish the game within a specific time to win. If the player loses, the timer resets and the player starts from the spawn location.',
        },
      ],
    },
    {
      title: 'Creating a 2D platformer',
      moduleNumber: 3,
      logo: '/images/courses/roblox/roblox-module-3.png',
      outline:
        'Learn how to change the controls for the camera and player. This will help you make it act like a 2D platformer.',
      lessons: [
        {
          title: 'Setting up camera and player control',
          outline:
            'Learn how to change the controls of the camera and player in Roblox studio.',
        },
        {
          title: 'Designing the level layout',
          outline:
            'Learn how to collect coins and keep up score while learning Roblox programming. Also, learn how to avoid death traps so that the player doesn’t die in the game.',
        },
        {
          title: 'Adding enemies and power',
          outline:
            'Learn how to add powerup to give player invisibility in Roblox and avoid being attacked by enemies.',
        },
        {
          title: 'Boss fight',
          outline:
            'Learn how to create a customised AI boss that the player has to defeat in order to move to the next level.',
        },
        {
          title: 'Creating levels ',
          outline:
            'Learn to design new levels in Roblox that will look different from the previous level and will also have challenging obstacles.',
        },
        {
          title: 'Finishing and testing the game',
          outline: 'Finish scripting and testing the game you created.',
        },
      ],
    },
    {
      title: 'Tycoon game',
      moduleNumber: 4,
      logo: '/images/courses/roblox/roblox-module-4.png',
      outline:
        'Learn advanced Roblox programming concepts while creating your own Tycoon game.',
      lessons: [
        {
          title: 'Setting up Tycoon system',

          outline:
            'Learn how to create a setup for the Tycoon for adding, creating and destroying components in Roblox.',
        },
        {
          title: 'Unlocking objects and leaderstats',
          outline:
            'Learn how to unlock objects and spawn objects into platforms.',
        },
        {
          title: 'Making money in Tycoon',
          outline:
            'Learn to set a way for the player to earn cash by creating a machine that creates objects that are shipped on a conveyor belt.',
        },
        {
          title: 'Saving data and upgrading',
          outline:
            'Learn how to save the data in Roblox for not only the money made so far, but also have the ability to upgrade items for the game as well.',
        },
        {
          title: 'Ownership and game passes',
          outline:
            'Learn how players can create ownership of the items they purchased.',
        },
        {
          title: 'Finishing and testing the game',
          outline:
            'Finish scripting and testing the game you created with Roblox coding.',
        },
      ],
    },
    {
      title: 'Racing game',
      moduleNumber: 5,
      logo: '/images/courses/roblox/roblox-module-5.png',
      outline:
        'Learn to create an addictive racing game. Players can create their track and checkpoints to track how many laps the player has collected.',
      lessons: [
        {
          title: 'Creating a car',

          outline:
            'Learn to build cars for the racing game, script how fast the cars go and give plates the ability to enter vehicles.',
        },
        {
          title: 'Creating a racetrack',
          outline: 'Learn to create a racing track for the Roblox game.',
        },
        {
          title: 'Scripting the game',
          outline:
            'Learn to spawn car objects onto the track for players to drive.',
        },
        {
          title: 'Game loop!',
          outline:
            'Learn creating a way to set, reset, destroy objects and also resetting game play elements for the game on Roblox studio.',
        },
        {
          title: 'Building a lobby area',
          outline:
            'In this lesson, we will learn to build lobby area for players entering the game.',
        },
        {
          title: 'Finishing and testing the game',
          outline: 'IFinish scripting and testing the game you created.',
        },
      ],
    },
    {
      title: 'Horror Game',
      moduleNumber: 6,
      logo: '/images/courses/roblox/roblox-module-6.png',
      outline:
        'Create Your Own Spine-Chilling Horror Game - Build terrifying adventures with animations, puzzles, and teleportation!',
      lessons: [
        {
          title: 'Building the Story Start: Dialogue & Objective System',
          outline:
            'Build a spine-chilling school environment and bring it to life with an animated typewriter dialogue and objective GUI system powered by RemoteEvents.',
        },
        {
          title: 'The Locked Door and the Key',
          outline:
            'Craft a locked door mystery — design a collectible key, trigger story dialogue, and script a smooth TweenService door unlock using ProximityPrompts.',
        },
        {
          title: 'The Stalker',
          outline:
            'Unleash a relentless NPC monster that hunts the player with server-side AI, walking animations, and bone-chilling 3D ambient sound.',
        },
        {
          title: 'Start Menu & Game Start',
          outline:
            'Design a cinematic start menu with a scriptable camera and a Play button that fires a RemoteEvent to launch the horror game.',
        },
        {
          title: 'Jumpscare Trigger',
          outline:
            'Trigger a heart-stopping jumpscare — flash a full-screen image and blast a scream sound using a hidden trigger zone and RemoteEvents.',
        },
        {
          title: 'Win Condition & Escape Trigger',
          outline:
            'Seal the game with a dramatic escape — script a ProximityPrompt win trigger, a "YOU ESCAPED!" overlay, and a teleport to safety.',
        },
      ],
    },
  ],
  AP_COMPUTER_SCIENCE_A: [
    {
      title: 'Introduction to Java',
      moduleNumber: 1,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-1.png',
      outline:
        'Learn the basics of the most popular language, i.e. Java, with the help of different activities. Also, learn about data types, conditionals and loops and functions. In addition, learn to create patterns.',
      lessons: [
        {
          title: 'Hey Java',
          outline: 'Get introduced to the language of possibilities, i.e.Java.',
        },
        {
          title: 'Welcome to data types & operators',
          outline:
            'Get introduced to the various data types and operators in Java. Also, learn about methods.',
        },
        {
          title: 'Conditionals',
          outline:
            'Get introduced to conditional statements and their application using java.',
        },
        {
          title: 'Advanced conditionals',
          outline:
            'Deep-dive into conditional statements specially the nested conditionals asked in the AP Computer Science A exam.',
        },
        {
          title: 'Iteration',
          outline: 'Introduction to loops in java and their applications.',
        },
        {
          title: 'Advanced iteration',
          outline:
            'Deep-dive into loops, specially the nested loops asked in the AP Computer Science A exam.',
        },
      ],
    },
    {
      title: 'Object Oriented Programming Using Java',
      moduleNumber: 2,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-2.png',
      outline:
        'Continue learning java by working on oops concepts, i.e., classes, objects, libraries, polymorphism, encapsulation, inheritance, abstraction and last but not least file handling.',
      lessons: [
        {
          title: 'OOPS!',

          outline:
            'Introduction to the Object-Oriented Programming System (OOPs) concepts.',
        },
        {
          title: 'Let’s inherit!',
          outline:
            'Get introduced to the concept of inheritance in Java using various activities.',
        },
        {
          title: 'Can you access me',
          outline:
            'Get introduced to many keywords like super, overriding, and overloading. Also, learn about different types of access modifiers and packages.',
        },
        {
          title: 'Get set go',
          outline:
            'Get introduced to the concept of encapsulation and learn about setters and getters.',
        },
        {
          title: 'I exist in many forms',
          outline:
            'Get introduced to the concept of polymorphism and abstraction.',
        },
        {
          title: 'Handling & Interface',
          outline: 'Learn the concept of file handling and interfaces.',
        },
      ],
    },
    {
      title: 'Practice questions on OOPS',
      moduleNumber: 3,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-3.png',
      outline:
        'Learn to solve the Free Response Question asked previously in the AP Computer Science A Exam about the OOPS concept.',
      lessons: [
        {
          title: 'Practice Free Response Question 1',

          outline:
            'Solve previously asked Free Response Questions about classes.',
        },
        {
          title: 'Practice Free Response Question 2',
          outline:
            'Solve previously asked Free Response Questions about objects.',
        },
        {
          title: 'Practice Free Response Question 3',

          outline:
            'Solve previously asked Free Response Questions about methods.',
        },
        {
          title: 'Practice Free Response Question 4',
          outline:
            'Solve previously asked Free Response Questions of complex type.',
        },
        {
          title: 'Practice Free Response Question 5',

          outline:
            'Solve previously asked Free Response Questions of complex type.',
        },
        {
          title: 'Practice Free Response Question 6',
          outline:
            'Solve previously asked Free Response Questions of complex type.',
        },
      ],
    },
    {
      title: 'Arrays, 2-D arrays & arraylist in Java',
      moduleNumber: 4,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-4.png',
      outline:
        'Elaborate introduction to the concepts of arrays,2d arrays & arraylist to help you know how arrays get space in the heap and memory mapping.',
      lessons: [
        {
          title: 'Introduction to arrays',
          outline:
            'Get introduced to arrays, declare an array, access its elements, and perform operations.',
        },
        {
          title: 'Kadanes algorithm',
          outline:
            'Learn about the Kadane algorithm and how to use it to find the maximum array sum and its applications.',
        },
        {
          title: 'Introduction to 2-dimensional arrays',
          outline: 'Get introduced to the concept of 2-Dimensional arrays.',
        },
        {
          title: 'Problems on 2-dimensional arrays',
          outline: 'Practice problems based on 2-Dimensional arrays.',
        },
        {
          title: 'Introduction to array list',
          outline: 'Get introduced to the concept of arraylist.',
        },
        {
          title: 'Problems on array list',
          outline: 'Practice problems related to arraylist.',
        },
      ],
    },
    {
      title: 'Introduction to recursion',
      moduleNumber: 5,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-5.png',
      outline:
        'Learn about recursion process, recursive function and how recursion works.',
      lessons: [
        {
          title: 'Call me recursion',
          outline:
            'Learn the basic concepts of recursion, how to use recursion, base case and recursive calls.',
        },
        {
          title: 'Types of recursion',
          outline:
            'Learn about types of recursion, their respective time complexities and how to use them.',
        },
        {
          title: 'Tower of Hanoi',
          outline:
            'Get introduced to the classic recursion problem name Tower of Hanoi.',
        },
        {
          title: 'Recursion in arrays',
          outline: 'Learn how to use and solve recursion in arrays.',
        },
        {
          title: 'Recursion in arraylist',
          outline: 'Learn how to use and solve recursion in arraylist.',
        },
        {
          title: 'Brainstorm',
          outline:
            'Solve some tricky recursive functions and analyse those functions.',
        },
      ],
    },
    {
      title: 'Introduction to searching & sorting',
      moduleNumber: 6,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-6.png',
      outline:
        'Learn about searching and sorting algorithms, concepts and  implementation using Java.',
      lessons: [
        {
          title: 'Introduction to binary search',
          outline:
            'Get introduced to the binary search algorithms using iteration & recursion both.',
        },
        {
          title: 'Introduction to bubble sort',
          outline:
            'Get introduced to sorting algorithms using iteration & recursion both.',
        },
        {
          title: 'Introduction to selection sort',
          outline:
            'Learn about selection sort algorithms using iteration & recursion both.',
        },
        {
          title: 'Introduction to insertion sort',
          outline:
            'Get introduced to the insertion sort algorithms using iteration & recursion both.',
        },
        {
          title: 'Introduction to quick sort',
          outline:
            'Learn about the quick sort algorithms using iteration & recursion both.',
        },
        {
          title: 'Introduction to serge sort',
          outline:
            'Get introduced to the merge sort algorithms using iteration & recursion both.',
        },
      ],
    },
    {
      title: 'Introduction to strings',
      moduleNumber: 7,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-7.png',
      outline:
        'Learn about palindrome, anagram, and basic problem solving in strings, {EXTRAS: Rabin Karp Algorithm, KMP Algorithm }',
      lessons: [
        {
          title: 'Introduction to Strings',
          outline:
            'Learn about the basics of strings in Python. Also, learn some basic operations on strings in Java.',
        },
        {
          title: 'String methods',
          outline:
            'Get introduced to the basics of strings in Java. Also, learn about operations on strings in Python and string methods.',
        },
        {
          title: 'String game',
          outline:
            'Learn counting the frequency of each character in the string and related programs.',
        },
        {
          title: 'Previous years problem discussion I',
          outline:
            'Discuss and practice previous year’s AP Computer Science A questions on strings.',
        },
        {
          title: 'String algorithms',
          outline:
            'Get introduced to KMP & Rabin Karp algorithm to the students.',
        },
        {
          title: 'Previous years problem discussion II',
          outline:
            'Discuss and practice previous year’s AP Computer Science A questions on strings.',
        },
      ],
    },
    {
      title: 'Practice question on arrays',
      moduleNumber: 8,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-8.png',
      outline:
        'Learn to solve the Free Response Question asked previously in the AP Computer Science A Exam about the Arrays concept.',
      lessons: [
        {
          title: 'PQ FRQ 1',
          outline:
            'Solve previously asked Free Response Questions about 1-Dimensional arrays.',
        },
        {
          title: 'PQ FRQ 2',
          outline:
            'Solve previously asked Free Response Questions about 2-Dimensional arrays.',
        },
        {
          title: 'PQ FRQ 3',
          outline:
            'Solve previously asked Free Response Questions about arraylist.',
        },
        {
          title: 'PQ FRQ 4',
          outline:
            'Solve previously asked Free Response Questions about recursion using 1-Dimensional arrays.',
        },
        {
          title: 'PQ FRQ 5',
          outline:
            'Solve previously asked Free Response Questions about recursion using 2-Dimensional arrays.',
        },
        {
          title: 'PQ FRQ 6',
          outline:
            'Solve previously asked Free Response Questions about recursion using arraylist.',
        },
      ],
    },
  ],
}

export const COURSE_RESOURCE_DATA: Record<
  CourseNameType,
  courseResourceType
> = {
  SCRATCH_PROGRAMMING: [
    {
      title: 'Introduction to Scratch - Block based programming language',
      description:
        'Block coding is a process used in computer programming where text-based software codes change to a visual block format to create animated games, characters, and even stories.',
      link:
        'https://www.codingal.com/coding-for-kids/coding-guides/block-coding-guide/',
    },
    {
      title: 'Learn to build Solar Project using Scratch programming',
      description:
        "Scratch is a free block-based programming platform designed primarily for kids. It's a visual programming interface where kids can code interactive stories, games and animations and share their creations through an online community.",
      link:
        'https://www.codingal.com/coding-for-kids/blog/how-to-build-a-solar-system-project/',
    },
    {
      title: '5 benefits of learning Scratch for kid',
      description:
        'Scratch programming encourages creative thinking in kids. It’s designed primarily for kids to encourage creativity by providing them with an easy to learn yet powerful programming platform. Tap on read more to know more about other benefits of Scratch programming.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/benefits-of-learning-scratch-for-kids/',
    },
    {
      title: '7 best Scratch games for kids',
      description:
        'Flappy Bird is one of the most popular games on Scratch. It’s a side-scroller game where you control a bird attempting to fly between columns of green pipes without hitting them. Tap on read more to explore the best Scratch games for kids.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/7-best-scratch-games-for-kids-to-learn-coding/',
    },
    {
      title: 'A beginner’s guide to block-based and text-based coding',
      description:
        'Block-based code will help you understand the basics and is a great place to start. Whereas, text-based coding is a natural progression for kids already versed in block-based coding.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/block-based-and-text-based-coding/',
    },
    {
      title: '10 Best Scratch Projects for Kids',
      description:
        'Maze Starter is a fun Scratch project where you move a ball to reach the goal using the arrow keys on your keyboard. You can customise the game by adding more obstacles and constructing new levels. Tap on read more to explore the best Scratch projects for kids.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/scratch-for-kids-projects-beginners/',
    },
  ],
  PYTHON_FOR_KIDS: [
    {
      title: 'Python for Kids: The Ultimate Guide to Learn Python',
      description:
        'Python is one of the popular and versatile programming languages. It is beginner friendly and therefore the best programming language to start with. It is considered to be one of the most used languages in the world.',
      link:
        'https://www.codingal.com/coding-for-kids/coding-guides/python-programming-for-kids-the-ultimate-guide-to-learn-python/',
    },
    {
      title: 'Strings in Python',
      description:
        'Python string is a data type in which the programming language stores any data in a computer. Python string is an array of bytes. A string is a sequence of characters. It is among the most popular data types in Python.',
      link: 'https://www.codingal.com/coding-for-kids/blog/strings-in-python/',
    },
    {
      title: 'Loops in Python',
      description:
        'Loops are a formidable abstraction in Python. Iterative looping, which loops employ, helps us eliminate repetitive code. This is incredibly useful when you want to repeat a function numerous times.',
      link: 'https://www.codingal.com/coding-for-kids/blog/loops-in-python/',
    },
    {
      title:
        'Top 5 reasons why Python programming for kids is perfect for your child',
      description:
        'Python is quick to learn even for kids or school students. Python programming is easy to read, write and learn. Tap on read more to know more about other reasons why Python programming for kids is perfect for your child.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/top-5-reasons-why-python-programming-for-kids-is-perfect-for-your-child/',
    },
    {
      title: 'Dictionaries in Python',
      description:
        'Dictionary in Python is a collection of key values used to store data like a my_ai_poet, unlike other data types, which hold only a single value as an element.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/dictionaries-in-python/',
    },
  ],
  PUBLIC_SPEAKING_GRADE_3_5: [
    {
      title: 'Public speaking for kids: A beginner’s guide',
      description:
        'Learn what public speaking for kids means, how to start safely, and how confidence builds through practice.',
    },
    {
      title: 'Public speaking classes for kids: What parents should look for',
      description:
        'A parent-friendly checklist to choose the right public speaking classes for kids and track real progress.',
    },
    {
      title: '1 minute speech topics for kids (Grade-wise list)',
      description:
        'A grade-wise list of 1 minute speech topics with a simple template to speak without fear.',
    },
    {
      title: 'Speech topics for kids in English (Easy, school-friendly topics)',
      description:
        'A list of easy speech topics kids can speak on confidently with ready openers and closers.',
    },
    {
      title: 'Presentation skills for kids (A step-by-step guide)',
      description:
        'Learn structure, delivery, body language, and Q and A tips to present confidently.',
    },
    {
      title:
        'Elocution and declamation topics for kids (With preparation tips)',
      description:
        'Topic ideas and a performance checklist for elocution and declamation speaking.',
    },
  ],
  PUBLIC_SPEAKING_GRADE_1_2: [
    {
      title: 'Public speaking for kids: A beginner\u2019s guide',
      description:
        'Learn what public speaking for kids means, how to start safely, and how confidence builds through practice.',
    },
    {
      title: 'Public speaking classes for kids: What parents should look for',
      description:
        'A parent-friendly checklist to choose the right public speaking classes for kids and track real progress.',
    },
    {
      title: '1 minute speech topics for kids (Grade-wise list)',
      description:
        'A grade-wise list of 1 minute speech topics with a simple template to speak without fear.',
    },
    {
      title: 'Speech topics for kids in English (Easy, school-friendly topics)',
      description:
        'A list of easy speech topics kids can speak on confidently with ready openers and closers.',
    },
    {
      title: 'Presentation skills for kids (A step-by-step guide)',
      description:
        'Learn structure, delivery, body language, and Q and A tips to present confidently.',
    },
    {
      title:
        'Elocution and declamation topics for kids (With preparation tips)',
      description:
        'Topic ideas and a performance checklist for elocution and declamation speaking.',
    },
  ],
}

export const RECOMMENDED_COURSE_PAGE_NAMES = [
  'SCRATCH_PROGRAMMING',
  'ROBOTICS',
  'ROBLOX',
] as const

type StudentProjectsType = Record<
  CourseNameType,
  {
    heading: string
    contents: {
      category: string
      title: string
      studentName: string
      studentGrade: string
      link?: string
      image: {
        name: string
        alt: string
      }
    }[]
  }
>
export const STUDENT_PROJECTS: StudentProjectsType = {
  SCRATCH_PROGRAMMING: {
    heading:
      'Amazing projects our students have created through our training program',
    contents: [
      {
        category: 'Scratch Project',
        title: 'Frog, snake, & angel',
        studentName: 'Bhavi Tushar Chavan',
        studentGrade: 'Grade 1',
        link: 'https://scratch.mit.edu/projects/546408701',
        image: {
          name: 'scratch-programming/projects/project-1',
          alt: 'Frog, snake, & angel',
        },
      },
      {
        category: 'Scratch Project',
        title: 'Basketball court',
        studentName: 'Arihaan',
        studentGrade: 'Grade 5',
        link: 'https://scratch.mit.edu/projects/517066280',
        image: {
          name: 'scratch-programming/projects/project-2',
          alt: 'Basketball court',
        },
      },
      {
        category: 'Scratch Project',
        title: 'Flying bees',
        studentName: 'Adhyan',
        studentGrade: 'Grade 4',
        link: 'https://scratch.mit.edu/projects/479526004',
        image: {
          name: 'scratch-programming/projects/project-3',
          alt: 'Flying bees',
        },
      },
      {
        category: 'Scratch Project',
        title: 'Talking duck',
        studentName: 'Luv',
        studentGrade: 'Grade 4',
        link: 'https://scratch.mit.edu/projects/478140246',
        image: {
          name: 'scratch-programming/projects/project-4',
          alt: 'Talking duck',
        },
      },
      {
        category: 'Scratch Project',
        title: 'Sibling conversation',
        studentName: 'Omee',
        studentGrade: 'Grade 7',
        link: 'https://scratch.mit.edu/projects/508130300',
        image: {
          name: 'scratch-programming/projects/project-5',
          alt: 'Sibling conversation',
        },
      },
    ],
  },
  PYTHON_FOR_KIDS: {
    heading: 'Amazing projects our students have created through our program',
    contents: [
      {
        category: 'Python Project',
        title: 'Triple number swap',
        studentName: 'Krishna Ajay Jeswani',
        studentGrade: 'Grade 9',
        link: 'https://replit.com/@Gerumpity/Triple-Number-Swap#main.py',
        image: {
          name: 'python-for-kids/projects/project-1',
          alt: 'Triple number swap',
        },
      },
      {
        category: 'Python Project',
        title: 'Mirrored Triangle',
        studentName: 'Danish Piroozmand',
        studentGrade: 'Grade 7',
        link: 'https://replit.com/@DanishPiroozman/Mirrored-Triangle?v=1',
        image: {
          name: 'python-for-kids/projects/project-2',
          alt: 'Mirrored Triangle',
        },
      },
      {
        category: 'Python Project',
        title: 'Random Password challenge',
        studentName: 'Reyan Gupta',
        studentGrade: 'Grade 9',
        link: 'https://replit.com/@RebellionStrike/random-password-normal?v=1',
        image: {
          name: 'python-for-kids/projects/project-3',
          alt: 'Random Password challenge',
        },
      },
      {
        category: 'Python Project',
        title: 'Rock paper scissor',
        studentName: 'Piyush Kushwaha',
        studentGrade: 'Grade 10',
        link:
          'https://replit.com/@gamer-boy-piyus/MeaslyProfuseCloudcomputing?v=1',
        image: {
          name: 'python-for-kids/projects/project-4',
          alt: 'Rock paper scissor',
        },
      },
      {
        category: 'Python Project',
        title: 'Jumpy connections',
        studentName: 'Yashneil Tripathi',
        studentGrade: 'Grade 9',
        link: 'https://replit.com/@YashneilPlays/PinkJumpyConnections?v=1',
        image: {
          name: 'python-for-kids/projects/project-5',
          alt: 'Jumpy connections',
        },
      },
      {
        category: 'Python Project',
        title: 'Age calculator',
        studentName: 'Rajan Yadav',
        studentGrade: 'Grade 8',
        link: 'https://replit.com/@RajanYadav1/Age-Calculator-1?v=1',
        image: {
          name: 'python-for-kids/projects/project-6',
          alt: 'Age calculator',
        },
      },
    ],
  },
  ROBLOX: {
    heading: 'Our students created some amazing projects',
    contents: [
      {
        category: 'Roblox Project',
        title: 'Race with speed',
        studentName: 'Mike',
        studentGrade: 'Grade 5',
        image: {
          name: 'roblox/projects/project-1',
          alt: 'Race with speed',
        },
      },
      {
        category: 'Roblox Project',
        title: 'Pizza land',
        studentName: 'Onyx Bank',
        studentGrade: 'Grade 4',
        image: {
          name: 'roblox/projects/project-2',
          alt: 'Pizza land',
        },
      },
      {
        category: 'Roblox Project',
        title: 'Hide and seek',
        studentName: 'Owen Sameul',
        studentGrade: 'Grade 5',
        image: {
          name: 'roblox/projects/project-3',
          alt: 'Hide and seek',
        },
      },
      {
        category: 'Roblox Project',
        title: 'Obby game',
        studentName: 'Tala Daniel',
        studentGrade: 'Grade 8',
        image: {
          name: 'roblox/projects/project-4',
          alt: 'Obby game',
        },
      },
      {
        category: 'Roblox Project',
        title: 'Rainbow stairs',
        studentName: 'Flora Navy',
        studentGrade: 'Grade 7',
        image: {
          name: 'roblox/projects/project-5',
          alt: 'Rainbow stairs',
        },
      },
      {
        category: 'Roblox Project',
        title: 'Amusement park',
        studentName: 'Neha',
        studentGrade: 'Grade 7',
        image: {
          name: 'roblox/projects/project-6',
          alt: 'Amusement park',
        },
      },
    ],
  },
  AP_COMPUTER_SCIENCE_A: {
    heading: 'Our students created some amazing projects',
    contents: [
      {
        category: 'Java Project',
        title: 'Mind Riddler',
        studentName: 'Jack Grayson',
        studentGrade: 'Grade 9',
        image: {
          name: 'ap-computer-science-a/projects/project-1',
          alt: 'Mind Riddler',
        },
      },
      {
        category: 'Java Project',
        title: 'Tower of Hanoi',
        studentName: 'Luca  Jaxon',
        studentGrade: 'Grade 10',
        image: {
          name: 'ap-computer-science-a/projects/project-2',
          alt: 'Tower of Hanoi',
        },
      },
      {
        category: 'Java Project',
        title: 'GetSet',
        studentName: 'Indigo Caleb',
        studentGrade: 'Grade 11',
        image: {
          name: 'ap-computer-science-a/projects/project-3',
          alt: 'GetSet',
        },
      },
      {
        category: 'Java Project',
        title: 'Access Me',
        studentName: 'Savannah',
        studentGrade: 'Grade 12',
        image: {
          name: 'ap-computer-science-a/projects/project-4',
          alt: 'Access Me',
        },
      },
    ],
  },
}

type MasterClassesType = Record<
  CourseNameType,
  {
    heading: string
    contents: {
      title: string
      link: string
      image: {
        name: string
        alt: string
      }
    }[]
  }
>
export const MASTER_CLASSES: MasterClassesType = {
  SCRATCH_PROGRAMMING: {
    heading: 'Scratch programming masterclasses',
    contents: [
      {
        title: 'Halloween Costume Game on Scratch',
        link:
          'https://www.codingal.com/coding-for-kids/masterclasses/scratch-game-halloween/',
        image: {
          name: '/scratch-programming/masterclasses/masterclass-1',
          alt: 'Halloween Costume Game on Scratch',
        },
      },
      {
        title: 'Animation and Game Design Masterclass',
        link:
          'https://www.codingal.com/coding-for-kids/masterclasses/animation-and-game-design-masterclass/',
        image: {
          name: '/scratch-programming/masterclasses/masterclass-2',
          alt: 'Animation and Game Design Masterclass',
        },
      },
      {
        title: 'Dance Party with Spiderman using Scratch Programming',
        link:
          'https://www.codingal.com/coding-for-kids/masterclasses/dance-party-with-spiderman-using-scratch-programming/',
        image: {
          name: '/scratch-programming/masterclasses/masterclass-3',
          alt: 'Dance Party with Spiderman using Scratch Programming',
        },
      },
      {
        title: 'Captain America Game using Scratch Programming',
        link:
          'https://www.codingal.com/coding-for-kids/masterclasses/building-a-captain-america-game-on-scratch/',
        image: {
          name: '/scratch-programming/masterclasses/masterclass-4',
          alt: 'Captain America Game using Scratch Programming',
        },
      },
    ],
  },
  PYTHON_FOR_KIDS: {
    heading: 'Python for Kids masterclasses',
    contents: [
      {
        title: 'Create a healthcare application using Python',
        link: 'https://www.youtube.com/watch?v=lQqKbxl5-60&t=298s',
        image: {
          name: '/python-for-kids/masterclasses/masterclass-1',
          alt: 'Create a healthcare application using Python',
        },
      },
      {
        title: 'BMI calculator using Python',
        link: 'https://www.youtube.com/watch?v=KX0go_va6HM&t=1125s',
        image: {
          name: '/python-for-kids/masterclasses/masterclass-2',
          alt: 'BMI calculator using Python',
        },
      },
    ],
  },
}

export const VIDEO_RESOURCES: MasterClassesType = {
  SCRATCH_PROGRAMMING: {
    heading:
      'Refresh your knowledge with these informative concept-based videos',
    contents: [
      {
        title:
          'Learn to build a solar system project using Scratch | Scratch tutorial',
        link: 'https://www.youtube.com/watch?v=Fu9gKxxXfgo',
        image: {
          name: '/scratch-programming/video-resources/video-1',
          alt:
            'Learn to build a solar system project using Scratch | Scratch tutorial',
        },
      },
      {
        title: 'Coding for Kids: An Introduction',
        link: 'https://www.youtube.com/watch?v=MGpT8rXRCf8&t=19s',
        image: {
          name: '/scratch-programming/video-resources/video-2',
          alt: 'Coding for Kids: An Introduction',
        },
      },
      {
        title: '5 Best Free Platforms for Kids to Learn to Code',
        link: 'https://www.youtube.com/watch?v=lsCObbRG370&t=29s',
        image: {
          name: '/scratch-programming/video-resources/video-3',
          alt: '5 Best Free Platforms for Kids to Learn to Code',
        },
      },
    ],
  },
  PYTHON_FOR_KIDS: {
    heading:
      'Refresh your knowledge about Python for Kids and Teens with these informative concept-based videos',
    contents: [
      {
        title: 'Types of coding: Block-based and text-based coding',
        link: 'https://www.youtube.com/watch?v=PI5yZGqGRWI',
        image: {
          name: '/python-for-kids/video-resources/video-1',
          alt: 'Types of coding: Block-based and text-based coding',
        },
      },
      {
        title: 'Coding for Kids: An Introduction',
        link: 'https://www.youtube.com/watch?v=MGpT8rXRCf8&t=19s',
        image: {
          name: '/python-for-kids/video-resources/video-2',
          alt: 'Coding for Kids: An Introduction',
        },
      },
      {
        title: 'Top 8 programming languages for kids',
        link: 'https://www.youtube.com/watch?v=-cSRp_T1Mu0',
        image: {
          name: '/python-for-kids/video-resources/video-3',
          alt: 'Top 8 programming languages for kids',
        },
      },
    ],
  },
}

type CourseQuizType = Record<
  CourseNameType,
  {
    heading: string
    contents: {
      title: string
      link: string
      grade: string
      totalProblems: string
      level: string
      tag: string
      image: {
        name: string
        alt: string
      }
    }[]
  }
>
export const COURSE_QUIZZES: CourseQuizType = {
  PYTHON_FOR_KIDS: {
    heading: 'Assess your knowledge with fun quizzes',
    contents: [
      {
        title: 'Fundamentals of Python Programming',
        link:
          'https://www.codingal.com/quizzes/fundamentals-of-python-programming/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Python',
        image: {
          name: '/python-for-kids/quizzes/quiz-1',
          alt: 'Fundamentals of Python Programming',
        },
      },
      {
        title: 'Loops in Python',
        link: 'https://www.codingal.com/quizzes/loops-in-python/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Python',
        image: {
          name: '/python-for-kids/quizzes/quiz-2',
          alt: 'Loops in Python',
        },
      },
      {
        title: 'Advanced Python Programming',
        link:
          'https://www.codingal.com/quizzes/advanced-python-programming/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Expert',
        tag: 'Python',
        image: {
          name: '/python-for-kids/quizzes/quiz-3',
          alt: 'Advanced Python Programming',
        },
      },
    ],
  },
  SCRATCH_PROGRAMMING: {
    heading: 'Assess your knowledge with fun quizzes',
    contents: [
      {
        title: 'Introduction to Scratch Programming',
        link:
          'https://www.codingal.com/quizzes/introduction-to-scratch-programming/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Scratch',
        image: {
          name: '/scratch-programming/quizzes/quiz-1',
          alt: 'Introduction to Scratch Programming',
        },
      },
      {
        title: 'Scratch intermediate',
        link: 'https://www.codingal.com/quizzes/scratch-intermediate/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Scratch',
        image: {
          name: '/scratch-programming/quizzes/quiz-2',
          alt: 'Scratch intermediate',
        },
      },
    ],
  },
  JAVASCRIPT: {
    heading: 'Assess your knowledge with fun quizzes',
    contents: [
      {
        title: 'Introduction to JavaScript',
        link: 'https://www.codingal.com/quizzes/introduction-to-js/arena/',
        grade: '9-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'JavaScript',
        image: {
          name: '/javascript/quizzes/quiz-1',
          alt: 'Introduction to JavaScript',
        },
      },
    ],
  },
  PUBLIC_SPEAKING_GRADE_3_5: {
    heading:
      'Assess your Public Speaking & Communication skills with fun quizzes',
    contents: [
      {
        title: 'Introduction to public speaking',
        link:
          'https://www.codingal.com/quizzes/introduction-to-public-speaking/arena/',
        grade: '3-5',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Public speaking',
        image: {
          name: '/public-speaking-and-communication/quizzes/quiz-4',
          alt: 'Introduction to public speaking',
        },
      },
      {
        title: 'Voice modulation and body language',
        link:
          'https://www.codingal.com/quizzes/voice-modulation-and-body-language/arena/',
        grade: '3-5',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Public speaking',
        image: {
          name: '/public-speaking-and-communication/quizzes/quiz-5',
          alt: 'Voice modulation and body language',
        },
      },
      {
        title: 'Presentation structure and storytelling',
        link:
          'https://www.codingal.com/quizzes/presentation-structure-and-storytelling/arena/',
        grade: '3-5',
        totalProblems: '10',
        level: 'Expert',
        tag: 'Public speaking',
        image: {
          name: '/public-speaking-and-communication/quizzes/quiz-6',
          alt: 'Presentation structure and storytelling',
        },
      },
    ],
  },
  PUBLIC_SPEAKING_GRADE_1_2: {
    heading:
      'Assess your Public Speaking & Communication skills with fun quizzes',
    contents: [
      {
        title: 'Speak & Smile',
        link: 'https://www.codingal.com/quizzes/speak-smile/arena/',
        grade: '1-2',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Public speaking',
        image: {
          name: '/public-speaking-and-communication/quizzes/quiz-1',
          alt: 'Speak & Smile',
        },
      },
      {
        title: 'My first speaking stars',
        link: 'https://www.codingal.com/quizzes/my-first-speaking-stars/arena/',
        grade: '1-2',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Public speaking',
        image: {
          name: '/public-speaking-and-communication/quizzes/quiz-2',
          alt: 'My first speaking stars',
        },
      },
      {
        title: 'Tiny Talkers',
        link: 'https://www.codingal.com/quizzes/tiny-talkers/arena/',
        grade: '1-2',
        totalProblems: '10',
        level: 'Expert',
        tag: 'Public speaking',
        image: {
          name: '/public-speaking-and-communication/quizzes/quiz-3',
          alt: 'Tiny Talkers',
        },
      },
    ],
  },
  CODING_GRADE_1_3: {
    heading: 'Test your coding skills with fun, beginner-friendly quizzes',
    contents: [
      {
        title: 'Teachable Machine: Teach Your Computer',
        link:
          'https://www.codingal.com/quizzes/teachable-machine-teach-your-computer/arena/',
        grade: '1-6',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'AI',
        image: {
          name:
            '/coding-for-kids/quizzes/teachable-machine-teach-your-computer',
          alt: 'Teachable Machine: Teach Your Computer',
        },
      },
      {
        title: 'Scratch Intermediate',
        link: 'https://www.codingal.com/quizzes/scratch-intermediate/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Scratch',
        image: {
          name: '/coding-for-kids/quizzes/scratch-intermediate',
          alt: 'Scratch Intermediate',
        },
      },
      {
        title: 'Turtle Programming Basics',
        link:
          'https://www.codingal.com/quizzes/turtle-programming-basics/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Turtle',
        image: {
          name: '/coding-for-kids/quizzes/turtle-programming-basics',
          alt: 'Turtle Programming Basics',
        },
      },
      {
        title: 'Motion Detection in AI',
        link: 'https://www.codingal.com/quizzes/motion-detection-in-ai/arena/',
        grade: '1-6',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'AI',
        image: {
          name: '/coding-for-kids/quizzes/motion-detection-in-ai',
          alt: 'Motion Detection in AI',
        },
      },
      {
        title: 'AI Code 101: What is a Neural Network?',
        link:
          'https://www.codingal.com/quizzes/ai-code-101-what-is-a-neural-network/arena/',
        grade: '1-6',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'AI',
        image: {
          name: '/coding-for-kids/quizzes/ai-code-101-what-is-a-neural-network',
          alt: 'AI Code 101: What is a Neural Network?',
        },
      },
      {
        title: 'Scratch: Using Variables and Loops',
        link:
          'https://www.codingal.com/quizzes/scratch-using-variables-and-loops/arena/',
        grade: '1-6',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Scratch',
        image: {
          name: '/coding-for-kids/quizzes/scratch-using-variables-and-loops',
          alt: 'Scratch: Using Variables and Loops',
        },
      },
      {
        title: 'Getting Started with Code.org',
        link:
          'https://www.codingal.com/quizzes/getting-started-with-codeorg/arena/',
        grade: '1-6',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Code.org',
        image: {
          name: '/coding-for-kids/quizzes/getting-started-with-codeorg',
          alt: 'Getting Started with Code.org',
        },
      },
      {
        title: 'Basic CSS: Styling Your Webpage',
        link:
          'https://www.codingal.com/quizzes/basic-css-styling-your-webpage/arena/',
        grade: '1-6',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'CSS',
        image: {
          name: '/coding-for-kids/quizzes/basic-css-styling-your-webpage',
          alt: 'Basic CSS: Styling Your Webpage',
        },
      },
      {
        title: 'Advanced Game Development',
        link:
          'https://www.codingal.com/quizzes/advanced-game-development/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Game Lab',
        image: {
          name: '/coding-for-kids/quizzes/advanced-game-development',
          alt: 'Advanced Game Development',
        },
      },
    ],
  },
  CODING_GRADE_4_5: {
    heading: 'Assess your coding skills with fun quizzes',
    contents: [
      {
        title: 'Introduction to Scratch Programming',
        link:
          'https://www.codingal.com/quizzes/introduction-to-scratch-programming-2/arena/',
        grade: '1-6',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Scratch',
        image: {
          name:
            '/coding-for-kids/quizzes/introduction-to-scratch-programming-2',
          alt: 'Introduction to Scratch Programming',
        },
      },
      {
        title: 'What is Artificial Intelligence?',
        link:
          'https://www.codingal.com/quizzes/what-is-artificial-intelligence/arena/',
        grade: '1-6',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'AI',
        image: {
          name: '/coding-for-kids/quizzes/what-is-artificial-intelligence',
          alt: 'What is Artificial Intelligence?',
        },
      },
      {
        title: 'Basic CSS: Styling Your Webpage',
        link:
          'https://www.codingal.com/quizzes/basic-css-styling-your-webpage/arena/',
        grade: '1-6',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'CSS',
        image: {
          name: '/coding-for-kids/quizzes/basic-css-styling-your-webpage',
          alt: 'Basic CSS: Styling Your Webpage',
        },
      },
      {
        title: 'Scratch: Using Variables and Loops',
        link:
          'https://www.codingal.com/quizzes/scratch-using-variables-and-loops/arena/',
        grade: '1-6',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Scratch',
        image: {
          name: '/coding-for-kids/quizzes/scratch-using-variables-and-loops',
          alt: 'Scratch: Using Variables and Loops',
        },
      },
      {
        title: 'Advanced Game Development',
        link:
          'https://www.codingal.com/quizzes/advanced-game-development/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Game Lab',
        image: {
          name: '/coding-for-kids/quizzes/advanced-game-development',
          alt: 'Advanced Game Development',
        },
      },
      {
        title: 'Scratch intermediate',
        link: 'https://www.codingal.com/quizzes/scratch-intermediate/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Scratch',
        image: {
          name: '/coding-for-kids/quizzes/scratch-intermediate',
          alt: 'Scratch intermediate',
        },
      },
    ],
  },
  CODING_GRADE_6_8: {
    heading: 'Assess your coding skills with fun quizzes',
    contents: [
      {
        title: 'Programming basics',
        link: 'https://www.codingal.com/quizzes/programming-basics/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Programming',
        image: {
          name: '/coding-for-kids/quizzes/programming-basics',
          alt: 'Programming basics',
        },
      },
      {
        title: 'Introduction to Website Development',
        link:
          'https://www.codingal.com/quizzes/introduction-to-website-development/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'HTML',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-website-development',
          alt: 'Introduction to Website Development',
        },
      },
      {
        title: 'Turtle Programming Basics',
        link:
          'https://www.codingal.com/quizzes/turtle-programming-basics/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Turtle',
        image: {
          name: '/coding-for-kids/quizzes/turtle-programming-basics',
          alt: 'Turtle Programming Basics',
        },
      },
      {
        title: 'Fundamentals of Python Programming',
        link:
          'https://www.codingal.com/quizzes/fundamentals-of-python-programming/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Python',
        image: {
          name: '/python-for-kids/quizzes/quiz-1',
          alt: 'Fundamentals of Python Programming',
        },
      },
      {
        title: 'Basics of HTML',
        link: 'https://www.codingal.com/quizzes/basics-of-html/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'HTML',
        image: {
          name: '/coding-for-kids/quizzes/basics-of-html',
          alt: 'Basics of HTML',
        },
      },
      {
        title: 'Introduction to Python: Object-Oriented Programming Basics',
        link:
          'https://www.codingal.com/quizzes/introduction-to-python-object-oriented-programming-basics/arena/',
        grade: '7-12',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Python',
        image: {
          name:
            '/coding-for-kids/quizzes/introduction-to-python-object-oriented-programming-basics',
          alt: 'Introduction to Python: Object-Oriented Programming Basics',
        },
      },
      {
        title: 'Introduction to Game Development',
        link:
          'https://www.codingal.com/quizzes/introduction-to-game-development/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Play Lab',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-game-development',
          alt: 'Introduction to Game Development',
        },
      },
      {
        title: 'Understanding ChatGPT and Language Models',
        link:
          'https://www.codingal.com/quizzes/understanding-chatgpt-and-language-models/arena/',
        grade: '7-12',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'AI',
        image: {
          name:
            '/coding-for-kids/quizzes/understanding-chatgpt-and-language-models',
          alt: 'Understanding ChatGPT and Language Models',
        },
      },
      {
        title: 'Introduction to Data Structures',
        link:
          'https://www.codingal.com/quizzes/introduction-to-data-structures/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Data Structures',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-data-structures',
          alt: 'Introduction to Data Structures',
        },
      },
      {
        title: 'Loops in Python',
        link: 'https://www.codingal.com/quizzes/loops-in-python/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Python',
        image: {
          name: '/python-for-kids/quizzes/quiz-2',
          alt: 'Loops in Python',
        },
      },
      {
        title: 'Advance Application Development',
        link:
          'https://www.codingal.com/quizzes/advance-application-development/arena/',
        grade: '4-8',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Thunkable',
        image: {
          name: '/coding-for-kids/quizzes/advance-application-development',
          alt: 'Advance Application Development',
        },
      },
      {
        title: 'Turtle Programming Advance',
        link:
          'https://www.codingal.com/quizzes/turtle-programming-advance/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Turtle',
        image: {
          name: '/coding-for-kids/quizzes/turtle-programming-advance',
          alt: 'Turtle Programming Advance',
        },
      },
      {
        title: 'Scratch intermediate',
        link: 'https://www.codingal.com/quizzes/scratch-intermediate/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Scratch',
        image: {
          name: '/coding-for-kids/quizzes/scratch-intermediate',
          alt: 'Scratch intermediate',
        },
      },
      {
        title: 'Advanced Python Programming',
        link:
          'https://www.codingal.com/quizzes/advanced-python-programming/arena/',
        grade: '4-12',
        totalProblems: '10',
        level: 'Expert',
        tag: 'Python',
        image: {
          name: '/coding-for-kids/quizzes/advanced-python-programming',
          alt: 'Advanced Python Programming',
        },
      },
    ],
  },
  CODING_GRADE_9_10: {
    heading: 'Assess your coding skills with fun quizzes',
    contents: [
      {
        title: 'Introduction to Java Programming',
        link:
          'https://www.codingal.com/quizzes/introduction-to-java-programming/arena/',
        grade: '9-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Java',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-java-programming',
          alt: 'Introduction to Java Programming',
        },
      },
      {
        title: 'Machine learning Intermediate',
        link:
          'https://www.codingal.com/quizzes/machine-learning-intermediate/arena/',
        grade: '9-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Machine Learning',
        image: {
          name: '/coding-for-kids/quizzes/machine-learning-intermediate',
          alt: 'Machine learning Intermediate',
        },
      },
      {
        title: 'Introduction to Github',
        link: 'https://www.codingal.com/quizzes/introduction-to-github/arena/',
        grade: '9-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Git',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-github',
          alt: 'Introduction to Github',
        },
      },
      {
        title: 'Introduction to Database using SQL',
        link:
          'https://www.codingal.com/quizzes/introduction-to-database-using-sql/arena/',
        grade: '9-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'SQL',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-database-using-sql',
          alt: 'Introduction to Database using SQL',
        },
      },
      {
        title: 'Introduction to JavaScript',
        link: 'https://www.codingal.com/quizzes/introduction-to-js/arena/',
        grade: '9-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'JavaScript',
        image: {
          name: '/javascript/quizzes/quiz-1',
          alt: 'Introduction to JavaScript',
        },
      },
      {
        title: 'Introduction to Algorithms',
        link:
          'https://www.codingal.com/quizzes/introduction-to-algorithms/arena/',
        grade: '9-12',
        totalProblems: '10',
        level: 'Expert',
        tag: 'Algorithms',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-algorithms',
          alt: 'Introduction to Algorithms',
        },
      },
    ],
  },
  CODING_GRADE_11_12: {
    heading: 'Assess your coding skills with fun quizzes',
    contents: [
      {
        title: 'Fundamentals of Python Programming',
        link:
          'https://www.codingal.com/quizzes/fundamentals-of-python-programming/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Programming',
        image: {
          name: '/python-for-kids/quizzes/quiz-1',
          alt: 'Fundamentals of Python Programming',
        },
      },
      {
        title: 'Introduction to Artificial Intelligence',
        link: 'https://www.codingal.com/quizzes/introduction-to-ai/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Artificial Intelligence',
        image: {
          name:
            '/coding-for-kids/quizzes/introduction-to-artificial-intelligence',
          alt: 'Introduction to Artificial Intelligence',
        },
      },
      {
        title: 'Understanding ChatGPT and Language Models',
        link:
          'https://www.codingal.com/quizzes/understanding-chatgpt-and-language-models/arena/',
        grade: '7-12',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'AI',
        image: {
          name:
            '/coding-for-kids/quizzes/understanding-chatgpt-and-language-models',
          alt: 'Understanding ChatGPT and Language Models',
        },
      },
      {
        title: 'Artificial Intelligence Intermediate',
        link:
          'https://www.codingal.com/quizzes/artificial-intelligence-intermediate/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'AI',
        image: {
          name: '/coding-for-kids/quizzes/artificial-intelligence-intermediate',
          alt: 'Artificial Intelligence Intermediate',
        },
      },
      {
        title: 'Introduction to Data Structures',
        link:
          'https://www.codingal.com/quizzes/introduction-to-data-structures/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Data Structures',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-data-structures',
          alt: 'Introduction to Data Structures',
        },
      },
      {
        title: 'Machine learning Intermediate',
        link:
          'https://www.codingal.com/quizzes/machine-learning-intermediate/arena/',
        grade: '9-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Machine Learning',
        image: {
          name: '/coding-for-kids/quizzes/machine-learning-intermediate',
          alt: 'Machine learning Intermediate',
        },
      },
      {
        title: 'Loops in Python',
        link: 'https://www.codingal.com/quizzes/loops-in-python/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Python',
        image: {
          name: '/python-for-kids/quizzes/quiz-2',
          alt: 'Loops in Python',
        },
      },
      {
        title: 'Introduction to Github',
        link: 'https://www.codingal.com/quizzes/introduction-to-github/arena/',
        grade: '7-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Git',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-github',
          alt: 'Introduction to Github',
        },
      },
      {
        title: 'Introduction to Java Programming',
        link:
          'https://www.codingal.com/quizzes/introduction-to-java-programming/arena/',
        grade: '9-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Java',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-java-programming',
          alt: 'Introduction to Java Programming',
        },
      },
      {
        title: 'Introduction to AI Ethics & Bias',
        link:
          'https://www.codingal.com/quizzes/introduction-to-ai-ethics-bias/arena/',
        grade: '7-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'AI',
        image: {
          name: '/coding-for-kids/quizzes/understanding-ai-ethics-bias',
          alt: 'Introduction to AI Ethics & Bias',
        },
      },
      {
        title: 'Introduction to Algorithms',
        link:
          'https://www.codingal.com/quizzes/introduction-to-algorithms/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Expert',
        tag: 'Algorithms',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-algorithms',
          alt: 'Introduction to Algorithms',
        },
      },
      {
        title: 'Advance Website Development',
        link:
          'https://www.codingal.com/quizzes/advance-website-development/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'CSS',
        image: {
          name: '/coding-for-kids/quizzes/advance-website-development',
          alt: 'Advance Website Development',
        },
      },
      {
        title: 'Introduction to Database using SQL',
        link:
          'https://www.codingal.com/quizzes/introduction-to-database-using-sql/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'SQL',
        image: {
          name: '/coding-for-kids/quizzes/introduction-to-database-using-sql',
          alt: 'Introduction to Database using SQL',
        },
      },
      {
        title: 'Introduction to JavaScript',
        link: 'https://www.codingal.com/quizzes/introduction-to-js/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'JavaScript',
        image: {
          name: '/javascript/quizzes/quiz-1',
          alt: 'Introduction to JavaScript',
        },
      },
    ],
  },
}
export const RECOMMENDED_TOPICS = [
  {
    topic: 'Scratch programming course',
    link: '/courses/scratch-programming/',
    image: 'scratch-page.jpg',
  },
  {
    topic: 'Python for Kids course',
    link: '/courses/python-for-kids/',
    image: 'python-page.jpg',
    isBestSeller: true,
  },
  {
    topic: 'Coding for kids free resource',
    link: '/coding-for-kids/',
    image: 'coding-for-kids.jpg',
  },
]
